// src/pages/Vigilancia.js - Force Rebuild: 2026-03-21 17:10
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import jsQR from 'jsqr';
import ConfirmDialog from '../components/ConfirmDialog';
import CapturaImagenesVisita from '../components/CapturaImagenesVisita';
import CapturaEvidenciaEntrega from '../components/CapturaEvidenciaEntrega';
import SuccessModal from '../components/SuccessModal';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    getDocsFromCache,
    onSnapshot,
    query,
    serverTimestamp,
    Timestamp,
    where,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    limit,
    orderBy,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
    deleteObject,
} from 'firebase/storage';
import {
    FaQrcode,
    FaBoxOpen,
    FaTruck,
    FaUsers,
    FaCalendarAlt,
    FaExclamationTriangle,
    FaClock,
    FaUserShield,
    FaCheckCircle,
    FaSearch,
    FaPlus,
    FaInfoCircle,
    FaEdit,
    FaTrash,
    FaTimes,
    FaIdCard,
    FaTools,
    FaHardHat,
    FaHistory,
    FaDownload,
    FaCamera,
    FaTimesCircle,
    FaQuestionCircle,
    FaArrowLeft,
    FaCar,
    FaWarehouse,
    FaClipboardCheck,
    FaChevronDown,
    FaChevronUp,
    FaChevronLeft,
    FaChevronRight,
    FaPrint,
    FaUser,
    FaPhone,
    FaHome,
    FaTag,
    FaPalette,
    FaUserCheck,
    FaUserFriends,
    FaEnvelope,
    FaClipboard,
    FaExclamationCircle,
    FaLock,
    FaDoorOpen,
    FaUnlock,
    FaSync,
    FaUnlink,
    FaWhatsapp,
    FaShareAlt,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { db, storage, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useModulos } from '../hooks/useModulos';
import { useRestriccionMorosidad } from '../hooks/useRestriccionMorosidad';
import { registerMovement } from '../utils/registerMovement';
import { validateQrOffline } from '../utils/offlineValidator';
import { pushToSyncQueue, processSyncQueue, getSyncQueue } from '../utils/offlineSyncQueue';
import './Vigilancia.css';
import './VigilanciaMobile.css';
import './VigilanciaPaqueteriaMobile.css';
import { saveImageToDevice, isNativePlatform } from '../utils/mobileMedia';
import { Share } from '@capacitor/share';

const buildKeySet = (raw) => {
    if (!raw) {
        return new Set();
    }
    const str = String(raw).trim();
    if (!str) {
        return new Set();
    }
    const normalized = str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    const variants = [
        str,
        str.toUpperCase(),
        str.toLowerCase(),
        str.replace(/\s+/g, ''),
        str.replace(/\s+/g, '').toUpperCase(),
        str.replace(/\s+/g, '').toLowerCase(),
        normalized,
        normalized.toUpperCase(),
        normalized.toLowerCase(),
        normalized.replace(/\s+/g, ''),
        normalized.replace(/\s+/g, '').toUpperCase(),
        normalized.replace(/\s+/g, '').toLowerCase(),
    ];
    return new Set(variants.filter((value) => value));
};

const extractUserKeys = (data) => {
    if (!data || typeof data !== 'object') {
        return new Set();
    }
    const rawCandidates = [
        data.numeroCasa,
        data.numeroLote,
        data.lote,
        data.houseLabel,
        data.departamento,
        data.casaAsignada?.numero,
        data.casa?.numero,
    ];
    const keys = new Set();
    rawCandidates.forEach((candidate) => {
        const set = buildKeySet(candidate);
        set.forEach((value) => keys.add(value));
    });
    return keys;
};

const buildNameFromParts = (parts) => parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0)
    .join(' ')
    .trim();

const extractResidentDisplayName = (data) => {
    if (!data || typeof data !== 'object') {
        return '';
    }

    const compositeNames = [
        buildNameFromParts([data.nombre, data.apellidos]),
        buildNameFromParts([data.nombre, data.apellidoPaterno, data.apellidoMaterno]),
        buildNameFromParts([data.nombres, data.apellidoPaterno, data.apellidoMaterno]),
        buildNameFromParts([data.propietarioNombre, data.propietarioApellidos]),
        buildNameFromParts([data.nombrePropietario, data.apellidoPropietario]),
    ];

    const candidates = [
        data.nombreCompleto,
        data.displayName,
        data.nombre,
        data.fullName,
        data.nombreUsuario,
        data.propietarioNombre,
        data.residenteNombre,
        data.duenoNombre,
        data.contacto?.nombre,
        data.titularNombre,
        ...compositeNames,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'string') {
            const trimmed = candidate.trim();
            if (trimmed) {
                return trimmed;
            }
        }
    }

    if (typeof data.email === 'string' && data.email.trim()) {
        return data.email.trim();
    }

    return '';
};

const toDateSafe = (value) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.valueOf()) ? null : value;
    }
    if (typeof value.toDate === 'function') {
        const date = value.toDate();
        return Number.isNaN(date.valueOf()) ? null : date;
    }
    return null;
};

const formatResidentPackageDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
    if (Number.isNaN(d.getTime())) return '';
    
    return d.toLocaleDateString('es-MX', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
    }) + ' - ' + d.toLocaleTimeString('es-MX', { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true 
    });
};

const toTitleCase = (value) => {
    if (!value) {
        return '';
    }
    return String(value)
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .map((chunk) => {
            const trimmed = chunk.trim();
            if (!trimmed) {
                return '';
            }
            return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
        })
        .filter(Boolean)
        .join(' ');
};

const VISITA_CATEGORIES = [
    { value: 'visita', label: 'Visita' },
    { value: 'proveedor', label: 'Proveedor' },
    { value: 'personal_limpieza', label: 'Personal de limpieza' },
    { value: 'servicio', label: 'Servicio' },
    { value: 'familia', label: 'Familiar' },
    { value: 'chofer', label: 'Chofer' },
];

const VEHICLE_COLORS = [
    'Blanco',
    'Negro',
    'Gris',
    'Plata',
    'Azul',
    'Rojo',
    'Verde',
    'Amarillo',
    'Café',
    'Beige',
    'Naranja',
    'Morado',
    'Otro',
];

const ACCESS_STATUS_OPTIONS = [
    { value: 'dentro', label: 'Dentro del fraccionamiento' },
    { value: 'fuera', label: 'Salida registrada' },
    { value: 'todos', label: 'Todos' },
];


const CATEGORY_STYLES = {
    visita: { bg: '#dbeafe', color: '#1e40af' }, // Blue
    proveedor: { bg: '#ffedd5', color: '#9a3412' }, // Orange
    personal_limpieza: { bg: '#dcfce7', color: '#166534' }, // Green
    servicio: { bg: '#f3e8ff', color: '#6b21a8' }, // Purple
    familia: { bg: '#fce7f3', color: '#9d174d' }, // Pink
    chofer: { bg: '#e5e7eb', color: '#374151' }, // Gray
    otro: { bg: '#f1f5f9', color: '#475569' } // Slate
};

const VEHICLE_COLOR_STYLES = {
    'Blanco': { bg: '#ffffff', color: '#0f172a', border: '1px solid #e2e8f0' },
    'Negro': { bg: '#0f172a', color: '#ffffff' },
    'Gris': { bg: '#64748b', color: '#ffffff' },
    'Plata': { bg: '#94a3b8', color: '#0f172a' },
    'Azul': { bg: '#3b82f6', color: '#ffffff' },
    'Rojo': { bg: '#ef4444', color: '#ffffff' },
    'Verde': { bg: '#10b981', color: '#ffffff' },
    'Amarillo': { bg: '#eab308', color: '#0f172a' },
    'Café': { bg: '#78350f', color: '#ffffff' },
    'Beige': { bg: '#f5f5dc', color: '#0f172a' },
    'Naranja': { bg: '#f97316', color: '#ffffff' },
    'Morado': { bg: '#a855f7', color: '#ffffff' },
    'Otro': { bg: '#f1f5f9', color: '#475569' }
};

const MAX_VISITAS_POR_CATEGORIA = 2;
const MAX_VISITA_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const INITIAL_HORARIO = {
    lunes: [],
    martes: [],
    miercoles: [],
    jueves: [],
    viernes: [],
    sabado: [],
    domingo: []
};

const DIAS_SEMANA_ORDEN = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const HOUR_OPTIONS = Array.from({ length: 17 }, (_, i) => {
    const hour = i + 6;
    return `${hour.toString().padStart(2, '0')}:00`;
});

// Función helper para obtener el label de una categoría
// Función helper para obtener el label de una categoría
const formatCategoriaLabel = (categoriaValue) => {
    if (!categoriaValue) return 'Sin categoría';
    const match = VISITA_CATEGORIES.find(cat => cat.value === categoriaValue);
    if (match) return match.label;
    
    // Capitalización básica para valores no encontrados
    const normalized = String(categoriaValue);
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

// Helper para renderizar un resumen legible del horario
const renderScheduleSummary = (horario) => {
    if (!horario) return null;
    
    const dayShorts = {
        lunes: 'Lun',
        martes: 'Mar',
        miercoles: 'Mié',
        jueves: 'Jue',
        viernes: 'Vie',
        sabado: 'Sáb',
        domingo: 'Dom'
    };

    const activeDays = DIAS_SEMANA_ORDEN
        .filter(dia => Array.isArray(horario[dia]) && horario[dia].length > 0)
        .map(dia => [dia, horario[dia]]);

    if (activeDays.length === 0) return null;

    return (
        <div className="visita-card-schedule" style={{ 
            marginTop: '12px', 
            padding: '10px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
        }}>
            <div className="schedule-summary-title" style={{ 
                fontSize: '0.8rem', 
                fontWeight: '800', 
                color: '#64748b', 
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
            }}>
                <FaClock style={{ fontSize: '0.8rem' }} /> Horarios Autorizados
            </div>
            <div className="schedule-days-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', 
                gap: '8px' 
            }}>
                {activeDays.map(([dia, slots]) => (
                    <div key={dia} className="schedule-day-item" style={{ 
                        fontSize: '0.75rem', 
                        color: '#475569' 
                    }}>
                        <span style={{ fontWeight: '700', color: '#1e293b' }}>{dayShorts[dia]}:</span> {slots.map(slot => typeof slot === 'string' ? slot : `${slot.inicio}-${slot.fin}`).join(', ')}
                    </div>
                ))}
            </div>
        </div>
    );
};


// Helper para renderizar esqueletos de carga Zen durante transiciones tras éxito
const renderZenTransitionSkeleton = () => {
    return (
        <div className="vigilancia-mobile-menu zen-shimmer" style={{ padding: '0 16px' }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="vigilancia-section-card skeleton-card" style={{ 
                    height: '80px', 
                    marginBottom: '16px', 
                    backgroundColor: '#ffffff', 
                    borderRadius: '20px', 
                    border: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 16px',
                    gap: '16px'
                }}>
                    <div className="skeleton-icon" style={{ 
                        width: '45px', 
                        height: '45px', 
                        borderRadius: '14px', 
                        backgroundColor: '#f1f5f9' 
                    }}></div>
                    <div className="section-card-content" style={{ flex: 1 }}>
                        <div className="skeleton-title" style={{ 
                            height: '14px', 
                            width: '40%', 
                            backgroundColor: '#f1f5f9', 
                            borderRadius: '4px', 
                            marginBottom: '6px' 
                        }}></div>
                        <div className="skeleton-description" style={{ 
                            height: '10px', 
                            width: '70%', 
                            backgroundColor: '#f8fafc', 
                            borderRadius: '4px' 
                        }}></div>
                    </div>
                </div>
            ))}
        </div>
    );
};


// Helper stable functions defined outside component to prevent re-creation
const listGuestAccessesFn = httpsCallable(functions, 'listGuestAccesses');
const registerManualGuestAccessFn = httpsCallable(functions, 'registerManualGuestAccess');
const markGuestVisitExitFn = httpsCallable(functions, 'markGuestVisitExit');

const mapAccessRecordFn = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const parseDate = (value) => {
        if (!value) {
            return null;
        }
        const date = new Date(value);
        return Number.isNaN(date.valueOf()) ? null : date;
    };

    return {
        ...raw,
        estadoAcceso: String(raw.estadoAcceso || '').toLowerCase() || 'dentro',
        entradaDate: parseDate(raw.entradaRegistradaIso),
        salidaDate: parseDate(raw.salidaRegistradaIso),
    };
};

const Vigilancia = () => {
    const navigate = useNavigate();
    const { userData, currentUser, casaActiva, misCasas } = useAuth();
    const { showNotification } = useNotification();

    // Efecto para procesar cola offline al reconectar
    useEffect(() => {
        const checkQueue = async () => {
            const queue = getSyncQueue();
            if (navigator.onLine && queue.length > 0 && !currentUser?.isOffline) {
                console.log('[Vigilancia] Conexión detectada y usuario online. Sincronizando cola offline...');
                const res = await processSyncQueue(currentUser);
                if (res.processed > 0) {
                    if (res.success) {
                        showNotification(`Se sincronizaron ${res.processed} movimientos offline exitosamente.`, 'success');
                    } else {
                        showNotification(`Se sincronizaron ${res.processed} movimientos, pero algunos fallaron.`, 'warning');
                    }
                    // Refrescar lista de accesos para ver los registros reales del servidor
                    setTimeout(() => {
                        fetchGuestAccesses({ silent: true });
                    }, 500);
                } else if (!res.success && res.failed > 0) {
                    showNotification(`Faltaron sincronizar ${res.failed} movimientos. Verifica conexión.`, 'warning');
                }
            }
        };

        checkQueue();
        window.addEventListener('online', checkQueue);
        return () => window.removeEventListener('online', checkQueue);
    }, [currentUser, showNotification]);
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const { modulos, loading: modulosLoading } = useModulos(fraccionamientoId);
    const normalizedRole = String(userData?.rol || '').toLowerCase();
    const isResident = normalizedRole === 'user' || normalizedRole === 'residente' || normalizedRole === 'inquilino' || normalizedRole === 'propietario' || normalizedRole === 'habitante';
    const isSuperAdmin = normalizedRole === 'superadmin' || normalizedRole === 'platformadmin' || normalizedRole === 'admin' || normalizedRole === 'administrador' || normalizedRole === 'manager' || normalizedRole === 'admin_fracc';
    const isCaseta = normalizedRole === 'caseta' || normalizedRole === 'guardia' || normalizedRole === 'security' || normalizedRole === 'vigilancia';
    const currentUid = userData?.uid || currentUser?.uid || null;

    const { isTabRestricted, isSubSectionRestricted } = useRestriccionMorosidad();
    const isResidentUser = normalizedRole === 'user' || normalizedRole === 'residente' || normalizedRole === 'inquilino' || normalizedRole === 'propietario';
    const isVigilanciaBloqueada = isResidentUser && isTabRestricted('vigilancia');
    const isQrBloqueado = isResidentUser && (isVigilanciaBloqueada || isSubSectionRestricted('vigilancia', 'generar_qr'));
    const isMisInvitacionesBloqueadas = isResidentUser && (isVigilanciaBloqueada || isSubSectionRestricted('vigilancia', 'mis_invitaciones'));
    const isPaqueteriaBloqueada = isResidentUser && (isVigilanciaBloqueada || isSubSectionRestricted('vigilancia', 'paqueteria'));
    const isVisitasFrecuentesBloqueadas = isResidentUser && (isVigilanciaBloqueada || isSubSectionRestricted('vigilancia', 'visitas_frecuentes'));

    const mostrarAlertaVigilancia = useCallback((mensaje = 'Esta función está temporalmente inhabilitada por adeudo pendiente. Regulariza tu cuenta en Finanzas.') => {
        Swal.fire({
            title: 'Servicio Inhabilitado',
            text: mensaje,
            icon: 'warning',
            confirmButtonText: 'Ir a Finanzas',
            showCancelButton: true,
            cancelButtonText: 'Cerrar',
            confirmButtonColor: '#0284c7'
        }).then((result) => {
            if (result.isConfirmed) {
                navigate('/finanzas');
            }
        });
    }, [navigate]);

    // Estados para Control de Acceso Multi-Panel y Aperturas de Emergencia
    const [controlAccesosConfig, setControlAccesosConfig] = useState(null);
    const [middlewareState, setMiddlewareState] = useState(null);
    const [accionandoPuerta, setAccionandoPuerta] = useState(null);
    const [mostrarMenuEmergencia, setMostrarMenuEmergencia] = useState(false);

    useEffect(() => {
        if (!fraccionamientoId) return;

        const unsubConfig = onSnapshot(
            doc(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_config', 'panel_config'),
            (docSnap) => {
                if (docSnap.exists()) {
                    setControlAccesosConfig(docSnap.data());
                } else {
                    setControlAccesosConfig(null);
                }
            },
            (error) => {
                console.warn('[Vigilancia] Error al escuchar panel_config:', error);
            }
        );

        const unsubState = onSnapshot(
            doc(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_config', 'middleware_state'),
            (docSnap) => {
                if (docSnap.exists()) {
                    setMiddlewareState(docSnap.data());
                } else {
                    setMiddlewareState(null);
                }
            },
            (error) => {
                console.warn('[Vigilancia] Error al escuchar middleware_state:', error);
            }
        );

        return () => {
            unsubConfig();
            unsubState();
        };
    }, [fraccionamientoId]);

    const [segundosRestantesEmergencia, setSegundosRestantesEmergencia] = useState(null);

    // Temporizador en vivo segundo a segundo para la auto-restauración de emergencia
    useEffect(() => {
        if (!middlewareState?.expiraEn || !middlewareState?.modoOperacion || middlewareState?.modoOperacion === 'normal') {
            setSegundosRestantesEmergencia(null);
            return;
        }

        const calcularSegundos = () => {
            const expiraMs = new Date(middlewareState.expiraEn).getTime();
            const ahoraMs = Date.now();
            const dif = Math.max(0, Math.floor((expiraMs - ahoraMs) / 1000));
            setSegundosRestantesEmergencia(dif);
        };

        calcularSegundos();
        const timer = setInterval(calcularSegundos, 1000);
        return () => clearInterval(timer);
    }, [middlewareState?.modoOperacion, middlewareState?.expiraEn]);

    const formatearTiempoRestante = (segundos) => {
        if (segundos === null || segundos === undefined) return '';
        const m = Math.floor(segundos / 60);
        const s = segundos % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    // Helper para evaluar el estado físico en línea/desconectado de una puerta
    const obtenerEstadoConexionPuerta = (puerta) => {
        if (!middlewareState || !middlewareState.online) {
            return { online: false, razon: 'Servidor local de caseta sin conexión' };
        }
        const k = String(puerta.keyId || puerta.id || '');
        if (k.startsWith('p2_') || puerta.panelId === 'panel_2' || puerta.panelId === 'panel2') {
            const ok = Boolean(middlewareState.panel2Connected);
            return { online: ok, razon: ok ? 'En línea' : 'Panel 2 (Cuarto Basura) desconectado físicamente' };
        }
        if (k.startsWith('p1_') || puerta.panelId === 'panel_1' || puerta.panelId === 'panel1') {
            const ok = Boolean(middlewareState.panel1Connected);
            return { online: ok, razon: ok ? 'En línea' : 'Panel 1 (Vehicular) desconectado físicamente' };
        }
        if (k.startsWith('p4_') || puerta.panelId === 'panel_4puertas' || puerta.panelId === 'panel4p') {
            const ok = Boolean(middlewareState.panel4pConnected !== false && (middlewareState.panel4pConnected || middlewareState.panelConnected));
            return { online: ok, razon: ok ? 'En línea' : 'Panel 4 Puertas desconectado físicamente' };
        }
        const ok = Boolean(middlewareState.panelConnected);
        return { online: ok, razon: ok ? 'En línea' : 'Panel desconectado físicamente' };
    };

    const userRoleKey = useMemo(() => {
        if (isSuperAdmin) return 'admin';
        if (isCaseta) return 'caseta';
        if (isResident) return 'residente';
        return 'residente';
    }, [isSuperAdmin, isCaseta, isResident]);

    const isRfidMorososActivo = modulos?.desactivacion_rfid_morosos !== false;

    const puertasAutorizadas = useMemo(() => {
        if (!controlAccesosConfig || modulos?.vigilancia === false) return [];
        const panel1Activo = controlAccesosConfig.panel1Activo !== false;
        const panel2Activo = controlAccesosConfig.panel2Activo !== false;
        const panel4pActivo = controlAccesosConfig.panel4pActivo !== false;

        const puertasObj = controlAccesosConfig.puertas || {};
        const lista = [];
        const ordenPuertas = ['p4_d1', 'p4_d2', 'p4_d3', 'p4_d4', 'p1_d1', 'p1_d2', 'p2_d1'];

        ordenPuertas.forEach((idKey) => {
            const p = puertasObj[idKey];
            if (!p) return;

            const esPanel4p = p.panelId === 'panel4p' || p.panelId === 'panel_4puertas' || idKey.startsWith('p4_');
            const esPanel1 = p.panelId === 'panel1' || p.panelId === 'panel_1' || idKey.startsWith('p1_');
            const esPanel2 = p.panelId === 'panel2' || p.panelId === 'panel_2' || idKey.startsWith('p2_');

            if (esPanel4p && !panel4pActivo) return;
            if (esPanel1 && !panel1Activo) return;
            if (esPanel2 && !panel2Activo) return;

            const rolesAut = Array.isArray(p.rolesAutorizados) ? p.rolesAutorizados : ['admin', 'caseta'];

            let usuarioAutorizado = false;
            if (isSuperAdmin || userRoleKey === 'admin') {
                usuarioAutorizado = rolesAut.includes('admin') || rolesAut.includes('superadmin');
            } else if (isCaseta) {
                usuarioAutorizado = rolesAut.includes('caseta');
            } else if (isResident) {
                usuarioAutorizado = rolesAut.includes('residente');
            } else {
                usuarioAutorizado = rolesAut.includes(userRoleKey);
            }

            if (usuarioAutorizado) {
                let panelNombreDinamico = p.panelNombre;
                let colorTema = '#2563eb';
                let bgTema = '#eff6ff';
                let borderTema = '#bfdbfe';

                if (esPanel1) {
                    panelNombreDinamico = controlAccesosConfig.panel1Nombre || 'Panel Principal';
                    colorTema = '#2563eb';
                    bgTema = '#eff6ff';
                    borderTema = '#bfdbfe';
                } else if (esPanel2) {
                    panelNombreDinamico = controlAccesosConfig.panel2Nombre || 'Panel Cuarto Basura';
                    colorTema = '#7c3aed';
                    bgTema = '#faf5ff';
                    borderTema = '#e9d5ff';
                } else if (esPanel4p) {
                    panelNombreDinamico = controlAccesosConfig.panel4pNombre || 'Panel 4 Puertas';
                    colorTema = '#059669';
                    bgTema = '#ecfdf5';
                    borderTema = '#a7f3d0';
                }

                const nombreLimpio = String(panelNombreDinamico || '')
                    .replace(/\s*\((?:Hikvision\s*)?DS-[A-Z0-9-]+\)/gi, '')
                    .replace(/\s*\([^)]*puertas?[^)]*\)/gi, '')
                    .trim();

                lista.push({
                    ...p,
                    keyId: idKey,
                    id: p.id !== undefined ? p.id : idKey,
                    panelNombre: nombreLimpio || panelNombreDinamico,
                    colorTema,
                    bgTema,
                    borderTema
                });
            }
        });

        return lista;
    }, [controlAccesosConfig, modulos?.vigilancia, isSuperAdmin, isCaseta, isResident, userRoleKey]);

    const handleAccionarAperturaPuerta = async (puerta) => {
        if (!fraccionamientoId || accionandoPuerta) return;

        const estadoConn = obtenerEstadoConexionPuerta(puerta);
        if (!estadoConn.online) {
            Swal.fire({
                title: 'Panel Fuera de Línea',
                html: `
                    <div style="font-size: 0.95rem; color: #475569; text-align: left; line-height: 1.5;">
                        <p>El <strong>${puerta.panelNombre}</strong> se encuentra <strong style="color: #dc2626;">desconectado físicamente</strong> en caseta.</p>
                        <p style="margin-top: 8px; color: #64748b;">Estatus: <em>${estadoConn.razon}</em>.</p>
                        <p style="margin-top: 8px; color: #b91c1c; font-weight: 600;">Por seguridad, la plataforma no permite encolar aperturas a paneles desconectados para evitar que los relevadores se accionen tardíamente cuando nadie esté presente.</p>
                    </div>
                `,
                icon: 'warning',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#64748b'
            });
            return;
        }

        const confirmacion = await Swal.fire({
            title: `¿Abrir ${puerta.nombre}?`,
            html: `
                <div style="font-size: 0.95rem; color: #475569; text-align: left; line-height: 1.5;">
                    <p><strong>Panel:</strong> ${puerta.panelNombre || puerta.panelId}</p>
                    <p><strong>Puerta:</strong> ${puerta.nombre}</p>
                    <p><strong>Duración de apertura:</strong> 6 segundos (retorno automático a seguro)</p>
                    ${puerta.accesoGeneral ? '<p style="color: #059669; font-weight: 600;">⭐ Puerta con Acceso General Activado</p>' : ''}
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, Abrir Puerta',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#64748b',
            reverseButtons: true
        });

        if (!confirmacion.isConfirmed) return;

        setAccionandoPuerta(puerta.keyId || puerta.id);

        try {
            let targetDoor = 1;
            let targetPanel = 'panel_4puertas';
            const k = String(puerta.keyId || puerta.id || '');
            if (k.startsWith('p4_d')) {
                targetDoor = parseInt(k.replace('p4_d', ''), 10) || 1;
                targetPanel = 'panel_4puertas';
            } else if (k.startsWith('p1_d')) {
                targetDoor = parseInt(k.replace('p1_d', ''), 10) || 1;
                targetPanel = 'panel_1';
            } else if (k.startsWith('p2_d')) {
                targetDoor = parseInt(k.replace('p2_d', ''), 10) || 1;
                targetPanel = 'panel_2';
            } else {
                targetDoor = typeof puerta.id === 'number' ? puerta.id : (parseInt(puerta.id, 10) || 1);
                targetPanel = puerta.panelId || 'panel_4puertas';
            }

            const nombreUsuario = userData?.nombre || userData?.email || 'Usuario';
            const rolUsuario = userData?.rol || 'administrador';

            await addDoc(collection(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_comandos'), {
                comando: 'abrir_puerta',
                parametros: {
                    puertaId: targetDoor,
                    panelId: targetPanel,
                    duracionSegundos: 6
                },
                estatus: 'pendiente',
                solicitadoPor: {
                    uid: currentUid,
                    nombre: nombreUsuario,
                    rol: rolUsuario
                },
                origen: 'vigilancia_cabecera',
                timestamp: serverTimestamp()
            });

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Apertura de Emergencia: ${puerta.nombre}`,
                    descripcion: `${nombreUsuario} (${rolUsuario}) accionó la apertura de ${puerta.nombre} [${puerta.panelNombre || targetPanel}] por 6 segundos.`,
                    categoria: 'Seguridad',
                    tipoMovimiento: 'apertura_remota',
                    origen: 'vigilancia',
                    metadata: {
                        puertaId: puerta.id,
                        panelId: targetPanel,
                        puertaNombre: puerta.nombre,
                        duracionSegundos: 6
                    }
                });
            } catch (errMov) {
                console.warn('[Vigilancia] Advertencia al registrar movimiento:', errMov.message);
            }

            try {
                await addDoc(collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones'), {
                    titulo: `Apertura Remota: ${puerta.nombre}`,
                    descripcion: `${nombreUsuario} (${rolUsuario}) accionó la apertura de ${puerta.nombre} (${puerta.panelNombre || targetPanel}) por 6 segundos.`,
                    mensaje: `${nombreUsuario} (${rolUsuario}) accionó la apertura de ${puerta.nombre} (${puerta.panelNombre || targetPanel}) por 6 segundos.`,
                    categoria: 'Vigilancia',
                    tipo: 'alerta_seguridad',
                    tipoMovimiento: 'apertura_remota',
                    origen: 'vigilancia',
                    leida: false,
                    creadoPor: nombreUsuario,
                    rol: rolUsuario,
                    actorRol: String(rolUsuario).toLowerCase(),
                    actorNombre: nombreUsuario,
                    fecha: serverTimestamp(),
                    fechaCreacion: serverTimestamp(),
                    timestamp: serverTimestamp()
                });
            } catch (errNotif) {
                console.warn('[Vigilancia] Advertencia al crear notificación:', errNotif.message);
            }

            Swal.fire({
                title: '¡Instrucción Enviada!',
                text: `${puerta.nombre} ha sido activada por 6 segundos.`,
                icon: 'success',
                timer: 3000,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });

        } catch (error) {
            console.error('[Vigilancia] Error accionando puerta:', error);
            Swal.fire({
                title: 'Error de Apertura',
                text: `No se pudo enviar el pulso a la puerta: ${error.message}`,
                icon: 'error'
            });
        } finally {
            setTimeout(() => {
                setAccionandoPuerta(null);
            }, 6000);
        }
    };

    const handleModoEmergencia = async (tipoEmergencia) => {
        if (!fraccionamientoId || !isSuperAdmin) return;
        setMostrarMenuEmergencia(false);

        const esAbrirTodo = tipoEmergencia === 'abrir_todo_indefinido';
        const tituloAccion = esAbrirTodo ? 'Abrir Todo por Tiempo Indefinido' : 'Cerrar Todo por Tiempo Indefinido (Lockdown)';
        const textoAccion = esAbrirTodo 
            ? 'TODAS las puertas y plumas vehiculares del fraccionamiento permanecerán abiertas sin restricción. Cualquier persona o vehículo podrá transitar libremente.'
            : 'TODAS las puertas y plumas vehiculares permanecerán bloqueadas. Nadie podrá entrar ni salir mediante tags RFID ni aperturas habituales.';

        const { value: formValues } = await Swal.fire({
            title: `🚨 ${tituloAccion}`,
            html: `
                <div style="font-size: 0.95rem; color: #334155; text-align: left; line-height: 1.5; margin-bottom: 1rem;">
                    <p style="color: ${esAbrirTodo ? '#059669' : '#dc2626'}; font-weight: 700; font-size: 1rem;">
                        ${textoAccion}
                    </p>
                    <p>Por seguridad, selecciona un <strong>Temporizador Failsafe de Restauración Automática</strong> para que el sistema regrese a modo normal si se olvida desactivar manualmente:</p>
                    <div style="margin-top: 12px; background: #f8fafc; padding: 12px; border-radius: 12px; border: 1px solid #e2e8f0;">
                        <label style="font-weight: 600; display: block; margin-bottom: 6px; color: #1e293b;">
                            ⏱️ Minutero / Duración del Modo de Emergencia:
                        </label>
                        <select id="swal-failsafe-select" class="swal2-input" style="width: 100%; margin: 0 0 8px 0; padding: 8px; font-size: 0.9rem;">
                            <option value="1">⚡ 1 Minuto (Prueba ultrarrápida)</option>
                            <option value="2">⚡ 2 Minutos (Prueba rápida)</option>
                            <option value="5">⚡ 5 Minutos (Prueba de campo)</option>
                            <option value="15">15 Minutos</option>
                            <option value="30">30 Minutos</option>
                            <option value="60" selected>1 Hora (Recomendado estándar)</option>
                            <option value="120">2 Horas</option>
                            <option value="240">4 Horas</option>
                            <option value="custom">✏️ Minutero Personalizado (Digitar minutos)</option>
                            <option value="0">⚠️ Sin Límite (Manual - Requiere Restauración Manual)</option>
                        </select>

                        <div id="swal-failsafe-custom-box" style="display: none; margin-top: 8px; padding-top: 8px; border-top: 1px dashed #cbd5e1;">
                            <label style="font-size: 0.82rem; font-weight: 600; color: #475569; display: block; margin-bottom: 4px;">
                                Ingresa la duración exacta en minutos:
                            </label>
                            <div style="display: flex; align-items: center; gap: 8px;">
                                <input type="number" id="swal-failsafe-custom-num" class="swal2-input" min="1" max="1440" value="1" style="width: 100%; margin: 0; padding: 6px 10px;" placeholder="Minutos">
                                <span style="font-weight: 600; color: #64748b; font-size: 0.9rem;">min</span>
                            </div>
                        </div>
                    </div>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Continuar a Confirmación Final',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: esAbrirTodo ? '#059669' : '#dc2626',
            cancelButtonColor: '#64748b',
            focusCancel: true,
            didOpen: () => {
                const select = document.getElementById('swal-failsafe-select');
                const customBox = document.getElementById('swal-failsafe-custom-box');
                const customNum = document.getElementById('swal-failsafe-custom-num');
                if (select && customBox) {
                    select.addEventListener('change', () => {
                        if (select.value === 'custom') {
                            customBox.style.display = 'block';
                            customNum?.focus();
                        } else {
                            customBox.style.display = 'none';
                        }
                    });
                }
            },
            preConfirm: () => {
                const select = document.getElementById('swal-failsafe-select');
                const customNum = document.getElementById('swal-failsafe-custom-num');
                let duracion = 60;
                if (select?.value === 'custom') {
                    duracion = parseInt(customNum?.value || '1', 10);
                    if (isNaN(duracion) || duracion < 1) duracion = 1;
                } else {
                    duracion = parseInt(select?.value || '60', 10);
                }
                return {
                    duracionMinutos: duracion
                };
            }
        });

        if (!formValues) return;

        const duracionMin = formValues.duracionMinutos;
        const textoDuracion = duracionMin > 0 ? `${duracionMin} minutos` : 'de forma Indefinida (Manual)';

        const panelesDetectados = [];
        if (controlAccesosConfig?.panel1Activo !== false) {
            panelesDetectados.push({
                nombre: controlAccesosConfig?.panel1Nombre || 'Panel 1 (Vehicular)',
                online: Boolean(middlewareState?.online && middlewareState?.panel1Connected)
            });
        }
        if (controlAccesosConfig?.panel2Activo !== false) {
            panelesDetectados.push({
                nombre: controlAccesosConfig?.panel2Nombre || 'Panel 2 (Cuarto Basura)',
                online: Boolean(middlewareState?.online && middlewareState?.panel2Connected)
            });
        }
        if (controlAccesosConfig?.panel4pActivo !== false && (controlAccesosConfig?.panel4p_ip || controlAccesosConfig?.panel3_ip)) {
            panelesDetectados.push({
                nombre: controlAccesosConfig?.panel4pNombre || 'Panel 4 Puertas',
                online: Boolean(middlewareState?.online && (middlewareState?.panel4pConnected || middlewareState?.panelConnected))
            });
        }

        const resumenPanelesHtml = panelesDetectados.length > 0
            ? `
                <div style="margin-top: 12px; background: #f8fafc; padding: 10px 12px; border-radius: 8px; border: 1px solid #e2e8f0; font-size: 0.85rem;">
                    <strong style="color: #1e293b; display: block; margin-bottom: 6px;">📡 Estado de Paneles Detectados:</strong>
                    ${panelesDetectados.map(p => `
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 4px;">
                            <span>${p.nombre}</span>
                            <span style="font-weight: 700; color: ${p.online ? '#16a34a' : '#dc2626'};">${p.online ? '🟢 En línea' : '🔴 Desconectado (Omitido)'}</span>
                        </div>
                    `).join('')}
                </div>
            `
            : '';

        const confirmacionFinal = await Swal.fire({
            title: '⚠️ ¿Confirmar Acción de Emergencia?',
            html: `
                <div style="font-size: 0.95rem; color: #475569; text-align: left;">
                    <p>Estás a punto de activar <strong>${tituloAccion}</strong> durante <strong>${textoDuracion}</strong>.</p>
                    <p style="color: #b91c1c; font-weight: 600;">Esta acción afectará inmediatamente los relevadores físicos de los paneles en línea.</p>
                    ${resumenPanelesHtml}
                    <p style="margin-top: 10px;">¿Deseas proceder?</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, Ejecutar Ahora',
            cancelButtonText: 'Cancelar Operación',
            confirmButtonColor: esAbrirTodo ? '#059669' : '#dc2626',
            cancelButtonColor: '#64748b'
        });

        if (!confirmacionFinal.isConfirmed) return;

        try {
            const nombreUsuario = userData?.nombre || userData?.email || 'Administrador';
            const rolUsuario = userData?.rol || 'administrador';

            Swal.fire({
                title: 'Aplicando Modo de Emergencia...',
                html: `
                    <div style="font-size: 0.95rem; color: #475569; text-align: center; margin-top: 8px;">
                        <p>Enviando instrucción directa a los paneles físicos en caseta...</p>
                        <p style="font-size: 0.85rem; color: #64748b; margin-top: 4px;">Por favor espera la confirmación de los relevadores.</p>
                    </div>
                `,
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const docRef = await addDoc(collection(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_comandos'), {
                comando: tipoEmergencia,
                parametros: {
                    panelId: 'todos',
                    duracionMinutos: duracionMin
                },
                estatus: 'pendiente',
                solicitadoPor: {
                    uid: currentUid,
                    nombre: nombreUsuario,
                    rol: rolUsuario
                },
                origen: 'vigilancia_cabecera',
                timestamp: serverTimestamp()
            });

            // Esperar confirmación de ejecución del Middleware en caseta (hasta 8 segundos)
            let resultadoCmd = null;
            try {
                resultadoCmd = await new Promise((resolve, reject) => {
                    let desuscrito = false;
                    const timeoutId = setTimeout(() => {
                        if (!desuscrito) {
                            desuscrito = true;
                            unsubscribe();
                            resolve(null);
                        }
                    }, 8000);

                    const unsubscribe = onSnapshot(doc(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_comandos', docRef.id), (snap) => {
                        if (snap.exists()) {
                            const d = snap.data();
                            if (d.estatus === 'completado') {
                                if (!desuscrito) {
                                    desuscrito = true;
                                    clearTimeout(timeoutId);
                                    unsubscribe();
                                    resolve(d);
                                }
                            } else if (d.estatus === 'error') {
                                if (!desuscrito) {
                                    desuscrito = true;
                                    clearTimeout(timeoutId);
                                    unsubscribe();
                                    reject(new Error(d.errorMsg || d.detalle || 'Error al accionar relevadores en caseta.'));
                                }
                            }
                        }
                    }, () => {
                        if (!desuscrito) {
                            desuscrito = true;
                            clearTimeout(timeoutId);
                            if (typeof unsubscribe === 'function') unsubscribe();
                            resolve(null);
                        }
                    });
                });
            } catch (errWait) {
                throw errWait;
            }

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Emergencia Activada: ${tituloAccion}`,
                    descripcion: `${nombreUsuario} (${rolUsuario}) activó el modo de emergencia "${tituloAccion}" con Failsafe de ${textoDuracion}.`,
                    categoria: 'Seguridad',
                    tipoMovimiento: 'alerta_emergencia',
                    origen: 'vigilancia',
                    metadata: {
                        modo: tipoEmergencia,
                        duracionMinutos: duracionMin,
                        failsafeTexto: textoDuracion
                    }
                });
            } catch (errMov) {
                console.warn('[Vigilancia] Error registrando movimiento:', errMov.message);
            }

            try {
                await addDoc(collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones'), {
                    titulo: `🚨 Control de Acceso: ${tituloAccion}`,
                    mensaje: `${nombreUsuario} (${rolUsuario}) activó "${tituloAccion}". Duración programada: ${textoDuracion}.`,
                    categoria: 'Seguridad',
                    tipo: 'alerta_critica',
                    leida: false,
                    creadoPor: nombreUsuario,
                    rol: rolUsuario,
                    fechaCreacion: serverTimestamp(),
                    timestamp: serverTimestamp()
                });
            } catch (errNotif) {
                console.warn('[Vigilancia] Error registrando notificación:', errNotif.message);
            }

            const panelesEnLineaTexto = resultadoCmd?.detalle || (panelesDetectados.filter(p => p.online).map(p => p.nombre).join(', ') || 'Paneles en línea');

            Swal.fire({
                title: '🚨 Modo de Emergencia Activado',
                html: `
                    <div style="font-size: 0.95rem; color: #334155; text-align: left; line-height: 1.5;">
                        <p>El controlador aplicó <strong>${tituloAccion}</strong>.</p>
                        <p style="margin-top: 6px;"><strong>Detalle:</strong> ${panelesEnLineaTexto}</p>
                        <p style="margin-top: 6px; color: #0284c7; font-weight: 600;">⏱️ Auto-restauración: ${textoDuracion}.</p>
                        <p style="font-size: 0.85rem; color: #64748b; margin-top: 8px;">
                            Monitorea el temporizador con la cuenta regresiva en vivo en el banner superior.
                        </p>
                    </div>
                `,
                icon: 'success'
            });

        } catch (error) {
            console.error('[Vigilancia] Error ejecutando modo de emergencia:', error);
            Swal.fire({
                title: 'Error de Ejecución',
                text: `No se pudo aplicar la orden de emergencia: ${error.message}`,
                icon: 'error'
            });
        }
    };

    const handleRestablecerModoNormal = async () => {
        if (!fraccionamientoId || !isSuperAdmin) return;
        setMostrarMenuEmergencia(false);

        const confirmacion = await Swal.fire({
            title: '¿Restablecer Modo Normal?',
            text: 'Todas las puertas y plumas regresarán al control ordinario mediante RFID, lecturas vehiculares y permisos configurados.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, Restablecer Normal',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2563eb'
        });

        if (!confirmacion.isConfirmed) return;

        try {
            const nombreUsuario = userData?.nombre || userData?.email || 'Administrador';
            const rolUsuario = userData?.rol || 'administrador';

            Swal.fire({
                title: 'Restableciendo Modo Normal...',
                html: `
                    <div style="font-size: 0.95rem; color: #475569; text-align: center; margin-top: 8px;">
                        <p>Restaurando control ordinario en los paneles físicos en caseta...</p>
                        <p style="font-size: 0.85rem; color: #64748b; margin-top: 4px;">Por favor espera la confirmación de los relevadores.</p>
                    </div>
                `,
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const docRef = await addDoc(collection(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_comandos'), {
                comando: 'restablecer_modo_normal',
                parametros: {
                    panelId: 'todos'
                },
                estatus: 'pendiente',
                solicitadoPor: {
                    uid: currentUid,
                    nombre: nombreUsuario,
                    rol: rolUsuario
                },
                origen: 'vigilancia_cabecera',
                timestamp: serverTimestamp()
            });

            // Esperar confirmación de ejecución del Middleware en caseta (hasta 8 segundos)
            let resultadoCmd = null;
            try {
                resultadoCmd = await new Promise((resolve, reject) => {
                    let desuscrito = false;
                    const timeoutId = setTimeout(() => {
                        if (!desuscrito) {
                            desuscrito = true;
                            unsubscribe();
                            resolve(null);
                        }
                    }, 8000);

                    const unsubscribe = onSnapshot(doc(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_comandos', docRef.id), (snap) => {
                        if (snap.exists()) {
                            const d = snap.data();
                            if (d.estatus === 'completado') {
                                if (!desuscrito) {
                                    desuscrito = true;
                                    clearTimeout(timeoutId);
                                    unsubscribe();
                                    resolve(d);
                                }
                            } else if (d.estatus === 'error') {
                                if (!desuscrito) {
                                    desuscrito = true;
                                    clearTimeout(timeoutId);
                                    unsubscribe();
                                    reject(new Error(d.errorMsg || d.detalle || 'Error al restablecer modo normal en caseta.'));
                                }
                            }
                        }
                    }, () => {
                        if (!desuscrito) {
                            desuscrito = true;
                            clearTimeout(timeoutId);
                            if (typeof unsubscribe === 'function') unsubscribe();
                            resolve(null);
                        }
                    });
                });
            } catch (errWait) {
                throw errWait;
            }

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: 'Restablecimiento de Control de Acceso',
                    descripcion: `${nombreUsuario} (${rolUsuario}) restauró el control de acceso a Modo Normal ordinario.`,
                    categoria: 'Seguridad',
                    tipoMovimiento: 'restablecer_modo_normal',
                    origen: 'vigilancia'
                });
            } catch (eMov) {}

            try {
                await addDoc(collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones'), {
                    titulo: 'Control de Acceso Restaurado',
                    mensaje: `${nombreUsuario} (${rolUsuario}) restauró el sistema a Modo Normal ordinario.`,
                    categoria: 'Control de Acceso',
                    tipo: 'info',
                    leida: false,
                    creadoPor: nombreUsuario,
                    rol: rolUsuario,
                    fechaCreacion: serverTimestamp(),
                    timestamp: serverTimestamp()
                });
            } catch (eNotif) {}

            Swal.fire({
                title: 'Modo Normal Restablecido',
                text: resultadoCmd?.detalle || 'Las puertas han vuelto a operar normalmente.',
                icon: 'success',
                timer: 3000,
                showConfirmButton: true
            });
        } catch (error) {
            Swal.fire({
                title: 'Error de Restauración',
                text: `No se pudo restablecer el modo normal: ${error.message}`,
                icon: 'error'
            });
        }
    };

    const [qrCode, setQrCode] = useState('');
    const [paquete, setPaquete] = useState({
        depto: '',
        empresa: '',
        destinatario: '',
        fecha: new Date().toISOString().split('T')[0],
        comentarios: '',
        hora: '08:00',
        guardia: '',
    });
    const [paquetes, setPaquetes] = useState([]); // Usaremos paqueteriaList en su lugar si existe, pero por ahora mantengo para no romper mis funciones nuevas
    const [showPaqueteriaModal, setShowPaqueteriaModal] = useState(false);
    const [paqueteParaEntregar, setPaqueteParaEntregar] = useState(null);
    const [showCapturaEvidencia, setShowCapturaEvidencia] = useState(false);
    const [isSavingPaquete, setIsSavingPaquete] = useState(false);
    const [registrandoPaquete, setRegistrandoPaquete] = useState(false);
    const [paqueteriaError, setPaqueteriaError] = useState('');
    const [paqueteriaFieldErrors, setPaqueteriaFieldErrors] = useState({});
    const [paqueteriaSearchDate, setPaqueteriaSearchDate] = useState(''); // Filtro por fecha para caseta
    const [residentPackagesSearchQuery, setResidentPackagesSearchQuery] = useState(''); // Búsqueda para residente
    const [destinatarioLookup, setDestinatarioLookup] = useState({
        status: 'idle',
        nombre: '',
        uid: null,
        depto: '',
    });
    const destinatarioTouchedRef = useRef(false);


    const [visitasFrecuentes, setVisitasFrecuentes] = useState([]);
    const [visitasLoading, setVisitasLoading] = useState(false);
    const [visitasError, setVisitasError] = useState('');
    const [visitasFilterDepto, setVisitasFilterDepto] = useState('');
    const [showVisitaModal, setShowVisitaModal] = useState(false);
    const [activeVisitaTab, setActiveVisitaTab] = useState('info'); // 'info' o 'horario'
    const [visitaForm, setVisitaForm] = useState({
        nombre: '',
        categoria: '',
        telefono: '',
        placa: '',
        color: '',
        notas: '',
        horario: INITIAL_HORARIO,
        identificacionUrl: '',
        identificacionName: ''
    });
    const [visitaArchivo, setVisitaArchivo] = useState(null);
    const [visitaSubmitting, setVisitaSubmitting] = useState(false);
    const [visitaFormError, setVisitaFormError] = useState('');
    const [visitaResultado, setVisitaResultado] = useState(null);
    const [showAllVisitas, setShowAllVisitas] = useState(false);
    const [detalleVisita, setDetalleVisita] = useState(null);
    const [detalleEditMode, setDetalleEditMode] = useState(false);
    const [detalleForm, setDetalleForm] = useState(null);
    const [detalleArchivo, setDetalleArchivo] = useState(null);
    
    // Función para resetear el formulario de visita frecuente
    const resetVisitaForm = useCallback(() => {
        setEditingVisitaId(null);
        setVisitaForm({
            nombre: '',
            categoria: '',
            telefono: '',
            placa: '',
            color: '',
            notas: '',
            horario: INITIAL_HORARIO,
            identificacionUrl: '',
            identificacionName: ''
        });
        setVisitaArchivo(null);
        setVisitaFormError('');
        setActiveVisitaTab('info');
    }, []);
    const [detalleSubmitting, setDetalleSubmitting] = useState(false);
    const [detalleDeleting, setDetalleDeleting] = useState(false);
    const [detalleConfirmDelete, setDetalleConfirmDelete] = useState(false);
    const [detalleError, setDetalleError] = useState('');
    const [editingVisitaId, setEditingVisitaId] = useState(null); // ID de la visita en edición
    const [showDeleteVisitaConfirm, setShowDeleteVisitaConfirm] = useState(false);
    const [visitaToDelete, setVisitaToDelete] = useState(null);

    const [showQrModal, setShowQrModal] = useState(false);
    const [expandedQrId, setExpandedQrId] = useState(null); // Para descripción desplegable en QR
    const [showQrValidationModal, setShowQrValidationModal] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);

    // Detectar si es móvil y estado de navegación (declarar temprano para usar en callbacks)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [selectedSection, setSelectedSection] = useState(null);

    // Función para resetear el formulario de paquetería
    const resetPaqueteForm = useCallback(() => {
        setPaquete({
            depto: '',
            empresa: '',
            destinatario: '',
            fecha: new Date().toISOString().split('T')[0],
            comentarios: '',
            hora: '08:00',
            guardia: userData?.displayName || extractResidentDisplayName(userData) || '',
        });
        setDestinatarioLookup({ status: 'idle', nombre: '', uid: null, depto: '' });
        setPaqueteriaFieldErrors({});
        setPaqueteriaError('');
        destinatarioTouchedRef.current = false;
    }, [userData]);

    // Función para cerrar el formulario y limpiar campos
    const handleClosePaqueteriaForm = useCallback(() => {
        resetPaqueteForm();
        setShowPaqueteriaFormModal(false);
        // Si estamos en móvil y la sección estaba seleccionada, volver al menú
        if (isMobile && selectedSection === 'paqueteria') {
            setSelectedSection(null);
        }
    }, [isMobile, resetPaqueteForm, selectedSection]);
    const validationVideoRef = useRef(null);
    const validationCanvasRef = useRef(null);
    const validationStreamRef = useRef(null);
    const qrScanIntervalRef = useRef(null);
    const [qrScannerError, setQrScannerError] = useState('');
    const [validationError, setValidationError] = useState('');
    const [validationSubmitting, setValidationSubmitting] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const validationInputRef = useRef(null);
    const validationResultRef = useRef(null);
    const [scannedHighlight, setScannedHighlight] = useState(false);
    const [scannedWarning, setScannedWarning] = useState(false);

    const playScanBeep = useCallback(() => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;
            const audioCtx = new AudioContext();
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(440, audioCtx.currentTime + 0.1);

            gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.1);

            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);

            oscillator.start();
            oscillator.stop(audioCtx.currentTime + 0.1);
        } catch (error) {
            console.warn('Vigilancia: no se pudo reproducir el pitido de escaneo', error);
        }
    }, []);
    const [qrForm, setQrForm] = useState({
        usarVisitaFrecuente: false,
        visitaFrecuenteId: '',
        nombre: '',
        telefono: '',
        numeroVisitantes: '1',
        fecha: '',
        hora: '',
        placa: '',
        color: '',
        categoria: '',
        observaciones: '',
        correoDestino: '',
        correoInvitado: '',
        tipoTransporte: 'En vehiculo',
        departamento: '',
        esMultiAcceso: false,
        duracionHoras: '4',
        limiteEscaneos: '1',
    });
    const qrModalBodyRef = useRef(null);
    const [qrFormError, setQrFormError] = useState('');
    const [qrSubmitting, setQrSubmitting] = useState(false);
    const [qrResult, setQrResult] = useState(null);
    const [showQrInfo, setShowQrInfo] = useState(false);
    const [showAdvancedQrOptions, setShowAdvancedQrOptions] = useState(false);
    const [showAdvancedSubModal, setShowAdvancedSubModal] = useState(false);
    const [showScheduleConfirm, setShowScheduleConfirm] = useState(false); // Modal de confirmación Zen
    const [useSavedSchedule, setUseSavedSchedule] = useState(false); // Flag para ocultar campos
    const [hasDecidedSchedule, setHasDecidedSchedule] = useState(null); // null | boolean para recordar decisión en sesión
    const [showBannerScheduleDetail, setShowBannerScheduleDetail] = useState(false); // Acordeón en el banner
    const [pendingVisita, setPendingVisita] = useState(null); // Visita seleccionada pendiente de confirmación
    const advancedQrOptionsRef = useRef(null);
    const qrVisitaSelectorRef = useRef(null);
    const [showFrecuentesInfo, setShowFrecuentesInfo] = useState(false);
    const generateQrCallableRef = useRef(null);
    const validateQrCallableRef = useRef(null);
    const visitHistoryCallableRef = useRef(null);
    const listGuestAccessesCallableRef = useRef(null);
    const registerManualAccessCallableRef = useRef(null);
    const markGuestExitCallableRef = useRef(null);

    const [visitHistoryModalOpen, setVisitHistoryModalOpen] = useState(false);
    const [visitHistory, setVisitHistory] = useState([]);
    const [visitHistoryLoading, setVisitHistoryLoading] = useState(false);
    const [visitHistoryError, setVisitHistoryError] = useState('');
    const [visitHistorySearch, setVisitHistorySearch] = useState('');
    const [visitHistoryDate, setVisitHistoryDate] = useState('');
    const [visitHistoryInsideOnly, setVisitHistoryInsideOnly] = useState(false);

    const [guestAccesses, setGuestAccesses] = useState([]);
    const [guestAccessesLoading, setGuestAccessesLoading] = useState(false);
    const [guestAccessesError, setGuestAccessesError] = useState('');
    const [guestAccessesStatusFilter, setGuestAccessesStatusFilter] = useState('dentro');
    const [guestAccessesSearch, setGuestAccessesSearch] = useState('');
    const [guestAccessesCurrentPage, setGuestAccessesCurrentPage] = useState(1);
    const [guestAccessesPerPage, setGuestAccessesPerPage] = useState(10);
    const [guestAccessRefreshing, setGuestAccessRefreshing] = useState(false);
    const [guestAccessActionId, setGuestAccessActionId] = useState(null);
    const [exitConfirmAccess, setExitConfirmAccess] = useState(null);
    const [showManualAccessModal, setShowManualAccessModal] = useState(false);
    const [manualAccessSubmitting, setManualAccessSubmitting] = useState(false);
    const [manualAccessError, setManualAccessError] = useState('');
    const [casasList, setCasasList] = useState([]);
    const [casasLoading, setCasasLoading] = useState(false);
    const [casaSuggestions, setCasaSuggestions] = useState([]);
    const [showCasaSuggestions, setShowCasaSuggestions] = useState(false);
    const [showManualVisitorSuggestions, setShowManualVisitorSuggestions] = useState(false);

    // Lista de paqueterías comunes (México / Bajío)
    const COURIER_COMPANIES = useMemo(() => [
        'Mercado Libre', 'DHL', 'FedEx', 'UPS', 'Amazon', 
        'Estafeta', 'RedPack', 'Paquetexpress', '90 Minutos', 
        'Sepomex (Correos de México)', 'iVoy', 'Quality Post',
        'J&T Express', 'Uber Flash', 'DiDi Entrega'
    ], []);
    const [courierSuggestions, setCourierSuggestions] = useState([]);
    const [showCourierSuggestions, setShowCourierSuggestions] = useState(false);
    const [showCapturaImagenesManual, setShowCapturaImagenesManual] = useState(false);
    const [datosAccesoManualPendiente, setDatosAccesoManualPendiente] = useState(null);
    const [showCapturaDatosVehiculo, setShowCapturaDatosVehiculo] = useState(false);
    const [datosVehiculoForm, setDatosVehiculoForm] = useState({ tipoTransporte: 'En vehiculo', placa: '', color: '' });
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState({ title: '', message: '' });

    // Estados para confirmación de cancelación de QR
    const [showCancelQrConfirm, setShowCancelQrConfirm] = useState(false);
    const [invitacionToCancel, setInvitacionToCancel] = useState(null);
    const [manualAccessForm, setManualAccessForm] = useState({
        visitante: '',
        departamento: '',
        categoria: '',
        numeroVisitantes: '1',
        notas: '',
        visitaFrecuenteId: '',
        placa: '',
        color: '',
        tipoTransporte: 'En vehiculo',
    });

    const isFetchingGuestAccessesRef = useRef(false);
    const fetchCountRef = useRef(0);

    // Estados para exportación de historial
    const [showVisitHistoryModal, setShowVisitHistoryModal] = useState(false);
    const [showMyQrModal, setShowMyQrModal] = useState(false);
    const [searchQueryMyQr, setSearchQueryMyQr] = useState('');
    const [statusFilterMyQr, setStatusFilterMyQr] = useState('Todos');
    const [myQrPage, setMyQrPage] = useState(1);
    const MY_QR_ITEMS_PER_PAGE = 6;
    const [exportDateFrom, setExportDateFrom] = useState('');
    const [exportDateTo, setExportDateTo] = useState('');
    const [exportLoading, setExportLoading] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);
    const HISTORY_ITEMS_PER_PAGE = 10;

    const myInvitations = useMemo(() => {
        if (!visitHistory || visitHistory.length === 0) return [];

        // Si es administrador o caseta, puede ver todo el historial en este modal
        if (isSuperAdmin || isCaseta || normalizedRole === 'admin' || normalizedRole === 'administrador') return visitHistory;
        // Si es residente, filtrar por su UID
        return visitHistory.filter(h => h.generadoPorUid === currentUid);
    }, [visitHistory, currentUid, isSuperAdmin, isCaseta, normalizedRole]);

    const filteredMyInvitations = useMemo(() => {
        let list = myInvitations;
        
        // Filtro por estado
        if (statusFilterMyQr !== 'Todos') {
            list = list.filter(inv => {
                const status = String(inv.estadoLabel || inv.estado || '').toLowerCase();
                const filter = statusFilterMyQr.toLowerCase();
                return status.includes(filter);
            });
        }

        // Filtro por texto
        if (searchQueryMyQr.trim()) {
            const query = searchQueryMyQr.toLowerCase().trim();
            list = list.filter(inv => {
                const name = String(inv.visitanteNombre || inv.visitante || '').toLowerCase();
                const date = String(inv.fechaVisitaLabel || '').toLowerCase();
                const depto = String(inv.departamento || inv.numeroCasa || '').toLowerCase();
                const code = String(inv.codigo || '').toLowerCase();
                return name.includes(query) || date.includes(query) || depto.includes(query) || code.includes(query);
            });
        }
        
        return list;
    }, [myInvitations, searchQueryMyQr, statusFilterMyQr]);

    // Resetear página al buscar o filtrar
    useEffect(() => {
        setMyQrPage(1);
    }, [searchQueryMyQr, statusFilterMyQr]);

    // Estados para visor de fotografías de seguridad
    const [showPhotoSearchSection, setShowPhotoSearchSection] = useState(false);
    const [photoSearchQuery, setPhotoSearchQuery] = useState('');
    const [photoSearchResults, setPhotoSearchResults] = useState([]);
    const [photoSearchLoading, setPhotoSearchLoading] = useState(false);
    const [photoSearchError, setPhotoSearchError] = useState('');
    const [selectedAccess, setSelectedAccess] = useState(null);
    const [showPhotosModal, setShowPhotosModal] = useState(false);
    const [photoTypeFilter, setPhotoTypeFilter] = useState('todos'); // 'todos', 'manual', 'qr'


    const stopValidationStream = useCallback(() => {
        const stream = validationStreamRef.current;
        if (stream) {
            stream.getTracks().forEach((track) => {
                try {
                    track.stop();
                } catch (stopError) {
                    console.error('Vigilancia: no se pudo detener la cámara', stopError);
                }
            });
            validationStreamRef.current = null;
        }
        if (validationVideoRef.current) {
            validationVideoRef.current.srcObject = null;
        }
    }, []);


    const ownerNombre = useMemo(() => {
        if (userData) {
            const detected = extractResidentDisplayName(userData);
            if (detected) {
                return detected;
            }
        }
        if (currentUser?.displayName) {
            return currentUser.displayName;
        }
        if (currentUser?.email) {
            return currentUser.email;
        }
        return currentUid || 'Residente';
    }, [currentUid, currentUser?.displayName, currentUser?.email, userData]);

    const ownerEmail = useMemo(() => {
        if (typeof userData?.email === 'string' && userData.email.trim()) {
            return userData.email.trim();
        }
        if (typeof currentUser?.email === 'string' && currentUser.email.trim()) {
            return currentUser.email.trim();
        }
        return '';
    }, [currentUser?.email, userData?.email]);

    const ownerDepartamento = useMemo(() => {
        const candidates = [
            casaActiva?.numero,
            casaActiva?.numeroCasa,
            casaActiva?.houseLabel,
            casaActiva?.numeroLote,
            casaActiva?.lote,
            Array.isArray(misCasas) && misCasas[0]?.numero,
            Array.isArray(misCasas) && misCasas[0]?.numeroCasa,
            userData?.departamento,
            userData?.numeroCasa,
            userData?.houseLabel,
            userData?.numeroLote,
            userData?.lote,
            userData?.casaAsignada?.numero,
        ];
        const match = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
        return match ? match.trim() : '';
    }, [userData, casaActiva, misCasas]);

    // Auto-llenar nombre del guardia cuando se monta o cambia el usuario
    useEffect(() => {
        if (isCaseta && !paquete.guardia) {
            let guardiaName = '';

            if (currentUser?.displayName) {
                guardiaName = currentUser.displayName;
            } else if (userData) {
                guardiaName = extractResidentDisplayName(userData);
            }

            if (guardiaName) {
                setPaquete((prev) => ({ ...prev, guardia: guardiaName }));
            }
        }
    }, [currentUser?.displayName, isCaseta, userData, paquete.guardia]);

    // EFECTO: Sincronización en tiempo real de caché de invitaciones (Modo Offline)
    // Mantiene las últimas 150 invitaciones en el caché de IndexedDB para validación offline
    useEffect(() => {
        if (!fraccionamientoId || isResident) return;

        console.log('📡 [Offline Sync] Activando listener de invitaciones...');
        try {
            const invitationsRef = collection(db, 'fraccionamientos', fraccionamientoId, 'invitacionesAcceso');
            // Usamos la misma query que el historial para maximizar hit-rate de caché y consistencia
            const q = query(invitationsRef, orderBy('creadoEn', 'desc'), limit(150));
            
            const unsubscribe = onSnapshot(q, (snapshot) => {
                // Firestore guarda automáticamente el resultado en la persistencia local (IndexedDB)
                console.log(`✅ [Offline Sync] ${snapshot.docs.length} invitaciones sincronizadas en caché local.`);
            }, (err) => {
                console.warn('⚠️ [Offline Sync] Error en listener de invitaciones:', err);
            });
            
            return () => {
                console.log('🛑 [Offline Sync] Deteniendo listener de invitaciones.');
                unsubscribe();
            };
        } catch (err) {
            console.error('❌ [Offline Sync] Error al configurar sync de invitaciones:', err);
        }
    }, [fraccionamientoId, isResident]);


    const [programacionesAcceso, setProgramacionesAcceso] = useState([]);
    const [programacionesLoading, setProgramacionesLoading] = useState(true);
    const [programacionesError, setProgramacionesError] = useState('');
    const [showProgramacionesModal, setShowProgramacionesModal] = useState(false);
    const [showPaqueteriaFormModal, setShowPaqueteriaFormModal] = useState(false);

    // --- EFECTO PARA OCULTAR EL BOTÓN FLOTANTE (Changelog FAB) CUANDO HAY UN MODAL ABIERTO ---
    useEffect(() => {
        const isAnyModalOpen = !!(
            showPaqueteriaFormModal || 
            showQrModal || 
            showQrValidationModal || 
            showVisitaModal || 
            showManualAccessModal || 
            showSuccessModal || 
            showVisitHistoryModal || 
            showMyQrModal || 
            showPhotosModal || 
            showProgramacionesModal || 
            showPaqueteriaModal || 
            showCapturaEvidencia || 
            showCapturaImagenesManual || 
            exitConfirmAccess || 
            showCancelQrConfirm
        );

        if (isAnyModalOpen) {
            document.body.classList.add('hide-floating-widgets');
        } else {
            document.body.classList.remove('hide-floating-widgets');
        }

        return () => {
            document.body.classList.remove('hide-floating-widgets');
        };
    }, [
        showPaqueteriaFormModal, 
        showQrModal, 
        showQrValidationModal, 
        showVisitaModal, 
        showManualAccessModal, 
        showSuccessModal, 
        showVisitHistoryModal, 
        showMyQrModal, 
        showPhotosModal, 
        showProgramacionesModal, 
        showPaqueteriaModal, 
        showCapturaEvidencia, 
        showCapturaImagenesManual, 
        exitConfirmAccess, 
        showCancelQrConfirm
    ]);
    const [paqueteriaList, setPaqueteriaList] = useState([]);

    // --- LÓGICA DE SINCRONIZACIÓN CON EL BOTÓN ATRÁS NATIVO ---
    const closeAllModals = useCallback(() => {
        setShowPaqueteriaFormModal(false);
        setShowQrModal(false);
        setShowQrValidationModal(false);
        setShowVisitaModal(false);
        setShowManualAccessModal(false);
        setShowSuccessModal(false);
        setShowVisitHistoryModal(false);
        setShowMyQrModal(false);
        setShowPhotosModal(false);
        setShowProgramacionesModal(false);
        setShowPaqueteriaModal(false);
        setShowCapturaEvidencia(false);
        setShowCapturaImagenesManual(false);
        setExitConfirmAccess(null);
        setShowCancelQrConfirm(false);
    }, []);

    useEffect(() => {
        const handlePopState = (event) => {
            // Si el estado tiene isModal: true, o si simplemente regresamos (state es null o diferente)
            // cerramos los modales. Esto permite que el botón "atrás" actúe como cancelar.
            closeAllModals();
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [closeAllModals]);

    const openModalWithHistory = useCallback((setter, value = true) => {
        // Al abrir un modal, empujamos un estado al historial
        window.history.pushState({ isModal: true }, '');
        setter(value);
    }, []);

    const [paqueteriaLoading, setPaqueteriaLoading] = useState(false);
    const [paqueteriaListaError, setPaqueteriaListaError] = useState('');
    const [paqueteriaUpdatingId, setPaqueteriaUpdatingId] = useState(null);
    const [paqueteriaSearch, setPaqueteriaSearch] = useState('');
    const [paqueteriaFilterPendiente, setPaqueteriaFilterPendiente] = useState(false);
    const [paqueteriaPage, setPaqueteriaPage] = useState(1);
    const CASETA_PACKAGES_PER_PAGE = isMobile ? 6 : 4;
    const [residentPackages, setResidentPackages] = useState([]);
    const [residentPackagesLoading, setResidentPackagesLoading] = useState(false);
    const [residentPackagesError, setResidentPackagesError] = useState('');
    const [residentPackagesPage, setResidentPackagesPage] = useState(1);
    const PACKAGES_PER_PAGE = 4;

    // Resetear a la primera página si la lista cambia significativamente o se acorta
    useEffect(() => {
        const totalPages = Math.ceil(residentPackages.length / PACKAGES_PER_PAGE);
        if (residentPackagesPage > totalPages && totalPages > 0) {
            setResidentPackagesPage(totalPages);
        } else if (residentPackages.length === 0) {
            setResidentPackagesPage(1);
        }
    }, [residentPackages.length, residentPackagesPage]);

    // Estados para secciones desplegables
    const [showVisitasActuales, setShowVisitasActuales] = useState(true);
    const [showVehiculosProveedores, setShowVehiculosProveedores] = useState(true);
    const [showVisitasFrecuentes, setShowVisitasFrecuentes] = useState(false);
    const [visitasPage, setVisitasPage] = useState(1);
    const VISITAS_PER_PAGE = 6;


    useEffect(() => {
        let isMounted = true;
        if (!fraccionamientoId || isResident) {
            setProgramacionesAcceso([]);
            setProgramacionesLoading(false);
            return () => { };
        }

        setProgramacionesLoading(true);
        const solicitudesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
        const unsubscribe = onSnapshot(
            query(solicitudesRef, where('segmento', '==', 'solicitudes')),
            (snapshot) => {
                if (!isMounted) return;
                try {
                    const items = snapshot.docs
                        .map((docSnap) => ({ id: docSnap.id, data: docSnap.data() || {} }))
                        .map(({ id, data }) => {
                            const tipo = toTitleCase((data.tipoSolicitud || '').toString().toLowerCase());
                            const tipoNormalized = (data.tipoSolicitud || '').toString().toLowerCase();
                            const estado = (data.estado || '').toString().toLowerCase();
                            const allowed = ['mudanza', 'construccion', 'remodelacion'];

                            if (!allowed.includes(tipoNormalized) || estado !== 'aprobado') {
                                return null;
                            }

                            const baseFecha = toDateSafe(data.fechaProgramada)
                                || toDateSafe(data.fechaInicioObra)
                                || toDateSafe(data.fechaInicioTrabajo);
                            let fechaInicio = baseFecha;
                            let fechaFin = toDateSafe(data.fechaTerminoObra);
                            let horaProgramada = data.horaProgramada || null;
                            let proveedor = '';
                            let etiquetaTipo = '';
                            let notas = '';
                            let duracionDias = Number(data.duracionDias) || null;
                            let horarioLlegada = null;

                            if (tipoNormalized === 'mudanza') {
                                const programada = toDateSafe(data.fechaProgramada);
                                fechaInicio = programada;
                                fechaFin = programada;
                                horaProgramada = data.horaProgramada || null;
                                proveedor = data.empresaMudanza || data.empresa || '';
                                const subtipo = toTitleCase(data.tipoMudanza || 'Mudanza');
                                etiquetaTipo = subtipo ? `Mudanza · ${subtipo}` : 'Mudanza autorizada';
                                notas = data.observaciones || '';
                            } else if (tipoNormalized === 'construccion') {
                                fechaInicio = toDateSafe(data.fechaInicioObra) || baseFecha;
                                fechaFin = toDateSafe(data.fechaTerminoObra) || fechaFin;
                                proveedor = data.empresaConstructora || data.encargadoObra || '';
                                const tipoConstruccion = toTitleCase(data.tipoConstruccion || 'Construcción');
                                etiquetaTipo = tipoConstruccion ? `Construcción · ${tipoConstruccion}` : 'Construcción autorizada';
                                notas = data.descripcionConstruccion || '';
                                horaProgramada = null;
                                horarioLlegada = data.horarioLlegada || null;
                            } else if (tipoNormalized === 'remodelacion') {
                                fechaInicio = toDateSafe(data.fechaInicioTrabajo) || baseFecha;
                                if (fechaInicio && duracionDias && duracionDias > 0) {
                                    const end = new Date(fechaInicio);
                                    end.setDate(end.getDate() + Math.max(duracionDias - 1, 0));
                                    fechaFin = end;
                                }
                                proveedor = data.nombreContratista || toTitleCase(data.responsableTrabajo) || '';
                                const tipoTrabajo = toTitleCase(data.tipoTrabajo || 'Remodelación');
                                etiquetaTipo = tipoTrabajo ? `Remodelación · ${tipoTrabajo}` : 'Remodelación autorizada';
                                notas = data.descripcionTrabajos || '';
                                horaProgramada = null;
                                horarioLlegada = data.horarioLlegada || null;
                            }

                            const ubicacion = data.lote
                                || data.houseLabel
                                || data.numeroCasa
                                || data.numeroLote
                                || 'Sin ubicación asignada';

                            const fechaComparable = fechaInicio
                                ? fechaInicio.getTime()
                                : (fechaFin ? fechaFin.getTime() : Number.POSITIVE_INFINITY);

                            if (!fechaInicio) return null;

                            return {
                                id,
                                tipoSolicitud: tipoNormalized,
                                etiquetaTipo: etiquetaTipo || tipo || 'Registro autorizado',
                                residente: data.nombreCompleto || data.uploaderNombre || 'Residente',
                                ubicacion,
                                proveedor: proveedor || null,
                                fechaInicio,
                                fechaFin,
                                horaProgramada,
                                horarioLlegada,
                                fechaComparable,
                                notas: notas || '',
                                duracionDias,
                            };
                        })
                        .filter(Boolean)
                        .sort((a, b) => a.fechaComparable - b.fechaComparable);

                    setProgramacionesAcceso(items);
                    setProgramacionesError('');
                    setProgramacionesLoading(false);
                } catch (processingError) {
                    if (!isMounted) return;
                    console.error('Vigilancia: error al procesar programaciones de acceso', processingError);
                    setProgramacionesAcceso([]);
                    setProgramacionesError('No se pudieron procesar los accesos programados.');
                    setProgramacionesLoading(false);
                }
            },
            (listenerError) => {
                if (!isMounted) return;
                console.error('Vigilancia: error al cargar accesos programados', listenerError);
                setProgramacionesError('No se pudieron cargar los accesos programados.');
                setProgramacionesAcceso([]);
                setProgramacionesLoading(false);
            },
        );

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, [fraccionamientoId, isResident]);

    // EFECTO: Cargar paquetes para el residente
    useEffect(() => {
        let isMounted = true;
        if (!fraccionamientoId || !isResident || !ownerDepartamento) {
            setResidentPackages([]);
            setResidentPackagesLoading(false);
            return;
        }

        setResidentPackagesLoading(true);
        // UNIFICADO: Usar 'paqueteria' que es la colección con reglas correctas
        const paquetesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'paqueteria');

        // OPTIMIZADO: Quitar orderBy para evitar necesidad de índice compuesto
        const q = query(
            paquetesRef,
            where('departamento', '==', ownerDepartamento),
            limit(40)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!isMounted) return;

            const pkgs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // ORDENAMIENTO EN MEMORIA: Más reciente primero
            pkgs.sort((a, b) => {
                const dateA = toDateSafe(a.fechaRecepcion) || new Date(0);
                const dateB = toDateSafe(b.fechaRecepcion) || new Date(0);
                return dateB - dateA;
            });

            setResidentPackages(pkgs);
            setResidentPackagesLoading(false);
            setResidentPackagesError('');
        }, (err) => {
            if (!isMounted) return;
            console.error('Error cargando paquetes residente:', err);
            setResidentPackagesError('No se pudieron cargar los paquetes.');
            setResidentPackagesLoading(false);
        });

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, [fraccionamientoId, isResident, ownerDepartamento]);

    useEffect(() => {
        let isMounted = true;
        if (!showPaqueteriaModal || isResident || !fraccionamientoId) {
            setPaqueteriaList([]);
            setPaqueteriaLoading(false);
            setPaqueteriaListaError('');
            return () => { isMounted = false; };
        }

        setPaqueteriaLoading(true);
        const paquetesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'paqueteria');
        const unsubscribe = onSnapshot(
            query(paquetesRef, orderBy('fechaRecepcion', 'desc'), limit(60)),
            (snapshot) => {
                if (!isMounted) return;
                try {
                    const items = snapshot.docs.map((docSnap) => {
                        const data = docSnap.data() || {};
                        const fechaRecepcion = typeof data.fechaRecepcion?.toDate === 'function'
                            ? data.fechaRecepcion.toDate()
                            : null;
                        const registradoEn = typeof data.registradoEn?.toDate === 'function'
                            ? data.registradoEn.toDate()
                            : null;
                        const entregadoEn = typeof data.entregadoEn?.toDate === 'function'
                            ? data.entregadoEn.toDate()
                            : null;
                        return {
                            id: docSnap.id,
                            departamento: data.departamento || '',
                            empresa: data.empresa || '',
                            destinatario: data.destinatario || '',
                            destinatarioUid: data.destinatarioUid || null,
                            guardiaRecibio: data.guardia || '',
                            guardiaEntrego: data.entregadoPor || null,
                            fechaRecepcion,
                            registradoEn,
                            entregadoEn,
                            estado: data.estado || 'pendiente',
                            comentarios: data.comentarios || '',
                        };
                    });

                    items.sort((a, b) => {
                        const timeA = a.fechaRecepcion ? a.fechaRecepcion.getTime() : 0;
                        const timeB = b.fechaRecepcion ? b.fechaRecepcion.getTime() : 0;
                        return timeB - timeA;
                    });

                    setPaqueteriaList(items);
                    setPaqueteriaListaError('');
                } catch (processingError) {
                    console.error('Vigilancia: error procesando listado de paquetería', processingError);
                    setPaqueteriaList([]);
                    setPaqueteriaListaError('No se pudo cargar la paquetería registrada.');
                } finally {
                    setPaqueteriaLoading(false);
                }
            },
            (listenerError) => {
                console.error('Vigilancia: error al escuchar listado de paquetería', listenerError);
                setPaqueteriaList([]);
                setPaqueteriaListaError('No se pudo cargar la paquetería registrada.');
                setPaqueteriaLoading(false);
            },
        );

        return unsubscribe;
    }, [showPaqueteriaModal, isResident, fraccionamientoId]);

    // Filtrado de paquetería para caseta
    const paqueteriaFiltrada = useMemo(() => {
        return paqueteriaList.filter((pkg) => {
            const matchesSearch =
                (pkg.departamento || '').toLowerCase().includes(paqueteriaSearch.toLowerCase()) ||
                (pkg.destinatario || '').toLowerCase().includes(paqueteriaSearch.toLowerCase()) ||
                (pkg.empresa || '').toLowerCase().includes(paqueteriaSearch.toLowerCase());

            const pkgDate = pkg.fechaRecepcion ? (pkg.fechaRecepcion.toDate ? pkg.fechaRecepcion.toDate() : new Date(pkg.fechaRecepcion)) : null;
            const matchesDate = !paqueteriaSearchDate || (pkgDate && pkgDate.toISOString().split('T')[0] === paqueteriaSearchDate);

            const matchesStatus = !paqueteriaFilterPendiente || pkg.estado === 'pendiente';

            return matchesSearch && matchesDate && matchesStatus;
        });
    }, [paqueteriaList, paqueteriaSearch, paqueteriaSearchDate, paqueteriaFilterPendiente]);

    // Paginación de paquetería para caseta
    const paqueteriaPaginada = useMemo(() => {
        const startIndex = (paqueteriaPage - 1) * CASETA_PACKAGES_PER_PAGE;
        return paqueteriaFiltrada.slice(startIndex, startIndex + CASETA_PACKAGES_PER_PAGE);
    }, [paqueteriaFiltrada, paqueteriaPage]);

    const totalPaqueteriaPages = Math.ceil(paqueteriaFiltrada.length / CASETA_PACKAGES_PER_PAGE);
    
    // Filtrado de paquetería para residente
    const residentPackagesFiltrados = useMemo(() => {
        if (!isResident) return [];
        const query = residentPackagesSearchQuery.toLowerCase();
        if (!query) return residentPackages;

        return residentPackages.filter((pkg) => {
            const empresaStr = (pkg.empresa || '').toLowerCase();
            
            const pkgDate = pkg.fechaRecepcion ? (pkg.fechaRecepcion.toDate ? pkg.fechaRecepcion.toDate() : new Date(pkg.fechaRecepcion)) : null;
            const formattedDate = pkgDate ? formatResidentPackageDate(pkgDate).toLowerCase() : '';

            // Búsqueda específica por empresa y fecha para residentes
            return empresaStr.includes(query) || formattedDate.includes(query);
        });
    }, [residentPackages, residentPackagesSearchQuery, isResident]);

    // Resetear paqueteriaPage cuando cambian los filtros
    useEffect(() => {
        setPaqueteriaPage(1);
    }, [paqueteriaSearch, paqueteriaSearchDate, paqueteriaFilterPendiente]);

    useEffect(() => {
        setResidentPackagesPage(1);
    }, [residentPackagesSearchQuery]);

    // Mostrar todas las programaciones de la semana
    const programacionesDestacadas = useMemo(() => {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const finSemana = new Date(hoy);
        finSemana.setDate(hoy.getDate() + 7);
        finSemana.setHours(23, 59, 59, 999);

        return programacionesAcceso.filter((prog) => {
            if (!prog.fechaInicio) return false;
            const fechaProg = prog.fechaInicio instanceof Date ? prog.fechaInicio : toDateSafe(prog.fechaInicio);
            if (!fechaProg) return false;
            fechaProg.setHours(0, 0, 0, 0);
            return fechaProg >= hoy && fechaProg <= finSemana;
        });
    }, [programacionesAcceso]);

    const visitasFiltradas = useMemo(() => {
        if (!isSuperAdmin && !isCaseta) {
            return visitasFrecuentes;
        }
        const term = visitasFilterDepto.trim().toLowerCase();
        if (!term) {
            return visitasFrecuentes;
        }
        return visitasFrecuentes.filter((item) => {
            const base = String(item.ownerDepartamento || '').toLowerCase();
            return base.includes(term);
        });
    }, [isSuperAdmin, isCaseta, visitasFilterDepto, visitasFrecuentes]);

    const [resolutedDeptoMap, setResolutedDeptoMap] = useState({});

    // Sincronizar resoluciones iniciales desde visitasFrecuentes
    useEffect(() => {
        if (!visitasFrecuentes.length) return;
        
        setResolutedDeptoMap(prev => {
            let changed = false;
            const newMap = { ...prev };
            visitasFrecuentes.forEach(visita => {
                if (visita.ownerDepartamento && !newMap[visita.id]) {
                    newMap[visita.id] = visita.ownerDepartamento;
                    changed = true;
                }
            });
            return changed ? newMap : prev;
        });
    }, [visitasFrecuentes]);

    // Resolver departamentos async para las que no lo tienen
    const resolvingFrecuentesRef = useRef(new Set());
    useEffect(() => {
        let isMounted = true;
        if (!fraccionamientoId || !visitasFrecuentes.length) {
            return () => { isMounted = false; };
        }

        const resolveVisitasDepartamentos = async () => {
            const toResolve = visitasFrecuentes.filter(v => 
                !v.ownerDepartamento && 
                !resolvingFrecuentesRef.current.has(v.id) &&
                !resolutedDeptoMap[v.id] &&
                v.ownerNombre
            ).slice(0, 15);

            if (toResolve.length === 0) return;

            const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
            const newResolutions = {};

            try {
                await Promise.all(toResolve.map(async (visita) => {
                    resolvingFrecuentesRef.current.add(visita.id);
                    try {
                        const q = query(casasRef, where('nombre_residente', '==', visita.ownerNombre), limit(1));
                        const casaSnap = await getDocs(q);
                        
                        if (!isMounted) return;

                        if (!casaSnap.empty) {
                            const casaData = casaSnap.docs[0].data();
                            newResolutions[visita.id] = casaData.numero || 'Sin número';
                        } else {
                            newResolutions[visita.id] = 'Sin asignar';
                        }
                    } catch (err) {
                        if (isMounted) resolvingFrecuentesRef.current.delete(visita.id);
                    }
                }));

                if (isMounted && Object.keys(newResolutions).length > 0) {
                    setResolutedDeptoMap(prev => ({ ...prev, ...newResolutions }));
                }
            } catch (error) {
                console.warn('Error en bloque de resolución:', error);
            }
        };

        resolveVisitasDepartamentos();
        return () => { isMounted = false; };
    }, [fraccionamientoId, visitasFrecuentes, resolutedDeptoMap]);

    const visitasPaginadas = useMemo(() => {
        const startIndex = (visitasPage - 1) * VISITAS_PER_PAGE;
        return visitasFiltradas.slice(startIndex, startIndex + VISITAS_PER_PAGE);
    }, [visitasFiltradas, visitasPage]);

    const totalVisitasPages = Math.ceil(visitasFiltradas.length / VISITAS_PER_PAGE);

    const visitasDestacadas = useMemo(() => {
        if (showAllVisitas) {
            return visitasFiltradas;
        }
        return visitasFiltradas.slice(0, 6);
    }, [showAllVisitas, visitasFiltradas]);


    const visitasPorCategoria = useMemo(() => {
        const counts = {};
        visitasFrecuentes.forEach((item) => {
            const key = String(item.categoria || '').toLowerCase();
            if (!counts[key]) {
                counts[key] = 0;
            }
            counts[key] += 1;
        });
        return counts;
    }, [visitasFrecuentes]);

    useEffect(() => {
        if (!detalleVisita) {
            return;
        }
        const updated = visitasFrecuentes.find((item) => item.id === detalleVisita.id);
        if (!updated) {
            if (!detalleSubmitting && !detalleDeleting) {
                setDetalleVisita(null);
                setDetalleForm(null);
                setDetalleArchivo(null);
                setDetalleEditMode(false);
                setDetalleConfirmDelete(false);
                setDetalleError('');
            }
            return;
        }
        if (updated === detalleVisita) {
            return;
        }
        setDetalleVisita(updated);
        if (!detalleEditMode) {
            setDetalleForm({
                nombre: updated.nombre || '',
                categoria: updated.categoria || '',
                placa: updated.placa || '',
                color: updated.color || '',
                notas: updated.notas || '',
            });
        }
    }, [detalleDeleting, detalleEditMode, detalleSubmitting, detalleVisita, visitasFrecuentes]);

    useEffect(() => {
        let isMounted = true;
        if (!fraccionamientoId || !currentUid || (!isResident && !isSuperAdmin && !isCaseta)) {
            setVisitasFrecuentes([]);
            setVisitasLoading(false);
            setVisitasError('');
            return () => { isMounted = false; };
        }

        setVisitasLoading(true);
        const visitasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'visitasFrecuentes');
        const visitasQuery = (isSuperAdmin || isCaseta)
            ? query(visitasRef, orderBy('createdAt', 'desc'), limit(200))
            : query(
                visitasRef,
                where('ownerUid', '==', currentUid),
                orderBy('createdAt', 'desc'),
                limit(40),
            );

        const unsubscribe = onSnapshot(
            visitasQuery,
            (snapshot) => {
                if (!isMounted) return;
                try {
                    const items = snapshot.docs.map((docSnap) => {
                        const data = docSnap.data() || {};
                        const createdAt = typeof data.createdAt?.toDate === 'function'
                            ? data.createdAt.toDate()
                            : null;
                        const updatedAt = typeof data.updatedAt?.toDate === 'function'
                            ? data.updatedAt.toDate()
                            : null;
                        return {
                            id: docSnap.id,
                            nombre: data.nombre || '',
                            telefono: data.telefono || '',
                            categoria: data.categoria || data.tipoVisitante || '',
                            placa: data.placa || '',
                            color: data.color || '',
                            notas: data.notas || '',
                            horario: data.horario || INITIAL_HORARIO,
                            identificacionUrl: data.identificacionUrl || '',
                            identificacionName: data.identificacionName || '',
                            identificacionSize: data.identificacionSize || null,
                            identificacionType: data.identificacionType || '',
                            identificacionPath: data.identificacionPath || '',
                            ownerUid: data.ownerUid || '',
                            ownerNombre: data.ownerNombre || '',
                            ownerDepartamento: data.ownerDepartamento || '',
                            fraccionamientoId: data.fraccionamientoId || fraccionamientoId,
                            createdAt,
                            updatedAt,
                            raw: data,
                        };
                    });
                    setVisitasFrecuentes(items);
                    setVisitasError('');
                } catch (processingError) {
                    if (!isMounted) return;
                    console.error('Vigilancia: error procesando visitas frecuentes', processingError);
                    setVisitasFrecuentes([]);
                    setVisitasError('No se pudieron procesar las visitas frecuentes.');
                } finally {
                    if (isMounted) setVisitasLoading(false);
                }
            },
            (listenerError) => {
                if (!isMounted) return;
                console.error('Vigilancia: error al escuchar visitas frecuentes', listenerError);
                setVisitasFrecuentes([]);
                setVisitasError('No se pudieron cargar las visitas frecuentes.');
                setVisitasLoading(false);
            },
        );

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, [fraccionamientoId, currentUid, isResident, isSuperAdmin, isCaseta]);

    // Generar horarios de 8am a 10pm para paquetería
    const horariosDisponibles = useMemo(() => {
        const horarios = [];
        for (let hora = 8; hora <= 22; hora++) {
            const horaStr = hora.toString().padStart(2, '0');
            horarios.push({ value: `${horaStr}:00`, label: `${hora === 12 ? 12 : hora > 12 ? hora - 12 : hora}:00 ${hora >= 12 ? 'pm' : 'am'}` });
            if (hora < 22) {
                horarios.push({ value: `${horaStr}:30`, label: `${hora === 12 ? 12 : hora > 12 ? hora - 12 : hora}:30 ${hora >= 12 ? 'pm' : 'am'}` });
            }
        }
        return horarios;
    }, []);

    const handlePaqueteChange = useCallback((event) => {
        const { name, value } = event.target;
        setPaquete((prev) => ({ ...prev, [name]: value }));
        
        // Limpiar error específico si existe
        if (paqueteriaFieldErrors[name]) {
            setPaqueteriaFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }

        if (name === 'destinatario') {
            destinatarioTouchedRef.current = true;
        }

        if (name === 'empresa') {
            const searchTerm = value.trim().toLowerCase();
            if (searchTerm.length > 0) {
                const filtered = COURIER_COMPANIES.filter(company => 
                    company.toLowerCase().includes(searchTerm)
                ).slice(0, 10);
                setCourierSuggestions(filtered);
                setShowCourierSuggestions(filtered.length > 0);
            } else {
                setCourierSuggestions([]);
                setShowCourierSuggestions(false);
            }
        }
        
        if (name === 'depto') {
            destinatarioTouchedRef.current = false;
            
            // Si es el campo departamento de paquetería, mostrar sugerencias
            const searchTerm = value.trim().toLowerCase();
            if (searchTerm.length > 0 && casasList.length > 0) {
                const filtered = casasList.filter(casa => {
                    const numero = String(casa.numero || '').toLowerCase();
                    return numero.includes(searchTerm);
                }).slice(0, 10); // Limitar a 10 sugerencias
                setCasaSuggestions(filtered);
                setShowCasaSuggestions(filtered.length > 0);
            } else {
                setCasaSuggestions([]);
                setShowCasaSuggestions(false);
            }
        }
    }, [paqueteriaFieldErrors, casasList, COURIER_COMPANIES]);

    const findDestinatario = useCallback(async (rawDepto) => {
        const trimmed = rawDepto.trim();
        if (!trimmed || !fraccionamientoId) {
            return null;
        }

        const targetKeys = buildKeySet(trimmed);
        console.log('[findDestinatario] Buscando departamento:', trimmed, 'Variantes generadas:', Array.from(targetKeys));

        if (targetKeys.size === 0) {
            return null;
        }

        // Primero buscar en casas - intentar múltiples campos
        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const casaFields = ['numero', 'numeroLote', 'lote', 'departamento', 'numeroCasa'];

        try {
            // Intentar búsqueda por cada campo y cada variante
            for (const field of casaFields) {
                for (const key of targetKeys) {
                    console.log('[findDestinatario] Buscando en casas con campo:', field, '==', key);
                    try {
                        const casaSnapshot = await getDocs(query(casasRef, where(field, '==', key)));
                        if (!casaSnapshot.empty) {
                            const casaDoc = casaSnapshot.docs[0];
                            const casaData = casaDoc.data();

                            console.log('[findDestinatario] ✅ Casa encontrada:', {
                                campo: field,
                                valor: key,
                                casaDocId: casaDoc.id,
                                casaData: casaData,
                            });

                            // Si la casa tiene propietarioId, buscar el propietario en usuarios
                            if (casaData.propietarioId) {
                                const usuarioRef = doc(db, 'usuarios', casaData.propietarioId);
                                try {
                                    const usuarioSnap = await getDoc(usuarioRef);
                                    if (usuarioSnap.exists()) {
                                        const userData = usuarioSnap.data();
                                        console.log('[findDestinatario] ✅ Usuario propietario encontrado por propietarioId:', {
                                            usuarioId: usuarioSnap.id,
                                            usuarioData: userData,
                                        });

                                        // Priorizar nombre_residente de la casa sobre los nombres del usuario
                                        const nombreFinal = casaData.nombre_residente || extractResidentDisplayName(userData) || userData.nombreCompleto || userData.displayName || 'Propietario';

                                        return {
                                            id: usuarioSnap.id,
                                            data: {
                                                ...userData,
                                                nombreCompleto: nombreFinal,
                                                displayName: nombreFinal,
                                            }
                                        };
                                    }
                                } catch (error) {
                                    console.warn('Vigilancia: error al obtener propietario desde propietarioId', error);
                                }
                            }

                            // Si no tiene propietarioId, buscar el usuario por el número de casa
                            console.log('[findDestinatario] Casa sin propietarioId. Buscando usuario por numero...', key);
                            const usuariosRef = collection(db, 'usuarios');
                            const userFields = ['numeroCasa', 'houseLabel', 'lote', 'numeroLote', 'departamento'];

                            for (const userField of userFields) {
                                for (const searchKey of targetKeys) {
                                    try {
                                        const userSnapshot = await getDocs(
                                            query(usuariosRef, where('fraccionamientoId', '==', fraccionamientoId), where(userField, '==', searchKey))
                                        );
                                        if (!userSnapshot.empty) {
                                            const usuarioSnap = userSnapshot.docs[0];
                                            const userData = usuarioSnap.data();

                                            console.log('[findDestinatario] ✅ Usuario encontrado por campo:', {
                                                field: userField,
                                                valor: searchKey,
                                                usuarioId: usuarioSnap.id,
                                                usuarioData: userData,
                                            });

                                            // Priorizar nombre_residente de la casa
                                            const nombreFinal = casaData.nombre_residente || extractResidentDisplayName(userData) || userData.nombreCompleto || userData.displayName || 'Propietario';

                                            return {
                                                id: usuarioSnap.id,
                                                data: {
                                                    ...userData,
                                                    nombreCompleto: nombreFinal,
                                                    displayName: nombreFinal,
                                                }
                                            };
                                        }
                                    } catch (error) {
                                        console.warn('Vigilancia: búsqueda de usuario por', userField, 'falló', error);
                                    }
                                }
                            }

                            // Si no encontró usuario pero encontró casa, retornar con info de casa usando nombre_residente
                            console.log('[findDestinatario] ⚠️ Casa encontrada pero sin usuario. Retornando info de casa.');
                            const nombreResidente = casaData.nombre_residente || extractResidentDisplayName(casaData) || casaData.nombrePropietario || 'Propietario';
                            console.log('[findDestinatario] Nombre residente extraído:', nombreResidente);

                            return {
                                id: null,  // Sin UID - no se guardará destinatarioUid
                                data: {
                                    nombreCompleto: nombreResidente,
                                    displayName: nombreResidente,
                                    numeroCasa: casaData[field] || key,
                                    houseLabel: casaData[field] || key,
                                },
                            };
                        }
                    } catch (fieldError) {
                        console.warn('[findDestinatario] Error buscando campo', field, ':', fieldError);
                    }
                }
            }

            console.log('[findDestinatario] ❌ No se encontró casa en ningún campo');
        } catch (error) {
            console.warn('Vigilancia: error al buscar en casas', error);
        }

        // Fallback: buscar directamente en usuarios
        console.log('[findDestinatario] 🔍 Casa no encontrada. Buscando directamente en usuarios...');
        const usuariosRef = collection(db, 'usuarios');

        const tryQuery = async (field) => {
            const matches = [];
            for (const key of targetKeys) {
                try {
                    console.log('[findDestinatario] Buscando usuario con campo:', field, '==', key);
                    const snapshot = await getDocs(query(usuariosRef, where('fraccionamientoId', '==', fraccionamientoId), where(field, '==', key)));
                    snapshot.forEach((docSnap) => {
                        console.log('[findDestinatario] ✅ Coincidencia en campo', field, ':', {
                            usuarioId: docSnap.id,
                            usuarioData: docSnap.data(),
                        });
                        matches.push({ id: docSnap.id, data: docSnap.data() });
                    });
                } catch (error) {
                    console.warn('Vigilancia: búsqueda de destinatario falló en campo', field, error);
                }
                if (matches.length > 0) {
                    return matches[0];
                }
            }
            return null;
        };

        const prioritizedFields = ['numeroCasa', 'houseLabel', 'lote', 'numeroLote', 'departamento'];
        for (const field of prioritizedFields) {
            const match = await tryQuery(field);
            if (match) {
                return match;
            }
        }

        try {
            console.log('[findDestinatario] 🔍 Última búsqueda: escaneando todos los usuarios...');
            const snapshot = await getDocs(query(usuariosRef, where('fraccionamientoId', '==', fraccionamientoId)));
            for (const docSnap of snapshot.docs) {
                const data = docSnap.data();
                const userKeys = extractUserKeys(data);
                const hasMatch = Array.from(targetKeys).some((key) => userKeys.has(key));
                if (hasMatch) {
                    console.log('[findDestinatario] ✅ Coincidencia por extractUserKeys:', {
                        usuarioId: docSnap.id,
                        usuarioData: data,
                    });
                    return { id: docSnap.id, data };
                }
            }
        } catch (error) {
            console.warn('Vigilancia: error al escanear usuarios', error);
        }

        console.log('[findDestinatario] ❌ No se encontró ningún destinatario');
        return null;
    }, [buildKeySet, extractUserKeys, fraccionamientoId]);

    useEffect(() => {
        if (qrResult && qrModalBodyRef.current) {
            setTimeout(() => {
                qrModalBodyRef.current.scrollTo({
                    top: qrModalBodyRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }, 100);
        }
    }, [qrResult]);

    useEffect(() => {
        const depto = paquete.depto.trim();

        if (!fraccionamientoId || !depto) {
            setDestinatarioLookup((prev) => (
                prev.status === 'idle' && prev.nombre === '' && prev.uid === null && prev.depto === ''
                    ? prev
                    : {
                        status: 'idle',
                        nombre: '',
                        uid: null,
                        depto: '',
                    }
            ));

            if (!depto && !destinatarioTouchedRef.current) {
                setPaquete((prev) => (prev.destinatario ? { ...prev, destinatario: '' } : prev));
            }

            return undefined;
        }

        let cancelled = false;
        const normalizedDepto = depto;
        const delayMs = 350;

        const timerId = setTimeout(async () => {
            if (cancelled) {
                return;
            }

            setDestinatarioLookup({
                status: 'searching',
                nombre: '',
                uid: null,
                depto: normalizedDepto,
            });

            try {
                const match = await findDestinatario(normalizedDepto);
                if (cancelled) {
                    return;
                }

                if (match) {
                    const nombreDetectado = extractResidentDisplayName(match.data);
                    setDestinatarioLookup({
                        status: 'found',
                        nombre: nombreDetectado,
                        uid: match.id,
                        depto: normalizedDepto,
                    });

                    if (!destinatarioTouchedRef.current && nombreDetectado) {
                        setPaquete((prev) => {
                            if (prev.destinatario === nombreDetectado) {
                                return prev;
                            }
                            return { ...prev, destinatario: nombreDetectado };
                        });
                    }
                } else {
                    setDestinatarioLookup({
                        status: 'not-found',
                        nombre: '',
                        uid: null,
                        depto: normalizedDepto,
                    });

                    if (!destinatarioTouchedRef.current) {
                        setPaquete((prev) => (prev.destinatario ? { ...prev, destinatario: '' } : prev));
                    }
                }
            } catch (error) {
                console.warn('Vigilancia: error en búsqueda automática de destinatario', error);
                if (!cancelled) {
                    setDestinatarioLookup({
                        status: 'error',
                        nombre: '',
                        uid: null,
                        depto: normalizedDepto,
                    });
                }
            }
        }, delayMs);

        return () => {
            cancelled = true;
            clearTimeout(timerId);
        };
    }, [findDestinatario, fraccionamientoId, paquete.depto]);

    const handleRegistrarPaquete = useCallback(async () => {
        if (!fraccionamientoId || !currentUser?.uid) {
            setPaqueteriaError('No pudimos validar tu sesión. Intenta nuevamente.');
            return;
        }

        const depto = paquete.depto.trim();
        const empresa = paquete.empresa.trim();
        const fechaInput = paquete.fecha || '';
        const horaInput = paquete.hora || '08:00';
        const guardia = paquete.guardia.trim();
        const destinatario = paquete.destinatario.trim();

        const errors = {};
        if (!depto) errors.depto = true;
        if (!empresa) errors.empresa = true;
        if (!destinatario) errors.destinatario = true;
        if (!guardia) errors.guardia = true;

        if (Object.keys(errors).length > 0) {
            setPaqueteriaFieldErrors(errors);
            setPaqueteriaError('Por favor completa todos los campos marcados del registro.');
            return;
        }

        // VALIDACIÓN ESTRICTA: El número de casa debe existir en casasList
        const exactMatch = casasList.find(casa => 
            String(casa.numero || '').toLowerCase().trim() === depto.toLowerCase()
        );

        if (!exactMatch) {
            setPaqueteriaFieldErrors({ depto: true });
            
            Swal.fire({
                title: 'Departamento No Encontrado',
                text: `El número de departamento "${depto}" no existe en el sistema. Selecciona uno válido de la lista.`,
                icon: 'error',
                timer: 6000,
                timerProgressBar: true,
                showCloseButton: true,
                confirmButtonColor: '#10b981',
                confirmButtonText: 'Entendido',
                willClose: () => {
                    // Devolver el foco al campo para facilitar la corrección
                    const input = document.querySelector('input[name="depto"]');
                    if (input) input.focus();
                }
            });
            return;
        }

        setPaqueteriaFieldErrors({});

        // Combinar fecha y hora evitando desfase de zona horaria (UTC vs Local)
        let fechaRecepcion;
        if (fechaInput) {
            const [hours, minutes] = horaInput.split(':').map(Number);
            const [year, month, day] = fechaInput.split('-').map(Number);
            // El constructor new Date(año, mesIndex, día...) usa tiempo LOCAL
            fechaRecepcion = new Date(year, month - 1, day, hours, minutes, 0, 0);
        } else {
            fechaRecepcion = new Date();
        }

        if (Number.isNaN(fechaRecepcion.getTime())) {
            setPaqueteriaError('La fecha de recepción no es válida.');
            return;
        }

        setPaqueteriaError('');
        setRegistrandoPaquete(true);

        const deptoVariants = buildKeySet(depto);
        let destinatarioUid = null;

        console.log('[REGISTRAR PAQUETE] Iniciando. depto:', depto, 'empresa:', empresa, 'destinatario:', destinatario);

        if (destinatarioLookup.status === 'found' && destinatarioLookup.uid) {
            const lookupVariants = buildKeySet(destinatarioLookup.depto || '');
            const hasMatchingDepto = Array.from(deptoVariants).some((variant) => lookupVariants.has(variant));
            if (hasMatchingDepto) {
                destinatarioUid = destinatarioLookup.uid;
                console.log('[REGISTRAR PAQUETE] destinatarioUid encontrado en lookup:', destinatarioUid);
            }
        }

        if (!destinatarioUid) {
            try {
                const lookupResult = await findDestinatario(depto);
                console.log('[REGISTRAR PAQUETE] findDestinatario resultado:', lookupResult);
                destinatarioUid = lookupResult?.id || null;
                console.log('[REGISTRAR PAQUETE] destinatarioUid asignado:', destinatarioUid);
            } catch (searchError) {
                console.warn('Vigilancia: no se pudo identificar destinatario por departamento', searchError);
            }
        }

        try {
            if (!navigator.onLine || currentUser?.isOffline) {
                console.log('[REGISTRAR PAQUETE] Modo offline detectado, enviando a cola de sincronización.');
                pushToSyncQueue('registerPaquete', {
                    fraccionamientoId,
                    departamento: depto,
                    empresa,
                    destinatario,
                    guardia,
                    fechaRecepcionIso: fechaRecepcion.toISOString(),
                    destinatarioUid,
                });

                // Almacenar también en caché local de Firestore para visibilidad inmediata
                const mockId = 'offline-' + Date.now();
                const mockPackage = {
                    id: mockId,
                    fraccionamientoId,
                    departamento: depto,
                    empresa,
                    destinatario,
                    guardia, // Se mapea a guardiaRecibio en el listener
                    fechaRecepcion: fechaRecepcion, // Objeto Date para el listener
                    registradoEn: new Date(),
                    destinatarioUid: destinatarioUid || null,
                    estado: 'pendiente',
                    offline: true
                };

                const paquetesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'paqueteria');
                // Sanitizar para evitar errores de Firebase por campos undefined
                const cleanMock = JSON.parse(JSON.stringify(mockPackage));
                // Restaurar la fecha que JSON.stringify convirtió a string
                cleanMock.fechaRecepcion = mockPackage.fechaRecepcion;
                cleanMock.registradoEn = mockPackage.registradoEn;

                addDoc(paquetesRef, cleanMock).catch(e => console.warn('Sync pended', e));

                setShowPaqueteriaFormModal(false);
                setSuccessModalData({
                    title: 'Paquete Registrado Offline',
                    message: `El paquete de ${empresa} para el Depto ${depto} ha sido guardado localmente. Se sincronizará automáticamente al conectar internet.`
                });
                openModalWithHistory(setShowSuccessModal);

                resetPaqueteForm();
                return;
            }

            const registroRef = collection(db, 'fraccionamientos', fraccionamientoId, 'paqueteria');
            console.log('[REGISTRAR PAQUETE] Guardando documento con:', {
                fraccionamientoId,
                departamento: depto,
                empresa,
                destinatario,
                guardia,
                destinatarioUid,
            });

            await addDoc(registroRef, {
                fraccionamientoId,
                departamento: depto,
                empresa,
                destinatario,
                guardia,
                fechaRecepcion: Timestamp.fromDate(fechaRecepcion),
                registradoEn: serverTimestamp(),
                registradoPor: currentUser.uid,
                destinatarioUid,
                estado: 'pendiente',
            });

            console.log('[REGISTRAR PAQUETE] Documento guardado exitosamente');

            try {
                const horaLabel = fechaRecepcion.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                const fechaLabel = fechaRecepcion.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Paquete recibido para ${depto}`,
                    descripcion: `Se registró paquetería de ${empresa} para ${destinatario}. Guardia: ${guardia}.`,
                    categoria: 'Vigilancia',
                    tipoMovimiento: 'paqueteria_registrada',
                    origen: 'vigilancia',
                    metadata: {
                        departamento: depto,
                        empresa,
                        destinatario,
                        guardia,
                        fechaRecepcionIso: fechaRecepcion.toISOString(),
                        fechaLabel,
                        horaLabel,
                        destinatarioUid,
                    },
                });
            } catch (movementError) {
                console.error('Vigilancia: no se pudo registrar movimiento de paquetería', movementError);
            }

            showNotification('Paquete registrado y notificación enviada.', 'success');
            
            setShowPaqueteriaFormModal(false);
            setSuccessModalData({
                title: 'Registro Exitoso',
                message: `Se ha registrado correctamente la entrada del paquete de ${empresa} para el Depto ${depto}. El residente será notificado.`
            });
            openModalWithHistory(setShowSuccessModal);

            resetPaqueteForm();
        } catch (error) {
            console.error('Vigilancia: error al registrar paquetería', error);
            setPaqueteriaError('No se pudo registrar el paquete. Intenta nuevamente.');
        } finally {
            setRegistrandoPaquete(false);
        }
    }, [currentUser?.uid, destinatarioLookup, findDestinatario, fraccionamientoId, paquete.depto, paquete.destinatario, paquete.empresa, paquete.fecha, paquete.hora, paquete.guardia, showNotification, resetPaqueteForm]);

    const getGenerateQrCallable = useCallback(() => {
        if (!generateQrCallableRef.current) {
            generateQrCallableRef.current = httpsCallable(functions, 'generateGuestVisitQr');
        }
        return generateQrCallableRef.current;
    }, []);

    const getValidateVisitCallable = useCallback(() => {
        if (!validateQrCallableRef.current) {
            try {
                validateQrCallableRef.current = httpsCallable(functions, 'validateGuestVisitQr');
            } catch (e) {
                console.error("Vigilancia: error al inicializar callable", e);
            }
        }
        return validateQrCallableRef.current;
    }, []);

    const resetQrForm = useCallback(() => {
        setQrForm({
            usarVisitaFrecuente: false,
            visitaFrecuenteId: '',
            nombre: '',
            telefono: '',
            numeroVisitantes: '1',
            fecha: '',
            hora: '',
            placa: '',
            color: '',
            categoria: '',
            observaciones: '',
            correoDestino: ownerEmail || '',
            correoInvitado: '',
            tipoTransporte: 'En vehiculo',
            departamento: ownerDepartamento || '',
            esMultiAcceso: false,
            duracionHoras: '4',
            limiteEscaneos: '1',
        });
        setQrFormError('');
        setQrResult(null);
        setShowAdvancedQrOptions(false);
    }, [ownerEmail, ownerDepartamento]);

    const handleGenerarQR = useCallback(() => {
        if (isResident && isQrBloqueado) {
            mostrarAlertaVigilancia('La generación de códigos QR de invitado está temporalmente inhabilitada por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
            return;
        }
        if (!isResident && !isSuperAdmin) {
            showNotification('Solo residentes o superadmin pueden generar códigos de invitado.', 'warning');
            return;
        }
        resetQrForm();
        openModalWithHistory(setShowQrModal);
    }, [isResident, isQrBloqueado, mostrarAlertaVigilancia, isSuperAdmin, resetQrForm, showNotification, openModalWithHistory]);

    const handleCloseQrModal = useCallback(() => {
        if (qrSubmitting) {
            return;
        }
        setShowQrModal(false);
        resetQrForm();
        setHasDecidedSchedule(null);
    }, [qrSubmitting, resetQrForm]);

    const handleOpenQrValidationModal = useCallback(() => {
        console.log('[Vigilancia] handleOpenQrValidationModal llamado');
        openModalWithHistory(setShowQrValidationModal);
        setCameraActive(true);
        setQrScannerError('');
        setValidationError('');
        setValidationResult(null);
    }, [openModalWithHistory]);

    const handleCloseQrValidationModal = useCallback(() => {
        setShowQrValidationModal(false);
        setCameraActive(false);
        setValidationError('');
        setQrScannerError('');
        setValidationResult(null);
        setValidationSubmitting(false);
        setQrCode('');
        stopValidationStream();
        // Si estamos en móvil y la sección estaba seleccionada, volver al menú
        if (isMobile && selectedSection === 'control-visitas') {
            // Usar setTimeout para evitar conflictos con el useEffect
            setTimeout(() => {
                setSelectedSection(null);
            }, 0);
        }
    }, [stopValidationStream, isMobile, selectedSection]);

    const handleToggleValidationCamera = useCallback(() => {
        setQrScannerError('');
        setCameraActive((prev) => !prev);
    }, []);

    const handleOpenVisitHistoryModal = useCallback(() => {
        setVisitHistoryError('');
        openModalWithHistory(setVisitHistoryModalOpen);
    }, [openModalWithHistory]);

    const handleCloseVisitHistoryModal = useCallback(() => {
        setVisitHistoryModalOpen(false);
    }, []);

    const handleOpenMyQrModal = useCallback(() => {
        if (isResident && isMisInvitacionesBloqueadas) {
            mostrarAlertaVigilancia('La consulta de invitaciones QR está temporalmente inhabilitada por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
            return;
        }
        resetQrForm();
        openModalWithHistory(setShowMyQrModal);
    }, [isResident, isMisInvitacionesBloqueadas, mostrarAlertaVigilancia, openModalWithHistory, resetQrForm]);

    const handleCloseMyQrModal = useCallback(() => {
        setShowMyQrModal(false);
    }, []);
    const handleExportVisitHistory = useCallback(() => {
        if (!Array.isArray(visitHistory) || visitHistory.length === 0) {
            showNotification('No hay registros para exportar.', 'info');
            return;
        }

        if (typeof window === 'undefined') {
            showNotification('La exportación solo está disponible en el navegador.', 'warning');
            return;
        }

        // FILTRAR: Solo visitas con QR (excluir manuales)
        const visitasConQr = visitHistory.filter((registro) => {
            const tieneCodigo = Boolean(registro.codigo && String(registro.codigo).trim());
            return tieneCodigo;
        });

        console.log('📊 [Exportar Vigilancia] Total registros:', visitHistory.length);
        console.log('📊 [Exportar Vigilancia] Con código QR:', visitasConQr.length);
        console.log('📊 [Exportar Vigilancia] Excluidos (manuales):', visitHistory.length - visitasConQr.length);

        if (visitasConQr.length === 0) {
            showNotification('No hay visitas con código QR para exportar.', 'info');
            return;
        }

        const header = [
            'Visitante',
            'Departamento',
            'Estado',
            'Personas',
            'Acceso con QR',
            'Código QR',
            'Categoría',
            'Placa',
            'Fecha de visita',
            'Hora de visita',
            'Registró entrada',
            'Correos destino',
            'Capturas de Seguridad',
        ];
        const rows = visitasConQr.map((registro) => ([
            escapeCsvValue(registro.visitante || ''),
            escapeCsvValue(registro.departamento || ''),
            escapeCsvValue(registro.estadoLabel || registro.estado || ''),
            escapeCsvValue(registro.numeroVisitantes || ''),
            escapeCsvValue('Sí'), // Siempre Sí porque solo exportamos QR
            escapeCsvValue(registro.codigo || ''),
            escapeCsvValue(registro.categoria || ''),
            escapeCsvValue(registro.placa || ''),
            escapeCsvValue(registro.fechaVisitaDiaLabel || registro.fechaVisitaLabel || ''),
            escapeCsvValue(registro.horaVisitaLabel || ''),
            escapeCsvValue(registro.validadoPorNombre || ''),
            escapeCsvValue(Array.isArray(registro.correosDestino) ? registro.correosDestino.join('; ') : registro.correosDestino || ''),
            escapeCsvValue(registro.capturasSeguridad ? 'Sí' : 'No'),
        ]));

        const csvContent = [header.join(','), ...rows.map((row) => row.join(','))].join('\r\n');
        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `historial-visitas-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showNotification('Descargando historial de visitas.', 'success');
    }, [visitHistory, showNotification]);

    useEffect(() => {
        if (!showQrValidationModal) {
            setCameraActive(false);
            setQrScannerError('');
            stopValidationStream();
            return;
        }
        setValidationError('');
    }, [showQrValidationModal, stopValidationStream]);

    useEffect(() => {
        if (!showQrValidationModal || !cameraActive) {
            stopValidationStream();
            // Limpiar intervalo de escaneo
            if (qrScanIntervalRef.current) {
                clearInterval(qrScanIntervalRef.current);
                qrScanIntervalRef.current = null;
            }
            return;
        }

        let cancelled = false;

        const enableCamera = async () => {
            if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
                setQrScannerError('Este dispositivo no tiene cámara disponible.');
                setCameraActive(false);
                return;
            }

            try {
                setQrScannerError('');

                // Verificar permisos primero
                if (navigator.permissions) {
                    try {
                        const permissionStatus = await navigator.permissions.query({ name: 'camera' });
                        if (permissionStatus.state === 'denied') {
                            setQrScannerError('Los permisos de cámara están denegados. Por favor, habilítalos en la configuración del navegador.');
                            setCameraActive(false);
                            return;
                        }
                    } catch (permError) {
                        // Si query no está soportado, continuar con getUserMedia
                        console.log('Permisos API no disponible, continuando con getUserMedia');
                    }
                }

                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'environment',
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    }
                });
                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }
                validationStreamRef.current = stream;
                if (validationVideoRef.current) {
                    validationVideoRef.current.srcObject = stream;

                    // Esperar a que el video esté listo antes de iniciar el escaneo
                    validationVideoRef.current.onloadedmetadata = () => {
                        if (validationVideoRef.current && !cancelled) {
                            validationVideoRef.current.play().then(() => {
                                // Iniciar escaneo de QR después de que el video esté reproduciéndose
                                startQRScanning();
                            }).catch((err) => {
                                console.error('Error al reproducir video:', err);
                            });
                        }
                    };
                }
            } catch (cameraError) {
                console.error('Vigilancia: no se pudo activar la cámara', cameraError);
                let errorMessage = 'No se pudo activar la cámara. ';
                if (cameraError.name === 'NotAllowedError' || cameraError.name === 'PermissionDeniedError') {
                    errorMessage += 'Por favor, permite el acceso a la cámara en la configuración del navegador.';
                } else if (cameraError.name === 'NotFoundError' || cameraError.name === 'DevicesNotFoundError') {
                    errorMessage += 'No se encontró ninguna cámara en este dispositivo.';
                } else if (cameraError.name === 'NotReadableError' || cameraError.name === 'TrackStartError') {
                    errorMessage += 'La cámara está siendo usada por otra aplicación.';
                } else {
                    errorMessage += 'Revisa los permisos del navegador.';
                }
                setQrScannerError(errorMessage);
                setCameraActive(false);
            }
        };

        const startQRScanning = () => {
            // Limpiar intervalo anterior si existe
            if (qrScanIntervalRef.current) {
                clearInterval(qrScanIntervalRef.current);
            }

            // Escanear cada 200ms (5 veces por segundo)
            qrScanIntervalRef.current = setInterval(() => {
                if (cancelled || !validationVideoRef.current || !validationCanvasRef.current) {
                    return;
                }

                const video = validationVideoRef.current;
                const canvas = validationCanvasRef.current;

                // Verificar que el video esté listo
                if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
                    // Ajustar tamaño del canvas al tamaño del video
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    // Dibujar el frame actual del video en el canvas
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Obtener los datos de la imagen
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

                    // Intentar detectar un código QR
                    const code = jsQR(imageData.data, imageData.width, imageData.height, {
                        inversionAttempts: 'dontInvert',
                    });

                    if (code && code.data) {
                        // Código QR detectado
                        const rawDetected = code.data.trim();
                        let detectedCode = rawDetected;

                        // Extraer código si viene en formato JSON
                        if (rawDetected.startsWith('{') && rawDetected.endsWith('}')) {
                            try {
                                const payload = JSON.parse(rawDetected);
                                if (payload.codigo) {
                                    detectedCode = String(payload.codigo).trim();
                                } else if (payload.type === 'visit-access' && payload.token) {
                                    const t = String(payload.token).trim();
                                    detectedCode = /^\d+$/.test(t) ? t : `VIS-${t.slice(0, 8).toUpperCase()}`;
                                }
                                console.log('📦 [Detección] JSON detectado, código extraído:', detectedCode);
                            } catch (e) {
                                console.warn('📦 [Detección] Error al parsear JSON detectado, se usará texto plano');
                            }
                        }

                        if (detectedCode && detectedCode !== qrCode) {
                            console.log('QR detectado:', detectedCode);
                            setQrCode(detectedCode);
                            setQrScannerError(''); // Limpiar errores previos

                            // Efectos visuales y sonoros de escaneo exitoso
                            playScanBeep();
                            setScannedHighlight(true);
                            setTimeout(() => setScannedHighlight(false), 2000);

                            // Auto-scroll al campo de código
                            if (validationInputRef.current) {
                                validationInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                validationInputRef.current.focus();
                            }
                        }
                    }
                }
            }, 200); // Escanear cada 200ms
        };

        enableCamera();

        return () => {
            cancelled = true;
            stopValidationStream();
            // Limpiar intervalo de escaneo
            if (qrScanIntervalRef.current) {
                clearInterval(qrScanIntervalRef.current);
                qrScanIntervalRef.current = null;
            }
        };
    }, [cameraActive, showQrValidationModal, stopValidationStream, qrCode]);

    const getVisitHistoryCallable = useCallback(() => {
        if (!visitHistoryCallableRef.current) {
            visitHistoryCallableRef.current = httpsCallable(functions, 'listGuestVisitHistory');
        }
        return visitHistoryCallableRef.current;
    }, []);

    const getListGuestAccessesCallable = useCallback(() => listGuestAccessesFn, []);

    const getRegisterManualAccessCallable = useCallback(() => registerManualGuestAccessFn, []);

    const getMarkGuestExitCallable = useCallback(() => markGuestVisitExitFn, []);

    // Use the stable function defined outside
    const mapAccessRecord = mapAccessRecordFn;

    const fetchGuestAccesses = useCallback(async ({ silent } = {}) => {
        if (!fraccionamientoId || isResident) {
            setGuestAccesses([]);
            setGuestAccessesError('');
            setGuestAccessesLoading(false);
            setGuestAccessRefreshing(false);
            return;
        }

        if (isFetchingGuestAccessesRef.current) {
            console.warn('⚠️ [Vigilancia] fetchGuestAccesses ya está en curso, omitiendo llamada paralela.');
            return;
        }

        fetchCountRef.current += 1;
        const currentFetchId = fetchCountRef.current;
        console.log(`🚀 [Vigilancia] Iniciando fetchGuestAccesses #${currentFetchId}`, {
            silent,
            fraccionamientoId,
            status: guestAccessesStatusFilter
        });

        isFetchingGuestAccessesRef.current = true;
        if (silent) {
            setGuestAccessRefreshing(true);
        } else {
            setGuestAccessesLoading(true);
        }
        setGuestAccessesError('');

        try {
            // Si el usuario está en modo offline simulado, no podemos llamar a Cloud Functions
            // porque no hay token JWT real. Forzamos el fallback local.
            if (currentUser?.isOffline) {
                throw new Error('offline-mock-user');
            }

            console.log(`📡 [Vigilancia] Llamando a getListGuestAccessesCallable #${currentFetchId}...`);
            const callable = getListGuestAccessesCallable();
            const response = await callable({
                fraccionamientoId,
                status: guestAccessesStatusFilter,
                limit: 400,
            });
            const data = response?.data || {};

            console.log(`✅ [Vigilancia] Respuesta recibida para fetchGuestAccesses #${currentFetchId}:`, data.success ? `Éxito. Items: ${data.items?.length}` : `Error: ${data.message}`);

            if (!data.success) {
                setGuestAccesses([]);
                setGuestAccessesError(data.message || 'No se pudieron cargar los accesos.');
                return;
            }

            const mapped = Array.isArray(data.items)
                ? data.items.map(record => {
                    const result = mapAccessRecord(record);
                    if (!result) console.warn('⚠️ [Vigilancia] No se pudo mapear registro:', record);
                    return result;
                }).filter(Boolean)
                : [];

            console.log(`📊 [Vigilancia] Actualizando guestAccesses con ${mapped.length} registros.`);
            setGuestAccesses(mapped);
            // Guardar copia local para soporte offline nativo ya que Cloud Functions no se cachean en Firebase IndexedDB
            try { localStorage.setItem('offline_guestAccesses', JSON.stringify(mapped)); } catch(e){}
        } catch (error) {
            // Si falla por red o por ser usuario mock, intentar carga directa de Firestore (Offline Fallback)
            if (!navigator.onLine || error.code === 'unavailable' || error.message?.includes('network') || error.message === 'offline-mock-user') {
                try {
                    console.log('📡 [Vigilancia] Detectado error de red o modo offline, leyendo caché de Accesos...');
                    
                    // Primer intento: Leer desde nuestro custom localStorage (que siempre tiene el último estado que vio el usuario online)
                    const localCache = localStorage.getItem('offline_guestAccesses');
                    if (localCache) {
                        setGuestAccesses(JSON.parse(localCache));
                        setGuestAccessesError('');
                        return;
                    }

                    // Segundo intento: Fallback nativo de Firebase
                    const q = query(
                        collection(db, 'fraccionamientos', fraccionamientoId, 'accesosVisitantes'),
                        orderBy('fechaEntrada', 'desc'),
                        limit(50)
                    );
                    
                    let snap;
                    try {
                        snap = await getDocsFromCache(q);
                    } catch (e) {
                        snap = await getDocs(q);
                    }
                    
                    const mapped = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setGuestAccesses(mapped);
                    setGuestAccessesError('');
                    return;
                } catch (firestoreError) {
                    console.error('❌ [Vigilancia] Error en fallback directo de Firestore:', firestoreError);
                }
            }
 
            console.error(`❌ [Vigilancia] Error en fetchGuestAccesses #${currentFetchId}:`, error);
            setGuestAccesses([]);
            setGuestAccessesError('No se pudieron cargar los accesos.');
        } finally {
            console.log(`🏁 [Vigilancia] Finalizado fetchGuestAccesses #${currentFetchId}`);
            isFetchingGuestAccessesRef.current = false;
            setGuestAccessesLoading(false);
            setGuestAccessRefreshing(false);
        }
    }, [fraccionamientoId, getListGuestAccessesCallable, guestAccessesStatusFilter, isResident, isSuperAdmin, isCaseta, mapAccessRecord]);

    useEffect(() => {
        if (!fraccionamientoId || isResident) return;
        
        const controller = new AbortController();
        fetchGuestAccesses({ silent: false });
        
        return () => controller.abort();
    }, [fetchGuestAccesses, fraccionamientoId, isResident]);

    const handleGuestAccessStatusFilterChange = useCallback((event) => {
        const value = event?.target?.value || 'todos';
        const exists = ACCESS_STATUS_OPTIONS.some((option) => option.value === value);
        setGuestAccessesStatusFilter(exists ? value : 'todos');
        setGuestAccessesCurrentPage(1);
    }, []);

    const handleGuestAccessSearchChange = useCallback((event) => {
        setGuestAccessesSearch(event?.target?.value || '');
        setGuestAccessesCurrentPage(1);
    }, []);

    const handleGuestAccessClearSearch = useCallback(() => {
        setGuestAccessesSearch('');
        setGuestAccessesCurrentPage(1);
    }, []);

    const handleRefreshGuestAccesses = useCallback(() => {
        fetchGuestAccesses({ silent: true });
    }, [fetchGuestAccesses]);

    const resetManualAccessForm = useCallback(() => {
        setManualAccessForm({
            visitante: '',
            departamento: '',
            categoria: '',
            numeroVisitantes: '1',
            notas: '',
            visitaFrecuenteId: '',
            placa: '',
            color: '',
            tipoTransporte: 'En vehiculo',
        });
        setManualAccessError('');
        setCasaSuggestions([]);
        setShowCasaSuggestions(false);
    }, []);

    const handleOpenManualAccessModal = useCallback(() => {
        resetManualAccessForm();
        openModalWithHistory(setShowManualAccessModal);
    }, [resetManualAccessForm, openModalWithHistory]);

    // Función para guardar imágenes en Firebase Storage
    const guardarImagenesVisita = useCallback(async (imagenes, visitante, tipo) => {
        const urls = {};
        const timestamp = Date.now();

        try {
            for (const [key, imagen] of Object.entries(imagenes || {})) {
                if (imagen && imagen.blob) {
                    if (!navigator.onLine || currentUser?.isOffline) {
                        try {
                            const res = await new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result);
                                reader.onerror = reject;
                                reader.readAsDataURL(imagen.blob);
                            });
                            urls[key] = res;
                        } catch (err) {
                            console.error(`Error convirtiendo imagen ${key} a Base64:`, err);
                        }
                        continue;
                    }

                    const nombreArchivo = `${tipo}_${visitante.replace(/\s+/g, '_')}_${key}_${timestamp}.jpg`;
                    const ruta = `visitantes/${fraccionamientoId}/${tipo}/${nombreArchivo}`;
                    const fileRef = storageRef(storage, ruta);

                    await uploadBytes(fileRef, imagen.blob);
                    const url = await getDownloadURL(fileRef);
                    urls[key] = url;
                }
            }
            return urls;
        } catch (error) {
            console.error('Error al guardar imágenes:', error);
            throw error;
        }
    }, [fraccionamientoId, currentUser]);

    // Manejar completado de captura de imágenes para acceso manual
    const handleCompletarCapturaManual = useCallback(async (imagenes, datosOverride = null) => {
        const datos = datosOverride || datosAccesoManualPendiente;
        if (!datos) {
            return;
        }

        setManualAccessSubmitting(true);

        try {
            console.log('📝 [Vigilancia] Iniciando proceso de guardado de imágenes...');
            const urlsImagenes = await guardarImagenesVisita(
                imagenes,
                datos.visitante,
                datos.tipo || 'manual'
            );

            console.log('📸 [Vigilancia] Imágenes guardadas en Storage:', urlsImagenes);

            // Determinar si es un acceso manual o validación de QR
            const localTipo = datos.tipo;
            const nombreVisitante = datos.visitante;
            const esQR = localTipo === 'qr';

            let response;
            const useOfflinePath = !navigator.onLine || currentUser?.isOffline;

            if (esQR) {
                console.log('🎯 [Vigilancia] Procesando acceso QR...');
                if (useOfflinePath) {
                    console.log('📡 [Offline] Validando QR localmente...');
                     const offlineRes = await validateQrOffline({ 
                         fraccionamientoId, 
                         codigo: datos.codigo, 
                         auth: { currentUser },
                         userData 
                     });
                     if (offlineRes.success) {
                         pushToSyncQueue('validateQr', {
                             fraccionamientoId,
                             codigo: datos.codigo,
                             imagenesUrls: urlsImagenes
                         });
                         if (offlineRes.visit) offlineRes.access = offlineRes.visit;
                     }
                     response = { data: offlineRes };
                } else {
                    try {
                        if (!validateQrCallableRef.current) {
                            validateQrCallableRef.current = httpsCallable(functions, 'validateGuestVisitQr');
                        }
                        const callable = validateQrCallableRef.current;
                        response = await callable({
                            fraccionamientoId,
                            codigo: datos.codigo,
                            imagenesUrls: urlsImagenes,
                        });
                    } catch (error) {
                        console.warn('⚠️ [Vigilancia] Fallo intento online para QR, reintentando offline:', error);
                         const offlineRes = await validateQrOffline({ 
                             fraccionamientoId, 
                             codigo: datos.codigo, 
                             auth: { currentUser },
                             userData 
                         });
                         if (offlineRes.success) {
                             pushToSyncQueue('validateQr', {
                                 fraccionamientoId,
                                 codigo: datos.codigo,
                                 imagenesUrls: urlsImagenes
                             });
                             if (offlineRes.visit) offlineRes.access = offlineRes.visit;
                         }
                         response = { data: offlineRes };
                    }
                }
            } else {
                console.log('🎯 [Vigilancia] Procesando acceso manual...');
                
                const registerOffline = async () => {
                    console.log('📡 [Offline] Registrando acceso manual localmente...');
                    pushToSyncQueue('registerManualAccess', {
                        ...datos,
                        fraccionamientoId,
                        tipoAcceso: 'manual',
                        imagenesUrls: urlsImagenes,
                        destinatarioUid: null,
                    });

                    const mockId = 'offline-' + Date.now();
                    const mockAccess = {
                        id: mockId,
                        ...datos,
                        fraccionamientoId,
                        imagenesUrls: urlsImagenes,
                        fechaEntrada: new Date(),
                        entradaRegistradaIso: new Date().toISOString(),
                        estadoAcceso: 'dentro',
                        offline: true
                    };

                    const manualRef = collection(db, 'fraccionamientos', fraccionamientoId, 'accesosVisitantes');
                    
                    // Asegurar que no haya campos undefined para Firestore
                    const cleanMock = JSON.parse(JSON.stringify(mockAccess));
                    addDoc(manualRef, cleanMock).catch(e => console.warn('Sync pended', e));
                    
                    showNotification('Acceso registrado localemente. Se sincronizará al conectar internet.', 'success');
                    return { data: { success: true, message: 'Acceso registrado localmente.', access: mockAccess } };
                };

                if (useOfflinePath) {
                    response = await registerOffline();
                } else {
                    try {
                        const callable = getRegisterManualAccessCallable();
                        response = await callable({
                            ...datos,
                            fraccionamientoId,
                            imagenesUrls: urlsImagenes,
                        });
                    } catch (error) {
                        console.warn('⚠️ [Vigilancia] Fallo intento online para manual, reintentando offline:', error);
                        response = await registerOffline();
                    }
                }
            }

            const data = response?.data || {};
            console.log('📥 [Vigilancia] Respuesta de registro recibida:', data);

            if (!data.success) {
                console.error('❌ [Vigilancia] Fallo en registro:', data.message);
                setManualAccessError(data.message || 'No se pudo registrar la entrada.');
                setShowCapturaImagenesManual(false);
                if (!esQR) {
                    openModalWithHistory(setShowManualAccessModal);
                }
                return;
            }
            
            // Mapear el registro
            console.log('🗺️ [Vigilancia] Mapeando registro recibido...');
            const record = data.access ? mapAccessRecord(data.access) : null;
            
            if (record) {
                console.log('✅ [Vigilancia] Registro mapeado para tabla:', record);
                setGuestAccesses((prev) => {
                    const filtered = prev.filter((item) => item.id !== record.id);
                    if (guestAccessesStatusFilter !== 'todos' && record.estadoAcceso !== guestAccessesStatusFilter) {
                        console.log('⚠️ [Vigilancia] El registro no coincide con el filtro:', guestAccessesStatusFilter);
                        return filtered;
                    }
                    console.log('🚀 [Vigilancia] Insertando record en guestAccesses');
                    return [record, ...filtered];
                });
            } else {
                console.warn('⚠️ [Vigilancia] No se pudo obtener el record de la respuesta. Verificando data.item...');
                // Fallback por si la función devuelve 'item' en lugar de 'access'
                const fallbackRecord = data.item ? mapAccessRecord(data.item) : null;
                if (fallbackRecord) {
                    setGuestAccesses(prev => [fallbackRecord, ...prev.filter(i => i.id !== fallbackRecord.id)]);
                }
            }

            console.log('🧼 [Vigilancia] Limpiando estados de captura...');
            setShowCapturaImagenesManual(false);
            resetManualAccessForm();
            setDatosAccesoManualPendiente(null);
            setShowManualAccessModal(false);
            setQrCode('');

            // Mostrar modal de éxito con cierre automático
            console.log('✨ [Vigilancia] Configurando SuccessModal para:', nombreVisitante);
            setSuccessModalData({
                title: esQR ? 'Validación Exitosa' : '¡Captura Exitosa!',
                message: esQR
                    ? 'Su código QR ha sido validado correctamente con los protocolos de seguridad de acceso para el fraccionamiento.'
                    : `Se han capturado correctamente las imágenes y la visita de ${nombreVisitante} accedió al fraccionamiento con los protocolos de acceso.`,
            });
            openModalWithHistory(setShowSuccessModal);
            // Asegurar que la tabla sea visible
            console.log('👁️ [Vigilancia] Asegurando visibilidad de tabla y refrescando...');
            if (isMobile) {
                console.log('📱 [Vigilancia] Retrasando retorno al menú principal (3s)');
                setTimeout(() => {
                    setSelectedSection(null);
                    setShowSuccessModal(false);
                }, 3000);
            }

            if (navigator.onLine && !currentUser?.isOffline) {
                fetchGuestAccesses({ silent: true });
            }
        } catch (error) {
            console.error('❌ [Vigilancia] Error fatal al registrar acceso:', error);
            showNotification(`Error al registrar acceso: ${error.message || 'Error desconocido'}`, 'error');
            setManualAccessError(`No se pudo registrar la entrada: ${error.message || 'Intenta nuevamente'}`);
            setShowCapturaImagenesManual(false);
            if (datosAccesoManualPendiente) {
                openModalWithHistory(setShowManualAccessModal);
            }
        } finally {
            setManualAccessSubmitting(false);
        }
    }, [datosAccesoManualPendiente, fetchGuestAccesses, fraccionamientoId, getRegisterManualAccessCallable, guardarImagenesVisita, guestAccessesStatusFilter, mapAccessRecord, resetManualAccessForm, showNotification, currentUser, userData]);

    // Manejar confirmación de placa y color del vehículo en caseta
    const handleConfirmarDatosVehiculo = useCallback(() => {
        if (!datosAccesoManualPendiente) return;

        const transp = datosVehiculoForm.tipoTransporte;
        const placa = transp === 'A pie' ? 'Sin vehículo' : datosVehiculoForm.placa.trim();
        const color = transp === 'A pie' ? 'A pie' : datosVehiculoForm.color;

        const datosActualizados = {
            ...datosAccesoManualPendiente,
            tipoTransporte: transp,
            placa: placa,
            color: color,
        };

        setDatosAccesoManualPendiente(datosActualizados);
        setShowCapturaDatosVehiculo(false);

        // Evaluar si las fotos están habilitadas para el fraccionamiento
        if (modulos?.vigilancia_captura_imagenes !== false) {
            console.log('📸 [Vigilancia] Fotos habilitadas. Abriendo Captura de Imágenes...');
            openModalWithHistory(setShowCapturaImagenesManual);
        } else {
            console.log('📡 [Vigilancia] Fotos deshabilitadas. Registrando acceso directamente...');
            // Ejecutar la rutina de completado inmediatamente pasando un mapa de imágenes vacío
            handleCompletarCapturaManual({}, datosActualizados);
        }
    }, [datosAccesoManualPendiente, datosVehiculoForm, handleCompletarCapturaManual, modulos?.vigilancia_captura_imagenes, openModalWithHistory]);

    // Manejar salida completa (abortar) del proceso de captura
    const handleExitCapturaManual = useCallback(() => {
        console.log('🚪 [Captura] Usuario abortó el proceso completamente');
        setShowCapturaImagenesManual(false);
        setDatosAccesoManualPendiente(null);
        setQrCode('');
        resetManualAccessForm();
        if (isMobile) {
            console.log('📱 [Vigilancia] Abortado: Retornando al menú principal (Móvil)');
            setSelectedSection(null);
        }
    }, [resetManualAccessForm]);

    // Manejar cancelación de captura de imágenes para acceso manual
    // Al cancelar, registra el acceso SIN imágenes
    const handleCancelarCapturaManual = useCallback(async () => {
        console.log('❌ [Cancelar Captura] Usuario optó por continuar sin imágenes');

        if (!datosAccesoManualPendiente) {
            setShowCapturaImagenesManual(false);
            openModalWithHistory(setShowManualAccessModal);
            return;
        }

        setManualAccessSubmitting(true);

        try {
            // Determinar si es un acceso manual o validación de QR
            const localTipo = datosAccesoManualPendiente.tipo;
            const nombreVisitante = datosAccesoManualPendiente.visitante;
            const esQR = localTipo === 'qr';

            let response;
            const useOfflinePath = !navigator.onLine || currentUser?.isOffline;

            if (esQR) {
                console.log('🎯 [Vigilancia/Cancelar] Procesando acceso QR SIN imágenes...');
                if (useOfflinePath) {
                    const offlineRes = await validateQrOffline({ 
                        fraccionamientoId, 
                        codigo: datosAccesoManualPendiente.codigo, 
                        auth: { currentUser },
                        userData 
                    });
                    if (offlineRes.success) {
                        pushToSyncQueue('validateQr', {
                            fraccionamientoId,
                            codigo: datosAccesoManualPendiente.codigo,
                            imagenesUrls: {}
                        });
                        if (offlineRes.visit) offlineRes.access = offlineRes.visit;
                    }
                    response = { data: offlineRes };
                } else {
                    try {
                        if (!validateQrCallableRef.current) {
                            validateQrCallableRef.current = httpsCallable(functions, 'validateGuestVisitQr');
                        }
                        const callable = validateQrCallableRef.current;
                        response = await callable({
                            fraccionamientoId,
                            codigo: datosAccesoManualPendiente.codigo,
                            imagenesUrls: {},
                        });
                    } catch (error) {
                        console.warn('⚠️ [Vigilancia/Cancelar] Fallo intento online para QR, reintentando offline:', error);
                         const offlineRes = await validateQrOffline({ 
                             fraccionamientoId, 
                             codigo: datosAccesoManualPendiente.codigo, 
                             auth: { currentUser },
                             userData 
                         });
                         if (offlineRes.success) {
                             pushToSyncQueue('validateQr', {
                                 fraccionamientoId,
                                 codigo: datosAccesoManualPendiente.codigo,
                                 imagenesUrls: {}
                             });
                             if (offlineRes.visit) offlineRes.access = offlineRes.visit;
                         }
                         response = { data: offlineRes };
                    }
                }
            } else {
                console.log('🎯 [Vigilancia/Cancelar] Procesando acceso manual SIN imágenes...');
                
                const registerOffline = async () => {
                    console.log('📡 [Offline] Registrando acceso manual localmente (sin imágenes)...');
                    pushToSyncQueue('registerManualAccess', {
                        ...datosAccesoManualPendiente,
                        fraccionamientoId,
                        tipoAcceso: 'manual',
                        imagenesUrls: {},
                        destinatarioUid: null,
                    });

                    const mockId = 'offline-' + Date.now();
                    const mockAccess = {
                        id: mockId,
                        ...datosAccesoManualPendiente,
                        fraccionamientoId,
                        imagenesUrls: {},
                        fechaEntrada: new Date(),
                        entradaRegistradaIso: new Date().toISOString(),
                        estadoAcceso: 'dentro',
                        offline: true
                    };

                    const manualRef = collection(db, 'fraccionamientos', fraccionamientoId, 'accesosVisitantes');
                    
                    // Asegurar que no haya campos undefined para Firestore
                    const cleanMock = JSON.parse(JSON.stringify(mockAccess));
                    addDoc(manualRef, cleanMock).catch(e => console.warn('Sync pended', e));
                    
                    showNotification('Acceso registrado localmente (sin imágenes).', 'success');
                    return { data: { success: true, message: 'Acceso registrado localmente.', access: mockAccess } };
                };

                if (useOfflinePath) {
                    response = await registerOffline();
                } else {
                    try {
                        const callable = getRegisterManualAccessCallable();
                        response = await callable({
                            ...datosAccesoManualPendiente,
                            fraccionamientoId,
                            imagenesUrls: {},
                        });
                    } catch (error) {
                        console.warn('⚠️ [Vigilancia/Cancelar] Fallo intento online para manual, reintentando offline:', error);
                        response = await registerOffline();
                    }
                }
            }

            const data = response?.data || {};
            console.log('📥 [Vigilancia/Cancelar] Respuesta de registro recibida:', data);

            if (!data.success) {
                console.error('❌ [Vigilancia/Cancelar] Fallo en registro:', data.message);
                setManualAccessError(data.message || 'No se pudo registrar la entrada.');
                setShowCapturaImagenesManual(false);
                if (!esQR) {
                    openModalWithHistory(setShowManualAccessModal);
                }
                return;
            }

            // Mapear el registro
            console.log('🗺️ [Vigilancia/Cancelar] Mapeando registro recibido...');
            const record = data.access ? mapAccessRecord(data.access) : null;
            
            if (record) {
                console.log('✅ [Vigilancia/Cancelar] Registro mapeado para tabla:', record);
                setGuestAccesses((prev) => {
                    const filtered = prev.filter((item) => item.id !== record.id);
                    if (guestAccessesStatusFilter !== 'todos' && record.estadoAcceso !== guestAccessesStatusFilter) {
                        console.log('⚠️ [Vigilancia/Cancelar] El registro no coincide con el filtro:', guestAccessesStatusFilter);
                        return filtered;
                    }
                    console.log('🚀 [Vigilancia/Cancelar] Insertando record en guestAccesses');
                    return [record, ...filtered];
                });
            } else {
                console.warn('⚠️ [Vigilancia/Cancelar] No se pudo obtener el record. Verificando data.item...');
                const fallbackRecord = data.item ? mapAccessRecord(data.item) : null;
                if (fallbackRecord) {
                    setGuestAccesses(prev => [fallbackRecord, ...prev.filter(i => i.id !== fallbackRecord.id)]);
                }
            }

            console.log('🧼 [Vigilancia/Cancelar] Limpiando estados...');
            resetManualAccessForm();
            setDatosAccesoManualPendiente(null);
            setShowManualAccessModal(false);
            setQrCode('');

            // Mostrar modal de éxito con cierre automático
            console.log('✨ [Vigilancia/Cancelar] Configurando SuccessModal para:', nombreVisitante);
            setSuccessModalData({
                title: esQR ? 'Validación Exitosa' : '¡Acceso Registrado!',
                message: esQR
                    ? 'Su código QR ha sido validado correctamente con los protocolos de seguridad de acceso para el fraccionamiento.'
                    : `La visita de ${nombreVisitante} accedió al fraccionamiento con los protocolos de acceso (sin imágenes de seguridad).`,
            });
            openModalWithHistory(setShowSuccessModal);

            // Asegurar que la tabla sea visible
            console.log('👁️ [Vigilancia/Cancelar] Asegurando visibilidad de tabla y refrescando...');
            if (isMobile) {
                console.log('📱 [Vigilancia/Cancelar] Retrasando retorno al menú principal (3s)');
                setTimeout(() => {
                    setSelectedSection(null);
                    setShowSuccessModal(false);
                }, 3000);
            }

            if (navigator.onLine && !currentUser?.isOffline) {
                fetchGuestAccesses({ silent: true });
            }
        } catch (error) {
            console.error('❌ [Vigilancia/Cancelar] Error fatal al registrar acceso:', error);
            showNotification(`Error al registrar acceso sin fotos: ${error.message || 'Error desconocido'}`, 'error');
            setManualAccessError(`No se pudo registrar la entrada: ${error.message || 'Intenta nuevamente'}`);
            if (datosAccesoManualPendiente) {
                openModalWithHistory(setShowManualAccessModal);
            }
        } finally {
            setManualAccessSubmitting(false);
        }
    }, [datosAccesoManualPendiente, fetchGuestAccesses, fraccionamientoId, getRegisterManualAccessCallable, guestAccessesStatusFilter, mapAccessRecord, resetManualAccessForm, showNotification, currentUser]);


    const handleCloseManualAccessModal = useCallback(() => {
        if (manualAccessSubmitting) {
            return;
        }
        setShowManualAccessModal(false);
        resetManualAccessForm();
        // Resetear selectedSection cuando se cierra el modal (solo en móvil)
        // Usar setTimeout para verificar el estado actual después del cierre
        if (isMobile) {
            setTimeout(() => {
                setSelectedSection((prev) => {
                    // Solo resetear si todavía está en 'registrar-entrada-manual'
                    return prev === 'registrar-entrada-manual' ? null : prev;
                });
            }, 100);
        }
    }, [manualAccessSubmitting, resetManualAccessForm, isMobile]);

    const handleManualAccessInputChange = useCallback((event) => {
        const { name, value } = event.target;

        setManualAccessForm((prev) => ({
            ...prev,
            [name]: name === 'numeroVisitantes' ? value.replace(/[^0-9]/g, '') : value,
        }));

        // Si es el campo departamento y no hay visita frecuente, mostrar sugerencias
        if (name === 'departamento' && !manualAccessForm.visitaFrecuenteId) {
            const searchTerm = value.trim().toLowerCase();
            if (searchTerm.length > 0 && casasList.length > 0) {
                const filtered = casasList.filter(casa => {
                    const numero = String(casa.numero || '').toLowerCase();
                    const nombreResidente = String(casa.nombre_residente || '').toLowerCase();
                    return numero.includes(searchTerm) || nombreResidente.includes(searchTerm);
                }).slice(0, 10); // Limitar a 10 sugerencias
                setCasaSuggestions(filtered);
                setShowCasaSuggestions(filtered.length > 0);
            } else {
                setCasaSuggestions([]);
                setShowCasaSuggestions(false);
            }
        } else if (name === 'departamento' && manualAccessForm.visitaFrecuenteId) {
            // Si hay visita frecuente, ocultar sugerencias
            setCasaSuggestions([]);
            setShowCasaSuggestions(false);
        }
        // Si cambia el transporte y es "A pie", limpiar placa y color para evitar inconsistencias
        if (name === 'tipoTransporte' && value === 'A pie') {
            setManualAccessForm(prev => ({ ...prev, placa: '', color: '' }));
        }

        // Si es el campo de nombre del visitante, activar las sugerencias
        if (name === 'visitante') {
            setShowManualVisitorSuggestions(true);
        }
    }, [manualAccessForm.visitaFrecuenteId, casasList]);

    const manualVisitorSuggestions = useMemo(() => {
        const term = (manualAccessForm.visitante || '').trim();
        if (!term || term.length < 2 || visitasFrecuentes.length === 0) {
            return [];
        }
        const normalize = (s) => String(s || '')
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase();
        const t = normalize(term);
        return visitasFrecuentes
            .filter((v) => normalize(v.nombre).includes(t))
            .slice(0, 5);
    }, [manualAccessForm.visitante, visitasFrecuentes]);

    const handleSelectCasaSuggestion = useCallback((casa) => {
        if (!casa) return;

        console.log('[Manual Access] 🏘️ Casa seleccionada:', {
            numero: casa.numero,
            nombreResidente: casa.nombre_residente,
        });

        setManualAccessForm((prev) => ({
            ...prev,
            departamento: casa.numero || prev.departamento,
        }));

        setCasaSuggestions([]);
        setShowCasaSuggestions(false);
    }, []);

    const handleSelectManualSuggestion = useCallback(async (visita) => {
        if (!visita) return;

        console.log('[Manual Access] 📋 Visita frecuente seleccionada:', {
            id: visita.id,
            nombre: visita.nombre,
            categoria: visita.categoria,
            ownerNombre: visita.ownerNombre,
            ownerUid: visita.ownerUid,
            ownerDepartamento: visita.ownerDepartamento,
            placa: visita.placa,
            color: visita.color,
        });

        let residentDepartamento = visita.ownerDepartamento || '';
        console.log('[Manual Access] 🔍 ownerDepartamento inicial:', residentDepartamento);

        // Si no tiene ownerDepartamento, usar ownerNombre para buscar en casas por nombre_residente
        if (!residentDepartamento && visita.ownerNombre && fraccionamientoId) {
            console.log('[Manual Access] 🔎 Buscando en casas por nombre_residente:', visita.ownerNombre);
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const casaSnap = await getDocs(query(casasRef, where('nombre_residente', '==', visita.ownerNombre)));

                console.log('[Manual Access] 📦 Casas encontradas por nombre_residente:', casaSnap.docs.length);

                if (!casaSnap.empty) {
                    const casaData = casaSnap.docs[0].data();
                    console.log('[Manual Access] 🏘️ Datos de casa encontrada:', {
                        numero: casaData.numero,
                        nombre_residente: casaData.nombre_residente,
                        propietarioId: casaData.propietarioId,
                    });

                    residentDepartamento = casaData.numero || '';
                    console.log('[Manual Access] ✅ Número de casa resuelto:', residentDepartamento);
                } else {
                    console.log('[Manual Access] ⚠️ No se encontraron casas con nombre_residente:', visita.ownerNombre);
                }
            } catch (err) {
                console.warn('[Manual Access] ❌ Error al buscar casa por nombre_residente:', err);
            }
        }

        console.log('[Manual Access] 📝 Rellenando formulario con:', {
            visitaFrecuenteId: visita.id,
            visitante: visita.nombre,
            categoria: visita.categoria,
            departamento: residentDepartamento,
            placa: visita.placa,
            color: visita.color,
        });

        setManualAccessForm((prev) => ({
            ...prev,
            visitaFrecuenteId: visita.id,
            visitante: visita.nombre || prev.visitante,
            categoria: visita.categoria || prev.categoria,
            departamento: residentDepartamento || prev.departamento,
            placa: visita.placa || prev.placa,
            color: visita.color || prev.color,
        }));

        // Ocultar sugerencias tras seleccionar
        setShowManualVisitorSuggestions(false);
    }, [fraccionamientoId]);

    // Cargar casas del fraccionamiento para el select de departamento
    const fetchCasasForManualAccess = useCallback(async () => {
        if (!fraccionamientoId) {
            setCasasList([]);
            return;
        }

        setCasasLoading(true);
        try {
            const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
            const q = query(casasRef, orderBy('numero'));
            const querySnapshot = await getDocs(q);
            const casasList = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(casa => {
                    // Filtrar casas: excluir las que tienen perfil "caseta" o "guardia"
                    if (!casa.perfil) return true;
                    const perfilLower = String(casa.perfil).toLowerCase().trim();
                    return perfilLower !== 'caseta' && perfilLower !== 'guardia';
                })
                .sort((a, b) => {
                    const numA = String(a.numero || '').toUpperCase();
                    const numB = String(b.numero || '').toUpperCase();
                    return numA.localeCompare(numB);
                });

            setCasasList(casasList);
            console.log('[Manual Access] Casas cargadas:', casasList.length);
        } catch (error) {
            console.error('[Manual Access] Error al cargar casas:', error);
            setCasasList([]);
        } finally {
            setCasasLoading(false);
        }
    }, [fraccionamientoId]);

    // Cargar casas cuando se abre el modal de acceso manual, modal QR o si es personal de caseta/admin (para el card de paquetería)
    useEffect(() => {
        const canRegisterPackages = !isResident && (isCaseta || isSuperAdmin);
        if (fraccionamientoId && (showManualAccessModal || showQrModal || canRegisterPackages) && !manualAccessForm.visitaFrecuenteId) {
            fetchCasasForManualAccess();
        }
    }, [showManualAccessModal, showQrModal, fraccionamientoId, manualAccessForm.visitaFrecuenteId, fetchCasasForManualAccess, isResident, isCaseta, isSuperAdmin]);

    const handleManualAccessFrecuenteSelect = useCallback(async (event) => {
        const value = event?.target?.value || '';
        if (!value) {
            setManualAccessForm((prev) => ({
                ...prev,
                visitaFrecuenteId: '',
            }));
            return;
        }

        const selected = visitasFrecuentes.find((item) => item.id === value);
        if (!selected) {
            setManualAccessForm((prev) => ({
                ...prev,
                visitaFrecuenteId: value,
            }));
            return;
        }

        let residentDepartamento = selected.ownerDepartamento || '';
        console.log('[Manual Access Select] 🔍 ownerDepartamento inicial:', residentDepartamento);

        // Si no tiene ownerDepartamento, buscar en casas por nombre_residente o ownerUid
        if (!residentDepartamento && fraccionamientoId) {
            console.log('[Manual Access Select] 🔎 Buscando en casas...', {
                ownerNombre: selected.ownerNombre,
                ownerUid: selected.ownerUid,
            });
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');

                // Primero intentar por nombre_residente
                if (selected.ownerNombre) {
                    const casaSnap = await getDocs(query(casasRef, where('nombre_residente', '==', selected.ownerNombre)));
                    if (!casaSnap.empty) {
                        const casaData = casaSnap.docs[0].data();
                        residentDepartamento = casaData.numero || '';
                        console.log('[Manual Access Select] ✅ Casa encontrada por nombre_residente:', residentDepartamento);
                    }
                }

                // Si no se encontró, intentar por userId
                if (!residentDepartamento && selected.ownerUid) {
                    const casaSnap = await getDocs(query(casasRef, where('userId', '==', selected.ownerUid)));
                    if (!casaSnap.empty) {
                        const casaData = casaSnap.docs[0].data();
                        residentDepartamento = casaData.numero || '';
                        console.log('[Manual Access Select] ✅ Casa encontrada por userId:', residentDepartamento);
                    }
                }

                if (!residentDepartamento) {
                    console.log('[Manual Access Select] ⚠️ No se encontró casa para:', {
                        ownerNombre: selected.ownerNombre,
                        ownerUid: selected.ownerUid,
                    });
                }
            } catch (err) {
                console.warn('[Manual Access Select] ❌ Error al buscar casa:', err);
            }
        }

        console.log('[Manual Access Select] 📝 Rellenando formulario con:', {
            visitaFrecuenteId: selected.id,
            visitante: selected.nombre,
            categoria: selected.categoria,
            departamento: residentDepartamento,
            placa: selected.placa,
            color: selected.color,
        });

        setManualAccessForm((prev) => ({
            ...prev,
            visitaFrecuenteId: value,
            visitante: selected.nombre || prev.visitante,
            categoria: selected.categoria || prev.categoria,
            telefono: selected.telefono || prev.telefono,
            departamento: residentDepartamento || prev.departamento,
            placa: selected.placa || prev.placa,
            color: selected.color || prev.color,
            notas: prev.notas || selected.notas || '',
        }));

        // Ocultar sugerencias de texto y casas tras selección de dropdown
        setShowManualVisitorSuggestions(false);
        setCasaSuggestions([]);
        setShowCasaSuggestions(false);
    }, [fraccionamientoId, visitasFrecuentes]);

    const handleSubmitManualAccess = useCallback(async (event) => {
        if (event) {
            event.preventDefault();
        }

        if (manualAccessSubmitting) {
            return;
        }

        const visitante = manualAccessForm.visitante.trim();
        const departamento = manualAccessForm.departamento.trim();
        const placa = manualAccessForm.placa.trim();
        const color = manualAccessForm.color.trim();
        const categoria = manualAccessForm.categoria.trim();

        if (!fraccionamientoId) {
            setManualAccessError('No se pudo determinar el fraccionamiento.');
            return;
        }

        if (!visitante) {
            setManualAccessError('Captura el nombre del visitante.');
            return;
        }

        if (!departamento) {
            setManualAccessError('Selecciona el departamento o lote del residente.');
            return;
        }

        // Validar que la casa seleccionada exista en la lista (solo si no hay visita frecuente)
        if (!manualAccessForm.visitaFrecuenteId) {
            const casaExiste = casasList.some(casa => casa.numero === departamento);
            if (!casaExiste) {
                setManualAccessError('El departamento seleccionado no existe en el fraccionamiento. Por favor, selecciona una opción válida.');
                return;
            }
        }

        if (!categoria) {
            setManualAccessError('Selecciona la categoría de la visita.');
            return;
        }

        // Placa y color son opcionales (puede ser visita sin vehículo)

        setManualAccessError('');

        // Guardar los datos del formulario y mostrar el modal de captura de imágenes
        const numeroVisitantesValue = Number(manualAccessForm.numeroVisitantes);
        const numeroVisitantes = Number.isFinite(numeroVisitantesValue) && numeroVisitantesValue > 0
            ? Math.max(1, Math.round(numeroVisitantesValue))
            : 1;

        const notas = manualAccessForm.notas.trim();

        const datosPendientes = {
            visitante,
            departamento,
            categoria,
            notas: notas || null,
            numeroVisitantes,
            visitaFrecuenteId: manualAccessForm.visitaFrecuenteId || null,
            placa: placa || null,
            color: color || null,
            tipoTransporte: manualAccessForm.tipoTransporte || 'En vehiculo',
        };

        console.log('🎯 [handleSubmitManualAccess] Guardando datos pendientes:', datosPendientes);
        setDatosAccesoManualPendiente(datosPendientes);

        console.log('🎯 [handleSubmitManualAccess] Ocultando modal de formulario...');
        setShowManualAccessModal(false);

        if (modulos?.vigilancia_captura_imagenes !== false) {
            console.log('🎯 [handleSubmitManualAccess] Mostrando modal de captura de imágenes...');
            setShowCapturaImagenesManual(true);
        } else {
            console.log('🎯 [handleSubmitManualAccess] Fotos deshabilitadas. Registrando acceso manual directamente...');
            handleCompletarCapturaManual({}, datosPendientes);
        }
    }, [fraccionamientoId, manualAccessForm, manualAccessSubmitting, casasList, modulos?.vigilancia_captura_imagenes, handleCompletarCapturaManual]);

    const performRegisterGuestExit = useCallback(async (access) => {
        if (!access || !access.id || !fraccionamientoId) {
            return;
        }
        setGuestAccessActionId(access.id);
        try {
            if (!navigator.onLine || currentUser?.isOffline) {
                console.log('📡 [Offline] Registrando salida localmente...');
                pushToSyncQueue('markGuestExit', {
                    fraccionamientoId,
                    accessId: access.id,
                });
                const accessRef = doc(db, 'fraccionamientos', fraccionamientoId, 'accesosVisitantes', access.id);
                // Non-blocking update local
                updateDoc(accessRef, {
                    estadoAcceso: 'fuera',
                    salidaRegistradaEn: new Date(),
                    salidaRegistradaIso: new Date().toISOString(),
                    salidaRegistradaPorNombre: userData?.nombre || userData?.nombreCompleto || 'Vigilancia',
                    offline: true
                }).catch(e => console.warn('Sync pended', e));
                
                showNotification('Salida guardada en cola. Se sincronizará al conectar internet.', 'success');
                
                // Actualizar el estado local para que refleje el cambio inmediatamente (y no desaparezca temporalmente si buscan en "Salidas").
                setGuestAccesses((prev) => prev.map((item) => {
                    if (item.id === access.id) {
                        return { 
                            ...item, 
                            estadoAcceso: 'fuera', 
                            salidaRegistradaIso: new Date().toISOString(),
                            salidaRegistradaPorNombre: userData?.nombre || userData?.nombreCompleto || 'Vigilancia'
                        };
                    }
                    return item;
                }));
                return;
            }
            const callable = getMarkGuestExitCallable();
            const response = await callable({
                fraccionamientoId,
                accessId: access.id,
                finalStatus: 'fuera',
            });
            const data = response?.data || {};
            if (!data.success) {
                showNotification(data.message || 'No se pudo registrar la salida.', 'error');
                return;
            }
            const record = data.access ? mapAccessRecord(data.access) : null;
            if (record) {
                setGuestAccesses((prev) => {
                    const remainder = prev.filter((item) => item.id !== record.id);
                    if (guestAccessesStatusFilter === 'todos' || record.estadoAcceso === guestAccessesStatusFilter) {
                        return [record, ...remainder];
                    }
                    return remainder;
                });
            }
            showNotification('Salida registrada correctamente.', 'success');
            fetchGuestAccesses({ silent: true });
        } catch (error) {
            console.error('Vigilancia: error al registrar salida de visita', error);
            showNotification('No se pudo registrar la salida. Intenta nuevamente.', 'error');
        } finally {
            setGuestAccessActionId(null);
            setExitConfirmAccess(null);
        }
    }, [fetchGuestAccesses, fraccionamientoId, getMarkGuestExitCallable, guestAccessesStatusFilter, mapAccessRecord, showNotification]);

    const handleRegisterGuestExit = useCallback((access) => {
        if (!access || !access.id) return;
        setExitConfirmAccess(access);
    }, []);

    const formatVisitaDate = useCallback((date) => {
        if (!(date instanceof Date)) {
            return 'Fecha no disponible';
        }
        return `${date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })} · ${date.toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
        })}`;
    }, []);

    const formatVisitaDateOnly = useCallback((date) => {
        if (!(date instanceof Date)) {
            return 'Fecha no disponible';
        }
        return date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        });
    }, []);

    const formatVisitaTimeOnly = useCallback((date) => {
        if (!(date instanceof Date)) {
            return '';
        }
        return date.toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
        });
    }, []);

    const getAccessStatusLabel = useCallback((status) => {
        const normalized = String(status || '').toLowerCase();
        if (normalized === 'dentro') {
            return 'Dentro del fraccionamiento';
        }
        if (normalized === 'fuera') {
            return 'Salida registrada';
        }
        return toTitleCase(normalized || 'Sin estado');
    }, []);

    const formatAccessTimestamp = useCallback((iso, label) => {
        if (label) {
            return label;
        }
        if (!iso) {
            return 'Sin registro';
        }
        const date = new Date(iso);
        if (Number.isNaN(date.valueOf())) {
            return 'Sin registro';
        }
        return formatVisitaDate(date);
    }, [formatVisitaDate]);

    const formatRegistradoPor = useCallback((access) => {
        if (!access) return '—';
        const isSalida = access.estadoAcceso === 'fuera';
        const rawNombre = (isSalida ? access.salidaRegistradaPorNombre : access.entradaRegistradaPorNombre) || '';
        const rawUid = (isSalida ? access.salidaRegistradaPorUid : access.entradaRegistradaPorUid) || '';

        // Detección de hardware / lector independiente de la marca
        const esLector = /lector|hikvision|hardware|sistema/i.test(rawNombre) || /hardware|sistema/i.test(rawUid);
        if (esLector) {
            return isSalida ? 'Lector Registró Salida' : 'Lector Registró Entrada';
        }

        const nombreLimpio = rawNombre.trim() || 'Vigilancia';
        return isSalida ? `Marcó salida: ${nombreLimpio}` : `Registró entrada: ${nombreLimpio}`;
    }, []);

    const guestAccessesVisible = useMemo(() => {
        const term = guestAccessesSearch.trim().toLowerCase();
        if (!term) {
            return guestAccesses;
        }

        return guestAccesses.filter((item) => {
            const tokens = [
                item.visitante,
                item.departamento,
                item.categoria,
                item.codigo,
                item.origen,
                item.tipo,
                item.notas,
                item.entradaRegistradaPorNombre,
                item.salidaRegistradaPorNombre,
            ];
            return tokens.some((token) => typeof token === 'string' && token.toLowerCase().includes(term));
        });
    }, [guestAccesses, guestAccessesSearch]);

    const totalGuestAccessPages = useMemo(() => {
        return Math.max(1, Math.ceil(guestAccessesVisible.length / guestAccessesPerPage));
    }, [guestAccessesVisible.length, guestAccessesPerPage]);

    const paginatedGuestAccesses = useMemo(() => {
        const start = (guestAccessesCurrentPage - 1) * guestAccessesPerPage;
        return guestAccessesVisible.slice(start, start + guestAccessesPerPage);
    }, [guestAccessesVisible, guestAccessesCurrentPage, guestAccessesPerPage]);

    const loadVisitHistory = useCallback(async ({ signal, silent, date } = {}) => {
        if (!fraccionamientoId) {
            if (!signal?.aborted) {
                setVisitHistory([]);
                setVisitHistoryError('');
            }
            return;
        }

        if (!silent && !signal?.aborted) {
            setVisitHistoryLoading(true);
        }
        if (!signal?.aborted) {
            setVisitHistoryError('');
        }

        try {
            console.log('🔍 [Vigilancia] Iniciando carga de historial:', { fraccionamientoId, currentUid, isSuperAdmin, isCaseta, date });
            let historyResult = { success: true, items: [] };
            let cloudItems = [];

            if (navigator.onLine) {
                const callable = getVisitHistoryCallable();
                const response = await callable({
                    fraccionamientoId,
                    limit: date ? 500 : 200,
                    date: date || null,
                });
                console.log('📊 [Vigilancia] Respuesta Cloud Function:', response?.data?.success ? `Éxito (${response.data.items?.length || 0} ítems)` : `Fallo: ${response?.data?.message}`);
                historyResult = response?.data || {};
                cloudItems = historyResult.success && Array.isArray(historyResult.items) ? historyResult.items : [];
            } else {
                console.log('📡 [Vigilancia] Modo offline detectado, omitiendo llamada a historial en la nube.');
            }

            if (signal?.aborted) {
                return;
            }
            
            // 2. FETCH DIRECTO DESDE Firestore (invitacionesAcceso) para asegurar datos frescos de QR
            let directItems = [];
            try {
                const invitationsRef = collection(db, 'fraccionamientos', fraccionamientoId, 'invitacionesAcceso');
                let directQuery;
                if (isSuperAdmin || isCaseta) {
                    console.log('📋 [Vigilancia] Query modo Admin/Caseta (ordenado por creadoEn)');
                    directQuery = query(invitationsRef, orderBy('creadoEn', 'desc'), limit(150));
                } else {
                    console.log(`👤 [Vigilancia] Query modo Propietario (generadoPorUid: ${currentUid})`);
                    directQuery = query(invitationsRef, where('generadoPorUid', '==', currentUid), orderBy('creadoEn', 'desc'), limit(50));
                }
                const snapshot = await getDocs(directQuery);
                if (signal?.aborted) return;

                directItems = snapshot.docs.map(docSnap => ({ 
                    id: docSnap.id, 
                    ...docSnap.data() 
                }));
                console.log(`✅ [Vigilancia] Fetch directo (invitacionesAcceso) exitoso: ${directItems.length} ítems`);
            } catch (directErr) {
                console.error('❌ [Vigilancia] Error fetch directo invitacionesAcceso:', directErr);
                console.log('💡 Sugerencia: Revisa los permisos de Firestore para la colección "invitacionesAcceso"');
            }

            // 3. MERGE Y DEDUPLICAR
            const combinedRaw = [...directItems];
            const seenIds = new Set(directItems.map(i => i.id));
            
            cloudItems.forEach(item => {
                if (!seenIds.has(item.id)) {
                    combinedRaw.push(item);
                }
            });

            // 4. MAPEAR DATOS
            const mapped = combinedRaw
                // FILTRO: Si es caseta, excluir QR pendientes
                .filter((item) => {
                    const estado = String(item?.estado || '').toLowerCase();
                    if (isCaseta && estado === 'pendiente') return false;
                    return true;
                })
                .map((item) => {
                    const fechaVisita = item && item.fechaVisitaIso ? new Date(item.fechaVisitaIso) : 
                                      (item.fechaVisita?.toDate ? item.fechaVisita.toDate() :
                                      (item.createdAt?.toDate ? item.createdAt.toDate() : 
                                      (item.creadoEn?.toDate ? item.creadoEn.toDate() : null)));
                    const validadoEn = item && item.validadoEnIso ? new Date(item.validadoEnIso) : 
                                     (item.validadoEn?.toDate ? item.validadoEn.toDate() : null);
                                     
                    const entradaRealDate = item.entradaRegistradaEn?.toDate ? item.entradaRegistradaEn.toDate() : validadoEn;
                    const entradaRealIso = entradaRealDate ? entradaRealDate.toISOString() : null;
                    
                    const estadoBase = String(item?.estado || 'pendiente').toLowerCase();
                    const fechaLabel = item.fechaVisitaLabel || (fechaVisita ? formatVisitaDate(fechaVisita) : 'Fecha no disponible');
                    const fechaSoloLabel = item.fechaVisitaDiaLabel || (fechaVisita ? formatVisitaDateOnly(fechaVisita) : 'Fecha no disponible');
                    const rawTimeOnly = item.horaVisitaLabel || (fechaVisita ? formatVisitaTimeOnly(fechaVisita) : '');
                    const horaSoloLabel = (rawTimeOnly === '00:00' || rawTimeOnly === '0:00') ? 'Todo el día' : rawTimeOnly;

                    return {
                        id: item.id,
                        codigo: item.codigo,
                        visitante: item.visitanteNombre || item.visitante || item.nombreVisitante || 'Visitante sin nombre',
                        visitanteNombre: item.visitanteNombre || '',
                        departamento: item.departamento || item.numeroCasa || '',
                        categoria: item.categoria || item.tipoVisita || '',
                        placa: item.placa || item.vehiculoPlaca || '',
                        fechaVisita,
                        fechaVisitaLabel: fechaLabel,
                        fechaVisitaDiaLabel: fechaSoloLabel,
                        horaVisitaLabel: horaSoloLabel,
                        estado: estadoBase,
                        estadoLabel: item.estadoLabel || (estadoBase ? estadoBase.charAt(0).toUpperCase() + estadoBase.slice(1) : 'Pendiente'),
                        numeroVisitantes: Number(item.numeroVisitantes) || 1,
                        // Nuevos campos para visualización de residente
                        esMultiAcceso: !!item.esMultiAcceso,
                        duracionHoras: item.duracionHoras || 0,
                        limiteSolicitudes: item.limiteSolicitudes || 1,
                        escaneosActuales: item.escaneosActuales || 0,
                        generadoPor: item.generadoPor || item.ownerNombre || '',
                        generadoPorUid: item.generadoPorUid || item.ownerUid || null,
                        correosDestino: Array.isArray(item.correosDestino) ? item.correosDestino : 
                                       (item.destinatarioEmail ? [item.destinatarioEmail] : []),
                        validadoPorNombre: item.validadoPorNombre || '',
                        validadoPorRol: item.validadoPorRol || '',
                        validadoEn,
                        validadoEnIso: item.validadoEnIso || (item.validadoEn?.toDate ? item.validadoEn.toDate().toISOString() : null),
                        validadoEnLabel: validadoEn ? formatVisitaDate(validadoEn) : '',
                        entradaRealDate,
                        entradaRealIso,
                        salidaRegistradaIso: item.salidaRegistradaIso || (item.salidaRegistradaEn?.toDate ? item.salidaRegistradaEn.toDate().toISOString() : null),
                        qrDataUrl: item.qrDataUrl || null,
                    };
                })
                .sort((a, b) => {
                    const timeA = a.entradaRealDate ? a.entradaRealDate.getTime() : (a.fechaVisita ? a.fechaVisita.getTime() : 0);
                    const timeB = b.entradaRealDate ? b.entradaRealDate.getTime() : (b.fechaVisita ? b.fechaVisita.getTime() : 0);
                    return timeB - timeA;
                });


            console.log(`📈 [Vigilancia] Historial final procesado: ${mapped.length} registros`);
            if (signal?.aborted) return;
            setVisitHistory(mapped);
        } catch (historyError) {
            console.error('Vigilancia: error al cargar historial de visitas', historyError);
            if (!signal?.aborted) {
                setVisitHistory([]);
                setVisitHistoryError('No se pudo cargar el historial de visitas.');
            }
        } finally {
            if (!signal?.aborted) {
                setVisitHistoryLoading(false);
            }
        }
    }, [formatVisitaDate, formatVisitaDateOnly, formatVisitaTimeOnly, fraccionamientoId, getVisitHistoryCallable, isSuperAdmin, isCaseta, currentUid]);

    // Efecto para recargar historial cuando cambia la fecha o se abre el modal
    useEffect(() => {
        if (visitHistoryModalOpen) {
            const controller = new AbortController();
            loadVisitHistory({ signal: controller.signal, date: visitHistoryDate });
            return () => controller.abort();
        }
    }, [visitHistoryDate, visitHistoryModalOpen, loadVisitHistory]);

    // Filtrado local del historial
    const filteredVisitHistory = useMemo(() => {
        let list = [...visitHistory];

        // 1. Filtro de búsqueda (código, nombre, depto, correo)
        if (visitHistorySearch) {
            const term = visitHistorySearch.toLowerCase().trim();
            list = list.filter(item => {
                const codigo = String(item.codigo || '').toLowerCase();
                const visitante = String(item.visitante || '').toLowerCase();
                const depto = String(item.departamento || '').toLowerCase();
                const emails = (item.correosDestino || []).map(e => String(e).toLowerCase());
                
                return codigo.includes(term) || 
                       visitante.includes(term) || 
                       depto.includes(term) || 
                       emails.some(e => e.includes(term));
            });
        }

        // 2. Filtro de fecha local estricto
        if (visitHistoryDate) {
            const [year, month, day] = visitHistoryDate.split('-');
            const y = parseInt(year, 10);
            const m = parseInt(month, 10) - 1;
            const d = parseInt(day, 10);
            
            list = list.filter(item => {
                const itemDate = item.entradaRealDate || item.fechaVisita;
                if (!itemDate) return false;
                return itemDate.getFullYear() === y && 
                       itemDate.getMonth() === m && 
                       itemDate.getDate() === d;
            });
        }

        // 3. Filtro "Solo los que están dentro"
        if (visitHistoryInsideOnly) {
            list = list.filter(item => {
                // Estado validado y sin salida registrada
                const estado = String(item.estado || '').toLowerCase();
                return estado === 'validado' && !item.salidaRegistradaIso;
            });
        }

        return list;
    }, [visitHistory, visitHistorySearch, visitHistoryInsideOnly]);

    // Manejar cancelación (invalidación) de una invitación QR pendiente
    const handleCancelarInvitacion = useCallback((invitacion) => {
        if (!invitacion?.id || !fraccionamientoId) return;
        setInvitacionToCancel(invitacion);
        setShowCancelQrConfirm(true);
    }, [fraccionamientoId]);

    // Ejecutar la cancelación real tras confirmar en el diálogo premium
    const executeCancelarInvitacion = useCallback(async () => {
        if (!invitacionToCancel || !fraccionamientoId) return;
        
        const invId = invitacionToCancel.id;
        setShowCancelQrConfirm(false);
        setInvitacionToCancel(null);

        try {
            console.log('🚫 [Vigilancia] Cancelando invitación:', invId);
            const invRef = doc(db, 'fraccionamientos', fraccionamientoId, 'invitacionesAcceso', invId);
            
            await updateDoc(invRef, {
                estado: 'validado',
                estadoLabel: 'Cancelado',
                canceladoEn: serverTimestamp(),
                canceladoPorUid: currentUid
            });

            showNotification('Invitación cancelada correctamente.', 'success');
            
            // Recargar historial
            loadVisitHistory({ silent: true });
        } catch (err) {
            console.error('❌ [Vigilancia] Error al cancelar invitación:', err);
            showNotification('No se pudo cancelar la invitación. Intenta de nuevo.', 'error');
        }
    }, [fraccionamientoId, db, currentUid, showNotification, loadVisitHistory, invitacionToCancel]);

    const handleValidarVisita = useCallback(async (event) => {
        if (event) {
            event.preventDefault();
        }

        if (validationSubmitting) {
            return;
        }

        const code = qrCode.trim();
        if (!code) {
            setValidationError('Escanea o ingresa un código válido.');
            return;
        }

        if (!fraccionamientoId) {
            setValidationError('No se pudo determinar el fraccionamiento. Actualiza la página e intenta nuevamente.');
            return;
        }

        setValidationError('');
        setValidationSubmitting(true);
        setValidationResult(null);

        try {
            let data;
            if (!navigator.onLine) {
                console.log('📡 [Vigilancia] Validando QR offline (checkOnly)...');
                data = await validateQrOffline({
                    fraccionamientoId,
                    codigo: code,
                    auth: { currentUser },
                    userData,
                    checkOnly: true
                });
            } else {
                const callable = getValidateVisitCallable();
                const response = await callable({
                    fraccionamientoId,
                    codigo: code,
                    soloVerificar: true,
                });
                data = response?.data || {};
            }
 
            const success = Boolean(data.success);
            const visit = data.visit && typeof data.visit === 'object' ? data.visit : null;
            const message = data.message || (success ? 'Visita validada correctamente.' : 'El código no es válido o ya fue utilizado.');

            setValidationResult({
                success,
                message,
                visit,
            });

            if (!success && (data.code === 'expired' || data.code === 'invalid-status' || message.toLowerCase().includes('expir') || message.toLowerCase().includes('válido'))) {
                setScannedWarning(true);
                setTimeout(() => setScannedWarning(false), 2000);
            }

            // Desplazar al resultado con un pequeño delay para asegurar el renderizado
            setTimeout(() => {
                if (validationResultRef.current) {
                    validationResultRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }, 100);

            if (success) {
                // Si la validación es exitosa, preparar datos para el modal de captura
                const visitData = visit || {};

                console.log('🎯 [handleValidarVisita] QR válido, preparando datos para captura de imágenes:', visitData);

                // Guardar los datos de la visita validada para usar después de capturar imágenes
                const datosPendientes = {
                    tipo: 'qr',
                    codigo: code,
                    invitacionId: data.invitacionId || visitData.invitacionId,
                    visitante: visitData.visitante || visitData.visitanteNombre,
                    departamento: visitData.departamento,
                    categoria: visitData.categoria,
                    numeroVisitantes: visitData.numeroVisitantes || 1,
                    placa: visitData.placaVehiculo || visitData.placa || '',
                    color: visitData.colorVehiculo || visitData.color || '',
                    notas: visitData.observaciones || visitData.notas || '',
                    tipoTransporte: visitData.tipoTransporte || (visitData.placaVehiculo || visitData.placa ? 'En vehiculo' : 'A pie'),
                };

                console.log('🎯 [handleValidarVisita] Guardando datos pendientes:', datosPendientes);
                setDatosAccesoManualPendiente(datosPendientes);

                const rawPlaca = visitData.placaVehiculo || visitData.placa || '';
                const cleanPlaca = (rawPlaca === 'Sin vehiculo' || rawPlaca === 'Sin vehículo') ? '' : rawPlaca;
                const rawColor = visitData.colorVehiculo || visitData.color || '';
                const cleanColor = (rawColor === 'A pie') ? '' : rawColor;

                setDatosVehiculoForm({
                    tipoTransporte: visitData.tipoTransporte || (cleanPlaca ? 'En vehiculo' : 'A pie'),
                    placa: cleanPlaca,
                    color: cleanColor,
                });

                console.log('🎯 [handleValidarVisita] Ocultando modal de validación QR...');
                setShowQrValidationModal(false);
                stopValidationStream();
                setCameraActive(false);
                setQrCode('');

                console.log('🎯 [handleValidarVisita] Mostrando modal intermedio de captura de datos de vehículo...');
                openModalWithHistory(setShowCapturaDatosVehiculo);
            } else {
                showNotification(message, 'warning');
            }
        } catch (error) {
            console.error('Vigilancia: error al validar visita', error);
            let message = 'No se pudo validar el código. Intenta nuevamente.';
            if (error?.details?.message) {
                message = error.details.message;
            } else if (typeof error?.message === 'string' && error.message) {
                message = error.message;
            }

            setValidationResult({
                success: false,
                message,
                visit: null,
            });
            showNotification(message, 'error');
        } finally {
            setValidationSubmitting(false);
        }
    }, [fraccionamientoId, getValidateVisitCallable, loadVisitHistory, qrCode, showNotification, stopValidationStream, validationSubmitting, currentUser, openModalWithHistory, userData]);

    useEffect(() => {
        if (!fraccionamientoId) return;
        if (!visitHistoryModalOpen && !showMyQrModal) {
            return undefined;
        }

        const controller = new AbortController();
        loadVisitHistory({ signal: controller.signal });

        return () => {
            controller.abort();
        };
    }, [loadVisitHistory, visitHistoryModalOpen, showMyQrModal, fraccionamientoId]);

    const guessPhoneFromVisita = useCallback((visita) => {
        if (!visita) {
            return '';
        }
        const candidates = [
            visita.telefono,
            visita.raw?.telefono,
            visita.raw?.telefonoVisitante,
            visita.raw?.celular,
            visita.raw?.phone,
            visita.raw?.contactoTelefono,
        ];
        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate.trim();
            }
        }
        return '';
    }, []);

    const guessVisitantesFromVisita = useCallback((visita) => {
        if (!visita) {
            return '1';
        }
        const candidates = [
            visita.raw?.numeroVisitantes,
            visita.raw?.visitantes,
            visita.raw?.personasAutorizadas,
            visita.raw?.numPersonas,
        ];
        for (const candidate of candidates) {
            if (candidate === null || candidate === undefined) {
                continue;
            }
            const numeric = Number(candidate);
            if (Number.isFinite(numeric) && numeric > 0) {
                return String(Math.max(1, Math.min(20, Math.round(numeric))));
            }
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate.trim();
            }
        }
        return '1';
    }, []);

    const populateQrFromVisita = useCallback(async (visita) => {
        if (!visita) {
            return;
        }

        let residentDepartamento = visita.ownerDepartamento || '';

        // Si no tiene ownerDepartamento, buscar en casas por nombre_residente
        if (!residentDepartamento && visita.ownerNombre && fraccionamientoId) {
            console.log('[QR] 🔎 Buscando en casas por nombre_residente:', visita.ownerNombre);
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const casaSnap = await getDocs(query(casasRef, where('nombre_residente', '==', visita.ownerNombre)));

                console.log('[QR] 📦 Casas encontradas por nombre_residente:', casaSnap.docs.length);

                if (!casaSnap.empty) {
                    const casaData = casaSnap.docs[0].data();
                    console.log('[QR] 🏘️ Datos de casa encontrada:', {
                        numero: casaData.numero,
                        nombre_residente: casaData.nombre_residente,
                    });

                    residentDepartamento = casaData.numero || '';
                    console.log('[QR] ✅ Número de casa resuelto:', residentDepartamento);
                } else {
                    console.log('[QR] ⚠️ No se encontraron casas con nombre_residente:', visita.ownerNombre);
                }
            } catch (err) {
                console.warn('[QR] ❌ Error al buscar casa por nombre_residente:', err);
            }
        }

        setQrForm((prev) => ({
            ...prev,
            nombre: visita.nombre || prev.nombre || '',
            telefono: guessPhoneFromVisita(visita) || prev.telefono || '',
            placa: visita.placa || prev.placa || '',
            color: visita.color || prev.color || '',
            categoria: visita.categoria || prev.categoria || '',
            observaciones: visita.notas || prev.observaciones || '',
            numeroVisitantes: guessVisitantesFromVisita(visita) || prev.numeroVisitantes || '1',
            departamento: residentDepartamento || prev.departamento || ownerDepartamento || '',
            tipoTransporte: visita.tipoTransporte || visita.tipo_transporte || prev.tipoTransporte || 'En vehiculo',
        }));
    }, [fraccionamientoId, guessPhoneFromVisita, guessVisitantesFromVisita, ownerDepartamento]);

    const handleToggleVisitaFrecuente = useCallback((event) => {
        const checked = event.target.checked;
        if (checked && visitasFrecuentes.length > 0) {
            // Activar flag y seleccionar la primera visita automáticamente
            setQrForm((prev) => ({ ...prev, usarVisitaFrecuente: true }));
            processVisitaFrecuenteSelection(visitasFrecuentes[0].id);
            
            // Poner el foco en el selector tras la actualización del DOM
            setTimeout(() => {
                qrVisitaSelectorRef.current?.focus();
            }, 100);
        } else {
            // Resetear la decisión de horarios al desmarcar la opción
            setHasDecidedSchedule(null);
            setUseSavedSchedule(false);
            
            setQrForm((prev) => {
                const base = {
                    ...prev,
                    usarVisitaFrecuente: checked,
                    visitaFrecuenteId: '',
                };
                if (!checked) {
                    return {
                        ...base,
                        nombre: '',
                        telefono: '',
                        placa: '',
                        color: '',
                        categoria: '',
                        observaciones: '',
                        correoInvitado: '',
                        departamento: '',
                    };
                }
                return base;
            });
        }
    }, [guessPhoneFromVisita, guessVisitantesFromVisita, visitasFrecuentes]);


    const handleOpenVisitaModal = useCallback(() => {
        if (isResident && isVisitasFrecuentesBloqueadas) {
            mostrarAlertaVigilancia('El registro de visitas frecuentes está temporalmente inhabilitado por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
            return;
        }
        resetVisitaForm();
        openModalWithHistory(setShowVisitaModal);
    }, [isResident, isVisitasFrecuentesBloqueadas, mostrarAlertaVigilancia, openModalWithHistory, resetVisitaForm]);

    const handleEditVisita = useCallback((visita) => {
        if (isResident && isVisitasFrecuentesBloqueadas) {
            mostrarAlertaVigilancia('La edición de visitas frecuentes está temporalmente inhabilitada por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
            return;
        }
        setEditingVisitaId(visita.id);
        setVisitaForm({
            nombre: visita.nombre || '',
            categoria: visita.categoria || '',
            telefono: visita.telefono || '',
            placa: visita.placa || '',
            color: visita.color || '',
            notas: visita.notas || '',
            horario: visita.horario || INITIAL_HORARIO,
            identificacionUrl: visita.identificacionUrl || '',
            identificacionName: visita.identificacionName || ''
        });
        setVisitaArchivo(null); // Opcional: permitir cambiar archivo o mantener el actual
        setVisitaFormError('');
        openModalWithHistory(setShowVisitaModal);
        setActiveVisitaTab('info');
    }, [isResident, isVisitasFrecuentesBloqueadas, mostrarAlertaVigilancia, openModalWithHistory]);

    const handleDeleteVisita = useCallback((visita) => {
        setVisitaToDelete(visita);
        setShowDeleteVisitaConfirm(true);
    }, []);

    const executeDeleteVisita = useCallback(async () => {
        if (!visitaToDelete || !fraccionamientoId) return;

        const { id, nombre, identificacionPath } = visitaToDelete;
        setVisitaSubmitting(true);

        try {
            const docRef = doc(db, 'fraccionamientos', fraccionamientoId, 'visitasFrecuentes', id);
            
            // 1. Marcar quién borra para que la Cloud Function notifique correctamente
            // Esto es necesario porque el trigger onDelete no tiene contexto del usuario autenticado
            await updateDoc(docRef, { 
                lastEditorRole: isSuperAdmin ? 'administrador' : 'residente',
                lastEditorUid: currentUid,
                isDeleting: true
            });

            // 2. Eliminar de Firestore
            await deleteDoc(docRef);

            // 2. Eliminar identificación de Storage (si existe)
            if (identificacionPath) {
                try {
                    await deleteObject(storageRef(storage, identificacionPath));
                } catch (stError) {
                    console.warn('No se pudo borrar el archivo de identificación:', stError);
                }
            }

            // 3. Registrar movimiento
            try {
                const actorNombre = isSuperAdmin ? 'Administrador' : (ownerNombre || 'Residente');
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Visita frecuente eliminada: ${nombre}`,
                    descripcion: `${actorNombre} eliminó a ${nombre} de la lista de ${ownerNombre || 'un residente'}.`,
                    categoria: 'Vigilancia',
                    tipoMovimiento: 'visita_eliminada',
                    origen: 'vigilancia',
                    metadata: { 
                        visitaId: id, 
                        visitante: nombre,
                        eliminadoPor: isSuperAdmin ? 'administrador' : 'residente',
                        propietario: ownerNombre
                    }
                });
            } catch (err) {
                console.warn('Error al registrar movimiento de eliminación:', err);
            }

            showNotification(`¡${nombre} ha sido eliminado correctamente!`, 'success');
        } catch (error) {
            console.error('Error al eliminar visita frecuente:', error);
            showNotification('No se pudo eliminar la visita frecuente.', 'error');
        } finally {
            setVisitaSubmitting(false);
            setShowDeleteVisitaConfirm(false);
            setVisitaToDelete(null);
        }
    }, [visitaToDelete, fraccionamientoId, db, storage, ownerNombre, isSuperAdmin, registerMovement, showNotification]);


    const applySchedulePreference = useCallback(async (visita, useIt) => {
        setUseSavedSchedule(useIt);
        await populateQrFromVisita(visita);
        
        if (useIt) {
            // Aplicar configuración de 7 días / multi-acceso
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');

            setQrForm(prev => ({
                ...prev,
                fecha: `${year}-${month}-${day}`,
                hora: `${hours}:${minutes}`,
                esMultiAcceso: true,
                duracionHoras: '168',
                limiteEscaneos: '1'
            }));
        }
    }, [populateQrFromVisita]);

    const processVisitaFrecuenteSelection = useCallback(async (visitaId) => {
        setQrForm((prev) => ({ ...prev, visitaFrecuenteId: visitaId }));
        
        if (!visitaId) {
            setUseSavedSchedule(false);
            return;
        }

        const visita = visitasFrecuentes.find((item) => item.id === visitaId);
        if (!visita) return;

        // Verificar si tiene un horario configurado
        const hasSchedule = visita.horario && Object.values(visita.horario).some(dia => Array.isArray(dia) && dia.length > 0);

        if (hasSchedule) {
            if (hasDecidedSchedule === null) {
                // Primera vez que se encuentra un horario en esta sesión: preguntar
                setPendingVisita(visita);
                setShowScheduleConfirm(true);
            } else {
                // Ya existe una decisión previa: aplicar automáticamente
                await applySchedulePreference(visita, hasDecidedSchedule);
            }
        } else {
            // No tiene horario: comportamiento estándar
            setUseSavedSchedule(false);
            await populateQrFromVisita(visita);
        }

        // Resetear el acordeón cada vez que se cambia de visitante
        setShowBannerScheduleDetail(false);
    }, [applySchedulePreference, hasDecidedSchedule, populateQrFromVisita, visitasFrecuentes]);


    const handleQrVisitaFrecuenteSelect = useCallback(async (event) => {
        const { value } = event.target;
        await processVisitaFrecuenteSelection(value);
    }, [processVisitaFrecuenteSelection]);

    const confirmUseSchedule = useCallback(async () => {
        if (!pendingVisita) return;
        
        setHasDecidedSchedule(true);
        await applySchedulePreference(pendingVisita, true);
        
        setShowScheduleConfirm(false);
        setPendingVisita(null);
        showNotification('Horario guardado activado correctamente.', 'success');
    }, [applySchedulePreference, pendingVisita, showNotification]);

    const declineUseSchedule = useCallback(async () => {
        if (!pendingVisita) return;
        
        setHasDecidedSchedule(false);
        await applySchedulePreference(pendingVisita, false);
        
        setShowScheduleConfirm(false);
        setPendingVisita(null);
    }, [applySchedulePreference, pendingVisita]);

    const handleQrFormChange = useCallback((event) => {
        const { name, value } = event.target;
        if (name === 'telefono') {
            const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
            setQrForm((prev) => ({ ...prev, [name]: numericValue }));
        } else {
            setQrForm((prev) => ({ ...prev, [name]: value }));
        }
    }, []);

    const handleDownloadQR = useCallback((manualData = null) => {
        // Si manualData es un evento de React, ignorarlo para usar qrResult por defecto
        const isEvent = manualData && (manualData.nativeEvent || manualData.target);
        const sourceData = (manualData && !isEvent) ? manualData : qrResult;
        
        if (!sourceData?.qrDataUrl) {
            console.warn('⚠️ [handleDownloadQR] No hay datos de QR para descargar (sourceData.qrDataUrl es null)');
            return;
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const visitorName = sourceData.visitanteNombre || sourceData.nombre || qrForm.nombre || 'Invitado';
        const reference = sourceData.codigo || '';
        const date = sourceData.fechaVisitaLabel || sourceData.fechaVisita || qrForm.fecha || '';
        const location = sourceData.visitanteDepto || sourceData.departamento || qrForm.departamento || '';

        // Definir dimensiones (formato vertical dinámico)
        let canvasHeight = 650;
        let scheduleRows = [];

        if (useSavedSchedule) {
            const visita = visitasFrecuentes.find(v => v.id === (sourceData.visitaFrecuenteId || qrForm.visitaFrecuenteId));
            const horario = visita?.horario || sourceData.horario;
            
            if (horario) {
                const DIAS_MAP = {
                    lunes: 'Lun', martes: 'Mar', miercoles: 'Mie', 
                    jueves: 'Jue', viernes: 'Vie', sabado: 'Sab', domingo: 'Dom'
                };
                
                DIAS_SEMANA_ORDEN.forEach(dia => {
                    const rangos = horario[dia];
                    if (Array.isArray(rangos) && rangos.length > 0) {
                        const rangosStr = rangos.map(r => `${r.inicio}-${r.fin}`).join(', ');
                        scheduleRows.push({ dia: DIAS_MAP[dia] || dia, rangos: rangosStr });
                    }
                });
            }
        }

        if (scheduleRows.length > 0) {
            // Aumentar altura según cantidad de filas (aprox 30px por fila + 60px para el título/box)
            canvasHeight += 60 + (scheduleRows.length * 30);
        }

        canvas.width = 500;
        canvas.height = canvasHeight;

        // Fondo y bordes
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Encabezado Púrpura (Zen style)
        ctx.fillStyle = '#6366f1'; // Indigo-500
        ctx.fillRect(0, 0, canvas.width, 100);

        // Texto Encabezado
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 28px "Segoe UI", Roboto, Arial';
        ctx.textAlign = 'center';
        ctx.fillText('CÓDIGO QR · VISITA', canvas.width / 2, 50);
        
        ctx.font = 'normal 18px "Segoe UI", Roboto, Arial';
        ctx.fillText('Pase de acceso — escanea para validar', canvas.width / 2, 85);

        // Código QR
        const img = new Image();
        img.onload = async () => {
            // Fondo suave para el QR
            ctx.fillStyle = '#f8fafc';
            ctx.beginPath();
            ctx.roundRect(50, 130, 400, 400, 20);
            ctx.fill();

            // Dibujar QR
            ctx.drawImage(img, 75, 155, 350, 350);

            // Sección de Horarios (Solo si existen)
            let currentY = 550;

            if (scheduleRows.length > 0) {
                // Título Sección Horarios
                ctx.fillStyle = '#4f46e5'; // Indigo-600
                ctx.font = 'bold 18px "Segoe UI", Roboto, Arial';
                ctx.textAlign = 'left';
                ctx.fillText('HORARIO DE ACCESO', 50, currentY);
                currentY += 15;

                // Caja de fondo para horarios
                ctx.fillStyle = '#f1f5f9';
                ctx.beginPath();
                const boxHeight = (scheduleRows.length * 30) + 20;
                ctx.roundRect(50, currentY, 400, boxHeight, 15);
                ctx.fill();
                ctx.strokeStyle = '#e2e8f0';
                ctx.lineWidth = 1;
                ctx.stroke();

                currentY += 35; // Primer línea de texto

                scheduleRows.forEach((row) => {
                    ctx.fillStyle = '#6366f1'; // Texto Día
                    ctx.font = 'bold 15px "Segoe UI", Roboto, Arial';
                    ctx.textAlign = 'left';
                    ctx.fillText(row.dia, 75, currentY);

                    ctx.fillStyle = '#475569'; // Texto Rangos
                    ctx.font = 'normal 15px "Segoe UI", Roboto, Arial';
                    ctx.textAlign = 'right';
                    ctx.fillText(row.rangos, 425, currentY);
                    
                    currentY += 30;
                });

                currentY += 20; // Espacio después de la caja
            }

            // Tabla de datos (Texto Gris Zen)
            ctx.fillStyle = '#64748b';
            ctx.font = 'bold 16px "Segoe UI", Roboto, Arial';
            ctx.textAlign = 'left';

            const startY = currentY;
            const lineHeight = 30;

            ctx.fillText('DPTO / LOTE', 50, startY);
            ctx.fillText('VISITANTE', 50, startY + lineHeight);
            ctx.fillText('FECHA', 50, startY + (lineHeight * 2));
            ctx.fillText('REFERENCIA', 50, startY + (lineHeight * 3));

            // Valores (Texto Azul Oscuro)
            ctx.fillStyle = '#1e293b';
            ctx.textAlign = 'right';
            ctx.fillText(location, 450, startY);
            ctx.fillText(visitorName, 450, startY + lineHeight);
            ctx.fillText(date, 450, startY + (lineHeight * 2));
            ctx.fillText(reference, 450, startY + (lineHeight * 3));

            // Marca de agua sutil
            ctx.fillStyle = '#cbd5e1';
            ctx.font = '12px "Segoe UI", Roboto, Arial';
            ctx.textAlign = 'center';
            ctx.fillText('domushabita.com.mx', canvas.width / 2, canvas.height - 15);

            // Disparar descarga o compartir en móviles
            const now = new Date();
            const mxParts = new Intl.DateTimeFormat('en-CA', {
                timeZone: 'America/Mexico_City',
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: false
            }).formatToParts(now);

            const getPart = (type) => mxParts.find(p => p.type === type)?.value || '00';
            const day = getPart('day');
            const month = getPart('month');
            const year = getPart('year');
            const hour = getPart('hour');
            const minute = getPart('minute');

            const dateStr = `${day}${month}${year}`;
            const timeStr = `${hour}-${minute}`;
            const sanitizedName = visitorName.replace(/\s+/g, '_');
            const dataUrl = canvas.toDataURL('image/png');
            const fileName = `QR_Visita_${sanitizedName}_${dateStr}_${timeStr}.png`;

            // Detectar entorno móvil para compartir nativamente
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 800;
            
            if (isMobile) {
                // Caso 1: App Nativa (Usar plugin de Capacitor para máxima estabilidad)
                if (isNativePlatform()) {
                    try {
                        await Share.share({
                            title: 'Código QR de Acceso',
                            text: `Invitación para ${visitorName} (${location})`,
                            url: dataUrl,
                            dialogTitle: 'Compartir Código QR',
                        });
                        return;
                    } catch (shareErr) {
                        console.warn('Error con plugin Share, intentando fallback visual:', shareErr);
                        
                        // FALLBACK VISUAL: Si el plugin share falla (común en algunas capas de personalización)
                        // Mostramos el QR en un modal para que el usuario pueda tomar captura o guardarlo
                        Swal.fire({
                            title: 'Tu Código QR',
                            text: 'Mantén presionada la imagen para guardarla o toma una captura de pantalla.',
                            imageUrl: dataUrl,
                            imageAlt: 'Código QR de Acceso',
                            confirmButtonText: 'Cerrar',
                            confirmButtonColor: '#2563eb',
                            customClass: {
                                image: 'qr-modal-image-stabilized'
                            },
                            didOpen: () => {
                                const container = Swal.getContainer();
                                if (container) {
                                    container.style.zIndex = '99999';
                                }
                            }
                        });
                        return;
                    }
                }

                // Caso 2: Navegador móvil (Web Share API)
                if (navigator.share) {
                    try {
                        const blob = await (await fetch(dataUrl)).blob();
                        const file = new File([blob], fileName, { type: 'image/png' });
                        
                        if (navigator.canShare && navigator.canShare({ files: [file] })) {
                            await navigator.share({
                                files: [file],
                                title: 'Código QR de Acceso',
                                text: `Invitación para ${visitorName} (${location})`,
                            });
                            return;
                        }
                    } catch (shareErr) {
                        if (shareErr.name !== 'AbortError') {
                            console.error('Error al compartir QR (Web API):', shareErr);
                        } else {
                            return; // Usuario canceló manualmente
                        }
                    }
                }
            }

            // Fallback: Descarga clásica para Desktop
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };
        img.src = sourceData.qrDataUrl;
    }, [qrResult, qrForm]);

    const handlePrintQR = useCallback(() => {
        if (!qrResult?.qrDataUrl) return;
        const visitorName = qrForm.nombre || 'Invitado';
        const reference = qrResult.codigo || '';
        
        // Crear el contenedor del overlay
        const printOverlay = document.createElement('div');
        printOverlay.id = 'print-qr-overlay';
        printOverlay.style.position = 'fixed';
        printOverlay.style.top = '0';
        printOverlay.style.left = '0';
        printOverlay.style.width = '100vw';
        printOverlay.style.height = '100vh';
        printOverlay.style.backgroundColor = '#ffffff';
        printOverlay.style.zIndex = '100000';
        printOverlay.style.overflowY = 'auto';
        printOverlay.style.padding = '40px 20px';
        printOverlay.style.boxSizing = 'border-box';
        printOverlay.style.display = 'flex';
        printOverlay.style.flexDirection = 'column';
        printOverlay.style.alignItems = 'center';
        printOverlay.style.fontFamily = "'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

        printOverlay.innerHTML = `
            <style>
                #print-qr-overlay .container { 
                    border: 2px solid #e2e8f0; 
                    border-radius: 24px; 
                    padding: 40px; 
                    display: inline-block; 
                    max-width: 400px;
                    width: 100%;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    background: white;
                    text-align: center;
                }
                #print-qr-overlay h1 { 
                    font-size: 20px; 
                    color: #10b981; 
                    margin: 0 0 20px 0;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                #print-qr-overlay .qr-image { 
                    width: 280px; 
                    height: 280px; 
                    margin-bottom: 20px; 
                    border: 1px solid #f1f5f9; 
                    padding: 15px; 
                    border-radius: 16px; 
                    background: #ffffff;
                }
                #print-qr-overlay .reference { 
                    font-size: 22px; 
                    font-weight: 900; 
                    color: #4f46e5; 
                    margin: 20px 0; 
                    display: block; 
                    text-align: center;
                    font-family: monospace;
                }
                #print-qr-overlay .info-grid { 
                    text-align: left; 
                    background: #f8fafc; 
                    padding: 20px; 
                    border-radius: 16px; 
                    font-size: 15px;
                    border: 1px solid #e2e8f0;
                }
                #print-qr-overlay .info-item { 
                    margin: 10px 0; 
                    display: flex;
                    justify-content: space-between;
                    border-bottom: 1px solid #e2e8f0;
                    padding-bottom: 8px;
                }
                #print-qr-overlay .info-item:last-child {
                    border-bottom: none;
                    padding-bottom: 0;
                }
                #print-qr-overlay .label { color: #64748b; font-weight: 600; font-size: 12px; text-transform: uppercase; }
                #print-qr-overlay .value { color: #1e293b; font-weight: 700; }
                #print-qr-overlay .footer { 
                    font-size: 11px; 
                    color: #94a3b8; 
                    margin-top: 30px;
                    font-weight: 500;
                }
                @media print {
                    body > *:not(#print-qr-overlay) { display: none !important; }
                    #print-qr-overlay { position: absolute !important; padding: 0 !important; }
                    #print-qr-overlay #close-window-btn { display: none !important; }
                    #print-qr-overlay .container { border: none !important; box-shadow: none !important; }
                }
            </style>
            
            <button id="close-window-btn" style="position:fixed; top:20px; right:20px; width:48px; height:48px; background:#6f47eb; color:white; border:none; border-radius:50%; font-size:28px; font-weight:bold; cursor:pointer; box-shadow:0 4px 12px rgba(0,0,0,0.3); z-index:100001; display:flex; align-items:center; justify-content:center;">&times;</button>
            
            <div class="container">
                <h1>Invitación de Acceso</h1>
                <img src="${qrResult.qrDataUrl}" class="qr-image" alt="QR" />
                <span class="reference">${reference}</span>
                <div class="info-grid">
                    <div class="info-item">
                        <span class="label">Visitante</span>
                        <span class="value">${visitorName}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Fecha</span>
                        <span class="value">${qrForm.fecha}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Ubicación</span>
                        <span class="value">${qrForm.departamento}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Válido</span>
                        <span class="value">24 Horas</span>
                    </div>
                </div>
                <p class="footer">Mi Fraccionamiento App - Gestión de Vigilancia Inteligente</p>
            </div>
        `;

        document.body.appendChild(printOverlay);
        document.body.style.overflow = 'hidden';

        const closeBtn = printOverlay.querySelector('#close-window-btn');
        closeBtn.onclick = () => {
            document.body.removeChild(printOverlay);
            document.body.style.overflow = 'auto';
        };

        // Dar un momento para renderizar antes de imprimir
        setTimeout(() => {
            window.print();
        }, 500);

    }, [qrResult, qrForm]);

    const handleSubmitQr = useCallback(async (event) => {
        if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }

        if (isResident && isQrBloqueado) {
            setQrFormError('Función inhabilitada por adeudo pendiente.');
            mostrarAlertaVigilancia('La generación de códigos QR de invitado está inhabilitada por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
            return;
        }

        if (!isResident && !isSuperAdmin) {
            setQrFormError('Solo los residentes o superadmin pueden generar códigos de invitado.');
            showNotification('Solo los residentes o superadmin pueden generar códigos de invitado.', 'warning');
            return;
        }

        if (!fraccionamientoId || !currentUid) {
            setQrFormError('No pudimos validar tu sesión. Intenta nuevamente.');
            showNotification('No pudimos validar tu sesión. Intenta nuevamente.', 'error');
            return;
        }

        const isSimplificado = modulos?.residente_qr_simplificado === true;

        const nombre = qrForm.nombre.trim();
        const telefono = isSimplificado ? '' : qrForm.telefono.trim();
        const numeroVisitantesValue = isSimplificado ? 1 : Number.parseInt(qrForm.numeroVisitantes, 10);
        const fecha = qrForm.fecha.trim();
        const hora = '00:00'; // Eliminado definitivamente
        const placa = isSimplificado ? 'Sin vehiculo' : qrForm.placa.trim().toUpperCase();
        const color = isSimplificado ? 'A pie' : qrForm.color.trim();
        const categoria = isSimplificado ? 'Invitado' : qrForm.categoria.trim();
        const observaciones = qrForm.observaciones.trim();
        const correoDestino = isSimplificado ? (ownerEmail || '') : (qrForm.correoDestino.trim() || ownerEmail || '');
        const correoInvitado = isSimplificado ? '' : qrForm.correoInvitado.trim();

        if (!correoDestino) {
            setQrFormError('Agrega un correo para enviar el QR.');
            return;
        }

        if (!EMAIL_REGEX.test(correoDestino)) {
            setQrFormError('El correo principal no es válido.');
            return;
        }

        if (!isSimplificado && correoInvitado && !EMAIL_REGEX.test(correoInvitado)) {
            setQrFormError('El correo del invitado no es válido.');
            return;
        }

        if (!nombre) {
            setQrFormError('El nombre del visitante es obligatorio.');
            return;
        }

        if (!isSimplificado) {
            if (!telefono) {
                setQrFormError('Captura el teléfono de contacto.');
                return;
            }

            if (telefono.length !== 10) {
                setQrFormError('El teléfono de contacto debe tener exactamente 10 dígitos.');
                return;
            }

            if (!categoria) {
                setQrFormError('Selecciona la categoría de la visita.');
                return;
            }

            if (qrForm.tipoTransporte !== 'A pie') {
                if (!placa) {
                    setQrFormError(qrForm.tipoTransporte === 'En moto' ? 'Captura las placas de la moto.' : 'Captura las placas del vehículo.');
                    return;
                }

                if (!color) {
                    setQrFormError(qrForm.tipoTransporte === 'En moto' ? 'Captura el color de la moto.' : 'Captura el color del vehículo.');
                    return;
                }
            }
        }

        if (!fecha) {
            setQrFormError('Selecciona la fecha de la visita.');
            return;
        }

        const fechaIsoCandidate = `${fecha}T${hora}`;
        const fechaVisitaDate = new Date(fechaIsoCandidate);
        if (Number.isNaN(fechaVisitaDate.valueOf())) {
            setQrFormError('La fecha ingresada no es válida.');
            return;
        }

        const numeroVisitantes = Number.isFinite(numeroVisitantesValue) && numeroVisitantesValue > 0
            ? Math.max(1, Math.min(20, numeroVisitantesValue))
            : 1;

        const resolvedTimeZone = 'America/Mexico_City'; // Forzado por requerimiento de negocio

        setQrSubmitting(true);
        setQrFormError('');

        // Validación de límite de escaneos (Cupo)
        const limiteEscaneosNum = Number.parseInt(qrForm.limiteEscaneos, 10) || 1;
        if (modulos.qr_contador_escaneos && limiteEscaneosNum > 15) {
            setQrSubmitting(false);
            setQrFormError('El límite de escaneos para un solo QR no puede ser mayor a 15 personas.');
            showNotification('El límite máximo de escaneos es 15 por seguridad.', 'error');
            return;
        }

        try {
            const callable = getGenerateQrCallable();
            const response = await callable({
                fraccionamientoId,
                visitante: {
                    nombre,
                    telefono,
                    numeroVisitantes,
                    placa: qrForm.tipoTransporte === 'A pie' ? 'Sin vehículo' : placa,
                    color: qrForm.tipoTransporte === 'A pie' ? 'A pie' : color,
                    tipoTransporte: qrForm.tipoTransporte,
                    observaciones,
                    visitaFrecuenteId: qrForm.visitaFrecuenteId || null,
                },
                visita: {
                    fechaHoraIso: fechaVisitaDate.toISOString(),
                    timeZone: resolvedTimeZone,
                    fechaLocal: fecha,
                    horaLocal: hora,
                    categoria: categoria || null,
                    // Nuevos parámetros de acceso
                    esMultiAcceso: qrForm.esMultiAcceso,
                    duracionHoras: qrForm.esMultiAcceso ? Number.parseInt(qrForm.duracionHoras, 10) : 0,
                    limiteEscaneos: Number.parseInt(qrForm.limiteEscaneos, 10) || 1,
                },
                visitanteDepartamento: qrForm.departamento || ownerDepartamento || null,
                destinatarioEmail: correoDestino,
                invitadoEmail: correoInvitado || null,
            });

            const data = response?.data || {};
            if (!data.success) {
                throw new Error(data.message || 'No se pudo generar el código QR.');
            }

            setQrResult(data);
            showNotification('QR de invitado generado y enviado por correo.', 'success');

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `QR de invitado generado para ${nombre}`,
                    descripcion: `${ownerNombre || 'Residente'} programó una visita para ${nombre} el ${fechaVisitaDate.toLocaleDateString('es-MX')} a las ${fechaVisitaDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}.`,
                    categoria: 'Vigilancia',
                    tipoMovimiento: 'invitacion_qr_generada',
                    origen: 'vigilancia',
                    metadata: {
                        invitacionId: data.invitacionId || null,
                        codigo: data.codigo || null,
                        fechaVisitaIso: data.fechaVisitaIso || fechaVisitaDate.toISOString(),
                        numeroVisitantes,
                        visitaFrecuenteId: qrForm.visitaFrecuenteId || null,
                        correoDestino,
                        correoInvitado: correoInvitado || null,
                        emailEnviado: !!data.emailEnviado,
                        emailsEnviadosA: Array.isArray(data.emailsEnviadosA) && data.emailsEnviadosA.length > 0 ? data.emailsEnviadosA : null,
                    },
                });
            } catch (movementError) {
                console.error('Vigilancia: no se pudo registrar movimiento de QR', movementError);
            }
        } catch (error) {
            console.error('Vigilancia: error al generar QR', error);
            const message = error?.message || 'No se pudo generar el código QR.';
            setQrFormError(message);
            showNotification(message, 'error');
        } finally {
            setQrSubmitting(false);
        }
    }, [currentUid, fraccionamientoId, getGenerateQrCallable, isResident, isSuperAdmin, ownerEmail, ownerNombre, qrForm.categoria, qrForm.color, qrForm.correoDestino, qrForm.correoInvitado, qrForm.departamento, qrForm.duracionHoras, qrForm.esMultiAcceso, qrForm.fecha, qrForm.hora, qrForm.limiteEscaneos, qrForm.nombre, qrForm.numeroVisitantes, qrForm.observaciones, qrForm.placa, qrForm.telefono, qrForm.tipoTransporte, qrForm.visitaFrecuenteId, registerMovement, showNotification, modulos, ownerDepartamento, isQrBloqueado, mostrarAlertaVigilancia]);


    const handleCloseVisitaModal = useCallback(() => {
        if (visitaSubmitting) {
            return;
        }
        setShowVisitaModal(false);
        setVisitaResultado(null);
        resetVisitaForm();
    }, [resetVisitaForm, visitaSubmitting]);

    const handleVisitaInputChange = useCallback((event) => {
        const { name, value } = event.target;
        if (name === 'telefono') {
            const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
            setVisitaForm((prev) => ({ ...prev, [name]: numericValue }));
        } else {
            setVisitaForm((prev) => ({ ...prev, [name]: value }));
        }
    }, []);

    const handleVisitasFilterChange = useCallback((event) => {
        setVisitasFilterDepto(event.target.value);
    }, []);

    const handleVisitaArchivoChange = useCallback((event) => {
        const [file] = event.target.files || [];
        if (!file) {
            setVisitaArchivo(null);
            return;
        }
        if (file.size > MAX_VISITA_FILE_SIZE) {
            setVisitaFormError('El archivo de identificación no puede superar los 5 MB.');
            setVisitaArchivo(null);
            return;
        }
        setVisitaArchivo(file);
        setVisitaFormError('');
    }, []);


    const getCategoriaBadgeClass = useCallback((value) => {
        const normalized = String(value || '').toLowerCase();
        switch (normalized) {
            case 'proveedor':
                return 'categoria-proveedor';
            case 'personal_limpieza':
                return 'categoria-personal-limpieza';
            case 'servicio':
                return 'categoria-servicio';
            case 'familia':
                return 'categoria-familia';
            case 'chofer':
                return 'categoria-chofer';
            case 'visita':
                return 'categoria-visita';
            default:
                return 'status-validado';
        }
    }, []);

    const handleVisitaHorarioChange = useCallback((dia, index, field, value) => {
        setVisitaForm(prev => {
            const newDiaHorario = [...prev.horario[dia]];
            newDiaHorario[index] = { ...newDiaHorario[index], [field]: value };
            return {
                ...prev,
                horario: { ...prev.horario, [dia]: newDiaHorario }
            };
        });
    }, []);

    const handleAddInterval = useCallback((dia) => {
        setVisitaForm(prev => {
            if (prev.horario[dia].length >= 3) return prev; // Límite de 3 rangos por día
            const newDiaHorario = [...prev.horario[dia], { inicio: '08:00', fin: '09:00' }];
            return {
                ...prev,
                horario: { ...prev.horario, [dia]: newDiaHorario }
            };
        });
    }, []);

    const handleRemoveInterval = useCallback((dia, index) => {
        setVisitaForm(prev => {
            const newDiaHorario = prev.horario[dia].filter((_, i) => i !== index);
            return {
                ...prev,
                horario: { ...prev.horario, [dia]: newDiaHorario }
            };
        });
    }, []);

    const handleDetalleHorarioChange = useCallback((dia, index, field, value) => {
        setDetalleForm(prev => {
            if (!prev) return prev;
            const newDiaHorario = [...prev.horario[dia]];
            newDiaHorario[index] = { ...newDiaHorario[index], [field]: value };
            return {
                ...prev,
                horario: { ...prev.horario, [dia]: newDiaHorario }
            };
        });
    }, []);

    const handleDetalleAddInterval = useCallback((dia) => {
        setDetalleForm(prev => {
            if (!prev) return prev;
            if (prev.horario[dia].length >= 3) return prev;
            const newDiaHorario = [...prev.horario[dia], { inicio: '08:00', fin: '09:00' }];
            return {
                ...prev,
                horario: { ...prev.horario, [dia]: newDiaHorario }
            };
        });
    }, []);

    const handleDetalleRemoveInterval = useCallback((dia, index) => {
        setDetalleForm(prev => {
            if (!prev) return prev;
            const newDiaHorario = prev.horario[dia].filter((_, i) => i !== index);
            return {
                ...prev,
                horario: { ...prev.horario, [dia]: newDiaHorario }
            };
        });
    }, []);

    const handleSubmitVisita = useCallback(async (event) => {
        if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }

        if (isResident && isVisitasFrecuentesBloqueadas) {
            setVisitaFormError('Función inhabilitada por adeudo pendiente.');
            mostrarAlertaVigilancia('El registro de visitas frecuentes está inhabilitado por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
            return;
        }

        if (!isResident) {
            setVisitaFormError('Solo los residentes pueden registrar visitas frecuentes.');
            return;
        }

        if (!fraccionamientoId || !currentUid) {
            setVisitaFormError('No pudimos validar tu sesión. Intenta nuevamente.');
            return;
        }

        const nombre = visitaForm.nombre.trim();
        const categoria = visitaForm.categoria.trim();
        const telefono = visitaForm.telefono ? visitaForm.telefono.trim() : '';
        const placa = visitaForm.placa.trim();
        const color = visitaForm.color.trim();
        const notas = visitaForm.notas.trim();

        if (!nombre || !telefono || !categoria) {
            setVisitaFormError('Completa nombre, teléfono y categoría para continuar.');
            return;
        }

        if (telefono.length !== 10) {
            setVisitaFormError('El teléfono debe tener exactamente 10 dígitos numéricos.');
            return;
        }

        const categoriaLabel = formatCategoriaLabel(categoria);
        const categoriaKey = categoria.toLowerCase();
        const categoriaCount = visitasPorCategoria[categoriaKey] || 0;
        
        // Determinar si estamos editando y si la categoría cambió
        const isEditing = Boolean(editingVisitaId);
        const visitaOriginal = isEditing ? visitasFrecuentes.find(v => v.id === editingVisitaId) : null;
        const mismaCategoria = visitaOriginal && visitaOriginal.categoria?.toLowerCase() === categoriaKey;

        // Validar límite: solo si es registro nuevo O si cambió a una categoría que ya está llena
        if (!mismaCategoria && categoriaCount >= MAX_VISITAS_POR_CATEGORIA) {
            setVisitaFormError(`Solo puedes registrar ${MAX_VISITAS_POR_CATEGORIA} ${categoriaLabel} por hogar.`);
            return;
        }

        // Validar identificación: obligatoria si es nuevo O si no tiene identificación previa
        if (!isEditing && !visitaArchivo) {
            setVisitaFormError('Adjunta una identificación oficial del visitante.');
            return;
        }

        // Si es edición pero no hay archivo nuevo ni identificación previa (caso raro)
        if (isEditing && !visitaArchivo && !visitaForm.identificacionUrl) {
            setVisitaFormError('Adjunta una identificación oficial del visitante.');
            return;
        }

        if (visitaArchivo && visitaArchivo.size > MAX_VISITA_FILE_SIZE) {
            setVisitaFormError('El archivo de identificación no puede superar los 5 MB.');
            return;
        }

        setVisitaSubmitting(true);
        setVisitaFormError('');

        let identificacionPath = '';
        let identificacionUrl = '';
        let identificacionName = '';
        let identificacionSize = null;
        let identificacionType = '';

        try {
            if (visitaArchivo) {
                const sanitizedName = visitaArchivo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                identificacionPath = `visitasFrecuentes/${fraccionamientoId}/${currentUid}/${Date.now()}_${sanitizedName}`;
                const fileRef = storageRef(storage, identificacionPath);
                await uploadBytes(fileRef, visitaArchivo, { contentType: visitaArchivo.type || undefined });
                identificacionUrl = await getDownloadURL(fileRef);
                identificacionName = visitaArchivo.name;
                identificacionSize = visitaArchivo.size;
                identificacionType = visitaArchivo.type || '';
            }

            // Obtener número de casa desde la colección casas
            let numeroCasaResuelto = ownerDepartamento || '';
            if (!numeroCasaResuelto && fraccionamientoId && currentUid) {
                console.log('[Visita Frecuente] 🔎 Buscando número de casa en colección casas...');
                try {
                    const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                    // Buscar por userId
                    const casasByUser = await getDocs(query(casasRef, where('userId', '==', currentUid), limit(1)));
                    if (!casasByUser.empty) {
                        const casaData = casasByUser.docs[0].data();
                        numeroCasaResuelto = String(casaData.numero || '');
                        console.log('[Visita Frecuente] ✅ Número de casa encontrado por userId:', numeroCasaResuelto);
                    } else {
                        // Si no se encuentra por userId, intentar por nombre_residente
                        if (ownerNombre) {
                            const casasByNombre = await getDocs(query(casasRef, where('nombre_residente', '==', ownerNombre), limit(1)));
                            if (!casasByNombre.empty) {
                                const casaData = casasByNombre.docs[0].data();
                                numeroCasaResuelto = String(casaData.numero || '');
                                console.log('[Visita Frecuente] ✅ Número de casa encontrado por nombre_residente:', numeroCasaResuelto);
                            }
                        }
                    }
                    if (!numeroCasaResuelto) {
                        console.log('[Visita Frecuente] ⚠️ No se encontró número de casa en colección casas');
                    }
                } catch (casaError) {
                    console.warn('[Visita Frecuente] ❌ Error al buscar número de casa:', casaError);
                }
            }

            console.log('[Visita Frecuente] 📝 Guardando visita frecuente con:', {
                nombre,
                categoria,
                ownerUid: currentUid,
                ownerNombre,
                ownerDepartamento: numeroCasaResuelto || ownerDepartamento,
            });

            const visitasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'visitasFrecuentes');
            const isEditing = Boolean(editingVisitaId);
            const targetDocRef = isEditing 
                ? doc(db, 'fraccionamientos', fraccionamientoId, 'visitasFrecuentes', editingVisitaId)
                : doc(visitasRef);

            const dataToSave = {
                fraccionamientoId,
                nombre,
                categoria,
                telefono: telefono || null,
                placa,
                color,
                notas,
                horario: visitaForm.horario,
                ownerUid: currentUid,
                ownerNombre,
                ownerDepartamento: numeroCasaResuelto || ownerDepartamento || null,
                updatedAt: serverTimestamp(),
                lastEditorRole: 'residente',
            };

            // Solo añadir createdAt si es nuevo
            if (!isEditing) {
                dataToSave.createdAt = serverTimestamp();
            }

            // Solo actualizar identificación si hay un nuevo archivo
            if (identificacionUrl) {
                dataToSave.identificacionUrl = identificacionUrl;
                dataToSave.identificacionPath = identificacionPath;
                dataToSave.identificacionName = identificacionName;
                dataToSave.identificacionSize = identificacionSize;
                dataToSave.identificacionType = identificacionType;
            }

            await setDoc(targetDocRef, dataToSave, { merge: true });

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: isEditing ? `Visita frecuente actualizada: ${nombre}` : `Nueva visita frecuente: ${nombre}`,
                    descripcion: `${ownerNombre || 'Residente'} ${isEditing ? 'actualizó' : 'registró'} a ${nombre} como ${categoriaLabel}.`,
                    categoria: 'Vigilancia',
                    tipoMovimiento: isEditing ? 'visita_actualizada' : 'visita_registrada',
                    origen: 'vigilancia',
                    metadata: {
                        visitaId: targetDocRef.id,
                        visitante: nombre,
                        categoria,
                        categoriaLabel,
                        telefono: telefono || null,
                        placa: placa || null,
                        color: color || null,
                        notas: notas || null,
                        horario: visitaForm.horario,
                        ownerUid: currentUid,
                        ownerNombre: ownerNombre || null,
                        ownerDepartamento: ownerDepartamento || null,
                        identificacionNombre: identificacionName || (isEditing ? 'Mantenida' : null),
                    },
                });
            } catch (movementError) {
                console.error('Vigilancia: no se pudo registrar movimiento de visita frecuente', movementError);
            }

            // Mostrar modal de éxito con notificación toast
            showNotification(`¡${nombre} ${isEditing ? 'actualizado' : 'registrado'} exitosamente!`, 'success');
            setVisitaResultado({
                success: true,
                mensaje: `¡Visita frecuente ${isEditing ? 'actualizada' : 'registrada'} correctamente!`,
            });
            resetVisitaForm();

            // Auto-cerrar modal después de 2 segundos
            setTimeout(() => {
                setVisitaResultado(null);
                setShowVisitaModal(false);
            }, 2000);
        } catch (error) {
            console.error('Vigilancia: error al registrar visita frecuente', error);

            // Construir mensaje de error más descriptivo
            let mensajeError = 'No se pudo registrar la visita. Intenta nuevamente.';
            if (error.code === 'permission-denied' || error.message?.includes('permission')) {
                mensajeError = 'No tienes permisos para registrar visitas. Contacta al administrador.';
            } else if (error.code === 'unauthenticated') {
                mensajeError = 'Tu sesión expiró. Por favor, inicia sesión nuevamente.';
            } else if (error.message) {
                mensajeError = `Error: ${error.message}`;
            }

            showNotification(mensajeError, 'error');
            setVisitaResultado({
                success: false,
                mensaje: mensajeError,
            });

            if (identificacionPath) {
                try {
                    await deleteObject(storageRef(storage, identificacionPath));
                } catch (storageError) {
                    console.warn('Vigilancia: no se pudo eliminar archivo temporal de identificación', storageError);
                }
            }
        } finally {
            setVisitaSubmitting(false);
        }
    }, [currentUid, db, editingVisitaId, fraccionamientoId, formatCategoriaLabel, isResident, ownerDepartamento, ownerNombre, registerMovement, resetVisitaForm, showNotification, storage, visitaArchivo, visitaForm, visitasFrecuentes, visitasPorCategoria, isVisitasFrecuentesBloqueadas, mostrarAlertaVigilancia]);

    const handleOpenDetalleVisita = useCallback((visita) => {
        if (!visita) {
            return;
        }
        setDetalleVisita(visita);
        setDetalleForm({
            nombre: visita.nombre || '',
            categoria: visita.categoria || '',
            placa: visita.placa || '',
            color: visita.color || '',
            notas: visita.notas || '',
            horario: visita.horario || INITIAL_HORARIO,
        });
        setDetalleArchivo(null);
        setDetalleEditMode(false);
        setDetalleSubmitting(false);
        setDetalleDeleting(false);
        setDetalleConfirmDelete(false);
        setDetalleError('');
    }, []);

    const handleCloseDetalleVisita = useCallback(() => {
        if (detalleSubmitting || detalleDeleting) {
            return;
        }
        setDetalleVisita(null);
        setDetalleForm(null);
        setDetalleArchivo(null);
        setDetalleEditMode(false);
        setDetalleConfirmDelete(false);
        setDetalleError('');
    }, [detalleDeleting, detalleSubmitting]);

    const handleDetalleInputChange = useCallback((event) => {
        const { name, value } = event.target;
        setDetalleForm((prev) => (prev ? { ...prev, [name]: value } : prev));
    }, []);

    const handleToggleDetalleEdit = useCallback(() => {
        if (!detalleVisita) {
            return;
        }
        setDetalleConfirmDelete(false);
        setDetalleEditMode((prev) => {
            const next = !prev;
            if (next && detalleVisita) {
                setDetalleForm({
                    nombre: detalleVisita.nombre || '',
                    categoria: detalleVisita.categoria || '',
                    placa: detalleVisita.placa || '',
                    color: detalleVisita.color || '',
                    notas: detalleVisita.notas || '',
                    horario: detalleVisita.horario || INITIAL_HORARIO,
                });
                setDetalleArchivo(null);
                setDetalleError('');
            }
            if (!next && detalleVisita) {
                setDetalleForm({
                    nombre: detalleVisita.nombre || '',
                    categoria: detalleVisita.categoria || '',
                    placa: detalleVisita.placa || '',
                    color: detalleVisita.color || '',
                    notas: detalleVisita.notas || '',
                    horario: detalleVisita.horario || INITIAL_HORARIO,
                });
                setDetalleArchivo(null);
                setDetalleError('');
            }
            return next;
        });
    }, [detalleVisita]);

    const handleDetalleArchivoChange = useCallback((event) => {
        const [file] = event.target.files || [];
        if (!file) {
            setDetalleArchivo(null);
            return;
        }
        if (file.size > MAX_VISITA_FILE_SIZE) {
            setDetalleError('El archivo de identificación no puede superar los 5 MB.');
            setDetalleArchivo(null);
            return;
        }
        setDetalleArchivo(file);
        setDetalleError('');
    }, []);

    const handleDetalleGuardar = useCallback(async () => {
        if (!isSuperAdmin || !detalleVisita || !detalleForm || !fraccionamientoId) {
            return;
        }

        const nombre = detalleForm.nombre.trim();
        const categoria = detalleForm.categoria.trim();
        const placa = detalleForm.placa.trim();
        const color = detalleForm.color.trim();
        const notas = detalleForm.notas.trim();
        const categoriaLabelAnterior = formatCategoriaLabel(detalleVisita.categoria);
        const categoriaLabelNueva = formatCategoriaLabel(categoria);

        const prevNombre = (detalleVisita.nombre || '').trim();
        const prevCategoria = (detalleVisita.categoria || '').trim();
        const prevPlaca = (detalleVisita.placa || '').trim();
        const prevColor = (detalleVisita.color || '').trim();
        const prevNotas = (detalleVisita.notas || '').trim();

        const cambios = {};
        const camposModificados = [];

        if (prevNombre !== nombre) {
            cambios.nombre = { anterior: prevNombre, nuevo: nombre };
            camposModificados.push('Nombre');
        }

        if (prevCategoria !== categoria) {
            cambios.categoria = { anterior: prevCategoria, nuevo: categoria };
            camposModificados.push('Categoría');
        }

        if (prevPlaca !== placa) {
            cambios.placa = { anterior: prevPlaca || null, nuevo: placa || null };
            camposModificados.push('Placas');
        }

        if (prevColor !== color) {
            cambios.color = { anterior: prevColor || null, nuevo: color || null };
            camposModificados.push('Color');
        }

        if (prevNotas !== notas) {
            cambios.notas = { anterior: prevNotas || null, nuevo: notas || null };
            camposModificados.push('Notas');
        }

        if (!nombre || !categoria) {
            setDetalleError('El nombre y la categoría son obligatorios.');
            return;
        }

        if (detalleArchivo && detalleArchivo.size > MAX_VISITA_FILE_SIZE) {
            setDetalleError('El archivo de identificación no puede superar los 5 MB.');
            return;
        }

        if (detalleArchivo) {
            cambios.identificacion = {
                anteriorNombre: detalleVisita.identificacionName || null,
                nuevoNombre: detalleArchivo.name,
                nuevoTipo: detalleArchivo.type || null,
            };
            camposModificados.push('Identificación');
        }

        setDetalleSubmitting(true);
        setDetalleError('');

        const visitaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'visitasFrecuentes', detalleVisita.id);

        let nuevoPath = detalleVisita.identificacionPath || '';
        let nuevoUrl = detalleVisita.identificacionUrl || '';
        let nuevoNombre = detalleVisita.identificacionName || '';
        let nuevoTamano = detalleVisita.identificacionSize || null;
        let nuevoTipo = detalleVisita.identificacionType || '';
        let uploadedPath = '';

        try {
            if (detalleArchivo) {
                const sanitizedName = detalleArchivo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                uploadedPath = `visitasFrecuentes/${fraccionamientoId}/${detalleVisita.ownerUid || 'sin-uid'}/${Date.now()}_${sanitizedName}`;
                const fileRef = storageRef(storage, uploadedPath);
                await uploadBytes(fileRef, detalleArchivo, { contentType: detalleArchivo.type || undefined });
                const downloadUrl = await getDownloadURL(fileRef);
                nuevoPath = uploadedPath;
                nuevoUrl = downloadUrl;
                nuevoNombre = detalleArchivo.name;
                nuevoTamano = detalleArchivo.size;
                nuevoTipo = detalleArchivo.type || '';
            }

            await updateDoc(visitaRef, {
                nombre,
                categoria,
                placa,
                color,
                notas,
                updatedAt: serverTimestamp(),
                identificacionUrl: nuevoUrl,
                identificacionPath: nuevoPath,
                identificacionName: nuevoNombre,
                identificacionSize: nuevoTamano,
                identificacionType: nuevoTipo,
                horario: detalleForm.horario || INITIAL_HORARIO,
                lastEditorRole: 'administrador',
            });

            try {
                const metadata = {
                    visitaId: detalleVisita.id,
                    visitante: nombre,
                    categoriaAnterior: detalleVisita.categoria || '',
                    categoriaNueva: categoria,
                    categoriaLabelAnterior,
                    categoriaLabelNueva,
                    ownerUid: detalleVisita.ownerUid || null,
                    ownerNombre: detalleVisita.ownerNombre || null,
                    ownerDepartamento: detalleVisita.ownerDepartamento || null,
                };

                if (Object.keys(cambios).length > 0) {
                    metadata.cambios = cambios;
                }

                const descripcionMovimiento = camposModificados.length > 0
                    ? `Actualización de ${camposModificados.join(', ')} para ${nombre}.`
                    : `Se revisó el registro de ${nombre} sin cambios aparentes.`;

                await registerMovement({
                    fraccionamientoId,
                    titulo: `Visita frecuente actualizada: ${nombre}`,
                    descripcion: descripcionMovimiento,
                    categoria: 'Vigilancia',
                    tipoMovimiento: 'visita_actualizada',
                    origen: 'vigilancia',
                    metadata,
                });
            } catch (movementError) {
                console.error('Vigilancia: no se pudo registrar movimiento de edición de visita frecuente', movementError);
            }

            if (detalleArchivo && detalleVisita.identificacionPath && detalleVisita.identificacionPath !== nuevoPath) {
                try {
                    await deleteObject(storageRef(storage, detalleVisita.identificacionPath));
                } catch (storageError) {
                    console.warn('Vigilancia: no se pudo limpiar archivo antiguo de identificación', storageError);
                }
            }

            setDetalleVisita((prev) => (prev && prev.id === detalleVisita.id
                ? {
                    ...prev,
                    nombre,
                    categoria,
                    placa,
                    color,
                    notas,
                    identificacionUrl: nuevoUrl,
                    identificacionPath: nuevoPath,
                    identificacionName: nuevoNombre,
                    identificacionSize: nuevoTamano,
                    identificacionType: nuevoTipo,
                }
                : prev));
            setDetalleEditMode(false);
            setDetalleArchivo(null);
            setDetalleConfirmDelete(false);
            showNotification('Visita frecuente actualizada.', 'success');
        } catch (error) {
            console.error('Vigilancia: error al actualizar visita frecuente', error);
            setDetalleError('No se pudieron guardar los cambios. Intenta nuevamente.');
            if (detalleArchivo && uploadedPath && detalleVisita.identificacionPath !== uploadedPath) {
                try {
                    await deleteObject(storageRef(storage, uploadedPath));
                } catch (storageError) {
                    console.warn('Vigilancia: no se pudo eliminar archivo temporal tras error', storageError);
                }
            }
        } finally {
            setDetalleSubmitting(false);
        }
    }, [detalleArchivo, detalleForm, detalleVisita, fraccionamientoId, formatCategoriaLabel, isSuperAdmin, registerMovement, setDetalleVisita, showNotification]);

    const handleDetalleDelete = useCallback(async () => {
        if (!isSuperAdmin || !detalleVisita || !fraccionamientoId) {
            return;
        }

        if (!detalleConfirmDelete) {
            setDetalleConfirmDelete(true);
            setDetalleError('Confirma que deseas eliminar este registro.');
            return;
        }

        const visitaSnapshot = detalleVisita;
        const categoriaLabel = formatCategoriaLabel(detalleVisita.categoria);

        setDetalleDeleting(true);
        setDetalleError('');

        const visitaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'visitasFrecuentes', detalleVisita.id);
        const pathToDelete = detalleVisita.identificacionPath || '';

        try {
            // 1. Marcar quién borra para que la Cloud Function notifique correctamente
            await updateDoc(visitaRef, { 
                lastEditorRole: 'administrador',
                lastEditorUid: currentUid,
                isDeleting: true 
            });

            // 2. Eliminar de Firestore
            await deleteDoc(visitaRef);
            
            if (pathToDelete) {
                try {
                    await deleteObject(storageRef(storage, pathToDelete));
                } catch (storageError) {
                    console.warn('Vigilancia: no se pudo eliminar archivo de identificación eliminado', storageError);
                }
            }

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Visita frecuente eliminada: ${visitaSnapshot.nombre || 'Visitante'}`,
                    descripcion: `Administrador eliminó el registro de ${visitaSnapshot.nombre || 'visitante'} (${categoriaLabel}) de la lista de ${visitaSnapshot.ownerNombre || 'un residente'}.`,
                    categoria: 'Vigilancia',
                    tipoMovimiento: 'visita_eliminada',
                    origen: 'vigilancia',
                    metadata: {
                        visitaId: visitaSnapshot.id,
                        visitante: visitaSnapshot.nombre || null,
                        categoria: visitaSnapshot.categoria || null,
                        categoriaLabel,
                        eliminadoPor: 'administrador',
                        ownerUid: visitaSnapshot.ownerUid || null,
                        ownerNombre: visitaSnapshot.ownerNombre || null,
                        ownerDepartamento: visitaSnapshot.ownerDepartamento || null,
                        placa: visitaSnapshot.placa || null,
                        color: visitaSnapshot.color || null,
                    },
                });
            } catch (movementError) {
                console.error('Vigilancia: no se pudo registrar movimiento de eliminación de visita frecuente', movementError);
            }

            showNotification('Visita frecuente eliminada.', 'success');
            setDetalleVisita(null);
            setDetalleForm(null);
            setDetalleArchivo(null);
            setDetalleEditMode(false);
        } catch (error) {
            console.error('Vigilancia: error al eliminar visita frecuente', error);
            setDetalleError('No se pudo eliminar la visita. Intenta nuevamente.');
        } finally {
            setDetalleDeleting(false);
            setDetalleConfirmDelete(false);
        }
    }, [detalleConfirmDelete, detalleVisita, fraccionamientoId, formatCategoriaLabel, isSuperAdmin, registerMovement, showNotification]);

    const formatProgramacionFecha = (programacion) => {
        if (!programacion) {
            return 'Fecha por definir';
        }

        const formatDate = (date) => date.toLocaleDateString('es-MX', {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
        });

        const { tipoSolicitud, fechaInicio, fechaFin, horaProgramada, horarioLlegada } = programacion;

        // Determinar qué campo de horario usar según el tipo
        const horario = tipoSolicitud === 'mudanza' ? horaProgramada : horarioLlegada;

        if (fechaInicio && fechaFin) {
            const sameDay = fechaInicio.toDateString() === fechaFin.toDateString();
            if (sameDay) {
                const base = formatDate(fechaInicio);
                if (horario) {
                    return `${base} · ${horario}`;
                }
                return base;
            }
            return `${formatDate(fechaInicio)} - ${formatDate(fechaFin)}`;
        }

        if (fechaInicio) {
            const base = formatDate(fechaInicio);
            if (horario) {
                return `${base} · ${horario}`;
            }
            return base;
        }

        if (fechaFin) {
            return formatDate(fechaFin);
        }

        return 'Fecha por definir';
    };

    const formatProgramacionDuracion = (programacion) => {
        if (!programacion) {
            return '';
        }
        if (programacion.tipoSolicitud === 'remodelacion' && programacion.duracionDias) {
            const dias = Number(programacion.duracionDias);
            if (Number.isInteger(dias) && dias > 0) {
                return dias === 1 ? 'Duración: 1 día' : `Duración: ${dias} días`;
            }
        }
        return '';
    };

    const formatResidentPackageDate = (fecha) => {
        if (!(fecha instanceof Date)) {
            return 'Fecha no disponible';
        }
        const dateLabel = fecha.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
        const timeLabel = fecha.toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
        });
        return `${dateLabel} · ${timeLabel}`;
    };

    const getPackageStatusLabel = (estado) => {
        const normalized = String(estado || '').toLowerCase();
        if (normalized === 'entregado') {
            return 'Entregado';
        }
        if (normalized === 'pendiente') {
            return 'Pendiente de recoger';
        }
        if (!normalized) {
            return 'En seguimiento';
        }
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const handleMarkPackageDelivered = async (registro) => {
        if (!registro?.id) {
            console.error('📦 [Paquetería] El registro no tiene ID válido');
            return;
        }

        console.log('📦 [Paquetería] Invitando a captura de evidencia para:', registro.id);
        setPaqueteriaUpdatingId(registro.id); // Para deshabilitar el botón en la lista mientras se procesa
        setPaqueteParaEntregar(registro);
        openModalWithHistory(setShowCapturaEvidencia);
    };

    const handleCancelarEvidencia = () => {
        console.log('📦 [Paquetería] Entrega cancelada por el usuario (Reverse)');
        setPaqueteParaEntregar(null);
        setPaqueteriaUpdatingId(null);
        setShowCapturaEvidencia(false);
    };

    const handleCompletarEvidencia = async (imagenEvidencia) => {
        if (!paqueteParaEntregar || !imagenEvidencia) return;

        try {
            if (!navigator.onLine || currentUser?.isOffline) {
                console.log('📡 [Offline] Guardando evidencia de entrega localmente...');
                let base64Data = null;
                
                if (imagenEvidencia?.blob) {
                    base64Data = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(imagenEvidencia.blob);
                    });
                }
                
                pushToSyncQueue('markPackageDelivered', {
                    fraccionamientoId: fraccionamientoId || 'generic',
                    paqueteId: paqueteParaEntregar.id,
                    departamento: paqueteParaEntregar.depto || paqueteParaEntregar.departamento,
                    empresa: paqueteParaEntregar.empresa,
                    imageBase64: base64Data,
                    entregadoPor: {
                        uid: currentUser?.uid || currentUid,
                        nombre: userData?.nombreCompleto || userData?.displayName || 'Guardia'
                    }
                });

                const pathId = fraccionamientoId || 'generic';
                const packageRef = doc(db, 'fraccionamientos', pathId, 'paqueteria', paqueteParaEntregar.id);
                // Non-blocking update local
                updateDoc(packageRef, {
                    status: 'entregado',
                    estado: 'entregado',
                    fechaEntrega: new Date(),
                    entregadoEn: new Date(),
                    offline: true,
                    entregadoPor: {
                        uid: currentUser?.uid || currentUid,
                        nombre: userData?.nombreCompleto || userData?.displayName || 'Guardia'
                    }
                }).catch(e => console.warn('Sync pended', e));

                showNotification('Entrega guardada en cola. Se sincronizará al conectar internet.', 'success');
                
                setPaqueteriaList(prev => prev.filter(p => p.id !== paqueteParaEntregar.id));
                setShowCapturaEvidencia(false);
                setPaqueteParaEntregar(null);
                setPaqueteriaUpdatingId(null);
                
                setSuccessModalData({
                    title: 'Paquete Entregado',
                    message: `La entrega para el Depto ${paqueteParaEntregar.depto || paqueteParaEntregar.departamento} ha sido guardada offline correctamente.`
                });
                openModalWithHistory(setShowSuccessModal);
                return;
            }

            console.log('📦 [Paquetería] Guardando entrega con evidencia...');
            
            let evidenciaUrl = '';
            if (imagenEvidencia.blob) {
                const pathId = fraccionamientoId || 'generic';
                const fileName = `paqueteria/${pathId}/${paqueteParaEntregar.id}_entrega_${Date.now()}.jpg`;
                const storageReference = storageRef(storage, fileName);
                const snapshot = await uploadBytes(storageReference, imagenEvidencia.blob);
                evidenciaUrl = await getDownloadURL(snapshot.ref);
            }

            const pathId = fraccionamientoId || 'generic';
            const packageRef = doc(db, 'fraccionamientos', pathId, 'paqueteria', paqueteParaEntregar.id);
            await updateDoc(packageRef, {
                status: 'entregado',
                estado: 'entregado',
                fechaEntrega: serverTimestamp(),
                entregadoEn: serverTimestamp(),
                evidenciaEntrega: evidenciaUrl,
                entregadoPor: {
                    uid: currentUser?.uid || currentUid,
                    nombre: userData?.nombreCompleto || userData?.displayName || 'Guardia'
                }
            });

            try {
                await registerMovement(
                    fraccionamientoId,
                    'Entrega de Paquete',
                    `Paquete de ${paqueteParaEntregar.empresa || 'Empresa'} entregado a Depto ${paqueteParaEntregar.depto || paqueteParaEntregar.departamento}.`,
                    currentUid,
                    userData?.nombreCompleto || 'Guardia'
                );
            } catch (movErr) {
                console.warn('No se pudo registrar movimiento:', movErr);
            }

            console.log('📦 [Paquetería] Paquete marcado como entregado con éxito');
            
            setShowCapturaEvidencia(false);
            setPaqueteParaEntregar(null);
            setPaqueteriaUpdatingId(null);

            setSuccessModalData({
                title: 'Paquete Entregado',
                message: `El paquete para el Depto ${paqueteParaEntregar.depto || paqueteParaEntregar.departamento} ha sido marcado como entregado correctamente.`
            });
            openModalWithHistory(setShowSuccessModal);

        } catch (error) {
            console.error('Error al marcar entrega:', error);
            showNotification('Error al procesar la entrega del paquete. Intenta de nuevo.', 'error');
            setPaqueteriaUpdatingId(null);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes || Number.isNaN(Number(bytes))) {
            return '';
        }
        const kiloBytes = Number(bytes) / 1024;
        if (kiloBytes < 1024) {
            return `${kiloBytes.toFixed(1)} KB`;
        }
        const megaBytes = kiloBytes / 1024;
        return `${megaBytes.toFixed(1)} MB`;
    };

    const escapeCsvValue = (value) => {
        const raw = value ?? '';
        const str = Array.isArray(raw) ? raw.join(', ') : String(raw);
        if (/[";,\n]/.test(str)) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };



    const gridClassName = isResident ? 'vigilancia-grid vigilancia-grid-residente' : 'vigilancia-grid';
    const qrFormDisabled = qrSubmitting || Boolean(qrResult);

    const location = useLocation();
    const canGoBackRef = useRef(false);

    // Estados para secciones colapsables

    // Calcular métricas
    const metrics = useMemo(() => {
        const ahora = new Date();
        const inicioDia = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
        const inicioSemana = new Date(ahora);
        inicioSemana.setDate(ahora.getDate() - ahora.getDay()); // Lunes de esta semana
        inicioSemana.setHours(0, 0, 0, 0);

        // Visitas dentro del condominio actualmente
        const visitasDentro = guestAccesses.filter(acceso => {
            const estado = (acceso.estado || '').toString().toLowerCase();
            return estado === 'dentro' || estado === 'validado';
        }).length;

        // Vehículos y proveedores programados (día y semana)
        const programacionesHoy = programacionesAcceso.filter(prog => {
            if (!prog.fechaInicio) return false;
            const fechaInicio = prog.fechaInicio instanceof Date ? prog.fechaInicio : new Date(prog.fechaInicio);
            const fechaFin = prog.fechaFin ? (prog.fechaFin instanceof Date ? prog.fechaFin : new Date(prog.fechaFin)) : fechaInicio;
            const hoy = ahora.getTime();
            const inicio = fechaInicio.getTime();
            const fin = fechaFin.getTime();
            return hoy >= inicio && hoy <= fin;
        }).length;

        const programacionesSemana = programacionesAcceso.filter(prog => {
            if (!prog.fechaInicio) return false;
            const fechaInicio = prog.fechaInicio instanceof Date ? prog.fechaInicio : new Date(prog.fechaInicio);
            const fechaFin = prog.fechaFin ? (prog.fechaFin instanceof Date ? prog.fechaFin : new Date(prog.fechaFin)) : fechaInicio;
            const inicioSem = inicioSemana.getTime();
            const finSem = new Date(inicioSemana).setDate(inicioSemana.getDate() + 7);
            const inicio = fechaInicio.getTime();
            const fin = fechaFin.getTime();
            return (inicio >= inicioSem && inicio < finSem) || (fin >= inicioSem && fin < finSem) || (inicio <= inicioSem && fin >= finSem);
        }).length;

        // Paquetería registrada (hoy)
        const paqueteriaHoy = paqueteriaList.filter(paq => {
            if (!paq.fechaRecepcion) return false;
            const fecha = paq.fechaRecepcion instanceof Date ? paq.fechaRecepcion : new Date(paq.fechaRecepcion);
            return fecha >= inicioDia;
        }).length;

        return {
            visitasDentro,
            programacionesHoy,
            programacionesSemana,
            paqueteriaHoy,
        };
    }, [guestAccesses, programacionesAcceso, paqueteriaList]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            // Si cambia a desktop, resetear la sección seleccionada
            if (window.innerWidth > 768) {
                setSelectedSection(null);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Manejar botón de regreso del móvil (navegador y app nativa)
    useEffect(() => {
        if (!isMobile) return;

        const isNative = Capacitor.isNativePlatform();

        // Agregar estado al history cuando se selecciona una sección
        if (selectedSection) {
            const currentState = window.history.state;
            if (!currentState || !currentState.section || currentState.section !== selectedSection) {
                window.history.pushState({ section: selectedSection }, '', location.pathname);
            }
        }

        // Manejar botón de retroceso del navegador (popstate)
        const handlePopState = (event) => {
            if (selectedSection) {
                // Si hay una sección seleccionada, volver a la vista principal
                event.preventDefault();
                setSelectedSection(null);
                // Agregar un nuevo estado al history para mantener la navegación
                window.history.pushState(null, '', location.pathname);
            }
            // Si no hay sección seleccionada, permitir el comportamiento normal del navegador
        };

        // Manejar botón de retroceso nativo de la app (Capacitor)
        let backButtonListener = null;
        if (isNative) {
            backButtonListener = App.addListener('backButton', ({ canGoBack }) => {
                if (selectedSection) {
                    // Si hay una sección seleccionada, volver a la vista principal
                    setSelectedSection(null);
                } else if (canGoBack) {
                    // Si el navegador puede ir hacia atrás, hacerlo
                    window.history.back();
                } else {
                    // Si no puede ir hacia atrás, salir de la app
                    App.exitApp();
                }
            });
        }

        // Agregar listener para navegador (solo si no es app nativa)
        if (!isNative) {
            window.addEventListener('popstate', handlePopState);
        }

        return () => {
            if (!isNative) {
                window.removeEventListener('popstate', handlePopState);
            }
            if (backButtonListener) {
                backButtonListener.remove();
            }
        };
    }, [isMobile, selectedSection, location.pathname]);

    // Referencia para evitar ejecuciones múltiples del efecto
    const navigationHandledRef = useRef(false);
    const lastSelectedSectionRef = useRef(null);

    // Referencia estable para handleOpenQrValidationModal
    const handleOpenQrValidationModalRef = useRef(handleOpenQrValidationModal);

    // Actualizar la referencia cuando cambie la función
    useEffect(() => {
        handleOpenQrValidationModalRef.current = handleOpenQrValidationModal;
    }, [handleOpenQrValidationModal]);

    // Manejar navegación cuando se selecciona una sección en móvil
    useEffect(() => {
        if (!isMobile || !selectedSection) {
            navigationHandledRef.current = false;
            lastSelectedSectionRef.current = null;
            return;
        }

        // Evitar ejecutar múltiples veces para la misma sección
        if (lastSelectedSectionRef.current === selectedSection && navigationHandledRef.current) {
            return;
        }

        // Marcar que estamos manejando esta navegación
        navigationHandledRef.current = true;
        lastSelectedSectionRef.current = selectedSection;

        // Solo manejar la navegación para secciones que requieren acción inmediata
        // Las secciones que solo muestran contenido se manejan en el render
        console.log('[Vigilancia] Navegando a sección:', selectedSection);

        // Usar if-else explícito para mayor control y evitar problemas con switch
        // IMPORTANTE: Solo ejecutar la acción correspondiente a cada sección
        if (selectedSection === 'control-visitas') {
            // Abrir modal de validar QR SOLO para esta sección
            console.log('[Vigilancia] Abriendo modal de validar QR para control-visitas');
            if (handleOpenQrValidationModalRef.current) {
                handleOpenQrValidationModalRef.current();
            }
        } else if (selectedSection === 'registrar-entrada-manual') {
            // Abrir modal de registrar entrada manual
            console.log('[Vigilancia] Abriendo modal de registrar entrada manual');
            // Abrir el modal - NO resetear selectedSection aquí
            // Se reseteará cuando el modal se cierre (en handleCloseManualAccessModal)
            handleOpenManualAccessModal();
        } else if (selectedSection === 'visitas-frecuentes') {
            // Abrir automáticamente la sección de visitas frecuentes
            console.log('[Vigilancia] Abriendo visitas frecuentes');
            setShowVisitasFrecuentes(true);
        } else if (selectedSection === 'paqueteria') {
            // Abrir modal para registrar paquete
            console.log('[Vigilancia] Abriendo modal de registrar paquete');
            openModalWithHistory(setShowPaqueteriaFormModal);
        } else if (selectedSection === 'visitantes-sitio') {
            // Solo mostrar la sección (se maneja en el render)
            console.log('[Vigilancia] Mostrando visitantes en sitio - no acción requerida');
            // No hacer nada, solo se muestra en el render
        } else if (selectedSection === 'vehiculos-proveedores') {
            // Solo mostrar la sección (se maneja en el render)
            console.log('[Vigilancia] Mostrando vehículos y proveedores - no acción requerida');
            // No hacer nada, solo se muestra en el render
        } else {
            console.log('[Vigilancia] Sección no reconocida:', selectedSection);
        }
        // 'visitantes-sitio' y 'vehiculos-proveedores' se manejan en el render
        // mostrando/ocultando las secciones correspondientes

        // Resetear el flag después de un breve delay para permitir cambios futuros
        setTimeout(() => {
            navigationHandledRef.current = false;
        }, 100);
    }, [isMobile, selectedSection]);

    // Secciones de navegación móvil
    const vigilanciaSections = [
        {
            id: 'control-visitas',
            title: 'Control de Visitas',
            description: 'Genera QR y valida accesos',
            icon: <FaQrcode />,
            color: '#667eea',
            bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        },
        {
            id: 'registrar-entrada-manual',
            title: '+ Registrar Entrada Manual',
            description: 'Registra visitantes manualmente',
            icon: <FaPlus />,
            color: '#3b82f6',
            bgColor: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        },
        {
            id: 'visitantes-sitio',
            title: 'Visitantes en Sitio',
            description: 'Monitorea entradas y salidas',
            icon: <FaUsers />,
            color: '#10b981',
            bgColor: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        },
        {
            id: 'paqueteria',
            title: 'Control de Paquetería',
            description: 'Registra y gestiona paquetes',
            icon: <FaBoxOpen />,
            color: '#f59e0b',
            bgColor: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        },
        {
            id: 'vehiculos-proveedores',
            title: 'Vehículos y Proveedores',
            description: 'Mudanzas y proveedores programados',
            icon: <FaTruck />,
            color: '#8b5cf6',
            bgColor: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        },
        {
            id: 'visitas-frecuentes',
            title: 'Visitas Frecuentes',
            description: 'Gestiona visitas recurrentes',
            icon: <FaCalendarAlt />,
            color: '#ec4899',
            bgColor: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
        },
    ];

    // Si es móvil y no hay sección seleccionada, mostrar menú de navegación
    if (isMobile && selectedSection === null && !isResident) {
        return (
            <div className="vigilancia-page">
                <header className="vigilancia-header">
                    <div className="vigilancia-header-content">
                        <div className="vigilancia-header-text">
                            <h1>Vigilancia y Seguridad</h1>
                            <p>Control de acceso y seguridad del condominio</p>
                        </div>
                    </div>
                </header>

                <div className="vigilancia-mobile-menu">
                    {vigilanciaSections.map((section) => (
                        <button
                            key={section.id}
                            className="vigilancia-section-card"
                            onClick={() => setSelectedSection(section.id)}
                            style={{ '--section-color': section.color, '--section-bg': section.bgColor }}
                        >
                            <div className="section-card-icon" style={{ background: section.bgColor }}>
                                {section.icon}
                            </div>
                            <div className="section-card-content">
                                <h3>{section.title}</h3>
                                <p>{section.description}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="vigilancia-page">


            {/* Banner de Modo de Emergencia Activo (Si aplica y el módulo de morosos está activo) */}
            {isRfidMorososActivo && middlewareState?.modoOperacion && middlewareState?.modoOperacion !== 'normal' && (
                <div className={`vigilancia-banner-emergencia ${middlewareState.modoOperacion === 'cerrado_indefinido' ? 'lockdown' : 'abierto'}`}>
                    <div className="banner-emergencia-info">
                        <div className="banner-emergencia-icon-wrapper">
                            <FaExclamationTriangle className="banner-emergencia-icon" />
                        </div>
                        <div className="banner-emergencia-textos">
                            <div className="banner-emergencia-titulo-row">
                                <strong className="banner-emergencia-titulo">
                                    {middlewareState.modoOperacion === 'cerrado_indefinido'
                                        ? '🔒 MODO LOCKDOWN ACTIVO (CIERRE TOTAL INDEFINIDO)'
                                        : '🔓 APERTURA TOTAL INDEFINIDA ACTIVA'}
                                </strong>
                                <span className="banner-emergencia-pill">EN VIVO</span>
                            </div>
                            <p className="banner-emergencia-descripcion">
                                {middlewareState.failsafeMinutos > 0 && middlewareState.expiraEn ? (
                                    <>
                                        Restauración automática programada ({middlewareState.failsafeMinutos} min).{' '}
                                        {segundosRestantesEmergencia !== null && (
                                            <span className="banner-emergencia-countdown" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                                                padding: '2px 8px',
                                                borderRadius: '6px',
                                                fontWeight: '800',
                                                fontFamily: 'monospace',
                                                fontSize: '0.95rem',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                margin: '0 6px',
                                                border: '1px solid rgba(0, 0, 0, 0.1)'
                                            }}>
                                                ⏱️ {formatearTiempoRestante(segundosRestantesEmergencia)}
                                            </span>
                                        )}
                                        <span className="banner-emergencia-hora">
                                            Expira: {new Date(middlewareState.expiraEn).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                        </span>
                                    </>
                                ) : (
                                    'Modo continuo activo sin límite de tiempo (Manual).'
                                )}
                            </p>
                        </div>
                    </div>
                    {isSuperAdmin && (
                        <button
                            type="button"
                            className="btn-restablecer-banner"
                            onClick={handleRestablecerModoNormal}
                        >
                            <FaSync className="btn-restablecer-icon" /> Restablecer Modo Normal
                        </button>
                    )}
                </div>
            )}

            <header className="vigilancia-header">
                {/* FILA SUPERIOR: Título a la izquierda + Botón Modos de Emergencia a la derecha */}
                <div className="vigilancia-header-top-row">
                    <div className="vigilancia-header-content">
                        <div className="vigilancia-header-text">
                            <h1>Control de Acceso</h1>
                            <p>Gestiona visitantes, proveedores y vehículos programados</p>
                        </div>
                    </div>

                    {/* Desplegable de Modos de Emergencia Indefinida (Solo Administradores y si el módulo está habilitado) */}
                    {isRfidMorososActivo && isSuperAdmin && (
                        <div className="emergencia-dropdown-container">
                            <button
                                type="button"
                                className="btn-emergencia-dropdown-trigger"
                                onClick={() => setMostrarMenuEmergencia(prev => !prev)}
                                title="Modos de Emergencia (Abrir o Cerrar Todo Indefinido)"
                            >
                                <FaExclamationTriangle />
                                <span>Modos Emergencia</span>
                                <FaChevronDown className={mostrarMenuEmergencia ? 'rotado' : ''} />
                            </button>

                            {mostrarMenuEmergencia && (
                                <div className="emergencia-dropdown-menu">
                                    <button
                                        type="button"
                                        className="emergencia-menu-item item-abrir"
                                        onClick={() => handleModoEmergencia('abrir_todo_indefinido')}
                                    >
                                        <FaUnlock className="menu-icon" />
                                        <div>
                                            <strong>Abrir Todo Indefinido</strong>
                                            <small>Tránsito libre en todas las puertas (Failsafe)</small>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        className="emergencia-menu-item item-cerrar"
                                        onClick={() => handleModoEmergencia('cerrar_todo_indefinido')}
                                    >
                                        <FaLock className="menu-icon" />
                                        <div>
                                            <strong>Cerrar Todo (Lockdown)</strong>
                                            <small>Bloqueo total inmediato de accesos (Failsafe)</small>
                                        </div>
                                    </button>
                                    <div className="emergencia-menu-divider" />
                                    <button
                                        type="button"
                                        className="emergencia-menu-item item-restablecer"
                                        onClick={handleRestablecerModoNormal}
                                    >
                                        <FaSync className="menu-icon" />
                                        <div>
                                            <strong>Restablecer Modo Normal</strong>
                                            <small>Restaurar RFID ordinario en paneles</small>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* FILA INFERIOR: Botones de Puertas Físicas Autorizadas */}
                {puertasAutorizadas.length > 0 && (
                    <div className="vigilancia-header-puertas-row">
                        {puertasAutorizadas.map((puerta) => {
                            const puertaKey = puerta.keyId || puerta.id;
                            const isAccionando = accionandoPuerta === puertaKey;
                            const estadoConn = obtenerEstadoConexionPuerta(puerta);
                            const esDesconectado = !estadoConn.online;
                            const colorTema = esDesconectado ? '#94a3b8' : (puerta.colorTema || '#2563eb');
                            const bgTema = esDesconectado ? '#f1f5f9' : (puerta.bgTema || '#eff6ff');
                            return (
                                <button
                                    key={puertaKey}
                                    type="button"
                                    className={`btn-emergencia-puerta ${isAccionando ? 'accionando' : ''} ${esDesconectado ? 'desconectado' : ''}`}
                                    onClick={() => handleAccionarAperturaPuerta(puerta)}
                                    disabled={isAccionando}
                                    title={esDesconectado ? `Panel ${puerta.panelNombre} Desconectado (${estadoConn.razon})` : `Abrir ${puerta.nombre} (6 seg)`}
                                    style={{
                                        borderLeft: `4px solid ${esDesconectado ? '#ef4444' : colorTema}`,
                                        opacity: esDesconectado ? 0.7 : 1
                                    }}
                                >
                                    <div className="btn-emergencia-icono" style={{ color: esDesconectado ? '#ef4444' : colorTema, background: bgTema }}>
                                        {esDesconectado ? <FaUnlink /> : <FaDoorOpen />}
                                    </div>
                                    <div className="btn-emergencia-texto">
                                        <span className="btn-emergencia-nombre" title={puerta.nombre}>
                                            {isAccionando ? 'Abriendo (6s)...' : puerta.nombre}
                                        </span>
                                        <span className="btn-emergencia-panel">
                                            <span className="btn-emergencia-panel-dot" style={{ backgroundColor: esDesconectado ? '#ef4444' : colorTema }} />
                                            <span className="btn-emergencia-panel-name">
                                                {puerta.panelNombre}
                                            </span>
                                            {esDesconectado && (
                                                <span className="badge-desconectado-header">🔴 Fuera de línea</span>
                                            )}
                                            {puerta.accesoGeneral && (
                                                <span className="badge-acceso-general-header"> ⭐ General</span>
                                            )}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </header>

            {/* Estado de Transición Zen para evitar pantalla blanca en móvil (Solo en secciones de registro) */}
            {isMobile && selectedSection && 
             ['registrar-entrada-manual', 'paqueteria', 'control-visitas'].includes(selectedSection) && 
             !showManualAccessModal && !showPaqueteriaFormModal && !showCapturaImagenesManual && (
                renderZenTransitionSkeleton()
            )}

            {/* Barra de acciones para administradores / caseta */}
            {!isResident && (!isMobile || selectedSection === null) && (
                <div className="vigilancia-actions-bar">
                    <button
                        type="button"
                        className="btn-vigilancia-action btn-vigilancia-primary"
                        onClick={handleOpenManualAccessModal}
                    >
                        <FaPlus /> Registrar Entrada Manual
                    </button>
                    {(isSuperAdmin || isCaseta) && (modulosLoading || (modulos.vigilancia === true && modulos.validar_qr === true)) && (
                        <button
                            type="button"
                            className="btn-vigilancia-action btn-vigilancia-secondary"
                            onClick={handleOpenQrValidationModal}
                        >
                            <FaCamera /> Validar QR de visita
                        </button>
                    )}
                    {isSuperAdmin && (modulosLoading || (modulos?.vigilancia === true && modulos?.generar_qr_invitado === true)) && (
                        <button
                            type="button"
                            className="btn-vigilancia-action btn-vigilancia-secondary"
                            onClick={handleGenerarQR}
                        >
                            <FaQrcode /> Generar QR para invitado
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn-vigilancia-action btn-vigilancia-ghost"
                        onClick={handleOpenVisitHistoryModal}
                    >
                        <FaHistory /> Historial y exportación
                    </button>

                    {isSuperAdmin && (modulosLoading || (modulos?.vigilancia === true && modulos?.generar_qr_invitado === true)) && (
                        <button
                            type="button"
                            className="btn-vigilancia-action btn-vigilancia-primary"
                            onClick={handleOpenMyQrModal}
                            style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                        >
                            <FaQrcode /> Mis Invitaciones QR
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn-vigilancia-action btn-vigilancia-ghost btn-refresh-only-icon"
                        onClick={handleRefreshGuestAccesses}
                        disabled={guestAccessesLoading || guestAccessRefreshing}
                        title="Actualizar"
                    >
                        <FaHistory />
                    </button>
                </div>
            )}

            {/* Barra de acciones para residentes */}
            {isResident && (modulosLoading || (modulos.vigilancia === true && modulos.generar_qr_invitado === true)) && (
                <div className="vigilancia-actions-bar">
                    <button
                        type="button"
                        className={`btn-vigilancia-action btn-vigilancia-primary ${isQrBloqueado ? 'btn-moroso-bloqueado' : ''}`}
                        onClick={handleGenerarQR}
                        title={isQrBloqueado ? 'Función inhabilitada por adeudo pendiente' : 'Generar QR para invitado'}
                        style={isQrBloqueado ? { opacity: 0.65, filter: 'grayscale(0.6)', cursor: 'not-allowed' } : {}}
                    >
                        {isQrBloqueado ? <FaLock /> : <FaQrcode />} Generar QR para invitado
                    </button>

                    <button
                        type="button"
                        className={`btn-vigilancia-action btn-vigilancia-primary ${isMisInvitacionesBloqueadas ? 'btn-moroso-bloqueado' : ''}`}
                        onClick={handleOpenMyQrModal}
                        title={isMisInvitacionesBloqueadas ? 'Función inhabilitada por adeudo pendiente' : 'Mis Invitaciones QR'}
                        style={isMisInvitacionesBloqueadas ? { opacity: 0.65, filter: 'grayscale(0.6)', cursor: 'not-allowed', backgroundColor: '#64748b', borderColor: '#64748b' } : { backgroundColor: '#10b981', borderColor: '#10b981' }}
                    >
                        {isMisInvitacionesBloqueadas ? <FaLock /> : <FaQrcode />} Mis Invitaciones QR
                    </button>
                </div>
            )}

            <div className="vigilancia-sections-container">
                {/* Sección: Visitantes Actuales */}
                {!isResident && (!isMobile || selectedSection === 'visitantes-sitio' || selectedSection === null) && (
                    <div className="vigilancia-collapsible-section">
                        <button
                            type="button"
                            className="vigilancia-collapsible-header"
                            onClick={() => {
                                const newState = !showVisitasActuales;
                                setShowVisitasActuales(newState);
                                if (newState && guestAccessesStatusFilter !== 'dentro') {
                                    setGuestAccessesStatusFilter('dentro');
                                }
                            }}
                        >
                            <div className="vigilancia-collapsible-header-content">
                                <div className="vigilancia-collapsible-icon" style={{ backgroundColor: '#eff6ff' }}>
                                    <FaUsers style={{ color: '#3b82f6' }} />
                                </div>
                                <div className="vigilancia-collapsible-title-wrapper">
                                    <h3>Visitantes Actuales</h3>
                                    <p>Personas dentro del fraccionamiento</p>
                                </div>
                            </div>
                            <div className="vigilancia-collapsible-toggle">
                                {showVisitasActuales ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </button>

                        {showVisitasActuales && (
                            <div className="vigilancia-collapsible-body">
                                <div className="accesos-filters">
                                    <div className="accesos-filter-select">
                                        <label htmlFor="accesos-status-filter">Filtrar por estado</label>
                                        <select
                                            id="accesos-status-filter"
                                            value={guestAccessesStatusFilter}
                                            onChange={handleGuestAccessStatusFilterChange}
                                        >
                                            {ACCESS_STATUS_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="accesos-search">
                                        <FaSearch aria-hidden="true" />
                                        <input
                                            type="text"
                                            placeholder="Busca por visitante, departamento, código o notas"
                                            value={guestAccessesSearch}
                                            onChange={handleGuestAccessSearchChange}
                                        />
                                        {guestAccessesSearch && (
                                            <button
                                                type="button"
                                                className="accesos-search-clear"
                                                onClick={handleGuestAccessClearSearch}
                                                style={{ position: 'absolute', right: '15px', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {guestAccessesLoading ? (
                                    <div className="accesos-hint">Cargando accesos recientes…</div>
                                ) : guestAccessesError ? (
                                    <div className="accesos-hint accesos-hint-error">
                                        <span>{guestAccessesError}</span>
                                        <button type="button" onClick={handleRefreshGuestAccesses}>Reintentar</button>
                                    </div>
                                ) : guestAccessesVisible.length === 0 ? (
                                    <div className="accesos-hint">No hay accesos con el filtro actual.</div>
                                ) : (
                                    <div className="table-container">
                                        {isMobile ? (
                                            <div className="accesos-mobile-cards">
                                                {paginatedGuestAccesses.map((access) => {
                                                    const statusClass = access.estadoAcceso === 'dentro' ? 'status-dentro' : 'status-fuera';
                                                    const timestampLabel = access.estadoAcceso === 'fuera'
                                                        ? formatAccessTimestamp(access.salidaRegistradaIso, access.salidaRegistradaLabel)
                                                        : formatAccessTimestamp(access.entradaRegistradaIso, access.entradaRegistradaLabel);

                                                    return (
                                                        <div key={access.id} className="acceso-mobile-card">
                                                            <div className="acceso-mobile-card-header">
                                                                <div className="acceso-visitor-info">
                                                                    <div className="acceso-name">{access.visitante || 'Visitante'}</div>
                                                                    {access.origen && (
                                                                        <div className="acceso-origin">
                                                                            {access.origen === 'manual' ? 'MANUAL' : (access.origen === 'qr' ? 'CÓDIGO QR' : access.origen.toUpperCase())}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <span className={`status-pill ${getCategoriaBadgeClass(access.categoria)}`}>
                                                                    {access.categoria ? formatCategoriaLabel(access.categoria) : 'Sin categoría'}
                                                                </span>
                                                            </div>
                                                            <div className="acceso-mobile-card-body">
                                                                <div className="acceso-detail-row">
                                                                    <span className="acceso-detail-label">Ubicación:</span>
                                                                    <span className="acceso-detail-value depto">{access.departamento || '—'}</span>
                                                                </div>
                                                                <div className="acceso-detail-row">
                                                                    <span className="acceso-detail-label">Registrado:</span>
                                                                    <span className="acceso-detail-value">{formatRegistradoPor(access)}</span>
                                                                </div>
                                                                <div className="acceso-detail-row">
                                                                    <span className="acceso-detail-label">Fecha/Hora:</span>
                                                                    <span className="acceso-detail-value">{timestampLabel}</span>
                                                                </div>
                                                            </div>
                                                            <div className="acceso-mobile-card-footer">
                                                                <span className={`status-pill ${statusClass}`}>
                                                                    {access.estadoAcceso === 'dentro' ? 'ENTRADA' : 'SALIDA'}
                                                                </span>
                                                                {access.estadoAcceso === 'dentro' && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn-vigilancia-action btn-marcar-salida"
                                                                        onClick={() => handleRegisterGuestExit(access)}
                                                                    >
                                                                        Marcar salida
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Visitante</th>
                                                        <th>Categoría</th>
                                                        <th>Departamento</th>
                                                        <th>Registrado por</th>
                                                        <th>Fecha y hora</th>
                                                        <th>Estado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedGuestAccesses.map((access) => {
                                                        const statusClass = access.estadoAcceso === 'dentro' ? 'status-dentro' : 'status-fuera';
                                                        const timestampLabel = access.estadoAcceso === 'fuera'
                                                            ? formatAccessTimestamp(access.salidaRegistradaIso, access.salidaRegistradaLabel)
                                                            : formatAccessTimestamp(access.entradaRegistradaIso, access.entradaRegistradaLabel);

                                                        return (
                                                            <tr key={access.id}>
                                                                <td>
                                                                    <div style={{ fontWeight: '800', color: '#0f172a' }}>{access.visitante || 'Visitante'}</div>
                                                                    {access.origen && (
                                                                        <div style={{ fontSize: '0.70rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginTop: '2px' }}>
                                                                            {access.origen === 'manual' ? 'MANUAL' : (access.origen === 'qr' ? 'CÓDIGO QR' : access.origen.toUpperCase())}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className={`status-pill ${getCategoriaBadgeClass(access.categoria)}`}>
                                                                        {access.categoria ? formatCategoriaLabel(access.categoria) : 'Sin categoría'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ fontWeight: '700' }}>{access.departamento || '—'}</td>
                                                                <td>
                                                                    <div style={{ fontSize: '0.85rem', color: '#334155', fontWeight: '500' }}>
                                                                        {formatRegistradoPor(access)}
                                                                    </div>
                                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                                        {access.numeroVisitantes || 1} {(access.numeroVisitantes || 1) === 1 ? 'persona' : 'personas'}
                                                                    </div>
                                                                </td>
                                                                <td>{timestampLabel}</td>
                                                                <td>
                                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                        <span className={`status-pill ${statusClass}`}>
                                                                            {access.estadoAcceso === 'dentro' ? 'ENTRADA' : 'SALIDA'}
                                                                        </span>
                                                                        {access.estadoAcceso === 'dentro' && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn-vigilancia-action btn-marcar-salida"
                                                                                onClick={() => handleRegisterGuestExit(access)}
                                                                                style={{ padding: '6px 12px', fontSize: '0.75rem', fontWeight: '700' }}
                                                                            >
                                                                                Marcar salida
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        )}

                                        {/* Barra de Paginación Zen */}
                                        {guestAccessesVisible.length > 0 && (
                                            <div className="guest-accesses-pagination-container">
                                                <div className="guest-pagination-info">
                                                    Mostrando <strong>{Math.min((guestAccessesCurrentPage - 1) * guestAccessesPerPage + 1, guestAccessesVisible.length)}</strong> – <strong>{Math.min(guestAccessesCurrentPage * guestAccessesPerPage, guestAccessesVisible.length)}</strong> de <strong>{guestAccessesVisible.length}</strong> visitantes
                                                </div>

                                                <div className="guest-pagination-actions">
                                                    <div className="guest-pagination-pagesize">
                                                        <label htmlFor="guest-per-page">Por página:</label>
                                                        <select
                                                            id="guest-per-page"
                                                            value={guestAccessesPerPage}
                                                            onChange={(e) => {
                                                                setGuestAccessesPerPage(Number(e.target.value));
                                                                setGuestAccessesCurrentPage(1);
                                                            }}
                                                        >
                                                            <option value={10}>10</option>
                                                            <option value={25}>25</option>
                                                            <option value={50}>50</option>
                                                        </select>
                                                    </div>

                                                    {totalGuestAccessPages > 1 && (
                                                        <div className="pagination-zen-container guest-zen-pagination" style={{ margin: 0, padding: 0, border: 'none' }}>
                                                            <button
                                                                type="button"
                                                                className="btn-zen-pagination"
                                                                disabled={guestAccessesCurrentPage === 1}
                                                                onClick={() => setGuestAccessesCurrentPage((prev) => Math.max(1, prev - 1))}
                                                                title="Página anterior"
                                                            >
                                                                <FaChevronLeft /> {!isMobile && ' Anterior'}
                                                            </button>

                                                            <div className="zen-pagination-numbers">
                                                                {Array.from({ length: totalGuestAccessPages }, (_, i) => i + 1).map((pageNum) => {
                                                                    if (
                                                                        totalGuestAccessPages > 6 &&
                                                                        Math.abs(pageNum - guestAccessesCurrentPage) > 2 &&
                                                                        pageNum !== 1 &&
                                                                        pageNum !== totalGuestAccessPages
                                                                    ) {
                                                                        if (pageNum === 2 || pageNum === totalGuestAccessPages - 1) {
                                                                            return <span key={pageNum} style={{ padding: '0 4px', color: '#94a3b8', alignSelf: 'center' }}>...</span>;
                                                                        }
                                                                        return null;
                                                                    }
                                                                    return (
                                                                        <button
                                                                            key={pageNum}
                                                                            type="button"
                                                                            className={`btn-zen-page-number ${guestAccessesCurrentPage === pageNum ? 'active' : ''}`}
                                                                            onClick={() => setGuestAccessesCurrentPage(pageNum)}
                                                                        >
                                                                            {pageNum}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>

                                                            <button
                                                                type="button"
                                                                className="btn-zen-pagination"
                                                                disabled={guestAccessesCurrentPage === totalGuestAccessPages}
                                                                onClick={() => setGuestAccessesCurrentPage((prev) => Math.min(totalGuestAccessPages, prev + 1))}
                                                                title="Página siguiente"
                                                            >
                                                                {!isMobile && 'Siguiente '} <FaChevronRight />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Sección: Vehículos y Proveedores Programados */}
                {!isResident && (!isMobile || selectedSection === 'vehiculos-proveedores' || selectedSection === null) && (
                    <div className="vigilancia-collapsible-section">
                        <button
                            type="button"
                            className="vigilancia-collapsible-header"
                            onClick={() => setShowVehiculosProveedores(!showVehiculosProveedores)}
                        >
                            <div className="vigilancia-collapsible-header-content">
                                <div className="vigilancia-collapsible-icon" style={{ backgroundColor: '#fff7ed' }}>
                                    <FaTruck style={{ color: '#f97316' }} />
                                </div>
                                <div className="vigilancia-collapsible-title-wrapper">
                                    <h3>Vehículos y Proveedores Programados</h3>
                                    <p>Mudanzas y proveedores agendados para hoy y próximos días</p>
                                </div>
                            </div>
                            <div className="vigilancia-collapsible-toggle">
                                {showVehiculosProveedores ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </button>
                        {showVehiculosProveedores && (
                            <div className="vigilancia-collapsible-body">
                                {programacionesLoading ? (
                                    <div className="accesos-hint">Cargando accesos programados…</div>
                                ) : programacionesAcceso.length === 0 ? (
                                    <div className="accesos-hint">No hay proveedores programados próximos.</div>
                                ) : (
                                    <div className="programacion-grid" style={{ display: 'grid', gap: '1rem' }}>
                                        {programacionesDestacadas.map((programacion) => {
                                            const tipoLower = (programacion.tipo || '').toLowerCase();
                                            // Por defecto verde (como la imagen de referencia para Mudanza)
                                            let borderClass = 'programacion-border-green';
                                            let icon = <FaTruck />;
                                            let iconColor = '#10b981'; // Green-500
                                            let labelColor = '#0f172a';

                                            if (tipoLower.includes('proveedor')) {
                                                borderClass = 'programacion-border-orange';
                                                icon = <FaBoxOpen />;
                                                iconColor = '#f97316'; // Orange-500
                                            }

                                            return (
                                                <div key={programacion.id} className={`programacion-card ${borderClass}`}>

                                                    {/* Header con Fecha y Hora al estilo de la imagen */}
                                                    <div className="programacion-card-top-row">
                                                        <div className="programacion-date-badge">
                                                            <FaCalendarAlt />
                                                            <span>{formatProgramacionFecha(programacion)}</span>
                                                        </div>
                                                    </div>

                                                    {/* Cuerpo de la tarjeta */}
                                                    <div className="programacion-card-content">
                                                        <div className="programacion-type-row">
                                                            <span style={{ color: labelColor, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <span style={{ color: labelColor }}>{icon}</span>
                                                                <strong>{programacion.etiquetaTipo || 'Mudanza'}</strong>
                                                                <span style={{ margin: '0 4px' }}>·</span>
                                                                <span>Entrada</span>
                                                            </span>
                                                        </div>

                                                        <div className="programacion-location-row">
                                                            <span style={{ color: '#ef4444' }}><FaUserShield /></span> {/* Rojo para icon de persona/depto */}
                                                            <span>Depto: <strong>{programacion.ubicacion || 'A-333'}</strong></span>
                                                        </div>
                                                    </div>

                                                    <div className="programacion-separator"></div>

                                                    <div className="programacion-registrado-por">
                                                        Registrado por: <span style={{ color: '#8b5cf6', fontWeight: '700' }}>{programacion.registradoPorNombre || 'Lizeth Olguin'}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Formulario de Registro de Paquetería */}
                {!isResident && (!isMobile || selectedSection === 'paqueteria') && (
                    <div className="vigilancia-card">
                        <div className="vigilancia-card-header">
                            <div className="vigilancia-card-header-content">
                                <div className="vigilancia-collapsible-icon" style={{ backgroundColor: '#ecfdf5' }}>
                                    <FaBoxOpen style={{ color: '#10b981' }} />
                                </div>
                                <h3>Registrar Paquete</h3>
                            </div>
                        </div>
                        <div className="vigilancia-card-body">
                            <div className="paqueteria-form-grid">
                                <div className="form-group" style={{ position: 'relative' }}>
                                    <label htmlFor="depto">Departamento *</label>
                                    <div className="input-with-icon">
                                        <input 
                                            type="text" 
                                            name="depto" 
                                            placeholder="Ej: 405-A" 
                                            value={paquete.depto} 
                                            onChange={handlePaqueteChange}
                                            className={paqueteriaFieldErrors.depto ? 'field-error' : ''}
                                        />
                                        {paqueteriaFieldErrors.depto && <FaExclamationCircle className="error-icon-suttle" title="Campo obligatorio" />}
                                    </div>
                                    {showCasaSuggestions && casaSuggestions.length > 0 && (
                                        <div className="visitas-modal-hint" role="list" style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                            zIndex: 1000,
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            marginTop: '4px',
                                        }}>
                                            <div style={{ padding: '8px 12px', fontSize: '0.875rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                                Sugerencias ({casaSuggestions.length}):
                                            </div>
                                            {casaSuggestions.map((casa) => (
                                                <button
                                                    key={casa.id}
                                                    type="button"
                                                    className="btn-inline"
                                                    onClick={() => {
                                                        setPaquete(prev => ({
                                                            ...prev,
                                                            depto: casa.numero,
                                                            destinatario: casa.nombre_residente || prev.destinatario
                                                        }));
                                                        destinatarioTouchedRef.current = true;
                                                        setCasaSuggestions([]);
                                                        setShowCasaSuggestions(false);
                                                    }}
                                                    style={{
                                                        display: 'block',
                                                        width: '100%',
                                                        textAlign: 'left',
                                                        padding: '10px 12px',
                                                        border: 'none',
                                                        borderBottom: '1px solid #f3f4f6',
                                                        backgroundColor: 'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: '0.875rem',
                                                        color: '#111827',
                                                    }}
                                                >
                                                    <strong>{casa.numero || 'Sin número'}</strong>
                                                    {casa.nombre_residente ? ` - ${casa.nombre_residente}` : ''}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="form-group" style={{ position: 'relative' }}>
                                    <label htmlFor="empresa">Empresa de Paquetería *</label>
                                    <input type="text" name="empresa" placeholder="DHL, FedEx, UPS, etc." value={paquete.empresa} onChange={handlePaqueteChange} autoCorrect="on" spellCheck="true" />
                                    {showCourierSuggestions && courierSuggestions.length > 0 && (
                                        <div className="visitas-modal-hint" role="list" style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                            zIndex: 1000,
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            marginTop: '4px',
                                        }}>
                                            <div style={{ padding: '8px 12px', fontSize: '0.875rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                                Sugerencias ({courierSuggestions.length}):
                                            </div>
                                            {courierSuggestions.map((company, idx) => (
                                                <button
                                                    key={`${company}-${idx}`}
                                                    type="button"
                                                    className="btn-inline"
                                                    onClick={() => {
                                                        setPaquete(prev => ({
                                                            ...prev,
                                                            empresa: company
                                                        }));
                                                        setCourierSuggestions([]);
                                                        setShowCourierSuggestions(false);
                                                    }}
                                                    style={{
                                                        display: 'block',
                                                        width: '100%',
                                                        textAlign: 'left',
                                                        padding: '10px 12px',
                                                        border: 'none',
                                                        borderBottom: '1px solid #f3f4f6',
                                                        backgroundColor: 'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: '0.875rem',
                                                        color: '#111827',
                                                    }}
                                                >
                                                    {company}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="destinatario">Destinatario *</label>
                                    <div className="input-with-icon">
                                        <input 
                                            type="text" 
                                            name="destinatario" 
                                            placeholder="Nombre del residente" 
                                            value={paquete.destinatario} 
                                            onChange={handlePaqueteChange}
                                            className={paqueteriaFieldErrors.destinatario ? 'field-error' : ''}
                                            autoCorrect="on"
                                            spellCheck="true"
                                            autoCapitalize="words"
                                        />
                                        {paqueteriaFieldErrors.destinatario && <FaExclamationCircle className="error-icon-suttle" title="Campo obligatorio" />}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="guardia">Guardia que recibe *</label>
                                    <div className="input-with-icon">
                                        <input 
                                            type="text" 
                                            name="guardia" 
                                            placeholder="Nombre del guardia" 
                                            value={paquete.guardia} 
                                            onChange={handlePaqueteChange}
                                            className={paqueteriaFieldErrors.guardia ? 'field-error' : ''}
                                        />
                                        {paqueteriaFieldErrors.guardia && <FaExclamationCircle className="error-icon-suttle" title="Campo obligatorio" />}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="fecha">Fecha de recepción</label>
                                    <input 
                                        type="date" 
                                        name="fecha" 
                                        value={paquete.fecha} 
                                        onChange={handlePaqueteChange}
                                        max={new Date().toISOString().split('T')[0]} 
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="hora">Hora de recepción</label>
                                    <select name="hora" value={paquete.hora} onChange={handlePaqueteChange}>
                                        {horariosDisponibles.map((horario) => (
                                            <option key={horario.value} value={horario.value}>{horario.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="paqueteria-form-actions">
                                <button
                                    type="button"
                                    className="btn-vigilancia-action btn-vigilancia-primary"
                                    onClick={handleRegistrarPaquete}
                                    disabled={registrandoPaquete}
                                >
                                    {registrandoPaquete ? 'Registrando…' : 'Registrar Paquete'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-vigilancia-action btn-vigilancia-secondary"
                                    onClick={() => openModalWithHistory(setShowPaqueteriaModal)}
                                >
                                    Ver Paquetes Registrados
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sección: Paquetes Registrados (Solo Residentes) */}
                {isResident && (
                    <div className="vigilancia-zen-section" style={{ marginBottom: '2rem' }}>
                        <div className="vigilancia-zen-header">
                            <h3 className="zen-section-title">Paquetes Registrados</h3>
                        </div>

                        {isPaqueteriaBloqueada ? (
                            <div className="amenidad-bloqueo-zen-card" style={{ padding: '2rem 1.5rem', textAlign: 'center', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', margin: '1rem 0' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px auto', fontSize: '1.25rem' }}>
                                    <FaLock />
                                </div>
                                <h4 style={{ margin: '0 0 6px 0', color: '#1e293b', fontSize: '1.05rem', fontWeight: 700 }}>Sección inhabilitada temporalmente</h4>
                                <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '0.875rem' }}>
                                    La consulta de paquetería está pausada por adeudo pendiente en tu cuenta.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => navigate('/finanzas')}
                                    className="btn-vigilancia-action"
                                    style={{ background: '#0284c7', color: '#ffffff', border: 'none', margin: '0 auto', padding: '8px 20px', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    Regularizar en Finanzas
                                </button>
                            </div>
                        ) : residentPackagesLoading ? (
                            <div className="accesos-hint">
                                <FaBoxOpen /> Cargando paquetes...
                            </div>
                        ) : residentPackages.length === 0 ? (
                            <div className="accesos-hint">
                                <FaBoxOpen /> No tienes paquetes registrados.
                            </div>
                        ) : (() => {
                            const totalPages = Math.ceil(residentPackagesFiltrados.length / PACKAGES_PER_PAGE);
                            const startIndex = (residentPackagesPage - 1) * PACKAGES_PER_PAGE;
                            const paginatedPackages = residentPackagesFiltrados.slice(startIndex, startIndex + PACKAGES_PER_PAGE);

                            return (
                                <>
                                    <div className="paquetes-grid-zen">
                                        {paginatedPackages.map((pkg) => {
                                            const isEntregado = pkg.entregado === true || pkg.estado === 'entregado';
                                            const statusLabel = isEntregado ? 'Entregado' : 'Pendiente';
                                            const statusClass = isEntregado ? 'pill-entregado' : 'pill-pendiente';

                                            const fechaRec = toDateSafe(pkg.fechaRecepcion);
                                            const fechaEnt = toDateSafe(pkg.fechaEntrega);

                                            return (
                                                <div key={pkg.id} className="paquete-card-zen">
                                                    <div className="card-zen-header">
                                                        <h4 className="card-zen-courier">{pkg.empresa || 'Paquetería'}</h4>
                                                        <span className={`card-zen-pill ${statusClass}`}>{statusLabel}</span>
                                                    </div>

                                                    <div className="card-zen-body">
                                                        <div className="card-zen-detail">
                                                            <span className="detail-label">DEPARTAMENTO</span>
                                                            <span className="detail-value">{pkg.departamento || 'N/A'}</span>
                                                        </div>
                                                        <div className="card-zen-detail">
                                                            <span className="detail-label">RECIBIDO</span>
                                                            <span className="detail-value">{pkg.guardia || pkg.recibidoPor || 'Prueba Caseta'}</span>
                                                        </div>
                                                    </div>

                                                    <div className="card-zen-divider"></div>

                                                    <div className="card-zen-footer">
                                                        <div className="footer-zen-row">
                                                            <span className="footer-zen-date">
                                                                {fechaRec ? fechaRec.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '') : '--'} · {fechaRec ? fechaRec.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false }) : '--'}
                                                            </span>
                                                            <span className="footer-zen-info">
                                                                {isEntregado ? (
                                                                    <>Por <strong>{typeof pkg.entregadoPor === 'object' && pkg.entregadoPor !== null ? (pkg.entregadoPor.nombre || 'Guardia') : (pkg.entregadoPor || 'Guardia')}</strong></>
                                                                ) : 'Esperando recogida'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="pagination-zen-container">
                                            <button
                                                className="btn-zen-pagination"
                                                disabled={residentPackagesPage === 1}
                                                onClick={() => setResidentPackagesPage(prev => Math.max(1, prev - 1))}
                                            >
                                                <FaChevronLeft /> {!isMobile && ' Anterior'}
                                            </button>
                                            
                                            <div className="zen-pagination-numbers">
                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                                    <button
                                                        key={pageNum}
                                                        className={`btn-zen-page-number ${residentPackagesPage === pageNum ? 'active' : ''}`}
                                                        onClick={() => setResidentPackagesPage(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                ))}
                                            </div>

                                            <button
                                                className="btn-zen-pagination"
                                                disabled={residentPackagesPage === totalPages}
                                                onClick={() => setResidentPackagesPage(prev => Math.min(totalPages, prev + 1))}
                                            >
                                                {!isMobile && 'Siguiente '} <FaChevronRight />
                                            </button>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                        <div className="zen-help-legend">
                            Contacta a vigilancia si detectas un paquete desconocido.
                        </div>
                    </div>
                )}

                {/* Sección: Visitas Frecuentes */}
                {(!isMobile || selectedSection === 'visitas-frecuentes' || selectedSection === null) && (
                    <div className="vigilancia-collapsible-section">
                        <button
                            type="button"
                            className="vigilancia-collapsible-header"
                            onClick={() => setShowVisitasFrecuentes(!showVisitasFrecuentes)}
                        >
                            <div className="vigilancia-collapsible-header-content">
                                <div className="vigilancia-collapsible-icon" style={{ backgroundColor: '#fce7f3' }}>
                                    <FaCalendarAlt style={{ color: '#db2777' }} />
                                </div>
                                <div className="vigilancia-collapsible-title-wrapper">
                                    <h3>Visitas Frecuentes</h3>
                                    <p>Lista de personas registradas por los residentes</p>
                                </div>
                            </div>
                            <div className="vigilancia-collapsible-toggle">
                                {showVisitasFrecuentes ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </button>
                        {showVisitasFrecuentes && (
                            <div className="vigilancia-collapsible-body">

                                {isResident && (
                                    <>
                                        {/* Mobile: Collapsible Info & Limits */}
                                        {isMobile ? (
                                            <>
                                                <div
                                                    className={`qr-modal-info-banner ${showFrecuentesInfo ? 'expanded' : 'collapsed'}`}
                                                    onClick={() => setShowFrecuentesInfo(!showFrecuentesInfo)}
                                                    style={{ marginBottom: '1rem', marginTop: '0' }}
                                                >
                                                    <div className="qr-info-header">
                                                        <span className="qr-info-title">
                                                            <FaInfoCircle /> Información y límites
                                                        </span>
                                                        <span className="qr-info-toggle-icon">
                                                            {showFrecuentesInfo ? <FaChevronUp /> : <FaChevronDown />}
                                                        </span>
                                                    </div>
                                                    {showFrecuentesInfo && (
                                                        <div className="visitas-frequency-content-mobile" style={{ marginTop: '1rem' }}>
                                                            <p className="visitas-residente-note" style={{ marginBottom: '1rem' }}>
                                                                Puedes registrar hasta {MAX_VISITAS_POR_CATEGORIA} personas por categoría. Comparte sus datos para agilizar su acceso.
                                                            </p>
                                                            <ul className="visitas-categoria-limits">
                                                                {VISITA_CATEGORIES.map((categoria) => {
                                                                    const key = categoria.value.toLowerCase();
                                                                    const count = visitasPorCategoria[key] || 0;
                                                                    return (
                                                                        <li key={categoria.value}>
                                                                            <span>{categoria.label}</span>
                                                                            <span>{count}/{MAX_VISITAS_POR_CATEGORIA}</span>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>

                                                <button
                                                    type="button"
                                                    className={`visitas-residente-add full-width ${isVisitasFrecuentesBloqueadas ? 'btn-moroso-bloqueado' : ''}`}
                                                    onClick={handleOpenVisitaModal}
                                                    title={isVisitasFrecuentesBloqueadas ? 'Función inhabilitada por adeudo pendiente' : 'Registrar visita frecuente'}
                                                    style={{
                                                        marginBottom: '1.5rem',
                                                        width: '100%',
                                                        justifyContent: 'center',
                                                        ...(isVisitasFrecuentesBloqueadas ? { opacity: 0.65, filter: 'grayscale(0.6)', cursor: 'not-allowed' } : {})
                                                    }}
                                                >
                                                    {isVisitasFrecuentesBloqueadas ? <FaLock /> : <FaPlus />} Registrar visita frecuente
                                                </button>
                                            </>
                                        ) : (
                                            /* Desktop: Original Grid Layout */
                                            <>
                                                <div className="visitas-residente-header">
                                                    <p className="visitas-residente-note">
                                                        Puedes registrar hasta {MAX_VISITAS_POR_CATEGORIA} personas por categoría. Comparte sus datos para agilizar su acceso.
                                                    </p>
                                                    <button
                                                        type="button"
                                                        className={`visitas-residente-add ${isVisitasFrecuentesBloqueadas ? 'btn-moroso-bloqueado' : ''}`}
                                                        onClick={handleOpenVisitaModal}
                                                        title={isVisitasFrecuentesBloqueadas ? 'Función inhabilitada por adeudo pendiente' : 'Registrar visita frecuente'}
                                                        style={isVisitasFrecuentesBloqueadas ? { opacity: 0.65, filter: 'grayscale(0.6)', cursor: 'not-allowed' } : {}}
                                                    >
                                                        {isVisitasFrecuentesBloqueadas ? <FaLock /> : <FaPlus />} Registrar visita frecuente
                                                    </button>
                                                </div>
                                                <ul className="visitas-categoria-limits">
                                                    {VISITA_CATEGORIES.map((categoria) => {
                                                        const key = categoria.value.toLowerCase();
                                                        const count = visitasPorCategoria[key] || 0;
                                                        return (
                                                            <li key={categoria.value}>
                                                                <span>{categoria.label}</span>
                                                                <span>{count}/{MAX_VISITAS_POR_CATEGORIA}</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </>
                                        )}
                                    </>
                                )}

                                {(isSuperAdmin || isCaseta) && (
                                    <div className="accesos-filters" style={{ margin: '0 0 1.5rem 0', background: 'transparent', padding: 0, border: 'none', boxShadow: 'none' }}>
                                        <div className="accesos-search" style={{ border: 'none' }}>
                                            <FaSearch aria-hidden="true" />
                                            <input
                                                id="visitas-filter"
                                                type="text"
                                                placeholder="Busca por visitante, departamento, categoría..."
                                                value={visitasFilterDepto}
                                                onChange={handleVisitasFilterChange}
                                                style={{ background: '#f8fafc' }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {visitasLoading ? (
                                    <div className="visitas-hint visitas-hint-loading">
                                        <FaSearch />
                                        <span>Cargando visitas frecuentes…</span>
                                    </div>
                                ) : visitasError ? (
                                    <div className="visitas-hint visitas-hint-error">
                                        <FaExclamationTriangle />
                                        <span>{visitasError}</span>
                                    </div>
                                ) : isResident ? (
                                    visitasFrecuentes.length === 0 ? (
                                        <div className="accesos-hint">
                                            <FaInfoCircle />
                                            <span>Aún no registras visitas frecuentes. Usa el botón para agregar a tu lista.</span>
                                        </div>
                                    ) : (
                                        <div className="visitas-residente-grid">
                                            {visitasFrecuentes.map((visita) => (
                                                <div key={visita.id} className="visita-frecuente-card">
                                                    <div className="visita-card-header">
                                                        <div className="visita-card-name">{visita.nombre || 'Visitante'}</div>
                                                        <span className={`status-pill ${getCategoriaBadgeClass(visita.categoria)}`}>
                                                            {formatCategoriaLabel(visita.categoria)}
                                                        </span>
                                                    </div>
                                                    <div className="visita-card-meta">
                                                        {visita.placa && <span className="meta-item">🚗 {visita.placa}</span>}
                                                        {visita.color && <span className="meta-item">🎨 {visita.color}</span>}
                                                        {visita.createdAt && <span className="meta-item">📅 {formatVisitaDate(visita.createdAt)}</span>}
                                                    </div>
                                                    {visita.notas && (
                                                        <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '4px 0' }}>{visita.notas}</p>
                                                    )}

                                                    {renderScheduleSummary(visita.horario)}

                                                    <div className="visita-card-footer" style={{ marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid #f1f5f9', width: '100%', display: 'flex', gap: '8px' }}>
                                                        <button 
                                                            type="button" 
                                                            className={`btn-vigilancia-action ${isVisitasFrecuentesBloqueadas ? 'btn-moroso-bloqueado' : ''}`}
                                                            style={{
                                                                flex: 1,
                                                                height: '34px',
                                                                fontSize: '0.8rem',
                                                                background: '#f8fafc',
                                                                color: isVisitasFrecuentesBloqueadas ? '#94a3b8' : '#4f46e5',
                                                                border: '1px solid #e2e8f0',
                                                                cursor: isVisitasFrecuentesBloqueadas ? 'not-allowed' : 'pointer'
                                                            }}
                                                            onClick={(e) => { e.stopPropagation(); handleEditVisita(visita); }}
                                                            title={isVisitasFrecuentesBloqueadas ? 'Función inhabilitada por adeudo pendiente' : 'Editar visita'}
                                                        >
                                                            {isVisitasFrecuentesBloqueadas ? <FaLock /> : <FaEdit />} Editar
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            className="btn-vigilancia-action"
                                                            style={{ width: '34px', height: '34px', padding: 0, justifyContent: 'center', background: '#fef2f2', color: '#dc2626', border: '1px solid #fee2e2' }}
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteVisita(visita); }}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                        {visita.identificacionUrl && (
                                                            <a
                                                                className="btn-vigilancia-action"
                                                                style={{ width: '34px', height: '34px', padding: 0, justifyContent: 'center', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' }}
                                                                href={visita.identificacionUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="Ver ID"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <FaIdCard />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ) : visitasFiltradas.length === 0 ? (
                                    <div className="visitas-hint visitas-hint-warning">
                                        <FaInfoCircle />
                                        <span>No hay visitas frecuentes registradas con el filtro actual.</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="visitas-residente-grid">
                                            {visitasPaginadas.map((visita) => (
                                                <div key={visita.id} className="visita-frecuente-card">
                                                    <div className="visita-card-header">
                                                        <div className="visita-card-name">{visita.nombre || 'Visitante'}</div>
                                                        <span className={`status-pill ${getCategoriaBadgeClass(visita.categoria)}`}>
                                                            {formatCategoriaLabel(visita.categoria)}
                                                        </span>
                                                    </div>
                                                    <div className="visita-card-meta">
                                                        {visita.placa && <span className="meta-item"><FaIdCard /> {visita.placa}</span>}
                                                        {visita.color && <span className="meta-item"><FaPalette /> {visita.color}</span>}
                                                        {visita.createdAt && <span className="meta-item"><FaCalendarAlt /> {formatVisitaDate(visita.createdAt)}</span>}
                                                        <span className="meta-item" style={{ background: '#eff6ff', color: '#1e40af' }}>
                                                            <FaHome /> {resolutedDeptoMap[visita.id] || visita.ownerDepartamento || 'Sin dato'}
                                                        </span>
                                                        <span className="meta-item" style={{ background: '#f5f3ff', color: '#5b21b6' }}>
                                                            <FaUser /> {visita.ownerNombre || 'Residente'}
                                                        </span>
                                                    </div>
                                                    {visita.notes || visita.notas ? (
                                                        <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '4px 0', lineHeight: '1.3' }}>{visita.notas || visita.notes}</p>
                                                    ) : null}

                                                    {renderScheduleSummary(visita.horario)}

                                                    <div className="visita-card-footer" style={{ marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #f1f5f9', width: '100%', display: 'flex', gap: '8px' }}>
                                                        <button
                                                            type="button"
                                                            className="btn-vigilancia-action btn-vigilancia-ghost"
                                                            style={{ flex: 1, justifyContent: 'center' }}
                                                            onClick={() => handleOpenDetalleVisita(visita)}
                                                        >
                                                            <FaInfoCircle /> Revisar
                                                        </button>
                                                        {visita.identificacionUrl && (
                                                            <a
                                                                className="btn-vigilancia-action btn-vigilancia-ghost"
                                                                style={{ padding: '8px', minWidth: '40px', justifyContent: 'center' }}
                                                                href={visita.identificacionUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="Ver Identificación"
                                                            >
                                                                <FaIdCard />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {totalVisitasPages > 1 && (
                                            <div className="paquetes-pagination" style={{ marginTop: '20px' }}>
                                                <button
                                                    type="button"
                                                    className="btn-pagination"
                                                    disabled={visitasPage === 1}
                                                    onClick={() => setVisitasPage(prev => Math.max(1, prev - 1))}
                                                >
                                                    <FaChevronLeft /> {isMobile ? '' : 'Anterior'}
                                                </button>
                                                <span className="pagination-info">
                                                    {isMobile ? `${visitasPage} / ${totalVisitasPages}` : `Página ${visitasPage} de ${totalVisitasPages}`}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn-pagination"
                                                    disabled={visitasPage === totalVisitasPages}
                                                    onClick={() => setVisitasPage(prev => Math.min(totalVisitasPages, prev + 1))}
                                                >
                                                    {isMobile ? '' : 'Siguiente'} <FaChevronRight />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal para registrar paquete */}
            {showPaqueteriaFormModal && !isResident && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content paqueteria-modal-compact">
                        <header className="vigilancia-modal-header">
                            <h2>Registrar Paquete</h2>
                            <button
                                type="button"
                                onClick={handleClosePaqueteriaForm}
                                aria-label="Cerrar"
                                disabled={registrandoPaquete}
                            >×</button>
                        </header>
                        <div className="vigilancia-modal-body">
                             <div className="form-group" style={{ position: 'relative' }}>
                                 <label htmlFor="depto">Departamento *</label>
                                 <div className="input-with-icon">
                                     <input 
                                         type="text" 
                                         name="depto" 
                                         placeholder="Ej: 405-A" 
                                         value={paquete.depto} 
                                         onChange={handlePaqueteChange}
                                         onFocus={() => {
                                             if (paquete.depto.trim().length > 0) setShowCasaSuggestions(true);
                                         }}
                                         onBlur={() => {
                                             setTimeout(() => setShowCasaSuggestions(false), 200);
                                         }}
                                         autoComplete="off"
                                         className={paqueteriaFieldErrors.depto ? 'field-error' : ''}
                                     />
                                     {paqueteriaFieldErrors.depto && <FaExclamationCircle className="error-icon-suttle" title="Campo obligatorio" />}
                                 </div>
                                 {showCasaSuggestions && casaSuggestions.length > 0 && (
                                    <div className="visitas-modal-hint zen-autocomplete-dropdown" role="list" style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                        zIndex: 1000,
                                        maxHeight: '220px',
                                        overflowY: 'auto',
                                        marginTop: '6px',
                                        padding: '4px 0'
                                    }}>
                                        <div style={{ padding: '10px 16px', fontSize: '0.75rem', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.025em', borderBottom: '1px solid #f1f5f9' }}>
                                            Departamentos Encontrados
                                        </div>
                                        {casaSuggestions.map((casa) => (
                                            <button
                                                key={casa.id}
                                                type="button"
                                                className="suggestion-item"
                                                onClick={() => {
                                                    setPaquete(prev => ({
                                                        ...prev,
                                                        depto: casa.numero,
                                                        destinatario: casa.nombre_residente || prev.destinatario
                                                    }));
                                                    destinatarioTouchedRef.current = true;
                                                    setCasaSuggestions([]);
                                                    setShowCasaSuggestions(false);
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    padding: '12px 16px',
                                                    border: 'none',
                                                    borderBottom: '1px solid #f8fafc',
                                                    backgroundColor: 'transparent',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <span style={{ fontWeight: '600', color: '#1e293b' }}>
                                                    {casa.numero || 'Sin número'}
                                                </span>
                                                {casa.nombre_residente && (
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '8px' }}>
                                                        {casa.nombre_residente}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                 )}
                             </div>
                            {paquete.depto.trim() && destinatarioLookup.status === 'searching' && (
                                <div className="paqueteria-hint paqueteria-hint-loading">
                                    <FaSearch />
                                    <span>Buscando residente para el departamento {destinatarioLookup.depto || paquete.depto.trim()}…</span>
                                </div>
                            )}
                            {paquete.depto.trim() && destinatarioLookup.status === 'found' && destinatarioLookup.nombre && (
                                <div className="paqueteria-hint paqueteria-hint-success">
                                    <FaCheckCircle />
                                    <span>Destinatario detectado: {destinatarioLookup.nombre}</span>
                                </div>
                            )}
                            {paquete.depto.trim() && destinatarioLookup.status === 'found' && !destinatarioLookup.nombre && (
                                <div className="paqueteria-hint paqueteria-hint-warning">
                                    <FaExclamationTriangle />
                                    <span>Departamento localizado pero sin nombre registrado. Completa el campo manualmente.</span>
                                </div>
                            )}
                            {paquete.depto.trim() && destinatarioLookup.status === 'not-found' && (
                                <div className="paqueteria-hint paqueteria-hint-warning">
                                    <FaExclamationTriangle />
                                    <span>No se encontró un residente para el departamento {destinatarioLookup.depto || paquete.depto.trim()}.</span>
                                </div>
                            )}
                            {paquete.depto.trim() && destinatarioLookup.status === 'error' && (
                                <div className="paqueteria-hint paqueteria-hint-error">
                                    <FaExclamationTriangle />
                                    <span>No se pudo validar el departamento. Intenta más tarde.</span>
                                </div>
                            )}
                            <div className="form-group" style={{ position: 'relative' }}>
                                <label htmlFor="empresa">Empresa de Paquetería *</label>
                                <div className="input-with-icon">
                                    <input 
                                        type="text" 
                                        name="empresa" 
                                        placeholder="DHL, FedEx, UPS, etc." 
                                        value={paquete.empresa} 
                                        onChange={handlePaqueteChange}
                                        className={paqueteriaFieldErrors.empresa ? 'field-error' : ''}
                                    />
                                    {paqueteriaFieldErrors.empresa && <FaExclamationCircle className="error-icon-suttle" title="Campo obligatorio" />}
                                </div>
                                {showCourierSuggestions && courierSuggestions.length > 0 && (
                                    <div className="visitas-modal-hint" role="list" style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                        zIndex: 1000,
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        marginTop: '4px',
                                    }}>
                                        <div style={{ padding: '8px 12px', fontSize: '0.875rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                            Sugerencias ({courierSuggestions.length}):
                                        </div>
                                        {courierSuggestions.map((company, idx) => (
                                            <button
                                                key={`${company}-${idx}`}
                                                type="button"
                                                className="btn-inline"
                                                onClick={() => {
                                                    setPaquete(prev => ({
                                                        ...prev,
                                                        empresa: company
                                                    }));
                                                    setCourierSuggestions([]);
                                                    setShowCourierSuggestions(false);
                                                }}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    padding: '10px 12px',
                                                    border: 'none',
                                                    borderBottom: '1px solid #f3f4f6',
                                                    backgroundColor: 'transparent',
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem',
                                                    color: '#111827',
                                                }}
                                            >
                                                {company}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label htmlFor="destinatario">Destinatario *</label>
                                <div className="input-with-icon">
                                    <input 
                                        type="text" 
                                        name="destinatario" 
                                        placeholder="Nombre del residente" 
                                        value={paquete.destinatario} 
                                        onChange={handlePaqueteChange}
                                        className={paqueteriaFieldErrors.destinatario ? 'field-error' : ''}
                                    />
                                    {paqueteriaFieldErrors.destinatario && <FaExclamationCircle className="error-icon-suttle" title="Campo obligatorio" />}
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="guardia"><FaUserShield /> Guardia que recibe *</label>
                                <div className="input-with-icon">
                                    <input
                                        type="text"
                                        name="guardia"
                                        placeholder="Nombre del guardia"
                                        value={paquete.guardia}
                                        onChange={handlePaqueteChange}
                                        className={paqueteriaFieldErrors.guardia ? 'field-error' : ''}
                                    />
                                    {paqueteriaFieldErrors.guardia && <FaExclamationCircle className="error-icon-suttle" title="Campo obligatorio" />}
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="fecha"><FaCalendarAlt /> Fecha de recepción</label>
                                <input 
                                    type="date" 
                                    name="fecha" 
                                    value={paquete.fecha} 
                                    onChange={handlePaqueteChange} 
                                    max={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="hora"><FaClock /> Hora de recepción</label>
                                <select name="hora" value={paquete.hora} onChange={handlePaqueteChange}>
                                    {horariosDisponibles.map((horario) => (
                                        <option key={horario.value} value={horario.value}>
                                            {horario.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {paqueteriaError && <div className="paqueteria-error" role="alert">{paqueteriaError}</div>}
                        </div>
                        <footer className="vigilancia-modal-footer" style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            justifyContent: isMobile ? 'space-between' : 'flex-end', 
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px 20px'
                        }}>
                            {isMobile && (
                                <button
                                    type="button"
                                    className="btn-vigilancia-secondary-icon"
                                    onClick={() => {
                                        resetPaqueteForm();
                                        setShowPaqueteriaFormModal(false);
                                        openModalWithHistory(setShowPaqueteriaModal);
                                    }}
                                    disabled={registrandoPaquete}
                                    title="Ver Paquetes Registrados"
                                    style={{
                                        height: '45px',
                                        borderRadius: '12px',
                                        fontSize: '1.2rem',
                                        backgroundColor: '#f1f5f9',
                                        border: '1px solid #e2e8f0',
                                        color: '#64748b',
                                        padding: '0'
                                    }}
                                >
                                    <FaHistory />
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={handleRegistrarPaquete}
                                disabled={registrandoPaquete}
                                style={{ flex: isMobile ? 1 : 'none' }}
                            >
                                {registrandoPaquete ? 'Registrando…' : 'Registrar Paquete'}
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {showManualAccessModal && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content vigilancia-modal-compact">
                        <header className="vigilancia-modal-header">
                            <h2>Registrar acceso manual</h2>
                            <button type="button" onClick={handleCloseManualAccessModal} aria-label="Cerrar" disabled={manualAccessSubmitting}>×</button>
                        </header>
                        <form onSubmit={handleSubmitManualAccess}>
                            <div className="vigilancia-modal-body manual-access-modal-body">
                                <p className="manual-access-description">Captura una entrada cuando se trate de visitas sin QR o autorizaciones verbales. La salida podrá registrarse desde la tabla principal.</p>

                                {visitasFrecuentes.length > 0 && (
                                    <div className="form-group">
                                        <label htmlFor="manual-visita-frecuente">
                                            <FaHistory style={{ marginRight: 8, color: '#94a3b8' }} />
                                            Tomar datos de una visita frecuente
                                        </label>
                                        <select
                                            id="manual-visita-frecuente"
                                            value={manualAccessForm.visitaFrecuenteId}
                                            onChange={handleManualAccessFrecuenteSelect}
                                            disabled={manualAccessSubmitting}
                                        >
                                            <option value="">Selecciona una visita frecuente</option>
                                            {visitasFrecuentes.map((visita) => (
                                                <option key={visita.id} value={visita.id}>
                                                    {visita.nombre || 'Visitante sin nombre'}{visita.ownerDepartamento ? ` · ${visita.ownerDepartamento}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="visitas-modal-hint">Autocompleta campos conocidos al seleccionar una visita frecuente.</small>
                                    </div>
                                )}

                                <div className="qr-modal-grid manual-access-grid">
                                    <div className="form-group span-full" style={{ position: 'relative' }}>
                                        <label htmlFor="manual-visitante">
                                            <FaUser style={{ marginRight: 8, color: '#6366f1' }} />
                                            Nombre del visitante *
                                            <span className="qr-help-icon" title="Nombre completo de la visita."><FaQuestionCircle /></span>
                                        </label>
                                        <input
                                            id="manual-visitante"
                                            name="visitante"
                                            type="text"
                                            value={manualAccessForm.visitante}
                                            onChange={handleManualAccessInputChange}
                                            onFocus={() => {
                                                if (manualAccessForm.visitante && manualAccessForm.visitante.trim().length >= 2) {
                                                    setShowManualVisitorSuggestions(true);
                                                }
                                            }}
                                            onBlur={() => {
                                                // Pequeño delay para permitir clicks en el dropdown
                                                setTimeout(() => setShowManualVisitorSuggestions(false), 200);
                                            }}
                                            disabled={manualAccessSubmitting}
                                            autoCorrect="on"
                                            spellCheck="true"
                                            autoCapitalize="words"
                                            autoComplete="off"
                                            required
                                        />
                                        {showManualVisitorSuggestions && manualVisitorSuggestions.length > 0 && (
                                            <div className="visitas-modal-hint zen-autocomplete-dropdown" role="list" style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '12px',
                                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                                zIndex: 1000,
                                                maxHeight: '220px',
                                                overflowY: 'auto',
                                                marginTop: '6px',
                                                padding: '4px 0'
                                            }}>
                                                <div style={{ padding: '10px 16px', fontSize: '0.75rem', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.025em', borderBottom: '1px solid #f1f5f9' }}>
                                                    Visitas Recientes
                                                </div>
                                                {manualVisitorSuggestions.map((s) => (
                                                    <button
                                                        key={s.id}
                                                        type="button"
                                                        className="suggestion-item"
                                                        onClick={() => handleSelectManualSuggestion(s)}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                            textAlign: 'left',
                                                            padding: '12px 16px',
                                                            border: 'none',
                                                            borderBottom: '1px solid #f8fafc',
                                                            backgroundColor: 'transparent',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            outline: 'none'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#f8fafc';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>
                                                                {s.nombre}
                                                            </span>
                                                            {s.ownerDepartamento && (
                                                                <span style={{ fontSize: '0.7rem', color: '#6366f1', fontWeight: '500' }}>
                                                                    <FaHome style={{ marginRight: 4, fontSize: '0.65rem' }} /> {s.ownerDepartamento}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                                            <FaChevronRight />
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group" style={{ position: 'relative' }}>
                                        <label htmlFor="manual-departamento">
                                            <FaHome style={{ marginRight: 8, color: '#6366f1' }} />
                                            Departamento / lote *
                                            <span className="qr-help-icon" title="Escribe el número de casa o parte del número para ver sugerencias."><FaQuestionCircle /></span>
                                        </label>
                                        <input
                                            id="manual-departamento"
                                            name="departamento"
                                            type="text"
                                            placeholder="Ej: 99, A-333, Lote 15"
                                            value={manualAccessForm.departamento}
                                            onChange={handleManualAccessInputChange}
                                            onFocus={() => {
                                                if (!manualAccessForm.visitaFrecuenteId && manualAccessForm.departamento.trim().length > 0) {
                                                    const searchTerm = manualAccessForm.departamento.trim().toLowerCase();
                                                    if (searchTerm.length > 0 && casasList.length > 0) {
                                                        const filtered = casasList.filter(casa => {
                                                            const numero = String(casa.numero || '').toLowerCase();
                                                            const nombreResidente = String(casa.nombre_residente || '').toLowerCase();
                                                            return numero.includes(searchTerm) || nombreResidente.includes(searchTerm);
                                                        }).slice(0, 10);
                                                        setCasaSuggestions(filtered);
                                                        setShowCasaSuggestions(filtered.length > 0);
                                                    }
                                                }
                                            }}
                                            onBlur={() => {
                                                // Ocultar sugerencias después de un pequeño delay para permitir el click
                                                setTimeout(() => setShowCasaSuggestions(false), 200);
                                            }}
                                            disabled={manualAccessSubmitting || (casasLoading && !manualAccessForm.visitaFrecuenteId)}
                                            required
                                            autoComplete="off"
                                        />
                                        {!manualAccessForm.visitaFrecuenteId && casasLoading && (
                                            <small className="visitas-modal-hint">Cargando casas...</small>
                                        )}
                                        {!manualAccessForm.visitaFrecuenteId && !casasLoading && casasList.length === 0 && (
                                            <small className="visitas-modal-hint" style={{ color: '#dc2626' }}>
                                                No se encontraron casas en el fraccionamiento.
                                            </small>
                                        )}
                                        {showCasaSuggestions && casaSuggestions.length > 0 && (
                                            <div className="visitas-modal-hint" role="list" style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                zIndex: 1000,
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                marginTop: '4px',
                                            }}>
                                                <div style={{ padding: '8px 12px', fontSize: '0.875rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                                    Sugerencias ({casaSuggestions.length}):
                                                </div>
                                                {casaSuggestions.map((casa) => (
                                                    <button
                                                        key={casa.id}
                                                        type="button"
                                                        className="btn-inline"
                                                        onClick={() => handleSelectCasaSuggestion(casa)}
                                                        style={{
                                                            display: 'block',
                                                            width: '100%',
                                                            textAlign: 'left',
                                                            padding: '10px 12px',
                                                            border: 'none',
                                                            borderBottom: '1px solid #f3f4f6',
                                                            backgroundColor: 'transparent',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem',
                                                            color: '#111827',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#f9fafb';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        <strong>{casa.numero || 'Sin número'}</strong>
                                                        {casa.nombre_residente ? ` - ${casa.nombre_residente}` : ''}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="manual-categoria">
                                            <FaTag style={{ marginRight: 8, color: '#6366f1' }} />
                                            Categoría *
                                            <span className="qr-help-icon" title="Tipo de visitante (servicio, familiar, proveedor, etc.)."><FaQuestionCircle /></span>
                                        </label>
                                        <select
                                            id="manual-categoria"
                                            name="categoria"
                                            value={manualAccessForm.categoria}
                                            onChange={handleManualAccessInputChange}
                                            disabled={manualAccessSubmitting}
                                            required
                                        >
                                            <option value="">Selecciona una categoría</option>
                                            {VISITA_CATEGORIES.map((categoria) => {
                                                const style = CATEGORY_STYLES[categoria.value] || {};
                                                return (
                                                    <option
                                                        key={categoria.value}
                                                        value={categoria.value}
                                                        style={{ backgroundColor: style.bg, color: style.color }}
                                                    >
                                                        {categoria.label}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    <div className="form-group span-full transporte-select-group">
                                        <label htmlFor="manual-transporte">
                                            <FaTruck style={{ marginRight: 8, color: '#6366f1' }} />
                                            Medio de transporte
                                        </label>
                                        <div className="transporte-options">
                                            <select
                                                id="manual-transporte"
                                                name="tipoTransporte"
                                                value={manualAccessForm.tipoTransporte}
                                                onChange={handleManualAccessInputChange}
                                                disabled={manualAccessSubmitting}
                                                className="modern-select"
                                            >
                                                <option value="En vehiculo">En vehículo</option>
                                                <option value="En moto">En moto</option>
                                                <option value="A pie">A pie</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="manual-numero">
                                            <FaUsers style={{ marginRight: 8, color: '#6366f1' }} />
                                            Número de personas
                                            <span className="qr-help-icon" title="Personas que ingresan con esta visita."><FaQuestionCircle /></span>
                                        </label>
                                        <input
                                            id="manual-numero"
                                            name="numeroVisitantes"
                                            type="number"
                                            min="1"
                                            max="50"
                                            value={manualAccessForm.numeroVisitantes}
                                            onChange={handleManualAccessInputChange}
                                            disabled={manualAccessSubmitting}
                                        />
                                    </div>



                                    {manualAccessForm.tipoTransporte !== 'A pie' && (
                                        <>
                                            <div className="form-group">
                                                <label htmlFor="manual-placa">
                                                    <FaIdCard style={{ marginRight: 8, color: '#6366f1' }} />
                                                    {manualAccessForm.tipoTransporte === 'En moto' ? 'Placas de la moto *' : 'Placas del vehículo *'}
                                                    <span className="qr-help-icon" title="Placas o identificación del vehículo (requerido)."><FaQuestionCircle /></span>
                                                </label>
                                                <input
                                                    id="manual-placa"
                                                    name="placa"
                                                    type="text"
                                                    placeholder="ABC-123"
                                                    value={manualAccessForm.placa}
                                                    onChange={handleManualAccessInputChange}
                                                    disabled={manualAccessSubmitting}
                                                    required={manualAccessForm.tipoTransporte !== 'A pie'}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="manual-color">
                                                    <FaPalette style={{ marginRight: 8, color: '#6366f1' }} />
                                                    {manualAccessForm.tipoTransporte === 'En moto' ? 'Color de la moto *' : 'Color del vehículo *'}
                                                    <span className="qr-help-icon" title="Color o descripción del vehículo (requerido)."><FaQuestionCircle /></span>
                                                </label>
                                                <select
                                                    id="manual-color"
                                                    name="color"
                                                    value={manualAccessForm.color}
                                                    onChange={handleManualAccessInputChange}
                                                    disabled={manualAccessSubmitting}
                                                    required={manualAccessForm.tipoTransporte !== 'A pie'}
                                                >
                                                    <option value="">Selecciona un color</option>
                                                    {VEHICLE_COLORS.map((color) => {
                                                        const style = VEHICLE_COLOR_STYLES[color] || {};
                                                        return (
                                                            <option
                                                                key={color}
                                                                value={color}
                                                                style={{ backgroundColor: style.bg, color: style.color }}
                                                            >
                                                                {color}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                        </>
                                    )}

                                    <div className="form-group span-full manual-textarea">
                                        <label htmlFor="manual-notas">
                                            <FaClipboard style={{ marginRight: 8, color: '#6366f1' }} />
                                            Notas para vigilancia
                                            <span className="qr-help-icon" title="Indicaciones adicionales para caseta."><FaQuestionCircle /></span>
                                        </label>
                                        <textarea
                                            id="manual-notas"
                                            name="notas"
                                            rows={3}
                                            placeholder="Indicaciones relevantes, duración estimada, vehículo, etc."
                                            value={manualAccessForm.notas}
                                            onChange={handleManualAccessInputChange}
                                            disabled={manualAccessSubmitting}
                                            autoCorrect="on"
                                            spellCheck="true"
                                            autoCapitalize="sentences"
                                        />
                                    </div>
                                </div>

                                {manualAccessError && (
                                    <div className="visitas-modal-error" role="alert">{manualAccessError}</div>
                                )}
                            </div>
                            <footer className="vigilancia-modal-footer">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleCloseManualAccessModal}
                                    disabled={manualAccessSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={manualAccessSubmitting}
                                >
                                    {manualAccessSubmitting ? 'Guardando…' : 'Registrar acceso'}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {visitaResultado && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content" style={{ maxWidth: '450px' }}>
                        <div className="vigilancia-modal-body" style={{
                            textAlign: 'center',
                            padding: '50px 40px',
                            background: visitaResultado.success
                                ? 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)'
                                : 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)'
                        }}>
                            <div style={{
                                fontSize: '64px',
                                marginBottom: '25px',
                                color: visitaResultado.success ? '#155724' : '#721c24',
                                fontWeight: 'bold'
                            }}>
                                {visitaResultado.success ? '✓' : '⚠'}
                            </div>
                            <h2 style={{
                                marginBottom: '15px',
                                fontSize: '24px',
                                color: visitaResultado.success ? '#155724' : '#721c24',
                                fontWeight: '700'
                            }}>
                                {visitaResultado.success ? '¡Registro exitoso!' : 'Error al registrar'}
                            </h2>
                            <p style={{
                                margin: '0 0 10px 0',
                                color: visitaResultado.success ? '#1b5e20' : '#600e0e',
                                lineHeight: '1.6',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}>
                                {visitaResultado.mensaje}
                            </p>
                            {visitaResultado.success && (
                                <p style={{
                                    margin: '15px 0 0 0',
                                    fontSize: '13px',
                                    opacity: 0.8,
                                    color: visitaResultado.success ? '#1b5e20' : '#600e0e'
                                }}>
                                    Cerrando en 2 segundos...
                                </p>
                            )}
                        </div>
                        <footer className="vigilancia-modal-footer">
                            <button
                                type="button"
                                className={visitaResultado.success ? 'btn-primary' : 'btn-secondary'}
                                onClick={() => {
                                    setVisitaResultado(null);
                                    if (visitaResultado.success) {
                                        setShowVisitaModal(false);
                                    }
                                }}
                                style={{
                                    flex: '1 1 100%',
                                    fontWeight: '600',
                                    fontSize: '15px'
                                }}
                            >
                                {visitaResultado.success ? 'Continuar' : 'Reintentar'}
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {showVisitaModal && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content">
                        <header className="vigilancia-modal-header">
                            <h2>{editingVisitaId ? 'Editar visita frecuente' : 'Nueva visita frecuente'}</h2>
                            <button type="button" onClick={handleCloseVisitaModal} aria-label="Cerrar" disabled={visitaSubmitting}>×</button>
                        </header>
                        <div className="visita-modal-tabs">
                            <button
                                type="button"
                                className={`visita-tab-btn ${activeVisitaTab === 'info' ? 'active' : ''}`}
                                onClick={() => setActiveVisitaTab('info')}
                            >
                                <FaInfoCircle /> Información
                            </button>
                            <button
                                type="button"
                                className={`visita-tab-btn ${activeVisitaTab === 'horario' ? 'active' : ''}`}
                                onClick={() => setActiveVisitaTab('horario')}
                            >
                                <FaClock /> Horarios de Acceso
                            </button>
                        </div>
                        <form onSubmit={handleSubmitVisita}>
                            <div className="vigilancia-modal-body">
                                {activeVisitaTab === 'info' ? (
                                    <div className="qr-modal-grid">
                                        <div className="form-group">
                                            <label htmlFor="visita-nombre">
                                                <FaUser style={{ marginRight: 8, color: '#6366f1' }} />
                                                Nombre completo *
                                            </label>
                                            <input
                                                id="visita-nombre"
                                                name="nombre"
                                                type="text"
                                                placeholder="Ej: Juan Pérez"
                                                value={visitaForm.nombre}
                                                onChange={handleVisitaInputChange}
                                                disabled={visitaSubmitting}
                                                required
                                                autoCorrect="on"
                                                spellCheck="true"
                                                autoCapitalize="words"
                                            />
                                            <small className="visitas-modal-hint">Nombre del visitante tal como aparece en su identificación oficial.</small>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="visita-telefono">
                                                <FaPhone style={{ marginRight: 8, color: '#6366f1' }} />
                                                Teléfono *
                                            </label>
                                            <input
                                                id="visita-telefono"
                                                name="telefono"
                                                type="tel"
                                                placeholder="Ej: 1234567890"
                                                value={visitaForm.telefono || ''}
                                                onChange={handleVisitaInputChange}
                                                disabled={visitaSubmitting}
                                                required
                                            />
                                            <small className="visitas-modal-hint">Teléfono de 10 dígitos para confirmaciones.</small>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="visita-categoria">
                                                <FaTag style={{ marginRight: 8, color: '#6366f1' }} />
                                                Categoría *
                                            </label>
                                            <select
                                                id="visita-categoria"
                                                name="categoria"
                                                value={visitaForm.categoria}
                                                onChange={handleVisitaInputChange}
                                                disabled={visitaSubmitting}
                                                required
                                            >
                                                <option value="">Selecciona una categoría</option>
                                                {VISITA_CATEGORIES.map((categoria) => (
                                                    <option key={categoria.value} value={categoria.value}>{categoria.label}</option>
                                                ))}
                                            </select>
                                            <small className="visitas-modal-hint">Selecciona el tipo de visita.</small>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="visita-placa">
                                                <FaTruck style={{ marginRight: 8, color: '#6366f1' }} />
                                                Placas (opcional)
                                            </label>
                                            <input
                                                id="visita-placa"
                                                name="placa"
                                                type="text"
                                                placeholder="ABC-1234"
                                                value={visitaForm.placa}
                                                onChange={handleVisitaInputChange}
                                                disabled={visitaSubmitting}
                                                autoCorrect="off"
                                                spellCheck="false"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="visita-color">
                                                <FaPalette style={{ marginRight: 8, color: '#6366f1' }} />
                                                Color del vehículo (opcional)
                                            </label>
                                            <input
                                                id="visita-color"
                                                name="color"
                                                type="text"
                                                placeholder="Gris, Azul..."
                                                value={visitaForm.color}
                                                onChange={handleVisitaInputChange}
                                                disabled={visitaSubmitting}
                                                autoCorrect="on"
                                                spellCheck="true"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="visita-identificacion">
                                                <FaIdCard style={{ marginRight: 8, color: '#6366f1' }} />
                                                Identificación (PDF o imagen)
                                            </label>
                                            <div className="file-upload-wrapper">
                                                <input
                                                    id="visita-identificacion"
                                                    type="file"
                                                    accept="image/*,application/pdf"
                                                    onChange={handleVisitaArchivoChange}
                                                    disabled={visitaSubmitting}
                                                    style={{ display: 'none' }}
                                                />
                                                <button
                                                    type="button"
                                                    className="custom-file-upload"
                                                    onClick={() => document.getElementById('visita-identificacion').click()}
                                                    disabled={visitaSubmitting}
                                                >
                                                    <FaSearch style={{ marginRight: 8 }} />
                                                    {editingVisitaId ? 'Cambiar identificación...' : 'Examinar...'}
                                                </button>
                                                <span className="file-name-display">
                                                    {visitaArchivo ? visitaArchivo.name : (visitaForm.identificacionUrl ? (visitaForm.identificacionName || 'Identificación cargada') : 'No se ha seleccionado archivo.')}
                                                </span>
                                            </div>
                                            <small className="visitas-modal-hint">
                                                {editingVisitaId ? 'Opcional si ya existe una identificación. Máx 5 MB.' : 'Obligatorio para seguridad. Máx 5 MB.'}
                                            </small>
                                        </div>
                                        <div className="form-group span-full" style={{ marginTop: '0.5rem' }}>
                                            <label htmlFor="visita-notas">
                                                <FaClipboard style={{ marginRight: 8, color: '#6366f1' }} />
                                                Notas para vigilancia (opcional)
                                            </label>
                                            <textarea
                                                id="visita-notas"
                                                name="notas"
                                                rows={2}
                                                placeholder="Indicaciones adicionales, consignas, etc."
                                                value={visitaForm.notas}
                                                onChange={handleVisitaInputChange}
                                                disabled={visitaSubmitting}
                                                autoCorrect="on"
                                                spellCheck="true"
                                                autoCapitalize="sentences"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="horario-section">
                                        <div className="qr-modal-info-banner" style={{ margin: '0 0 1rem 0' }}>
                                            <FaClock style={{ marginRight: 8 }} />
                                            Define los días y rangos de horas permitidos (6:00 AM - 10:00 PM). Fuera de estos horarios el QR será rechazado.
                                        </div>
                                        {DIAS_SEMANA_ORDEN.map((dia) => (
                                            <div key={dia} className={`dia-horario-row ${visitaForm.horario[dia].length > 0 ? 'active' : ''}`}>
                                                <div className="dia-header">
                                                    <div className="dia-label">
                                                        {visitaForm.horario[dia].length > 0 ? (
                                                            <FaCheckCircle style={{ color: '#10b981' }} />
                                                        ) : (
                                                            <FaCalendarAlt style={{ color: '#94a3b8' }} />
                                                        )}
                                                        {dia}
                                                    </div>
                                                    {visitaForm.horario[dia].length < 3 && (
                                                        <button 
                                                            type="button" 
                                                            className="btn-add-interval" 
                                                            style={{ width: 'auto', marginTop: 0 }}
                                                            onClick={() => handleAddInterval(dia)}
                                                        >
                                                            <FaPlus /> Rango
                                                        </button>
                                                    )}
                                                </div>
                                                {visitaForm.horario[dia].length > 0 ? (
                                                    <div className="intervalos-list">
                                                        {visitaForm.horario[dia].map((intervalo, idx) => (
                                                            <div key={idx} className="intervalo-item">
                                                                <div className="intervalo-inputs">
                                                                    <select
                                                                        value={intervalo.inicio}
                                                                        onChange={(e) => handleVisitaHorarioChange(dia, idx, 'inicio', e.target.value)}
                                                                    >
                                                                        {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                                                                    </select>
                                                                    <span>a</span>
                                                                    <select
                                                                        value={intervalo.fin}
                                                                        onChange={(e) => handleVisitaHorarioChange(dia, idx, 'fin', e.target.value)}
                                                                    >
                                                                        {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                                                                    </select>
                                                                </div>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn-remove-interval"
                                                                    onClick={() => handleRemoveInterval(dia, idx)}
                                                                >
                                                                    <FaTrash />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <small style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin acceso programado para este día.</small>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {visitaFormError && (
                                    <div className="visitas-modal-error" role="alert" style={{ marginTop: '1rem' }}>{visitaFormError}</div>
                                )}
                            </div>
                            <footer className="vigilancia-modal-footer">
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={visitaSubmitting}
                                >
                                    {visitaSubmitting ? 'Guardando…' : editingVisitaId ? 'Actualizar Datos' : 'Guardar Visita Frecuente'}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {showQrValidationModal && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content vigilancia-modal-compact">
                        <header className="vigilancia-modal-header">
                            <h2><FaCamera /> Validar código de visita</h2>
                            <button type="button" onClick={handleCloseQrValidationModal} aria-label="Cerrar">×</button>
                        </header>
                        <form onSubmit={handleValidarVisita}>
                            <div className="vigilancia-modal-body validation-modal-body">
                                <p className="validation-modal-description">Escanea el código QR con la cámara o captura el código alfanumérico para autorizar la entrada.</p>

                                <div className="validation-modal-controls">
                                    <button
                                        type="button"
                                        className={`validation-modal-camera ${cameraActive ? 'active' : ''}`}
                                        onClick={handleToggleValidationCamera}
                                    >
                                        {cameraActive ? 'Detener cámara' : 'Activar cámara'}
                                    </button>
                                    <span className="validation-modal-hint">Requiere permisos de cámara del navegador.</span>
                                </div>

                                <div className="validation-modal-preview">
                                    {cameraActive ? (
                                        <>
                                            <video ref={validationVideoRef} autoPlay playsInline muted className="validation-video" />
                                            <canvas ref={validationCanvasRef} style={{ display: 'none' }} />
                                        </>
                                    ) : (
                                        <div className="validation-video-placeholder">
                                            <FaQrcode />
                                            <p>Cuando actives la cámara, posiciona el código frente al recuadro para detectarlo.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="validation-code">Código QR capturado</label>
                                    <input
                                        id="validation-code"
                                        ref={validationInputRef}
                                        type="text"
                                        placeholder="Código numérico (8 dígitos) o VIS-0000"
                                        className={scannedHighlight ? 'scanned-highlight' : scannedWarning ? 'scanned-warning' : ''}
                                        value={qrCode}
                                        onChange={(event) => setQrCode(event.target.value)}
                                    />
                                    <small className="validation-modal-hint">Si tu dispositivo no cuenta con cámara, ingresa el código manualmente.</small>
                                </div>

                                {validationError && (
                                    <div className="visitas-modal-error" role="alert">{validationError}</div>
                                )}
                                {qrScannerError && (
                                    <div className="visitas-modal-error" role="alert">{qrScannerError}</div>
                                )}
                                {validationSubmitting && (
                                    <div className="validation-modal-progress" role="status">
                                        Validando código. Espera un momento…
                                    </div>
                                )}
                                {validationResult && (
                                    <div
                                        ref={validationResultRef}
                                        className={`validation-result ${validationResult.success ? 'success' : 'error'}`}
                                        role="status"
                                    >
                                        <div className="validation-result-icon">
                                            {validationResult.success ? <FaCheckCircle /> : <FaTimesCircle />}
                                        </div>
                                        <div className="validation-result-text">
                                            <p className="validation-result-title">{validationResult.message}</p>
                                            {validationResult.visit && (
                                                <div className="validation-result-details">
                                                    {validationResult.visit.visitante && (
                                                        <p><strong>Visitante:</strong> {validationResult.visit.visitante}</p>
                                                    )}
                                                    {validationResult.visit.departamento && (
                                                        <p><strong>Departamento:</strong> {validationResult.visit.departamento}</p>
                                                    )}
                                                    {validationResult.visit.fechaVisitaLabel && (
                                                        <p><strong>Fecha programada:</strong> {validationResult.visit.fechaVisitaLabel}</p>
                                                    )}
                                                    {validationResult.visit.estadoLabel && (
                                                        <p><strong>Estado:</strong> {validationResult.visit.estadoLabel}</p>
                                                    )}
                                                    {typeof validationResult.visit.numeroVisitantes === 'number' && (
                                                        <p><strong>Visitantes autorizados:</strong> {validationResult.visit.numeroVisitantes}</p>
                                                    )}
                                                    {validationResult.visit.validadoEnLabel && (
                                                        <p><strong>Validado:</strong> {validationResult.visit.validadoEnLabel}</p>
                                                    )}
                                                    {(validationResult.visit.observaciones || validationResult.visit.notas) && (
                                                        <p><strong>Observaciones:</strong> {validationResult.visit.observaciones || validationResult.visit.notas}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <footer className="vigilancia-modal-footer">
                                <button type="button" className="btn-secondary" onClick={handleCloseQrValidationModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary" disabled={validationSubmitting}>
                                    {validationSubmitting ? 'Validando…' : 'Validar código'}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {showQrModal && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content vigilancia-modal-compact qr-generation-modal">
                        <header className="vigilancia-modal-header">
                            <h2><FaQrcode /> Generar QR de Invitado</h2>
                            <button type="button" onClick={handleCloseQrModal} aria-label="Cerrar" disabled={qrSubmitting}>×</button>
                        </header>
                        <div
                            className={`qr-modal-info-banner ${showQrInfo ? 'expanded' : 'collapsed'}`}
                            onClick={() => setShowQrInfo(!showQrInfo)}
                        >
                            <div className="qr-info-header">
                                <span className="qr-info-title">
                                    <FaInfoCircle /> Información importante
                                </span>
                                <span className="qr-info-toggle-icon">
                                    {showQrInfo ? <FaChevronUp /> : <FaChevronDown />}
                                </span>
                            </div>
                            {showQrInfo && (
                                <p className="qr-info-content">
                                    El código QR que generes será válido únicamente por <strong>24 horas del día programado</strong> para la visita. Solo puedes programar visitas con <strong>máximo 7 días de anticipación</strong>.
                                </p>
                            )}
                        </div>
                        <form onSubmit={handleSubmitQr} style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            flex: 1, 
                            overflow: 'hidden' 
                        }}>
                            <div className="vigilancia-modal-body qr-modal-body" ref={qrModalBodyRef} style={{
                                flex: 1,
                                overflowY: 'auto'
                            }}>
                                {qrFormError && (
                                    <div className="visitas-modal-error" role="alert">{qrFormError}</div>
                                )}

                                {modulos?.visitas_frecuentes !== false && (
                                    <>
                                        <div className="qr-modal-toggle">
                                            <label htmlFor="qr-usa-frecuente">
                                                <input
                                                    id="qr-usa-frecuente"
                                                    type="checkbox"
                                                    checked={qrForm.usarVisitaFrecuente}
                                                    onChange={handleToggleVisitaFrecuente}
                                                    disabled={qrFormDisabled}
                                                />
                                                <span>
                                                    <FaHistory style={{ marginRight: 8, color: '#6366f1' }} />
                                                    Usar datos de una visita frecuente
                                                </span>
                                            </label>
                                            {!qrForm.usarVisitaFrecuente && visitasFrecuentes.length === 0 && (
                                                <span className="qr-modal-hint">Registra visitas frecuentes para autocompletar más rápido.</span>
                                            )}
                                        </div>

                                        {qrForm.usarVisitaFrecuente && visitasFrecuentes.length > 0 && (
                                            <div className="form-group">
                                                <label htmlFor="qr-visita-frecuente">
                                                    <FaUserCheck style={{ marginRight: 8, color: '#6366f1' }} />
                                                    Selecciona visita frecuente
                                                </label>
                                                <select
                                                    ref={qrVisitaSelectorRef}
                                                    id="qr-visita-frecuente"
                                                    value={qrForm.visitaFrecuenteId}
                                                    onChange={handleQrVisitaFrecuenteSelect}
                                                    disabled={qrFormDisabled}
                                                >
                                                    <option value="">Elige un visitante</option>
                                                    {visitasFrecuentes.map((visita) => (
                                                        <option key={visita.id} value={visita.id}>
                                                            {visita.nombre || 'Visitante sin nombre'}{visita.placa ? ` · ${visita.placa}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="qr-modal-hint">Al seleccionar se llenarán los datos conocidos automáticamente.</span>
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="qr-modal-grid">
                                    <div className="form-group" style={{ gridColumn: modulos?.residente_qr_simplificado === true ? 'span 2' : 'span 1' }}>
                                        <label htmlFor="qr-nombre">
                                            <FaUser style={{ marginRight: 8, color: '#6366f1' }} />
                                            Nombre del <span style={{ whiteSpace: 'nowrap' }}>visitante *</span>
                                            <span className="qr-help-icon" title="Nombre del visitante tal como aparece en su identificación."><FaQuestionCircle /></span>
                                        </label>
                                        <input
                                            id="qr-nombre"
                                            name="nombre"
                                            type="text"
                                            placeholder="Ej: Juan Pérez"
                                            value={qrForm.nombre}
                                            onChange={handleQrFormChange}
                                            disabled={qrFormDisabled}
                                            required
                                        />
                                    </div>
                                    
                                    {modulos?.residente_qr_simplificado !== true && (
                                        <>
                                            <div className="form-group">
                                                <label htmlFor="qr-telefono">
                                                    <FaPhone style={{ marginRight: 8, color: '#6366f1' }} />
                                                    Teléfono de <span style={{ whiteSpace: 'nowrap' }}>contacto *</span>
                                                    <span className="qr-help-icon" title="Número al que se puede contactar al visitante."><FaQuestionCircle /></span>
                                                </label>
                                                <input
                                                    id="qr-telefono"
                                                    name="telefono"
                                                    type="tel"
                                                    placeholder="4421234567"
                                                    value={qrForm.telefono}
                                                    onChange={handleQrFormChange}
                                                    disabled={qrFormDisabled}
                                                    required={modulos?.residente_qr_simplificado !== true}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="qr-numero">
                                                    <FaUsers style={{ marginRight: 8, color: '#6366f1' }} />
                                                    Número de visitantes *
                                                    <span className="qr-help-icon" title="Cantidad total de personas que ingresarán con esta invitación."><FaQuestionCircle /></span>
                                                </label>
                                                <input
                                                    id="qr-numero"
                                                    name="numeroVisitantes"
                                                    type="number"
                                                    min="1"
                                                    max="20"
                                                    value={qrForm.numeroVisitantes}
                                                    onChange={handleQrFormChange}
                                                    disabled={qrFormDisabled}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {!useSavedSchedule ? (
                                        <div className="form-group">
                                            <label htmlFor="qr-fecha">
                                                <FaCalendarAlt style={{ marginRight: 8, color: '#6366f1' }} />
                                                Fecha de la visita *
                                                <span className="qr-help-icon" title="Día programado para el acceso del invitado."><FaQuestionCircle /></span>
                                            </label>
                                            <input
                                                id="qr-fecha"
                                                name="fecha"
                                                type="date"
                                                value={qrForm.fecha}
                                                onChange={handleQrFormChange}
                                                disabled={qrFormDisabled}
                                                required
                                            />
                                        </div>
                                    ) : (
                                        <div className="form-group qr-full-width">
                                            <div className="qr-schedule-active-banner">
                                                <div className="banner-main-wrapper">
                                                    <div className="banner-icon"><FaHistory /></div>
                                                    <div className="banner-content">
                                                        <strong>Horario guardado activo</strong>
                                                        <p>El QR será válido por 7 días siguiendo los rangos configurados en el perfil.</p>
                                                        <button 
                                                            type="button" 
                                                            className="banner-detail-toggle"
                                                            onClick={() => setShowBannerScheduleDetail(!showBannerScheduleDetail)}
                                                        >
                                                            {showBannerScheduleDetail ? <FaChevronUp /> : <FaChevronDown />}
                                                            {showBannerScheduleDetail ? 'Ocultar horarios' : 'Ver horarios configurados'}
                                                        </button>
                                                    </div>
                                                    <button 
                                                        type="button" 
                                                        className="banner-action-btn"
                                                        onClick={() => {
                                                            setUseSavedSchedule(false);
                                                            setHasDecidedSchedule(false);
                                                        }}
                                                    >
                                                        Cambiar
                                                    </button>
                                                </div>

                                                {showBannerScheduleDetail && (
                                                    <div className="banner-schedule-expanded">
                                                        <div className="schedule-detail-grid">
                                                            {(() => {
                                                                const visita = visitasFrecuentes.find(v => v.id === qrForm.visitaFrecuenteId);
                                                                const horario = visita?.horario;
                                                                if (!horario) return <p className="no-schedule-msg">No hay horarios definidos.</p>;

                                                                const DIAS_MAP = {
                                                                    lunes: 'Lun', martes: 'Mar', miercoles: 'Mie', 
                                                                    jueves: 'Jue', viernes: 'Vie', sabado: 'Sab', domingo: 'Dom'
                                                                };

                                                                return DIAS_SEMANA_ORDEN.map(dia => {
                                                                    const rangos = horario[dia];
                                                                    if (!Array.isArray(rangos) || rangos.length === 0) return null;
                                                                    return (
                                                                        <div key={dia} className="schedule-detail-row">
                                                                            <span className="day-label">{DIAS_MAP[dia]}</span>
                                                                            <span className="time-ranges">{rangos.map(r => `${r.inicio}-${r.fin}`).join(', ')}</span>
                                                                        </div>
                                                                    );
                                                                });
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {modulos?.residente_qr_simplificado !== true && (
                                        <>
                                            <div className="form-group">
                                                <label htmlFor="qr-categoria">
                                                    <FaTag style={{ marginRight: 8, color: '#6366f1' }} />
                                                    Categoría *
                                                    <span className="qr-help-icon" title="Clasifica la visita para reportes y control (ej. proveedor, invitado, servicio)."><FaQuestionCircle /></span>
                                                </label>
                                                <select
                                                    id="qr-categoria"
                                                    name="categoria"
                                                    value={qrForm.categoria}
                                                    onChange={handleQrFormChange}
                                                    disabled={qrFormDisabled}
                                                    required={modulos?.residente_qr_simplificado !== true}
                                                >
                                                    <option value="">Selecciona una categoría</option>
                                                    {VISITA_CATEGORIES.map((cat) => (
                                                        <option key={cat.value} value={cat.value}>
                                                            {cat.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="qr-transporte">
                                                    <FaTruck style={{ marginRight: 8, color: '#6366f1' }} />
                                                    Medio de transporte *
                                                    <span className="qr-help-icon" title="Indica cómo llegará el invitado para facilitar su acceso."><FaQuestionCircle /></span>
                                                </label>
                                                <select
                                                    id="qr-transporte"
                                                    name="tipoTransporte"
                                                    value={qrForm.tipoTransporte}
                                                    onChange={handleQrFormChange}
                                                    disabled={qrFormDisabled}
                                                    required={modulos?.residente_qr_simplificado !== true}
                                                >
                                                    <option value="En vehiculo">En vehículo</option>
                                                    <option value="En moto">En moto</option>
                                                    <option value="A pie">A pie</option>
                                                </select>
                                            </div>

                                            {qrForm.tipoTransporte !== 'A pie' && (
                                                <>
                                                    <div className="form-group">
                                                        <label htmlFor="qr-placa">
                                                            <FaIdCard style={{ marginRight: 8, color: '#6366f1' }} />
                                                            {qrForm.tipoTransporte === 'En moto' ? 'Placas de la moto *' : 'Placas del vehículo *'}
                                                            <span className="qr-help-icon" title="Placas del vehículo del invitado (requerido)."><FaQuestionCircle /></span>
                                                        </label>
                                                        <input
                                                            id="qr-placa"
                                                            name="placa"
                                                            type="text"
                                                            placeholder="ABC-123"
                                                            value={qrForm.placa}
                                                            onChange={handleQrFormChange}
                                                            disabled={qrFormDisabled}
                                                            required={qrForm.tipoTransporte !== 'A pie'}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label htmlFor="qr-color">
                                                            <FaPalette style={{ marginRight: 8, color: '#6366f1' }} />
                                                            {qrForm.tipoTransporte === 'En moto' ? 'Color de la moto *' : 'Color del vehículo *'}
                                                            <span className="qr-help-icon" title="Color o descripción del vehículo (requerido)."><FaQuestionCircle /></span>
                                                        </label>
                                                        <select
                                                            id="qr-color"
                                                            name="color"
                                                            value={qrForm.color}
                                                            onChange={handleQrFormChange}
                                                            disabled={qrFormDisabled}
                                                            required={qrForm.tipoTransporte !== 'A pie'}
                                                        >
                                                            <option value="">Selecciona un color</option>
                                                            {VEHICLE_COLORS.map((color) => (
                                                                <option key={color} value={color}>
                                                                    {color}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                    <div className="form-group">
                                        <label htmlFor="qr-departamento">
                                            <FaHome style={{ marginRight: 8, color: '#6366f1' }} />
                                            Departamento / Lote *
                                            <span className="qr-help-icon" title="Número o identificador del residente anfitrión (ej: A-333, Lote 15)."><FaQuestionCircle /></span>
                                        </label>
                                        <input
                                            id="qr-departamento"
                                            name="departamento"
                                            type="text"
                                            list={!isResident ? "casas-qr-suggestions" : undefined}
                                            placeholder="Ej: A-333, Lote 15"
                                            value={qrForm.departamento}
                                            onChange={handleQrFormChange}
                                            disabled={qrFormDisabled || isResident}
                                            required
                                        />
                                        {!isResident && Array.isArray(casasList) && casasList.length > 0 && (
                                            <datalist id="casas-qr-suggestions">
                                                {casasList.map((casa) => {
                                                    const num = casa.numero || casa.numeroCasa || casa.id;
                                                    const residentName = casa.nombre_residente || casa.propietario || '';
                                                    return (
                                                        <option key={casa.id || num} value={num}>
                                                            {residentName ? `${num} — ${residentName}` : num}
                                                        </option>
                                                    );
                                                })}
                                            </datalist>
                                        )}
                                        {isResident && Array.isArray(misCasas) && misCasas.length > 1 && (
                                            <select
                                                className="form-control"
                                                style={{ marginTop: '6px', fontSize: '0.85rem', borderColor: '#cbd5e1' }}
                                                value={qrForm.departamento}
                                                onChange={(e) => setQrForm(prev => ({ ...prev, departamento: e.target.value }))}
                                                disabled={qrFormDisabled}
                                                title="Seleccionar cuál de tus propiedades emite la invitación"
                                            >
                                                {misCasas.map((c) => {
                                                    const num = c.numero || c.numeroCasa || c.id;
                                                    return (
                                                        <option key={c.id || num} value={num}>
                                                            Propiedad asignada: {num}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        )}
                                        <span className="qr-modal-hint">Número de casa, departamento o lote del residente que invita.</span>
                                    </div>
                                    
                                    {modulos?.residente_qr_simplificado !== true && (
                                        <>
                                            <div className="form-group">
                                                <label htmlFor="qr-correo">
                                                    <FaEnvelope style={{ marginRight: 8, color: '#6366f1' }} />
                                                    Correo para enviar el QR *
                                                    <span className="qr-help-icon" title="Correo del residente donde se enviará el QR."><FaQuestionCircle /></span>
                                                </label>
                                                <input
                                                    id="qr-correo"
                                                    name="correoDestino"
                                                    type="email"
                                                    placeholder="correo@residente.com"
                                                    value={qrForm.correoDestino}
                                                    onChange={handleQrFormChange}
                                                    disabled={qrFormDisabled}
                                                    required={modulos?.residente_qr_simplificado !== true}
                                                />
                                                <span className="qr-modal-hint">Usaremos este correo para enviarte la imagen del código QR.</span>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="qr-correo-invitado">
                                                    <FaEnvelope style={{ marginRight: 8, color: '#6366f1' }} />
                                                    Correo del invitado
                                                    <span className="qr-help-icon" title="Correo del invitado para enviarle una copia del QR (opcional)."><FaQuestionCircle /></span>
                                                </label>
                                                <input
                                                    id="qr-correo-invitado"
                                                    name="correoInvitado"
                                                    type="email"
                                                    placeholder="correo@invitado.com"
                                                    value={qrForm.correoInvitado}
                                                    onChange={handleQrFormChange}
                                                    disabled={qrFormDisabled}
                                                />
                                                <span className="qr-modal-hint">Si lo capturamos, también enviaremos el QR directamente al invitado.</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {!useSavedSchedule && modulos && (modulos.qr_multi_acceso || modulos.qr_contador_escaneos || modulosLoading) && (
                                    <div className="advanced-qr-options" style={{
                                        marginTop: '1.25rem',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '1rem',
                                        border: '1px solid #cbd5e1',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                                        overflow: 'hidden'
                                    }} ref={advancedQrOptionsRef}>
                                        <button 
                                            type="button" 
                                            onClick={() => {
                                                if (isMobile) {
                                                    setShowAdvancedSubModal(true);
                                                } else {
                                                    setShowAdvancedQrOptions(prev => {
                                                        const nextState = !prev;
                                                        if (nextState) {
                                                            setTimeout(() => {
                                                                 advancedQrOptionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                            }, 100);
                                                        }
                                                        return nextState;
                                                    });
                                                }
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '1rem 1.25rem',
                                                border: 'none',
                                                background: '#eff6ff',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                color: '#1e40af',
                                                fontWeight: '800',
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            <span style={{ fontSize: '0.92rem', color: '#1e3a8a' }}>
                                                {isMobile ? '⚙ Configuración Avanzada' : (showAdvancedQrOptions ? '▲ Opciones avanzadas de Acceso' : '▼ Opciones avanzadas de Acceso')}
                                            </span>
                                            {isMobile && <FaChevronRight />}
                                        </button>
                                        
                                        {!isMobile && showAdvancedQrOptions && (
                                            <div style={{ padding: '0 20px 20px 20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                                                <div style={{
                                                    backgroundColor: '#eff6ff',
                                                    color: '#1e40af',
                                                    padding: '10px 14px',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    marginBottom: '20px',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: '10px'
                                                }}>
                                                    <FaInfoCircle style={{ marginTop: '2px', flexShrink: 0 }} />
                                                    <span><strong>Nota importante:</strong> Debes configurar solo una de las dos opciones (Multi-Acceso o Límite de escaneos). Al usar una, la otra se bloqueará automáticamente.</span>
                                                </div>

                                                <div style={{ 
                                                    display: 'grid', 
                                                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                                                    gap: '20px',
                                                    alignItems: 'start' 
                                                }}>
                                                    {modulos.qr_multi_acceso && (
                                                        <div className="form-group" style={{ margin: 0, opacity: parseInt(qrForm.limiteEscaneos || '1') > 1 ? 0.5 : 1 }}>
                                                            <label style={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: '10px', 
                                                                cursor: parseInt(qrForm.limiteEscaneos || '1') > 1 ? 'not-allowed' : 'pointer',
                                                                fontWeight: '600', 
                                                                color: '#334155',
                                                                minHeight: '32px',
                                                                marginBottom: '5px'
                                                            }}>
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={qrForm.esMultiAcceso}
                                                                    disabled={parseInt(qrForm.limiteEscaneos || '1') > 1 || qrFormDisabled}
                                                                    onChange={(e) => setQrForm(prev => ({ 
                                                                        ...prev, 
                                                                        esMultiAcceso: e.target.checked,
                                                                        limiteEscaneos: e.target.checked ? '1' : prev.limiteEscaneos 
                                                                    }))}
                                                                    style={{ width: '18px', height: '18px', cursor: 'inherit', margin: 0 }}
                                                                />
                                                                Permitir Multi-Acceso
                                                            </label>
                                                            <div style={{ visibility: qrForm.esMultiAcceso ? 'visible' : 'hidden', paddingLeft: '28px', marginTop: '-10px' }}>
                                                                <select 
                                                                    name="duracionHoras"
                                                                    value={qrForm.duracionHoras}
                                                                    onChange={handleQrFormChange}
                                                                    disabled={qrFormDisabled}
                                                                    style={{ 
                                                                        width: '100%', 
                                                                        padding: '8px', 
                                                                        borderRadius: '8px', 
                                                                        border: '1px solid #cbd5e1',
                                                                        backgroundColor: 'white',
                                                                        fontSize: '0.9rem',
                                                                        height: '38px'
                                                                    }}
                                                                >
                                                                    {[1, 2, 4, 8, 12, 24].map(h => (
                                                                        <option key={h} value={h}>{h} {h === 1 ? 'hora' : 'horas'}</option>
                                                                    ))}
                                                                </select>
                                                                <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginTop: '5px' }}>
                                                                    Duración de validez
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {modulos.qr_contador_escaneos && (
                                                        <div className="form-group" style={{ margin: 0, opacity: qrForm.esMultiAcceso ? 0.5 : 1 }}>
                                                            <label htmlFor="qr-limite" style={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: '8px', 
                                                                fontWeight: '600', 
                                                                marginBottom: '5px', 
                                                                color: '#334155',
                                                                minHeight: '32px'
                                                            }}>
                                                                <FaUsers style={{ color: '#6366f1' }} /> Cupo / Límite de escaneos
                                                            </label>
                                                            <div>
                                                                <input 
                                                                    id="qr-limite"
                                                                    type="number"
                                                                    name="limiteEscaneos"
                                                                    min="1"
                                                                    max="15"
                                                                    value={qrForm.limiteEscaneos}
                                                                    disabled={qrForm.esMultiAcceso || qrFormDisabled}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setQrForm(prev => ({
                                                                            ...prev,
                                                                            limiteEscaneos: val,
                                                                            esMultiAcceso: parseInt(val || '1') > 1 ? false : prev.esMultiAcceso
                                                                        }));
                                                                    }}
                                                                    style={{ 
                                                                        width: '100%', 
                                                                        padding: '8px', 
                                                                        borderRadius: '8px', 
                                                                        border: '1px solid #cbd5e1',
                                                                        boxSizing: 'border-box',
                                                                        fontSize: '0.9rem',
                                                                        height: '38px',
                                                                        marginTop: 0,
                                                                        backgroundColor: qrForm.esMultiAcceso ? '#f1f5f9' : 'white',
                                                                        cursor: qrForm.esMultiAcceso ? 'not-allowed' : 'text'
                                                                    }}
                                                                    placeholder="Ej: Entre 2 y 15"
                                                                />
                                                                <span style={{ fontSize: '0.7rem', color: '#64748b', display: 'block', marginTop: '5px' }}>
                                                                    Número máximo de usos del QR
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="form-group" style={{ marginTop: '1.25rem' }}>
                                    <label htmlFor="qr-observaciones">
                                        <FaClipboard style={{ marginRight: 8, color: '#6366f1' }} />
                                        Observaciones para vigilancia
                                        <span className="qr-help-icon" title="Instrucciones para la caseta: duración estimada, lugar a visitar, notas especiales."><FaQuestionCircle /></span>
                                    </label>
                                    <textarea
                                        id="qr-observaciones"
                                        name="observaciones"
                                        rows={3}
                                        placeholder="Agrega indicaciones adicionales, duración estimada, etc."
                                        value={qrForm.observaciones}
                                        onChange={handleQrFormChange}
                                        disabled={qrFormDisabled}
                                    />
                                </div>

                                {qrResult && (() => {
                                    // 1. Formatear Fecha y Horario en Español México
                                    let fechaTexto = 'Hoy';
                                    if (qrForm.fecha) {
                                        try {
                                            const [y, m, d] = qrForm.fecha.split('-').map(Number);
                                            const dt = new Date(y, m - 1, d, 12, 0, 0);
                                            const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                                            const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                                            fechaTexto = `${dias[dt.getDay()]}, ${d} de ${meses[m - 1]} de ${y}`;
                                        } catch (_) {
                                            fechaTexto = qrForm.fecha;
                                        }
                                    }

                                    let horaTexto = 'Horario abierto';
                                    if (qrForm.hora) {
                                        try {
                                            const [h, min] = qrForm.hora.split(':').map(Number);
                                            const ampm = h >= 12 ? 'p.m.' : 'a.m.';
                                            const h12 = h % 12 || 12;
                                            horaTexto = `${String(h12).padStart(2, '0')}:${String(min).padStart(2, '0')} ${ampm}`;
                                        } catch (_) {
                                            horaTexto = qrForm.hora;
                                        }
                                    }

                                    const deptoDestino = qrForm.departamento || ownerDepartamento || userData?.casa || 'mi vivienda';
                                    const fraccNombre = userData?.fraccionamientoNombre || 'el fraccionamiento';
                                    const transporteTxt = qrForm.tipoTransporte || (qrForm.placa ? 'En vehículo' : 'A pie');
                                    const tienePlacas = qrForm.placa && qrForm.placa.trim() && qrForm.placa.trim().toLowerCase() !== 'sin vehículo';
                                    const vehiculoDetalle = tienePlacas ? ` (Placas: ${qrForm.placa.trim()}${qrForm.color && qrForm.color !== 'A pie' ? ` - ${qrForm.color.trim()}` : ''})` : '';
                                    const urlImagenQr = qrResult.qrCardUrl || `https://api.qrserver.com/v1/create-qr-code/?size=350x350&data=${qrResult.codigo}`;
                                    const reglasTextoInvitado = qrResult.reglasCirculacionTexto || `🛡️ *Reglas de Seguridad y Circulación:*
• *Velocidad máxima:* 20 km/h en calles / 10 km/h en estacionamientos.
• *Estacionamiento:* Respetar cajones asignados o de visitas; no obstruir accesos ni cocheras.
• *Circulación:* Ceder el paso a peatones siempre y respetar señalamientos viales.
• *Niveles de ruido:* Mantener volumen moderado (máx. 55 dB día / 50 dB noche).`;

                                    const mensajeInvitado = `¡Hola${qrForm.nombre ? ' ' + qrForm.nombre.trim() : ''}! 👋
Te comparto tu pase de acceso oficial para ingresar a *${fraccNombre}*.

📋 *Detalles de tu Visita:*
• *Destino:* Casa ${deptoDestino}
• *Fecha:* ${fechaTexto}
• *Horario:* ${horaTexto}
• *Medio de transporte:* ${transporteTxt}${vehiculoDetalle}
• *Código / Folio de acceso:* *${qrResult.codigo}*

📲 *Tu Código QR de Acceso:*
Abre tu tarjeta de acceso con código QR en alta resolución aquí:
${urlImagenQr}

${reglasTextoInvitado}

🛡️ *Instrucciones en Caseta:*
Al llegar a la caseta de vigilancia, muestra el código QR desde tu celular o proporciona tu folio de acceso (*${qrResult.codigo}*) al guardia de seguridad para ingresar sin contratiempos. ¡Te esperamos!`;

                                    const telInvitadoLimpio = String(qrForm.telefono || '').replace(/\D/g, '');
                                    const enlaceCompartirInvitado = telInvitadoLimpio.length === 10
                                        ? `https://wa.me/52${telInvitadoLimpio}?text=${encodeURIComponent(mensajeInvitado)}`
                                        : `https://wa.me/?text=${encodeURIComponent(mensajeInvitado)}`;

                                    return (
                                        <div className="qr-modal-preview" role="status">
                                            <div className="qr-modal-preview-image">
                                                <img src={qrResult.qrDataUrl} alt="Código QR generado" />
                                                <div className="qr-modal-actions-side">
                                                    {/* Botones WhatsApp Oficial y Compartir (Condicionados al Módulo) */}
                                                    {modulos?.vigilancia_botones_whatsapp_qr !== false && (
                                                        <>
                                                            <a
                                                                href={`https://wa.me/522223256532?text=${encodeURIComponent(`Hola, por favor envíame la tarjeta QR de mi pase de acceso con folio ${qrResult.codigo}`)}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn-qr-save btn-qr-whatsapp"
                                                                title="Recibir tarjeta oficial con QR en mi WhatsApp"
                                                            >
                                                                <FaWhatsapp /> Enviar a mi WhatsApp
                                                            </a>

                                                            <a
                                                                href={enlaceCompartirInvitado}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn-qr-save btn-qr-share"
                                                                title="Reenviar pase de acceso detallado directamente a tu invitado"
                                                            >
                                                                <FaShareAlt /> Compartir con invitado
                                                            </a>
                                                        </>
                                                    )}

                                                    <button
                                                        type="button"
                                                        className="btn-qr-save"
                                                        onClick={() => handleDownloadQR()}
                                                        title="Guardar como imagen"
                                                    >
                                                        <FaDownload /> Guardar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-qr-save btn-qr-print"
                                                        onClick={handlePrintQR}
                                                        title="Imprimir QR"
                                                    >
                                                        <FaPrint /> Imprimir
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="qr-modal-preview-info">
                                                <p>
                                                    Código de referencia:&nbsp;
                                                    <strong>{qrResult.codigo}</strong>
                                                </p>
                                                <p>
                                                    {qrResult.emailEnviado
                                                        ? (() => {
                                                            const destinatarios = Array.isArray(qrResult.emailsEnviadosA) && qrResult.emailsEnviadosA.length > 0
                                                                ? qrResult.emailsEnviadosA
                                                                : [qrResult.destinatarioEmail || qrForm.correoDestino, qrResult.invitadoEmail].filter(Boolean);
                                                            const list = Array.from(new Set(destinatarios));
                                                            return list.length > 0
                                                                ? `Correo enviado a ${list.join(', ')}.`
                                                                : 'Correo enviado correctamente.';
                                                        })()
                                                        : `El correo no pudo enviarse: ${qrResult.emailError || 'verifica la configuración de correo.'}`}
                                                </p>
                                                <span className="qr-modal-hint">Puedes reenviar este código desde tu correo o cerrar para generar otro.</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                            <footer className="vigilancia-modal-footer qr-modal-footer" style={{ flexShrink: 0 }}>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleCloseQrModal}
                                    disabled={qrSubmitting}
                                >
                                    Cerrar
                                </button>
                                {!qrResult && (
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={qrSubmitting}
                                    >
                                        {qrSubmitting ? 'Generando…' : 'Generar y enviar QR'}
                                    </button>
                                )}
                                {qrResult && (
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        onClick={handleCloseQrModal}
                                        disabled={qrSubmitting}
                                    >
                                        Listo
                                    </button>
                                )}
                            </footer>
                        </form>
                        
                        {/* SUB-MODAL MÓVIL PARA OPCIONES AVANZADAS */}
                        {showAdvancedSubModal && (
                            <div className="advanced-options-overlay">
                                <div className="advanced-options-card">
                                    <div className="advanced-options-header">
                                        <h3>Configuración Avanzada</h3>
                                        <button onClick={() => setShowAdvancedSubModal(false)} className="close-sub-btn">✕</button>
                                    </div>
                                    <div className="advanced-options-body">
                                        <p className="submodal-hint">Ajusta los parámetros de acceso especiales para este código QR.</p>
                                        
                                        {modulos.qr_multi_acceso && (
                                            <div className="submodal-group">
                                                <div className="submodal-row">
                                                    <div className="submodal-label-group">
                                                        <FaUsers className="submodal-icon" />
                                                        <span>Multi-Acceso</span>
                                                    </div>
                                                    <div 
                                                        className={`zen-switch ${qrForm.esMultiAcceso ? 'active' : ''}`}
                                                        style={{ opacity: parseInt(qrForm.limiteEscaneos || '1') > 1 ? 0.3 : 1 }}
                                                        onClick={() => {
                                                            if (parseInt(qrForm.limiteEscaneos || '1') <= 1) {
                                                                setQrForm(prev => ({ 
                                                                    ...prev, 
                                                                    esMultiAcceso: !prev.esMultiAcceso,
                                                                    limiteEscaneos: !prev.esMultiAcceso ? '1' : prev.limiteEscaneos 
                                                                }));
                                                            }
                                                        }}
                                                    >
                                                        <div className="zen-switch-handle"></div>
                                                    </div>
                                                </div>
                                                {qrForm.esMultiAcceso && (
                                                    <div className="submodal-details">
                                                        <label>Validez del código:</label>
                                                        <select 
                                                            name="duracionHoras"
                                                            value={qrForm.duracionHoras}
                                                            onChange={handleQrFormChange}
                                                            className="submodal-select"
                                                        >
                                                            {[1, 2, 4, 8, 12, 24].map(h => (
                                                                <option key={h} value={h}>{h} {h === 1 ? 'hora' : 'horas'}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {modulos.qr_contador_escaneos && (
                                            <div className="submodal-group">
                                                <div className="submodal-label-group" style={{ marginBottom: '12px' }}>
                                                    <FaUserFriends className="submodal-icon" />
                                                    <span>Cupo / Límite de personas</span>
                                                </div>
                                                <div className="submodal-counter-row" style={{ opacity: qrForm.esMultiAcceso ? 0.3 : 1 }}>
                                                    <button 
                                                        type="button"
                                                        disabled={qrForm.esMultiAcceso || qrFormDisabled}
                                                        onClick={() => setQrForm(prev => ({ 
                                                            ...prev, 
                                                            limiteEscaneos: String(Math.max(1, parseInt(prev.limiteEscaneos || '1') - 1)) 
                                                        }))}
                                                    >-</button>
                                                    <span className="counter-val">{qrForm.limiteEscaneos || '1'}</span>
                                                    <button 
                                                        type="button"
                                                        disabled={qrForm.esMultiAcceso || qrFormDisabled}
                                                        onClick={() => setQrForm(prev => ({ 
                                                            ...prev, 
                                                            limiteEscaneos: String(Math.min(15, parseInt(prev.limiteEscaneos || '1') + 1)) 
                                                        }))}
                                                    >+</button>
                                                </div>
                                                <p className="counter-hint">Máximo 15 personas por código.</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="advanced-options-footer">
                                        <button type="button" className="confirm-sub-btn" onClick={() => setShowAdvancedSubModal(false)}>Listo</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {visitHistoryModalOpen && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content vigilancia-modal-large">
                        <header className="vigilancia-modal-header">
                            <h2><FaHistory /> Historial de visitas</h2>
                            <button type="button" onClick={handleCloseVisitHistoryModal} aria-label="Cerrar">×</button>
                        </header>
                        <div className="vigilancia-modal-body visit-history-body">
                            <div className="visit-history-toolbar" style={{ 
                                display: 'flex', 
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: '15px', 
                                alignItems: isMobile ? 'stretch' : 'center', 
                                marginBottom: '25px',
                                backgroundColor: '#f1f5f9',
                                padding: '15px',
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0'
                            }}>
                                <div className="search-box" style={{ flex: '1', margin: 0, position: 'relative' }}>
                                    <FaSearch style={{ 
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#94a3b8',
                                        pointerEvents: 'none',
                                        fontSize: '0.85rem'
                                    }} />
                                    <input
                                        type="text"
                                        placeholder="Buscar código, nombre, depto..."
                                        value={visitHistorySearch}
                                        onChange={(e) => setVisitHistorySearch(e.target.value)}
                                        style={{ 
                                            paddingLeft: '35px', 
                                            borderRadius: '12px', 
                                            border: '1px solid #cbd5e1',
                                            height: '40px',
                                            fontSize: '0.85rem',
                                            width: '100%',
                                            backgroundColor: '#ffffff',
                                            boxSizing: 'border-box',
                                            transition: 'all 0.2s ease'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '30px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div className="date-filter-box" style={{ position: 'relative' }}>
                                        <input
                                            type="date"
                                            value={visitHistoryDate}
                                            onChange={(e) => setVisitHistoryDate(e.target.value)}
                                            style={{
                                                height: '40px',
                                                borderRadius: '12px',
                                                border: '1px solid #cbd5e1',
                                                padding: '0 10px',
                                                fontSize: '0.85rem',
                                                backgroundColor: '#ffffff',
                                                cursor: 'pointer',
                                                color: '#334155',
                                                boxSizing: 'border-box',
                                                width: '100%'
                                            }}
                                            title="Filtrar por fecha específica"
                                        />
                                    </div>

                                    <label style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px', 
                                        fontSize: '0.85rem', 
                                        color: '#475569',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        padding: '0 10px',
                                        height: '40px',
                                        backgroundColor: '#ffffff',
                                        borderRadius: '12px',
                                        border: '1px solid #cbd5e1'
                                    }}>
                                        <input 
                                            type="checkbox" 
                                            checked={visitHistoryInsideOnly}
                                            onChange={(e) => setVisitHistoryInsideOnly(e.target.checked)}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        <span>Solo dentro</span>
                                    </label>
                                </div>
                            </div>

                            {visitHistoryLoading ? (
                                <div className="visitas-hint visitas-hint-loading">
                                    <FaSearch />
                                    <span>Cargando datos del historial…</span>
                                </div>
                            ) : visitHistoryError ? (
                                <div className="visitas-hint visitas-hint-error">
                                    <FaExclamationTriangle />
                                    <span>{visitHistoryError}</span>
                                </div>
                            ) : filteredVisitHistory.length === 0 ? (
                                <div className="visitas-hint visitas-hint-warning" style={{ flexDirection: 'column', gap: '15px', padding: '40px 0' }}>
                                    <FaInfoCircle style={{ fontSize: '2rem', opacity: 0.5 }} />
                                    <span style={{ textAlign: 'center' }}>
                                        {visitHistorySearch || visitHistoryDate || visitHistoryInsideOnly 
                                            ? 'No se encontraron resultados que coincidan con los filtros aplicados.'
                                            : 'No hay registros de visitas disponibles actualmente.'}
                                    </span>
                                    {(visitHistorySearch || visitHistoryDate || visitHistoryInsideOnly) && (
                                        <button 
                                            className="btn-vigilancia-ghost" 
                                            style={{ fontSize: '0.8rem', color: '#4338ca', textDecoration: 'underline' }}
                                            onClick={() => {
                                                setVisitHistorySearch('');
                                                setVisitHistoryDate('');
                                                setVisitHistoryInsideOnly(false);
                                            }}
                                        >
                                            Limpiar filtros
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-container visit-history-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th style={{ width: isMobile ? '30%' : '20%' }}>Código</th>
                                                <th style={{ width: isMobile ? '35%' : '25%' }}>Visitante</th>
                                                <th style={{ width: isMobile ? '35%' : '25%' }}>Ubicación y Fecha</th>
                                                <th style={{ display: isMobile ? 'none' : 'table-cell', width: '15%' }}>Estado</th>
                                                <th style={{ display: isMobile ? 'none' : 'table-cell', width: '15%' }}>Generado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredVisitHistory
                                                .slice((historyPage - 1) * HISTORY_ITEMS_PER_PAGE, historyPage * HISTORY_ITEMS_PER_PAGE)
                                                .map((registro) => {
                                                    const estadoSlug = String(registro.estado || '').replace(/\s+/g, '-').toLowerCase();
                                                    return (
                                                        <tr key={registro.id}>
                                                            <td style={{ padding: '10px 8px' }}>
                                                                <strong style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', color: '#1e293b' }}>{registro.codigo}</strong>
                                                                <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '2px' }}>
                                                                    {registro.numeroVisitantes === 1 ? '1 pers. autoriz.' : `${registro.numeroVisitantes} pers. autoriz.`}
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '10px 8px' }}>
                                                                <div style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', fontWeight: '600', color: '#334155' }}>{registro.visitante}</div>
                                                                {registro.correosDestino.length > 0 && (
                                                                    <div style={{ fontSize: '0.65rem', color: '#94a3b8', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={registro.correosDestino.join(', ')}>
                                                                        {registro.correosDestino[0]}
                                                                    </div>
                                                                )}
                                                            </td>
                                                <td style={{ padding: '10px 8px' }}>
                                                                <div style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', fontWeight: '700', color: '#4338ca' }}>
                                                                    {registro.numeroCasa || registro.departamento || 'S/A'}
                                                                </div>
                                                                <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>
                                                                    {registro.entradaRealIso ? (
                                                                        (() => {
                                                                            const d = new Date(registro.entradaRealIso);
                                                                            const dia = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '');
                                                                            const hora = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: true });
                                                                            return `Entrada: ${dia}, ${hora}`;
                                                                        })()
                                                                    ) : (
                                                                        `Prog: ${registro.fechaVisitaLabel}`
                                                                    )}
                                                                </div>
                                                                {isMobile && (
                                                                    <div style={{ marginTop: '4px' }}>
                                                                        <span className={`status-pill status-${estadoSlug || 'pendiente'}`} style={{ fontSize: '0.6rem', padding: '1px 5px' }}>
                                                                            {registro.estadoLabel}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td style={{ display: isMobile ? 'none' : 'table-cell', padding: '10px 8px' }}>
                                                                <span className={`status-pill status-${estadoSlug || 'pendiente'}`} style={{ fontSize: '0.7rem' }}>
                                                                    {registro.estadoLabel}
                                                                    {registro.offline && (
                                                                        <FaHistory 
                                                                            title="Sincronización pendiente" 
                                                                            style={{ marginLeft: '6px', fontSize: '0.8em', verticalAlign: 'middle', animation: 'sync-spin 2s linear infinite' }} 
                                                                        />
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td style={{ display: isMobile ? 'none' : 'table-cell', padding: '10px 8px', fontSize: '0.75rem', color: '#64748b' }}>
                                                                {registro.generadoPor || 'Residente'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <footer className="vigilancia-modal-footer">
                            {/* Paginación */}
                            {filteredVisitHistory.length > HISTORY_ITEMS_PER_PAGE && (
                                <div className="history-pagination">
                                    <button
                                        type="button"
                                        className="btn-vigilancia-ghost"
                                        disabled={historyPage === 1}
                                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                                    >
                                        &lt; Anterior
                                    </button>
                                    <span className="pagination-info">
                                        Página {historyPage} de {Math.ceil(filteredVisitHistory.length / HISTORY_ITEMS_PER_PAGE)}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn-vigilancia-ghost"
                                        disabled={historyPage >= Math.ceil(filteredVisitHistory.length / HISTORY_ITEMS_PER_PAGE)}
                                        onClick={() => setHistoryPage((p) => Math.min(Math.ceil(filteredVisitHistory.length / HISTORY_ITEMS_PER_PAGE), p + 1))}
                                    >
                                        Siguiente &gt;
                                    </button>
                                </div>
                            )}
                            <button type="button" className="btn-secondary" onClick={handleCloseVisitHistoryModal}>
                                Cerrar
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {showMyQrModal && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content vigilancia-modal-large">
                        <header className="vigilancia-modal-header" style={{ backgroundColor: '#10b981' }}>
                            <h2><FaQrcode /> Mis Invitaciones QR</h2>
                            <button type="button" onClick={handleCloseMyQrModal} aria-label="Cerrar">×</button>
                        </header>
                        <div className="vigilancia-modal-body visit-history-body">
                            <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '0.95rem' }}>
                                {isSuperAdmin || isCaseta 
                                    ? 'Consulta y gestiona todos los códigos QR de acceso generados en el fraccionamiento.'
                                    : 'Aquí puedes ver y volver a descargar los códigos QR que has generado. Asegúrate de que estén vigentes para que funcionen.'}
                            </p>

                            <div className="visit-history-toolbar" style={{ marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'flex-start' }}>
                                <div className="search-box" style={{ flex: '1', margin: 0, position: 'relative' }}>
                                    <FaSearch style={{ 
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#94a3b8',
                                        pointerEvents: 'none',
                                        fontSize: '0.85rem'
                                    }} />
                                    <input
                                        type="text"
                                        placeholder="Buscar por nombre, fecha, depto o código..."
                                        value={searchQueryMyQr}
                                        onChange={(e) => setSearchQueryMyQr(e.target.value)}
                                        style={{ 
                                            paddingLeft: '35px', 
                                            borderRadius: '12px', 
                                            border: '1px solid #e2e8f0',
                                            height: '38px',
                                            fontSize: '0.85rem',
                                            width: '100%',
                                            backgroundColor: '#f8fafc',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                                <div className="filter-box" style={{ width: '150px' }}>
                                    <select
                                        value={statusFilterMyQr}
                                        onChange={(e) => setStatusFilterMyQr(e.target.value)}
                                        style={{
                                            height: '38px',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            padding: '0 10px',
                                            fontSize: '0.85rem',
                                            width: '100%',
                                            backgroundColor: '#f8fafc',
                                            cursor: 'pointer',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        <option value="Todos">Todos</option>
                                        <option value="Pendiente">Pendientes</option>
                                        <option value="Validado">Validados</option>
                                        <option value="Salida">Salidas</option>
                                        <option value="Cancelado">Cancelados</option>
                                    </select>
                                </div>
                            </div>
                            
                            {visitHistoryLoading ? (
                                <div className="visitas-hint visitas-hint-loading">
                                    <FaSearch />
                                    <span>{isSuperAdmin || isCaseta ? 'Buscando invitaciones del fraccionamiento…' : 'Buscando tus invitaciones…'}</span>
                                </div>
                            ) : (() => {
                                if (filteredMyInvitations.length === 0) {
                                    return (
                                        <div className="visitas-hint" style={{ 
                                            flexDirection: 'column', 
                                            gap: '20px', 
                                            padding: '60px 20px',
                                            opacity: 0.7 
                                        }}>
                                            <FaQrcode style={{ fontSize: '4rem', color: '#cbd5e1' }} />
                                            <span style={{ fontSize: '1.1rem', color: '#94a3b8', fontWeight: '500' }}>
                                                {searchQueryMyQr 
                                                    ? 'No se encontraron resultados para tu búsqueda.'
                                                    : (isSuperAdmin || isCaseta 
                                                        ? 'No se encontraron invitaciones QR registradas.'
                                                        : 'No tienes invitaciones QR generadas recientemente.')}
                                            </span>
                                        </div>
                                    );
                                }
                                return (
                                    <>
                                        <div className="table-container visit-history-table">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: isMobile ? '35%' : '20%', fontSize: isMobile ? '0.7rem' : 'inherit' }}>Visitante</th>
                                                        <th style={{ display: isMobile ? 'none' : 'table-cell', width: '12%' }}>Referencia</th>
                                                        <th style={{ width: isMobile ? '22%' : '18%', fontSize: isMobile ? '0.7rem' : 'inherit' }}>Fecha</th>
                                                        <th style={{ width: isMobile ? '18%' : '15%', fontSize: isMobile ? '0.7rem' : 'inherit' }}>Acceso</th>
                                                        <th style={{ width: isMobile ? '12%' : '15%', fontSize: isMobile ? '0.7rem' : 'inherit' }}>Estado</th>
                                                        <th style={{ width: isMobile ? '13%' : '20%', fontSize: isMobile ? '0.7rem' : 'inherit' }}>Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredMyInvitations
                                                        .slice((myQrPage - 1) * MY_QR_ITEMS_PER_PAGE, myQrPage * MY_QR_ITEMS_PER_PAGE)
                                                        .map((registro) => {
                                                        const estadoSlug = String(registro.estado || '').replace(/\s+/g, '-').toLowerCase();
                                                        const isExpanded = expandedQrId === registro.id;

                                                        // Lógica para columna de Acceso
                                                        const isMulti = registro.esMultiAcceso;
                                                        const hasLimit = registro.limiteSolicitudes > 1;
                                                        const scansLeft = registro.limiteSolicitudes - registro.escaneosActuales;
                                                        const isExpired = scansLeft <= 0 && hasLimit && !isMulti;
                                                        return (
                                                            <React.Fragment key={registro.id}>
                                                                <tr>
                                                                    <td style={{ padding: '8px 10px' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                                                            {registro.observaciones && (
                                                                                <button 
                                                                                    onClick={() => setExpandedQrId(isExpanded ? null : registro.id)}
                                                                                    style={{ 
                                                                                        background: 'none', 
                                                                                        border: 'none', 
                                                                                        color: '#f97316', 
                                                                                        cursor: 'pointer',
                                                                                        padding: '2px 0 0 0',
                                                                                        fontSize: '0.9rem',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        flexShrink: 0
                                                                                    }}
                                                                                    title="Ver descripción"
                                                                                >
                                                                                    {isExpanded ? <FaChevronUp /> : <FaInfoCircle />}
                                                                                </button>
                                                                            )}
                                                                            <div style={{ minWidth: 0 }}>
                                                                                <strong style={{ 
                                                                                    fontSize: isMobile ? '0.75rem' : '0.85rem',
                                                                                    display: 'block',
                                                                                    lineHeight: '1.2'
                                                                                }}>
                                                                                    {registro.visitanteNombre || registro.visitante || 'Visitante sin nombre'}
                                                                                </strong>
                                                                                <div className="visit-history-sub" style={{ fontSize: '0.7rem', color: '#64748b' }}>
                                                                                    <span>{registro.departamento || registro.numeroCasa || 'Sin asignado'}</span>
                                                                                </div>
                                                                                {isMobile && (
                                                                                    <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '1px' }}>
                                                                                        Ref: {registro.codigo}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ display: isMobile ? 'none' : 'table-cell', fontSize: '0.8rem' }}>{registro.codigo}</td>
                                                                    <td style={{ padding: '8px 10px' }}>
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                                                            <span style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', fontWeight: '500' }}>{registro.fechaVisitaDiaLabel}</span>
                                                                            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{registro.horaVisitaLabel}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '8px 10px' }}>
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                                            {isMulti ? (
                                                                                <span style={{ 
                                                                                    fontSize: '0.65rem', 
                                                                                    backgroundColor: '#e0e7ff', 
                                                                                    color: '#4338ca', 
                                                                                    padding: '2px 6px', 
                                                                                    borderRadius: '4px',
                                                                                    fontWeight: '700',
                                                                                    width: 'fit-content'
                                                                                }}>
                                                                                    MULTI ({registro.duracionHoras}h)
                                                                                </span>
                                                                            ) : hasLimit ? (
                                                                                <span style={{ 
                                                                                    fontSize: '0.65rem', 
                                                                                    backgroundColor: isExpired ? '#fee2e2' : '#f0fdf4', 
                                                                                    color: isExpired ? '#991b1b' : '#166534',
                                                                                    padding: '2px 6px', 
                                                                                    borderRadius: '4px',
                                                                                    fontWeight: '700',
                                                                                    width: 'fit-content'
                                                                                }}>
                                                                                    {registro.escaneosActuales} / {registro.limiteSolicitudes} USOS
                                                                                </span>
                                                                            ) : (
                                                                                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Único</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '8px 10px' }}>
                                                                        <span className={`status-pill status-${estadoSlug || 'pendiente'}`} style={{
                                                                            fontSize: '0.6rem',
                                                                            padding: '1px 6px'
                                                                        }}>
                                                                            {isMobile ? (
                                                                                registro.estadoLabel === 'Pendiente' ? 'Pnd' :
                                                                                registro.estadoLabel === 'Validado' ? 'Val' :
                                                                                registro.estadoLabel === 'Salida' ? 'Sal' :
                                                                                registro.estadoLabel === 'Cancelado' ? <FaTimesCircle title="Cancelado" /> :
                                                                                registro.estadoLabel
                                                                            ) : registro.estadoLabel}
                                                                        </span>
                                                                    </td>
                                                                <td>
                                                                    {estadoSlug === 'pendiente' && (
                                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                                            <button 
                                                                                className="btn-qr-save-zen" 
                                                                                title="Descargar QR"
                                                                                onClick={async () => {
                                                                                    let dataUrl = registro.qrDataUrl;
                                                                                    if (!dataUrl && registro.codigo) {
                                                                                        try {
                                                                                            const QRCode = (await import('qrcode')).default;
                                                                                            dataUrl = await QRCode.toDataURL(registro.codigo, {
                                                                                                width: 512,
                                                                                                margin: 2,
                                                                                                color: { dark: '#000000', light: '#ffffff' }
                                                                                            });
                                                                                        } catch (qrErr) {
                                                                                            console.error('Error regenerando QR:', qrErr);
                                                                                        }
                                                                                    }
                                                                                    
                                                                                    if (dataUrl) {
                                                                                        const downloadData = {
                                                                                            qrDataUrl: dataUrl,
                                                                                            codigo: registro.codigo,
                                                                                            visitanteNombre: registro.visitanteNombre || registro.visitante,
                                                                                            visitanteDepto: registro.departamento || registro.numeroCasa,
                                                                                            fechaVisitaLabel: registro.fechaVisitaLabel
                                                                                        };
                                                                                        setQrResult(downloadData);
                                                                                        handleDownloadQR(downloadData);
                                                                                    } else {
                                                                                        showNotification('El código base no está disponible para descarga directa.', 'info');
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <FaDownload />
                                                                            </button>
                                                                            <button 
                                                                                className="btn-qr-cancel-zen" 
                                                                                title="Cancelar Invitación"
                                                                                onClick={() => handleCancelarInvitacion(registro)}
                                                                            >
                                                                                <FaTimesCircle />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {isExpanded && registro.observaciones && (
                                                                <tr key={`${registro.id}-desc`} style={{ backgroundColor: '#fff7ed' }}>
                                                                    <td colSpan="5" style={{ padding: '10px 15px', borderBottom: '1px solid #ffedd5' }}>
                                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                                                            <FaInfoCircle style={{ color: '#f97316', marginTop: '3px', flexShrink: 0 }} />
                                                                            <span style={{ fontSize: '0.8rem', color: '#9a3412', lineHeight: '1.4' }}>
                                                                                <strong>Descripción:</strong> {registro.observaciones}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Paginación */}
                                        {filteredMyInvitations.length > MY_QR_ITEMS_PER_PAGE && (
                                            <div className="history-pagination" style={{ marginTop: '20px', justifyContent: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="btn-vigilancia-ghost"
                                                    disabled={myQrPage === 1}
                                                    onClick={() => setMyQrPage((p) => Math.max(1, p - 1))}
                                                >
                                                    <FaChevronLeft /> {isMobile ? '' : 'Anterior'}
                                                </button>
                                                <span className="pagination-info" style={{ margin: isMobile ? '0 10px' : '0 20px', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                                                    {isMobile ? `${myQrPage} / ${Math.ceil(filteredMyInvitations.length / MY_QR_ITEMS_PER_PAGE)}` : `Página ${myQrPage} de ${Math.ceil(filteredMyInvitations.length / MY_QR_ITEMS_PER_PAGE)}`}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn-vigilancia-ghost"
                                                    disabled={myQrPage >= Math.ceil(filteredMyInvitations.length / MY_QR_ITEMS_PER_PAGE)}
                                                    onClick={() => setMyQrPage((p) => Math.min(Math.ceil(filteredMyInvitations.length / MY_QR_ITEMS_PER_PAGE), p + 1))}
                                                >
                                                    {isMobile ? '' : 'Siguiente'} <FaChevronRight />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        <footer className="vigilancia-modal-footer">
                            <button type="button" className="btn-secondary" onClick={handleCloseMyQrModal} style={{ backgroundColor: '#4f46e5', color: 'white' }}>
                                Cerrar
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {detalleVisita && (
                <div className="vigilancia-modal" role="dialog" aria-modal="true">
                    <div className="vigilancia-modal-content vigilancia-modal-large" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                        <header className="vigilancia-modal-header light-theme" style={{
                            background: '#ffffff',
                            borderBottom: 'none',
                            padding: '1.5rem 2rem 1rem 2rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            {/* Título en color NEGRO explícito */}
                            <h2 style={{
                                color: '#000000',
                                fontSize: '1.5rem',
                                fontWeight: '800',
                                margin: 0,
                                textShadow: 'none'
                            }}>Detalle de visita frecuente</h2>
                            <button
                                type="button"
                                onClick={handleCloseDetalleVisita}
                                aria-label="Cerrar"
                                disabled={detalleSubmitting || detalleDeleting}
                                style={{
                                    background: 'transparent',
                                    color: '#64748b',
                                    fontSize: '1.5rem',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                ×
                            </button>
                        </header>
                        <div className="vigilancia-modal-body detalle-visita-body" style={{ padding: '0 2rem 2rem 2rem' }}>
                            {detalleError && (
                                <div className="visitas-modal-error" role="alert">{detalleError}</div>
                            )}

                            {detalleVisita ? (
                                <>
                                    {/* SECCIÓN: REGISTRADO POR */}
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            color: '#94a3b8',
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px'
                                        }}>
                                            Registrado por
                                        </div>
                                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a' }}>
                                            {detalleVisita.ownerNombre || 'Residente'}
                                        </div>
                                        <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                                            Departamento {detalleVisita.ownerDepartamento || 'Sin dato'}
                                        </div>
                                    </div>

                                    {!detalleEditMode ? (
                                        <>
                                            {/* GRID PRINCIPAL: 3 Columnas mejorado */}
                                            <div className="detalle-visita-grid" style={{
                                                gap: '1.5rem',
                                                marginBottom: '2rem'
                                            }}>
                                                {/* NOMBRE */}
                                                <div>
                                                    <div className="detalle-label">NOMBRE</div>
                                                    <div className="detalle-value">
                                                        {detalleVisita.nombre || 'Sin dato'}
                                                    </div>
                                                </div>

                                                {/* TELÉFONO */}
                                                <div>
                                                    <div className="detalle-label">TELÉFONO</div>
                                                    <div className="detalle-value">
                                                        {detalleVisita.telefono || '—'}
                                                    </div>
                                                </div>

                                                {/* CATEGORÍA */}
                                                <div>
                                                    <div className="detalle-label">CATEGORÍA</div>
                                                    <div>
                                                        <span className={`status-pill ${getCategoriaBadgeClass(detalleVisita.categoria)}`}
                                                            style={{
                                                                fontSize: '0.85rem',
                                                                padding: '4px 12px',
                                                                borderRadius: '20px',
                                                                textTransform: 'uppercase',
                                                                fontWeight: '700'
                                                            }}>
                                                            {formatCategoriaLabel(detalleVisita.categoria)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* PLACAS */}
                                                <div>
                                                    <div className="detalle-label">PLACAS</div>
                                                    <div className="detalle-value">
                                                        {detalleVisita.placa || '—'}
                                                    </div>
                                                </div>

                                                {/* COLOR */}
                                                <div>
                                                    <div className="detalle-label">COLOR</div>
                                                    <div className="detalle-value">
                                                        {detalleVisita.color || '—'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* NOTAS */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <div style={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: '700',
                                                    color: '#94a3b8',
                                                    letterSpacing: '0.05em',
                                                    textTransform: 'uppercase',
                                                    marginBottom: '4px'
                                                }}>
                                                    Notas
                                                </div>
                                                <div style={{ fontSize: '0.95rem', color: '#334155' }}>
                                                    {detalleVisita.notas || 'Sin notas'}
                                                </div>
                                            </div>

                                            {/* RESUMEN DE HORARIO (Zen) */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                {renderScheduleSummary(detalleVisita.horario)}
                                            </div>

                                            {/* FOOTER INFO: Registrado, Actualizado, Identificación */}
                                            <div style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '2rem',
                                                paddingTop: '1.5rem',
                                                borderTop: '1px solid #f1f5f9'
                                            }}>
                                                <div>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: '700',
                                                        color: '#94a3b8',
                                                        letterSpacing: '0.05em',
                                                        textTransform: 'uppercase',
                                                        marginBottom: '2px'
                                                    }}>
                                                        Registrado
                                                    </div>
                                                    <div style={{ fontSize: '0.9rem', color: '#475569' }}>
                                                        {detalleVisita.createdAt ? formatVisitaDate(detalleVisita.createdAt) : 'Sin dato'}
                                                    </div>
                                                </div>

                                                <div>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: '700',
                                                        color: '#94a3b8',
                                                        letterSpacing: '0.05em',
                                                        textTransform: 'uppercase',
                                                        marginBottom: '2px'
                                                    }}>
                                                        Actualizado
                                                    </div>
                                                    <div style={{ fontSize: '0.9rem', color: '#475569' }}>
                                                        {detalleVisita.updatedAt ? formatVisitaDate(detalleVisita.updatedAt) : 'Sin dato'}
                                                    </div>
                                                </div>

                                                <div>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: '700',
                                                        color: '#94a3b8',
                                                        letterSpacing: '0.05em',
                                                        textTransform: 'uppercase',
                                                        marginBottom: '2px'
                                                    }}>
                                                        Identificación
                                                    </div>
                                                    {detalleVisita.identificacionUrl ? (
                                                        <a
                                                            href={detalleVisita.identificacionUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                textDecoration: 'none',
                                                                color: '#3b82f6',
                                                                fontSize: '0.9rem',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            <FaIdCard />
                                                            <span>
                                                                {detalleVisita.identificacionName || 'Ver archivo'}
                                                                {detalleVisita.identificacionSize ? ` · ${formatFileSize(detalleVisita.identificacionSize)}` : ''}
                                                            </span>
                                                        </a>
                                                    ) : (
                                                        <span style={{ fontSize: '0.9rem', color: '#94a3b8', fontStyle: 'italic' }}>No adjuntada</span>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="detalle-visita-edit">
                                            <div className="form-group">
                                                <label htmlFor="detalle-nombre">Nombre *</label>
                                                <input
                                                    id="detalle-nombre"
                                                    name="nombre"
                                                    type="text"
                                                    value={detalleForm?.nombre || ''}
                                                    onChange={handleDetalleInputChange}
                                                    disabled={detalleSubmitting}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="detalle-telefono">Teléfono *</label>
                                                <input
                                                    id="detalle-telefono"
                                                    name="telefono"
                                                    type="tel"
                                                    value={detalleForm?.telefono || ''}
                                                    onChange={handleDetalleInputChange}
                                                    disabled={detalleSubmitting}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="detalle-categoria">Categoría *</label>
                                                <select
                                                    id="detalle-categoria"
                                                    name="categoria"
                                                    value={detalleForm?.categoria || ''}
                                                    onChange={handleDetalleInputChange}
                                                    disabled={detalleSubmitting}
                                                >
                                                    <option value="">Selecciona una categoría</option>
                                                    {VISITA_CATEGORIES.map((categoria) => (
                                                        <option key={categoria.value} value={categoria.value}>{categoria.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="detalle-placa">Placas</label>
                                                <input
                                                    id="detalle-placa"
                                                    name="placa"
                                                    type="text"
                                                    value={detalleForm?.placa || ''}
                                                    onChange={handleDetalleInputChange}
                                                    disabled={detalleSubmitting}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="detalle-color">Color</label>
                                                <input
                                                    id="detalle-color"
                                                    name="color"
                                                    type="text"
                                                    value={detalleForm?.color || ''}
                                                    onChange={handleDetalleInputChange}
                                                    disabled={detalleSubmitting}
                                                />
                                            </div>
                                            <div className="form-group span-full">
                                                <label htmlFor="detalle-notas">Notas</label>
                                                <textarea
                                                    id="detalle-notas"
                                                    name="notas"
                                                    rows="3"
                                                    value={detalleForm?.notas || ''}
                                                    onChange={handleDetalleInputChange}
                                                    disabled={detalleSubmitting}
                                                ></textarea>
                                            </div>

                                            <div className="form-group span-full" style={{ marginTop: '1rem', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                                                    <FaClock style={{ color: '#4f46e5' }} />
                                                    <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>Programación de horarios</h4>
                                                </div>
                                                
                                                <div className="detalle-horario-editor" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                    {DIAS_SEMANA_ORDEN.map((dia) => (
                                                        <div key={dia} style={{ 
                                                            padding: '12px', 
                                                            backgroundColor: (detalleForm?.horario?.[dia]?.length > 0) ? '#f5f3ff' : '#f8fafc',
                                                            borderRadius: '12px',
                                                            border: (detalleForm?.horario?.[dia]?.length > 0) ? '1px solid #ddd6fe' : '1px solid #e2e8f0'
                                                        }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: (detalleForm?.horario?.[dia]?.length > 0) ? '10px' : '0' }}>
                                                                <span style={{ fontWeight: '700', textTransform: 'capitalize', color: (detalleForm?.horario?.[dia]?.length > 0) ? '#5b21b6' : '#64748b' }}>
                                                                    {dia}
                                                                </span>
                                                                {(detalleForm?.horario?.[dia]?.length || 0) < 3 && (
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => handleDetalleAddInterval(dia)}
                                                                        style={{ 
                                                                            padding: '4px 10px', 
                                                                            fontSize: '0.75rem', 
                                                                            borderRadius: '6px', 
                                                                            background: '#4f46e5', 
                                                                            color: 'white', 
                                                                            border: 'none',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        <FaPlus /> Agregar rango
                                                                    </button>
                                                                )}
                                                            </div>
                                                            
                                                            {detalleForm?.horario?.[dia]?.map((intervalo, idx) => (
                                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                                    <select
                                                                        value={intervalo.inicio}
                                                                        onChange={(e) => handleDetalleHorarioChange(dia, idx, 'inicio', e.target.value)}
                                                                        style={{ padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                                                                    >
                                                                        {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                                                                    </select>
                                                                    <span style={{ fontSize: '0.8rem' }}>a</span>
                                                                    <select
                                                                        value={intervalo.fin}
                                                                        onChange={(e) => handleDetalleHorarioChange(dia, idx, 'fin', e.target.value)}
                                                                        style={{ padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                                                                    >
                                                                        {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                                                                    </select>
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => handleDetalleRemoveInterval(dia, idx)}
                                                                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px' }}
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p>El registro seleccionado ya no está disponible.</p>
                            )}
                        </div>
                        <footer className="vigilancia-modal-footer">
                            {isSuperAdmin ? (
                                <>
                                    <button
                                        type="button"
                                        className={`btn-danger ${detalleConfirmDelete ? 'btn-danger-confirm' : ''}`}
                                        onClick={handleDetalleDelete}
                                        disabled={detalleSubmitting || detalleDeleting}
                                    >
                                        {detalleDeleting ? 'Eliminando…' : detalleConfirmDelete ? 'Confirmar eliminación' : <><FaTrash /> Eliminar</>}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primary" // Cambiado a primary (azul) para Editar
                                        onClick={() => {
                                            if (detalleEditMode) {
                                                handleToggleDetalleEdit(); // Cancelar edición
                                            } else {
                                                handleToggleDetalleEdit(); // Entrar en modo edición
                                            }
                                        }}
                                        disabled={detalleSubmitting || detalleDeleting}
                                        style={{ backgroundColor: detalleEditMode ? '#64748b' : '#3b82f6', borderColor: 'transparent' }} // Gris si es cancelar, Azul si es editar
                                    >
                                        {detalleEditMode ? 'Cancelar edición' : <><FaEdit /> Editar</>}
                                    </button>
                                    {detalleEditMode && (
                                        <button
                                            type="button"
                                            className="btn-vigilancia-action"
                                            onClick={handleDetalleGuardar}
                                            disabled={detalleSubmitting}
                                            style={{ backgroundColor: '#a855f7', color: 'white', border: 'none' }} // Morado para guardar
                                        >
                                            {detalleSubmitting ? 'Guardando…' : 'Guardar cambios'}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleCloseDetalleVisita}
                                    disabled={detalleSubmitting || detalleDeleting}
                                >
                                    Cerrar
                                </button>
                            )}
                        </footer>
                    </div>
                </div>
            )
            }

            {
                !isResident && showProgramacionesModal && (
                    <div className="vigilancia-modal" role="dialog" aria-modal="true">
                        <div className="vigilancia-modal-content">
                            <header className="vigilancia-modal-header">
                                <h2><FaCalendarAlt /> Vehículos y proveedores programados</h2>
                                <button type="button" onClick={() => setShowProgramacionesModal(false)} aria-label="Cerrar">×</button>
                            </header>
                            <div className="vigilancia-modal-body">
                                {programacionesAcceso.length === 0 ? (
                                    <p>No hay movimientos autorizados para mostrar.</p>
                                ) : (
                                    <ul className="mudanza-modal-list">
                                        {programacionesAcceso.map((programacion) => (
                                            <li key={programacion.id}>
                                                <div>
                                                    <strong>{programacion.etiquetaTipo}</strong>
                                                    <span>{formatProgramacionFecha(programacion)}</span>
                                                </div>
                                                <div>{programacion.residente} · {programacion.ubicacion}</div>
                                                {programacion.proveedor && <div>Proveedor: {programacion.proveedor}</div>}
                                                {formatProgramacionDuracion(programacion) && (
                                                    <div>{formatProgramacionDuracion(programacion)}</div>
                                                )}
                                                {programacion.notas && (
                                                    <div>{programacion.notas}</div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <footer className="vigilancia-modal-footer">
                                <button type="button" className="btn-secondary" onClick={() => setShowProgramacionesModal(false)}>Cerrar</button>
                            </footer>
                        </div>
                    </div>
                )
            }

            {
                !isResident && showPaqueteriaModal && (
                    <div className="vigilancia-modal" role="dialog" aria-modal="true">
                        <div className="vigilancia-modal-content vigilancia-modal-large" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <header className="vigilancia-modal-header light-theme" style={{
                                background: '#ffffff',
                                borderBottom: 'none',
                                padding: '1.5rem 2rem 1rem 2rem',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h2 style={{
                                    color: '#000000',
                                    fontSize: '1.5rem',
                                    fontWeight: '800',
                                    margin: 0,
                                    textShadow: 'none'
                                }}>Paquetería registrada</h2>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowPaqueteriaModal(false);
                                        // Si estamos en móvil y la sección estaba seleccionada, volver al menú
                                        if (isMobile && selectedSection === 'paqueteria') {
                                            setSelectedSection(null);
                                        }
                                    }}
                                    aria-label="Cerrar"
                                    style={{
                                        background: 'transparent',
                                        color: '#64748b',
                                        fontSize: '1.5rem',
                                        border: 'none',
                                        cursor: 'pointer'
                                    }}
                                >×</button>
                            </header>
                            <div className="vigilancia-modal-body" style={{ padding: '0 2rem 2rem 2rem' }}>
                                <div className="paqueteria-filters-container" style={{ 
                                    display: 'flex', 
                                    gap: '12px', 
                                    marginBottom: '20px', 
                                    flexWrap: 'wrap',
                                    alignItems: 'center'
                                }}>
                                    <div className="paqueteria-search-wrapper" style={{ 
                                        flex: '1', 
                                        minWidth: '200px',
                                        position: 'relative'
                                    }}>
                                        <FaSearch style={{ 
                                            position: 'absolute', 
                                            left: '12px', 
                                            top: '50%', 
                                            transform: 'translateY(-50%)', 
                                            color: '#94a3b8' 
                                        }} />
                                        <input
                                            type="text"
                                            className="paqueteria-search-input"
                                            placeholder="Buscar por Depto, Residente o Empresa..."
                                            value={paqueteriaSearch}
                                            onChange={(e) => setPaqueteriaSearch(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '10px 10px 10px 35px',
                                                borderRadius: '12px',
                                                border: '1.5px solid #e2e8f0',
                                                fontSize: '0.85rem',
                                                backgroundColor: '#f8fafc',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div className="paqueteria-date-filter" style={{ width: isMobile ? '100%' : '160px', position: 'relative' }}>
                                        {isMobile && (
                                            <FaCalendarAlt style={{ 
                                                position: 'absolute', 
                                                left: '8px', 
                                                top: '50%', 
                                                transform: 'translateY(-50%)', 
                                                color: '#94a3b8',
                                                fontSize: '0.8rem',
                                                pointerEvents: 'none',
                                                zIndex: 1
                                            }} />
                                        )}
                                        <input
                                            type="date"
                                            className="paqueteria-date-input"
                                            value={paqueteriaSearchDate}
                                            onChange={(e) => setPaqueteriaSearchDate(e.target.value)}
                                            style={{
                                                width: '100%',
                                                height: '41px',
                                                padding: isMobile ? '0 5px 0 25px' : '0 10px',
                                                borderRadius: '12px',
                                                border: '1.5px solid #e2e8f0',
                                                fontSize: '0.85rem',
                                                backgroundColor: '#f8fafc',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <label className="paqueteria-checkbox-wrapper" style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px', 
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        fontSize: '0.85rem',
                                        color: '#64748b'
                                    }}>
                                        <input
                                            type="checkbox"
                                            className="paqueteria-checkbox"
                                            checked={paqueteriaFilterPendiente}
                                            onChange={(e) => setPaqueteriaFilterPendiente(e.target.checked)}
                                        />
                                        <span className="paqueteria-checkbox-label">{isMobile ? 'Pnd' : 'Solo pendientes'}</span>
                                    </label>
                                </div>

                                {paqueteriaLoading ? (
                                    <p>Cargando paquetería…</p>
                                ) : paqueteriaListaError ? (
                                    <div className="paqueteria-hint paqueteria-hint-error">{paqueteriaListaError}</div>
                                ) : paqueteriaFiltrada.length === 0 ? (
                                    <p>No se encontraron paquetes con los filtros actuales.</p>
                                ) : (
                                    <div className="paqueteria-modal-cards-container">
                                        {paqueteriaPaginada.map((registro) => {
                                            const estadoSlug = String(registro.estado || 'pendiente').toLowerCase().replace(/\s+/g, '-');
                                            const isEntregado = registro.estado === 'entregado';
                                            return (
                                                <div key={registro.id} className="paqueteria-modal-card">
                                                    <div className="paqueteria-card-header">
                                                        <span className="paqueteria-card-title">Departamento {registro.departamento || 'Sin dato'}</span>
                                                        <span className={`status-pill status-${estadoSlug}`}>{getPackageStatusLabel(registro.estado)}</span>
                                                    </div>
                                                    <div className="paqueteria-card-body">
                                                        <div className="paqueteria-card-details">
                                                            <div className="paqueteria-detail-row">
                                                                <span className="paqueteria-detail-label">Residente:</span>
                                                                <span className="paqueteria-detail-value">{registro.destinatario || 'Sin nombre'}</span>
                                                            </div>
                                                            <div className="paqueteria-detail-row">
                                                                <span className="paqueteria-detail-label">Empresa:</span>
                                                                <span className="paqueteria-detail-value">{registro.empresa || 'Paquetería'}</span>
                                                            </div>
                                                            <div className="paqueteria-detail-row">
                                                                <span className="paqueteria-detail-label">FECHA:</span>
                                                                <span className="paqueteria-detail-value">{registro.fechaRecepcion ? formatResidentPackageDate(registro.fechaRecepcion) : 'Sin fecha'}</span>
                                                            </div>
                                                            <div className="paqueteria-detail-row">
                                                                <span className="paqueteria-detail-label">RECIBIDO:</span>
                                                                <span className="paqueteria-detail-value">{registro.guardiaRecibio || 'N/A'}</span>
                                                            </div>
                                                            {isEntregado && (
                                                                <>
                                                                    <div className="paqueteria-detail-row">
                                                                        <span className="paqueteria-detail-label">FECHA ENTREGA:</span>
                                                                        <span className="paqueteria-detail-value">{formatResidentPackageDate(registro.entregadoEn)}</span>
                                                                    </div>
                                                                    <div className="paqueteria-detail-row">
                                                                        <span className="paqueteria-detail-label">ENTREGADO POR:</span>
                                                                        <span className="paqueteria-detail-value">{typeof registro.guardiaEntrego === 'object' && registro.guardiaEntrego !== null ? registro.guardiaEntrego.nombre : (registro.guardiaEntrego || 'Vigilancia')}</span>
                                                                    </div>
                                                                </>
                                                            )}
                                                            {registro.comentarios && (
                                                                <div className="paqueteria-detail-row full-width">
                                                                    <span className="paqueteria-detail-label">Comentarios:</span>
                                                                    <span className="paqueteria-detail-value">{registro.comentarios}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {!isEntregado && (
                                                            <div className="paqueteria-card-actions">
                                                                <button
                                                                    type="button"
                                                                    className="btn-paqueteria-action-purple"
                                                                    onClick={() => handleMarkPackageDelivered(registro)}
                                                                    disabled={paqueteriaUpdatingId === registro.id}
                                                                >
                                                                    {paqueteriaUpdatingId === registro.id ? 'Actualizando…' : 'Marcar como entregado'}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {totalPaqueteriaPages > 1 && (
                                            <div className="paquetes-pagination">
                                                <button
                                                    type="button"
                                                    className="btn-pagination"
                                                    disabled={paqueteriaPage === 1}
                                                    onClick={() => setPaqueteriaPage(prev => Math.max(1, prev - 1))}
                                                >
                                                    <FaChevronLeft /> {isMobile ? '' : 'Anterior'}
                                                </button>
                                                <span className="pagination-info">
                                                    {isMobile ? `${paqueteriaPage} / ${totalPaqueteriaPages}` : `Página ${paqueteriaPage} de ${totalPaqueteriaPages}`}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn-pagination"
                                                    disabled={paqueteriaPage === totalPaqueteriaPages}
                                                    onClick={() => setPaqueteriaPage(prev => Math.min(totalPaqueteriaPages, prev + 1))}
                                                >
                                                    {isMobile ? '' : 'Siguiente'} <FaChevronRight />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <footer className="vigilancia-modal-footer" style={{ borderTop: '1px solid #f1f5f9', padding: '1.25rem 2rem' }}>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => {
                                        setShowPaqueteriaModal(false);
                                        // Si estamos en móvil y la sección estaba seleccionada, volver al menú
                                        if (isMobile && selectedSection === 'paqueteria') {
                                            setSelectedSection(null);
                                        }
                                    }}
                                    style={{ width: '100%', fontWeight: '700' }}
                                >Cerrar</button>
                            </footer>
                        </div>
                    </div>
                )
            }

            {/* Modal de captura de evidencia para entrega de paquetes */}
            {
                showCapturaEvidencia && paqueteParaEntregar && (
                    <CapturaEvidenciaEntrega
                        onComplete={handleCompletarEvidencia}
                        onCancel={handleCancelarEvidencia}
                        beneficiario={paqueteParaEntregar.destinatario || `Depto ${paqueteParaEntregar.depto}`}
                        paqueteInfo={paqueteParaEntregar}
                    />
                )
            }

            {/* Modal de captura intermedia de datos del vehículo */}
            {
                showCapturaDatosVehiculo && datosAccesoManualPendiente && (
                    <div className="vigilancia-modal-overlay" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        backdropFilter: 'blur(4px)',
                        padding: '16px'
                    }}>
                        <div className="vigilancia-modal-content" style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            width: '100%',
                            maxWidth: '450px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '90vh',
                            padding: 0
                        }}>
                            <header className="vigilancia-modal-header" style={{
                                padding: '16px 20px',
                                borderBottom: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h2 style={{ fontSize: '1.2rem', color: '#0f172a', margin: 0, fontWeight: 700 }}>
                                    🚙 Registro de Transporte
                                </h2>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCapturaDatosVehiculo(false);
                                        setDatosAccesoManualPendiente(null);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.2rem',
                                        color: '#94a3b8',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <FaTimes />
                                </button>
                            </header>

                            <div style={{ padding: '20px', overflowY: 'auto' }}>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 0, marginBottom: '16px' }}>
                                    Confirma el medio de transporte del visitante <strong>{datosAccesoManualPendiente.visitante}</strong> y completa sus datos para el acceso.
                                </p>

                                <div className="form-group" style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label htmlFor="vehiculo-tipoTransporte" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155' }}>
                                        Medio de transporte
                                    </label>
                                    <select
                                        id="vehiculo-tipoTransporte"
                                        value={datosVehiculoForm.tipoTransporte}
                                        onChange={(e) => setDatosVehiculoForm(prev => ({
                                            ...prev,
                                            tipoTransporte: e.target.value
                                        }))}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        <option value="En vehiculo">En vehículo</option>
                                        <option value="En moto">En moto</option>
                                        <option value="A pie">A pie</option>
                                    </select>
                                </div>

                                {datosVehiculoForm.tipoTransporte !== 'A pie' && (
                                    <>
                                        <div className="form-group" style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label htmlFor="vehiculo-placa" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155' }}>
                                                Placas
                                            </label>
                                            <input
                                                id="vehiculo-placa"
                                                type="text"
                                                placeholder="ABC-123"
                                                value={datosVehiculoForm.placa}
                                                onChange={(e) => setDatosVehiculoForm(prev => ({
                                                    ...prev,
                                                    placa: e.target.value.toUpperCase()
                                                }))}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '0.9rem'
                                                }}
                                                required
                                            />
                                        </div>

                                        <div className="form-group" style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label htmlFor="vehiculo-color" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155' }}>
                                                Color
                                            </label>
                                            <select
                                                id="vehiculo-color"
                                                value={datosVehiculoForm.color}
                                                onChange={(e) => setDatosVehiculoForm(prev => ({
                                                    ...prev,
                                                    color: e.target.value
                                                }))}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '0.9rem'
                                                }}
                                                required
                                            >
                                                <option value="">Selecciona un color</option>
                                                {VEHICLE_COLORS.map((col) => (
                                                    <option key={col} value={col}>{col}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </>
                                )}
                            </div>

                            <footer style={{
                                padding: '16px 20px',
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCapturaDatosVehiculo(false);
                                        setDatosAccesoManualPendiente(null);
                                    }}
                                    style={{
                                        padding: '10px 16px',
                                        backgroundColor: '#f1f5f9',
                                        color: '#475569',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleConfirmarDatosVehiculo()}
                                    disabled={
                                        datosVehiculoForm.tipoTransporte !== 'A pie' && 
                                        (!datosVehiculoForm.placa.trim() || !datosVehiculoForm.color)
                                    }
                                    style={{
                                        padding: '10px 16px',
                                        backgroundColor: '#4f46e5',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        opacity: (datosVehiculoForm.tipoTransporte !== 'A pie' && (!datosVehiculoForm.placa.trim() || !datosVehiculoForm.color)) ? 0.6 : 1
                                    }}
                                >
                                    Continuar
                                </button>
                            </footer>
                        </div>
                    </div>
                )
            }

            {/* Modal de captura de imágenes para acceso manual */}
            {
                showCapturaImagenesManual && datosAccesoManualPendiente && (
                    <CapturaImagenesVisita
                        onComplete={handleCompletarCapturaManual}
                        onCancel={handleCancelarCapturaManual}
                        onExit={handleExitCapturaManual}
                        visitanteNombre={datosAccesoManualPendiente.visitante || 'Visitante'}
                        tipoTransporte={datosAccesoManualPendiente.tipoTransporte || 'En vehiculo'}
                    />
                )
            }

            {/* Modal de éxito con cierre automático (12 segundos para QR según requerimiento) */}
            <SuccessModal
                open={showSuccessModal}
                title={successModalData.title}
                message={successModalData.message}
                duration={12000}
                onClose={() => setShowSuccessModal(false)}
            />

            {/* Modal de confirmación de salida */}
            <ConfirmDialog
                open={!!exitConfirmAccess}
                title="Concluir visita"
                message={
                    <span>
                        ¿Confirmas que la visita de <strong>{exitConfirmAccess?.visitante || 'este visitante'}</strong> ha finalizado?
                        <br /><br />
                        Al registrar la salida, el registro de acceso se cerrará formalmente.
                    </span>
                }
                confirmLabel="Sí, registrar salida"
                cancelLabel="Aún no"
                onConfirm={() => performRegisterGuestExit(exitConfirmAccess)}
                onCancel={() => setExitConfirmAccess(null)}
                theme="premium"
                tone="success"
            />

            {/* Modal de confirmación de cancelación de QR */}
            <ConfirmDialog
                open={showCancelQrConfirm}
                title="Cancelar Invitación"
                message={
                    <span>
                        ¿Estás seguro de que deseas cancelar la invitación para <strong>"{invitacionToCancel?.visitante}"</strong>?
                        <br /><br />
                        Al confirmar, el código QR será invalidado y ya no podrá utilizarse para ingresar al fraccionamiento.
                    </span>
                }
                confirmLabel="Sí, Cancelar Invitación"
                cancelLabel="Mantener Activa"
                onConfirm={executeCancelarInvitacion}
                onCancel={() => {
                    setShowCancelQrConfirm(false);
                    setInvitacionToCancel(null);
                }}
                theme="premium"
                tone="danger"
            />

            <ConfirmDialog
                open={showDeleteVisitaConfirm}
                title="Eliminar Visita Frecuente"
                message={
                    <span>
                        ¿Estás seguro de que deseas eliminar a <strong>"{visitaToDelete?.nombre}"</strong>?
                        <br /><br />
                        Se borrarán todos sus datos y su identificación del sistema. Esta acción no se puede deshacer.
                    </span>
                }
                confirmLabel="Sí, Eliminar Permanentemente"
                cancelLabel="Cancelar"
                onConfirm={executeDeleteVisita}
                onCancel={() => {
                    setShowDeleteVisitaConfirm(false);
                    setVisitaToDelete(null);
                }}
                theme="premium"
                tone="danger"
            />

            {showScheduleConfirm && (
                <div className="schedule-confirm-overlay" role="dialog" aria-modal="true">
                    <div className="schedule-confirm-modal">
                        <header className="schedule-confirm-header">
                            <div className="confirm-header-icon">
                                <FaHistory />
                            </div>
                            <h3>¿Usar configuración guardada?</h3>
                        </header>
                        
                        <div className="schedule-confirm-body">
                            <p>
                                Hemos detectado que <strong>{pendingVisita?.nombre}</strong> tiene horarios de acceso configurados en su perfil.
                            </p>
                            <div className="confirm-hint">
                                <p>Si usas esta configuración, el QR será válido por 7 días siguiendo los rangos específicos del perfil.</p>
                            </div>
                        </div>

                        <footer className="schedule-confirm-footer">
                            <button 
                                type="button" 
                                className="btn-confirm" 
                                onClick={confirmUseSchedule}
                            >
                                Sí, usar horario guardado
                            </button>
                            <button 
                                type="button" 
                                className="btn-decline" 
                                onClick={declineUseSchedule}
                            >
                                No, llenar manualmente
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div >
    );
};

export default Vigilancia;
